import React, { useState, useRef, useEffect, useCallback, useMemo, createContext, useContext } from "react";


import { BrowserRouter, Routes, Route, NavLink, useNavigate, useLocation, Navigate } from "react-router-dom";


import Papa from "papaparse";


import TimingoflowLogo from "./Timingoflowlogo.png";


import {


  BarChart as ReBarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, AreaChart, Area, LineChart, Line,
  PieChart as RePieChart, Pie, Cell, ResponsiveContainer,


} from "recharts";





// ─── Constants ───────────────────────────────────────────────────────────────


const API = process.env.REACT_APP_API_URL || (window.location.hostname === "localhost" ? "http://localhost:8000" : "https://leadgen-1087946781393.europe-west1.run.app");





const LEAD_STATUSES = [
  { value: "new",                 label: "New",                    color: "bg-slate-700 text-slate-300 border-slate-600" },
  { value: "contacted_no_answer", label: "Contacted (No Answer)",  color: "bg-blue-950 text-blue-300 border-blue-800" },
  { value: "contacted_answered",  label: "Contacted (Answered)",   color: "bg-sky-950 text-sky-300 border-sky-800" },
  { value: "interested",          label: "Interested",             color: "bg-amber-950 text-amber-300 border-amber-800" },
  { value: "closed",              label: "Closed",                 color: "bg-emerald-950 text-emerald-300 border-emerald-800" },
  { value: "not_interested",      label: "Not Interested",         color: "bg-red-950 text-red-400 border-red-900" },
];

const PIPELINE_COLUMNS = [
  { id: "new",                 label: "New",            icon: "N",  color: "text-slate-300",   border: "border-slate-600" },
  { id: "contacted_no_answer", label: "No Answer",      icon: "NA", color: "text-blue-300",    border: "border-blue-700" },
  { id: "contacted_answered",  label: "Answered",       icon: "A",  color: "text-sky-300",     border: "border-sky-600" },
  { id: "interested",          label: "Interested",     icon: "I",  color: "text-amber-300",   border: "border-amber-600" },
  { id: "closed",              label: "Closed",         icon: "OK", color: "text-emerald-300", border: "border-emerald-600" },
  { id: "not_interested",      label: "Not Interested", icon: "NO", color: "text-red-400",     border: "border-red-800" },
];


const CATEGORIES = [


  "Restaurants","Dentists","Lawyers","Plumbers","Hair Salons","Gyms",


  "Accountants","Real Estate Agents","Veterinarians","Electricians",


  "Photographers","Wedding Planners","Cleaners","Personal Trainers",


  "Marketing Agencies","Web Designers","Architects","Financial Advisors",


  "Insurance Agents","Car Dealers","Hotels","Coffee Shops","Bakeries","Florists",


];
const LOCATIONS = [
  "New York, NY","Los Angeles, CA","Chicago, IL","Houston, TX","Miami, FL",
  "San Francisco, CA","Seattle, WA","Boston, MA","Atlanta, GA","Dallas, TX",
  "Denver, CO","Austin, TX","Portland, OR","Nashville, TN","Phoenix, AZ",
  "San Diego, CA","Philadelphia, PA","Washington, DC","Las Vegas, NV","Orlando, FL",
  "London, UK","Toronto, Canada","Lagos, Nigeria","Dubai, UAE","Sydney, Australia",
];


const PIE_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4"];


//  Call Priority Score 
function computePriority(lead) {
  if (!lead.phone) return { score: 0, label: "No Phone", color: "text-slate-600" };
  let score = 0;
  if (!lead.website) score += 3;
  const issues = lead.detected_issues || detectIssues(lead);
  if (issues.includes("Broken booking link")) score += 5;
  if (issues.includes("Manual booking (email only)")) score += 3;
  if (issues.includes("No booking system")) score += 2;
  // Score AI-detected issues (each unique issue adds points)
  const knownIssues = new Set(["Broken booking link", "Manual booking (email only)", "No booking system", "No website", "No major issues detected"]);
  const aiIssueCount = issues.filter(i => !knownIssues.has(i)).length;
  score += Math.min(aiIssueCount * 2, 6);
  const rating = parseFloat(lead.rating) || 0;
  if (rating >= 4.0) score += 2;
  const reviews = parseInt(lead.reviews) || 0;
  if (reviews >= 20) score += 2;
  // Factor in AI compatibility score
  if (lead.ai_compat_score != null) {
    if (lead.ai_compat_score >= 70) score += 3;
    else if (lead.ai_compat_score >= 40) score += 1;
    else score -= 1;
  }
  if (score >= 8) return { score, label: "🔥 Hot", color: "text-orange-400" };
  if (score >= 5) return { score, label: " Medium", color: "text-amber-400" };
  return { score, label: "Cold", color: "text-slate-500" };
}

//  Problem Detector (client-side fallback) 
function detectIssues(lead) {
  if (lead.detected_issues && lead.detected_issues.length > 0) return lead.detected_issues;
  const issues = [];
  if (!lead.website) issues.push("No website");
  else if (lead.email && !lead.website) issues.push("Manual booking (email only)");
  return issues;
}

//  Smart Signal Display 
// Returns { signals: string[], type: "ai" | "generic" | "none" }
// AI signals = goal-aware insights from Gemini (generated on save/score)
// Generic = booking-system noise from scraper (irrelevant for most orgs)
const _BOOKING_SIGNALS = new Set(["Broken booking link", "Manual booking (email only)", "No booking system", "No website", "No major issues detected"]);
function getDisplaySignals(lead) {
  const all = (lead.detected_issues || detectIssues(lead)).filter(s => s && s !== "No relevant signals detected");
  const aiSignals = all.filter(s => !_BOOKING_SIGNALS.has(s));
  if (aiSignals.length > 0) return { signals: aiSignals, type: "ai" };
  // No AI signals yet — for no-website leads show that, otherwise suppress booking noise
  if (!lead.website) return { signals: ["No website"], type: "generic" };
  return { signals: [], type: "none" };
}

//  Time Zone Detector 
const CITY_TZ_MAP = {
  "new york": "America/New_York", "los angeles": "America/Los_Angeles", "chicago": "America/Chicago",
  "houston": "America/Chicago", "miami": "America/New_York", "san francisco": "America/Los_Angeles",
  "seattle": "America/Los_Angeles", "boston": "America/New_York", "atlanta": "America/New_York",
  "dallas": "America/Chicago", "denver": "America/Denver", "austin": "America/Chicago",
  "portland": "America/Los_Angeles", "nashville": "America/Chicago", "phoenix": "America/Phoenix",
  "san diego": "America/Los_Angeles", "philadelphia": "America/New_York", "washington": "America/New_York",
  "las vegas": "America/Los_Angeles", "orlando": "America/New_York",
  "london": "Europe/London", "toronto": "America/Toronto", "lagos": "Africa/Lagos",
  "dubai": "Asia/Dubai", "sydney": "Australia/Sydney", "paris": "Europe/Paris",
  "berlin": "Europe/Berlin", "tokyo": "Asia/Tokyo", "mumbai": "Asia/Kolkata",
  "singapore": "Asia/Singapore", "hong kong": "Asia/Hong_Kong", "delhi": "Asia/Kolkata",
  "bangkok": "Asia/Bangkok", "istanbul": "Europe/Istanbul", "moscow": "Europe/Moscow",
  "cape town": "Africa/Johannesburg", "johannesburg": "Africa/Johannesburg",
  "nairobi": "Africa/Nairobi", "cairo": "Africa/Cairo", "riyadh": "Asia/Riyadh",
  "kuala lumpur": "Asia/Kuala_Lumpur", "jakarta": "Asia/Jakarta", "manila": "Asia/Manila",
  "sao paulo": "America/Sao_Paulo", "mexico city": "America/Mexico_City",
  "buenos aires": "America/Argentina/Buenos_Aires", "santiago": "America/Santiago",
  "amsterdam": "Europe/Amsterdam", "madrid": "Europe/Madrid", "rome": "Europe/Rome",
  "zurich": "Europe/Zurich", "vienna": "Europe/Vienna", "dublin": "Europe/Dublin",
  "manchester": "Europe/London", "birmingham": "Europe/London", "glasgow": "Europe/London",
  "vancouver": "America/Vancouver", "montreal": "America/Toronto", "calgary": "America/Edmonton",
  "auckland": "Pacific/Auckland", "melbourne": "Australia/Melbourne", "brisbane": "Australia/Brisbane",
  "perth": "Australia/Perth",
};

function getLeadTimezone(lead) {
  const addr = (lead.address || "").toLowerCase();
  for (const [city, tz] of Object.entries(CITY_TZ_MAP)) {
    if (addr.includes(city)) {
      try {
        const now = new Date();
        const localTime = now.toLocaleTimeString("en-US", { timeZone: tz, hour: "numeric", minute: "2-digit", hour12: true });
        const hour = parseInt(now.toLocaleString("en-US", { timeZone: tz, hour: "numeric", hour12: false }));
        let callStatus;
        if (hour >= 9 && hour < 18) callStatus = { label: "🟢 Good time", color: "text-emerald-400" };
        else if ((hour >= 7 && hour < 9) || (hour >= 18 && hour < 21)) callStatus = { label: "🟡 Early/Late", color: "text-amber-400" };
        else callStatus = { label: "🔴 Do not call", color: "text-red-400" };
        return { localTime, callStatus, tz };
      } catch { return null; }
    }
  }
  return null;
}

//  Script Helper 
function getScripts(lead) {
  const issues = lead.detected_issues || detectIssues(lead);
  const scripts = [];
  if (issues.includes("Broken booking link"))
    scripts.push("Hi, I tried booking through your website but the link doesn’t seem to be working. I help businesses fix exactly that — would you have a moment to chat?");
  if (issues.includes("No website"))
    scripts.push("Hi, I noticed you don’t have a website currently. I help local businesses get online and attract more customers — would you be interested?");
  if (issues.includes("Manual booking (email only)"))
    scripts.push("Hi, are you currently handling bookings manually through email? I can help set up an easy online booking system for you.");
  if (issues.includes("No booking system"))
    scripts.push("Hi, I was checking out your business and noticed there’s no online booking option. I help businesses set that up — interested in hearing more?");
  if (scripts.length === 0 && !lead.website)
    scripts.push("Hi, I noticed your business doesn’t have a website yet. I help local businesses get found online — can I share how?");
  if (scripts.length === 0)
    scripts.push("Hi, I came across your business and would love to chat about how I can help you get more customers.");
  return scripts;
}





// ─── localStorage helpers ──────────────────────────────────────────────────────


function loadLS(key, fallback) {


  try { return JSON.parse(localStorage.getItem(key)) ?? fallback; } catch { return fallback; }


}


function saveLS(key, value) {


  try { localStorage.setItem(key, JSON.stringify(value)); } catch {}


}





// ─── Auth Context ─────────────────────────────────────────────────────────────


const AuthCtx = createContext(null);


function useAuth() { return useContext(AuthCtx); }





function AuthProvider({ children }) {


  const [token, setToken]   = useState(() => loadLS("ls_token", null));


  const [user,  setUser]    = useState(() => loadLS("ls_user",  null));


  // Expose user info to tracking system
  useEffect(() => {
    if (user && user.email) {
      window.currentUserEmail = user.email;
      window.currentLeadId = user.lead_id || null;
    } else {
      window.currentUserEmail = null;
      window.currentLeadId = null;
    }
  }, [user]);


  const login = useCallback((tok, usr) => {


    setToken(tok); setUser(usr);


    saveLS("ls_token", tok); saveLS("ls_user", usr);


  }, []);





  const logout = useCallback(() => {


    setToken(null); setUser(null);


    localStorage.removeItem("ls_token"); localStorage.removeItem("ls_user");


  }, []);





  const authFetch = useCallback(async (url, opts = {}) => {


    const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
    // Let the browser set Content-Type automatically for FormData (multipart boundary must not be overridden)
    if (opts.body instanceof FormData) delete headers["Content-Type"];


    if (token) headers["Authorization"] = `Bearer ${token}`;


    const resp = await fetch(`${API}${url}`, { ...opts, headers });


    if (resp.status === 401) { logout(); throw new Error("Session expired."); }


    return resp;


  }, [token, logout]);





  return (


    <AuthCtx.Provider value={{ token, user, login, logout, authFetch }}>


      {children}


    </AuthCtx.Provider>


  );


}

//  Theme Context 
const ThemeCtx = createContext(null);
function useTheme() { return useContext(ThemeCtx); }

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => loadLS("ls_theme", "dark"));
  
  const toggleTheme = useCallback(() => {
    const newTheme = theme === "dark" ? "light" : "dark";
    setTheme(newTheme);
    saveLS("ls_theme", newTheme);
  }, [theme]);
  
  useEffect(() => {
    document.documentElement.classList.remove("dark", "light");
    document.documentElement.classList.add(theme);
    document.body.style.backgroundColor = theme === "dark" ? "#020617" : "#ffffff";
    document.body.style.color = theme === "dark" ? "#ffffff" : "#111827";
  }, [theme]);
  
  return (
    <ThemeCtx.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeCtx.Provider>
  );
}

// Helper component for themed containers
function ThemedContainer({ children, className = "" }) {
  const { theme } = useTheme();
  const baseClasses = theme === 'dark'
    ? 'bg-slate-950 text-white'
    : 'bg-white text-gray-900';
  return <div className={`${baseClasses} ${className}`}>{children}</div>;
}






function RequireAuth({ children }) {


  const { token, user } = useAuth();


  if (!token || !user) return <Navigate to="/login" replace />;


  if (user.status === "pending")  return <PendingPage />;


  if (user.status === "rejected") return <RejectedPage />;


  return children;


}


function RequireAdmin({ children }) {


  const { user } = useAuth();


  if (!user || user.role !== "admin") return <Navigate to="/app" replace />;


  return children;


}





// ─── Shared UI ─────────────────────────────────────────────────────────────────


function SkeletonRow({ cols = 9 }) {


  return (


    <tr className="animate-pulse border-b border-slate-800/50">


      {[...Array(cols)].map((_, i) => (


        <td key={i} className="px-4 py-3"><div className="h-4 bg-slate-800 rounded w-full" /></td>


      ))}


    </tr>


  );


}


function StatusBadge({ has }) {


  return has


    ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-950 text-emerald-400 border border-emerald-800"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />Yes</span>


    : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-950 text-red-400 border border-red-900"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />No</span>;


}


function StarRating({ rating }) {


  if (!rating) return <span className="text-slate-600 text-xs">-</span>;


  const val = parseFloat(rating);
  const full = Math.floor(val);
  const half = val - full >= 0.3;
  return (
    <div className="flex items-center gap-1">
      <div className="flex text-amber-400 text-xs">
        {[...Array(5)].map((_, i) => (
          <span key={i} className={i < full ? "text-amber-400" : (i === full && half) ? "text-amber-400/50" : "text-slate-700"}>
            {i < full ? "★" : (i === full && half) ? "★" : "☆"}
          </span>
        ))}
      </div>
      <span className="text-xs font-semibold text-slate-400">{val.toFixed(1)}</span>
    </div>
  );


}


function StatCard({ icon, label, value, subText, sub, color="text-indigo-300", ring="border-indigo-800/40" }) {


  return (


    <div className={`bg-slate-900 border ${ring} rounded-xl p-4`}>


      {icon && <div className="text-2xl mb-2">{icon}</div>}


      <p className={`text-3xl font-bold ${color}`}>{value}</p>


      <p className="text-xs font-semibold text-slate-400 mt-0.5">{label}</p>


      {(subText || sub) && <p className="text-xs text-slate-600 mt-0.5">{subText || sub}</p>}


    </div>


  );


}


function SortTh({ col, sort, doSort, children }) {


  return (


    <th className="px-4 py-3 text-left font-semibold cursor-pointer hover:text-slate-200 transition select-none whitespace-nowrap" onClick={() => doSort(col)}>


      {children} <span className="opacity-40">{sort.col===col?(sort.dir==="asc"?"^":"v"):"~"}</span>


    </th>


  );


}


function LeadStatusSelect({ value, onChange, compact=false }) {


  const st = LEAD_STATUSES.find((s) => s.value === value) || LEAD_STATUSES[0];


  return (


    <select value={value||"new"} onChange={(e)=>onChange(e.target.value)} onClick={(e)=>e.stopPropagation()}


      className={`border rounded-md font-semibold cursor-pointer outline-none transition text-xs ${st.color} ${compact?"px-1.5 py-0.5":"px-2 py-1"} bg-transparent`}>


      {LEAD_STATUSES.map((s)=><option key={s.value} value={s.value} className="bg-slate-900 text-white">{s.label}</option>)}


    </select>


  );


}





// --- Public Marketing Pages -------------------------------------------------------------

// Shared Navigation Component
function PublicNav() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { theme, toggleTheme } = useTheme();
  
  return (
    <nav className={`sticky top-0 z-50 backdrop-blur-sm border-b ${theme === 'dark' ? 'bg-slate-950/95 border-slate-800/60' : 'bg-white/95 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src={TimingoflowLogo} alt="TimingoFlow" className="w-16 h-16" />
            <span className={`font-bold text-lg tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>TimingoFlow</span>
          </div>
          
          <div className="hidden md:flex items-center gap-6">
            <button onClick={() => navigate('/features')} className={`text-sm font-medium transition ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Features</button>
            <button onClick={() => navigate('/pricing')} className={`text-sm font-medium transition ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Pricing</button>
            <button onClick={() => navigate('/about')} className={`text-sm font-medium transition ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>About</button>
            <button onClick={() => navigate('/contact')} className={`text-sm font-medium transition ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Contact</button>
            
            {/* Theme Toggle */}
            <button onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} className={`p-2 rounded-lg transition focus-visible:ring-2 focus-visible:ring-indigo-500 ${theme === 'dark' ? 'bg-slate-800 hover:bg-slate-700 text-yellow-400' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'}`} title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}>
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            
            {token ? (
              <button onClick={() => navigate('/app')} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-semibold transition shadow-lg shadow-indigo-900/30">Dashboard</button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className={`text-sm font-medium transition ${theme === 'dark' ? 'text-slate-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Sign In</button>
                <button onClick={() => { if (window.trackEvent) window.trackEvent('cta_book_demo_clicked', { location: 'navbar' }); navigate('/demo'); }} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-semibold transition shadow-lg shadow-indigo-900/30">Book Demo</button>
              </>
            )}
          </div>

          <div className="md:hidden flex gap-2">
            {/* Mobile Theme Toggle */}
            <button onClick={toggleTheme} aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`} className={`p-2 rounded-lg transition focus-visible:ring-2 focus-visible:ring-indigo-500 ${theme === 'dark' ? 'bg-slate-800 text-yellow-400' : 'bg-gray-100 text-gray-700'}`}>
              {theme === 'dark' ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
            {token ? (
              <button onClick={() => navigate('/app')} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold">Dashboard</button>
            ) : (
              <>
                <button onClick={() => navigate('/login')} className={`px-3 py-2 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-600'}`}>Sign In</button>
                <button onClick={() => navigate('/demo')} className="px-3 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold">Demo</button>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

// Shared Footer Component  
function PublicFooter() {
  const navigate = useNavigate();
  const { theme } = useTheme();

  return (
    <footer className={`border-t py-12 ${theme === 'dark' ? 'bg-slate-950 border-slate-800/60' : 'bg-gray-50 border-gray-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-4">
                            <img src={TimingoflowLogo} alt="TimingoFlow" className="w-16 h-16" />

              <span className={`font-bold text-lg tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>TimingoFlow</span>
            </div>
            <p className={`text-sm ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>Find verified B2B leads, generate personalized outreach, and track every follow-up in one workspace.</p>
          </div>

          <div>
            <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Product</h4>
            <div className="space-y-2">
              <button onClick={() => navigate('/features')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Features</button>
              <button onClick={() => navigate('/pricing')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Pricing</button>
              <button onClick={() => navigate('/demo')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Book Demo</button>
            </div>
          </div>

          <div>
            <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Company</h4>
            <div className="space-y-2">
              <button onClick={() => navigate('/about')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>About</button>
              <button onClick={() => navigate('/contact')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Contact</button>
              <button onClick={() => navigate('/security')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Security</button>
            </div>
          </div>

          <div>
            <h4 className={`font-semibold mb-3 text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Legal</h4>
            <div className="space-y-2">
              <button onClick={() => navigate('/privacy')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Privacy Policy</button>
              <button onClick={() => navigate('/terms')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Terms of Service</button>
              <button onClick={() => navigate('/refund-policy')} className={`block text-sm transition ${theme === 'dark' ? 'text-slate-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}>Refund Policy</button>
            </div>
            <div className={`space-y-1 text-sm mt-4 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>
              <p><a href="mailto:team@timingoflow.com" className={`transition ${theme === 'dark' ? 'hover:text-white' : 'hover:text-gray-900'}`}>team@timingoflow.com</a></p>
            </div>
          </div>
        </div>

        <div className={`border-t pt-6 flex flex-col sm:flex-row justify-between items-center gap-4 ${theme === 'dark' ? 'border-slate-800' : 'border-gray-200'}`}>
          <p className={`text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}> © {new Date().getFullYear()} TimingoFlow. All rights reserved. Not affiliated with Google or LinkedIn.</p>
          <div className={`flex gap-4 text-xs ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>
            <button onClick={() => navigate('/privacy')} className={`transition ${theme === 'dark' ? 'hover:text-slate-300' : 'hover:text-gray-700'}`}>Privacy Policy</button>
            <button onClick={() => navigate('/terms')} className={`transition ${theme === 'dark' ? 'hover:text-slate-300' : 'hover:text-gray-700'}`}>Terms of Service</button>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ─── SEO: per-route title / meta description / canonical / OG tags ─────────
// CRA serves one static index.html for every route, so without this every
// page (pricing, about, contact...) reports the homepage's title/description
// to crawlers and social previews. This hook overwrites the relevant <head>
// tags on mount and restores the homepage defaults on unmount.
const SITE_URL = "https://timingoflow.com";
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-preview-1200x630.png`;
const DEFAULT_SEO = {
  title: "TimingoFlow - AI Prospecting Workspace for Agencies",
  description: "Find local B2B leads, personalize outreach, send follow-ups, and track replies from one dashboard. Built for agencies, consultants, and B2B service providers.",
  path: "/",
};

function setMetaTag(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function useSEO({ title, description, path, image = DEFAULT_OG_IMAGE, noindex = false }) {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = title;

    setMetaTag("name", "title", title);
    setMetaTag("name", "description", description);
    setMetaTag("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    setMetaTag("property", "og:title", title);
    setMetaTag("property", "og:description", description);
    setMetaTag("name", "twitter:title", title);
    setMetaTag("name", "twitter:description", description);

    const url = `${SITE_URL}${path === "/" ? "" : path}`;
    setMetaTag("property", "og:url", url);
    setMetaTag("name", "twitter:url", url);

    setMetaTag("property", "og:image", image);
    setMetaTag("name", "twitter:image", image);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    return () => {
      document.title = prevTitle;
      setMetaTag("name", "title", DEFAULT_SEO.title);
      setMetaTag("name", "description", DEFAULT_SEO.description);
      setMetaTag("name", "robots", "index, follow");
      setMetaTag("property", "og:title", DEFAULT_SEO.title);
      setMetaTag("property", "og:description", DEFAULT_SEO.description);
      setMetaTag("name", "twitter:title", DEFAULT_SEO.title);
      setMetaTag("name", "twitter:description", DEFAULT_SEO.description);
      setMetaTag("property", "og:image", DEFAULT_OG_IMAGE);
      setMetaTag("name", "twitter:image", DEFAULT_OG_IMAGE);
      const defaultUrl = `${SITE_URL}${DEFAULT_SEO.path}`;
      setMetaTag("property", "og:url", defaultUrl);
      setMetaTag("name", "twitter:url", defaultUrl);
      if (canonical) canonical.setAttribute("href", defaultUrl);
    };
  }, [title, description, path, image, noindex]);
}

// Homepage Component
function HomePage() {
  useSEO({
    title: DEFAULT_SEO.title,
    description: DEFAULT_SEO.description,
    path: "/",
  });
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [demoEmail, setDemoEmail] = useState('');
  const [demoName, setDemoName] = useState('');
  const [demoPhone, setDemoPhone] = useState('');
  const [demoIndustry, setDemoIndustry] = useState('');
  
  const handleDemoSubmit = (e) => {
    e.preventDefault();
    // Navigate to demo page with pre-filled data
    navigate('/demo', { state: { name: demoName, email: demoEmail, phone: demoPhone, industry: demoIndustry } });
  };
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      
      {/* Sticky CTA Button (Mobile) */}
      <div className="md:hidden fixed bottom-4 right-4 z-40">
        <button onClick={() => navigate('/demo')} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-full text-sm font-bold shadow-2xl shadow-indigo-900/50 transition">
          Book Demo
        </button>
      </div>
      
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/20 to-transparent pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-32 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-950/50 border border-indigo-800/50 rounded-full text-xs text-indigo-300 font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-indigo-400 animate-pulse"></span>
                Currently onboarding a limited number of agencies
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mb-6 leading-tight">
                Find <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">verified B2B leads</span>, write outreach, and book more client meetings
              </h1>

              <p className="text-lg sm:text-xl text-slate-400 mb-8 leading-relaxed">
                TimingoFlow helps agencies, consultants, and B2B service providers build targeted prospect lists, generate AI-personalized outreach, and manage follow-ups from one dashboard.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <button onClick={() => { if (window.trackEvent) window.trackEvent('cta_start_trial_clicked', { location: 'hero' }); navigate('/signup'); }} className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-base transition shadow-xl shadow-indigo-900/40 inline-flex items-center justify-center gap-2">
                  Start Free Trial
                </button>
                <button onClick={() => { if (window.trackEvent) window.trackEvent('cta_watch_demo_clicked', { location: 'hero' }); navigate('/landing'); }} className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl font-bold text-base transition inline-flex items-center justify-center gap-2">
                  Watch 2-Minute Demo
                </button>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 text-sm text-slate-400">
                <div className="flex items-center gap-2">
                  <span className="text-green-400"></span>
                  No contracts. Cancel anytime.
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-green-400"></span>
                  Setup in under 24 hours
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl">
                <div className="space-y-4">
                  <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                    <span className="text-sm font-semibold text-slate-400">Lead Pipeline</span>
                    <span className="text-xs text-green-400">15-25% Response Rate</span>
                  </div>
                  {[
                    { name: 'BrightClean SF', email: 'info@brightcleansf.com', status: 'Replied' },
                    { name: 'Golden Bay Cleaning', email: 'contact@goldenbay.com', status: 'Interested' },
                    { name: 'Sparkle Home Services', email: 'hello@sparklehome.com', status: 'Booked' },
                  ].map((lead, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-slate-800/50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-white">{lead.name}</p>
                        <p className="text-xs text-slate-500">{lead.email}</p>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-1 rounded ${
                        lead.status === 'Booked' ? 'bg-green-900 text-green-300' :
                        lead.status === 'Interested' ? 'bg-amber-900 text-amber-300' :
                        'bg-blue-900 text-blue-300'
                      }`}>{lead.status}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Problem Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Most Agencies Struggle to Get Consistent Clients</h2>
            <p className="text-slate-400 text-lg">Sound familiar?</p>
          </div>
          
          <div className="grid sm:grid-cols-2 gap-6">
            {[
              { icon: '', problem: 'Spending hours finding leads manually' },
              { icon: '', problem: 'Sending messages that get ignored' },
              { icon: '', problem: 'No predictable pipeline' },
              { icon: '', problem: 'Losing leads due to poor follow-up' },
            ].map(({ icon, problem }, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-slate-900 border border-slate-800 rounded-xl">
                <div className="text-3xl">{icon}</div>
                <p className="text-lg text-slate-300 flex-1">{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Solution Section */}
      <section id="how-it-works" className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">How You Get Clients Consistently</h2>
            <p className="text-slate-400 text-lg">Simple. Predictable. Effective.</p>
          </div>
          
          <div className="grid lg:grid-cols-4 gap-8">
            {[
              { num: '1', title: 'We Find', desc: 'We find businesses already needing your service using advanced search', icon: '' },
              { num: '2', title: 'We Contact', desc: 'We contact them with personalized outreach that gets responses', icon: '' },
              { num: '3', title: 'They Respond', desc: 'They respond and book meetings directly with you', icon: '' },
              { num: '4', title: 'You Close', desc: 'You focus on closing — not chasing', icon: '' },
            ].map(({ num, title, desc, icon }) => (
              <div key={num} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-indigo-600 text-3xl mb-4">{icon}</div>
                <div className="inline-block px-3 py-1 bg-slate-800 text-slate-400 rounded-full text-xs font-bold mb-3">Step {num}</div>
                <h3 className="text-xl font-bold mb-2">{title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who Uses TimingoFlow */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Who Uses TimingoFlow?</h2>
            <p className="text-slate-400 text-lg">Built for teams that need a steady flow of new clients</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { icon: '🎨', title: 'Web Design Agencies' },
              { icon: '📈', title: 'SEO Agencies' },
              { icon: '📣', title: 'Marketing Agencies' },
              { icon: '🚀', title: 'SaaS Founders' },
              { icon: '🤝', title: 'B2B Consultants' },
              { icon: '📅', title: 'Appointment Setters' },
              { icon: '🧑‍💼', title: 'Recruiters' },
              { icon: '🏢', title: 'B2B Service Providers' },
            ].map(({ icon, title }, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center hover:border-indigo-600 transition">
                <div className="text-3xl mb-2" aria-hidden="true">{icon}</div>
                <h3 className="font-semibold text-sm">{title}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Why not just use spreadsheets, Apollo, or manual search?</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-slate-800">
                  <th scope="col" className="text-left py-4 pr-4 text-slate-400 font-semibold">Approach</th>
                  <th scope="col" className="text-left py-4 px-4 text-slate-400 font-semibold">Where it falls short</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { name: 'Manual Google search', gap: 'Slow — hours spent finding and copying contact info by hand' },
                  { name: 'Spreadsheets', gap: 'No follow-up system, no reminders, easy to lose track of a lead' },
                  { name: 'Apollo & similar databases', gap: 'Broad, generic database — not focused on local or niche services' },
                ].map((row, i) => (
                  <tr key={i} className="border-b border-slate-800/60">
                    <td className="py-4 pr-4 font-semibold text-slate-200 whitespace-nowrap">{row.name}</td>
                    <td className="py-4 px-4 text-slate-400">{row.gap}</td>
                  </tr>
                ))}
                <tr className="bg-indigo-950/30">
                  <td className="py-4 pr-4 font-bold text-indigo-300 whitespace-nowrap">TimingoFlow</td>
                  <td className="py-4 px-4 text-indigo-200 font-medium">Search, AI outreach, pipeline, and follow-up reminders in one place</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Frequently Asked Questions</h2>
          </div>
          <div className="space-y-4">
            {[
              { q: 'Where do leads come from?', a: 'We surface publicly available business information — business name, category, public contact details, and web presence — gathered through search and enrichment.' },
              { q: 'Are emails verified?', a: 'Yes. Each contact is flagged as verified, risky, or unknown so you know deliverability risk before you send.' },
              { q: 'Can I export leads?', a: 'Yes, you can export your saved leads to CSV at any time.' },
              { q: 'Can I use it for any country?', a: 'Yes — search by city, country, or keyword to target any market you sell into.' },
              { q: 'Does TimingoFlow send emails for me?', a: 'TimingoFlow generates personalized outreach and can send on your schedule, but you stay in control — review and edit any message before it goes out.' },
              { q: 'Is this compliant?', a: 'We work only with publicly available business information and give you full control over your outreach. See our Privacy Policy and Security page for details.' },
              { q: 'Can my agency use it for multiple clients?', a: 'Yes — use saved folders and pipelines to keep each client\'s prospects and campaigns separate.' },
              { q: 'What happens when I hit my monthly lead limit?', a: 'You can upgrade your plan at any time from billing settings, or wait until your limit resets at the start of your next billing cycle.' },
            ].map(({ q, a }, i) => (
              <details key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-5 group">
                <summary className="font-semibold cursor-pointer list-none flex items-center justify-between focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 rounded">
                  {q}
                  <span className="text-slate-500 group-open:rotate-180 transition-transform" aria-hidden="true">▾</span>
                </summary>
                <p className="text-slate-400 text-sm mt-3 leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Product Demo Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">See It In Action</h2>
            <p className="text-slate-400 text-lg">Here's exactly what happens behind the scenes</p>
          </div>
          
          <div className="space-y-12">
            {/* Lead Generation */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-indigo-950 border border-indigo-800 rounded-full text-xs text-indigo-300 font-semibold mb-4">A. Lead Generation</div>
                <h3 className="text-2xl font-bold mb-3">Find Qualified Leads In Real-Time</h3>
                <p className="text-slate-400 leading-relaxed mb-4">Search "marketing agencies in Atlanta" and get verified businesses with contact info instantly.</p>
                <ul className="space-y-2">
                  {['Verified phone & email', 'Business ratings & reviews', 'Website analysis', 'Real-time data'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="text-green-400"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <div className="bg-slate-800 rounded-lg p-4 mb-4">
                  <p className="text-xs text-slate-500 mb-2">Search Query</p>
                  <p className="text-sm text-white font-mono">cleaning services in San Francisco</p>
                </div>
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 mb-2">Results (142 businesses found)</div>
                  {['BrightClean SF', 'Golden Bay Cleaning', 'Sparkle Home'].map((name, i) => (
                    <div key={i} className="p-3 bg-slate-800/50 rounded text-xs">
                      <div className="font-semibold text-white">{name}</div>
                      <div className="text-slate-500"> +1-415-xxx-xxxx —  contact@...</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            {/* Message Generation */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="md:order-2">
                <div className="inline-block px-3 py-1 bg-indigo-950 border border-indigo-800 rounded-full text-xs text-indigo-300 font-semibold mb-4">B. Message Generation</div>
                <h3 className="text-2xl font-bold mb-3">Personalized Messages Automatically</h3>
                <p className="text-slate-400 leading-relaxed mb-4">Every message is tailored to the specific business — not generic spam.</p>
                <ul className="space-y-2">
                  {['Uses their business info', 'Natural, human tone', 'Mentions specific pain points', 'High response rate'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="text-green-400"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="md:order-1 bg-slate-900 border border-slate-800 rounded-xl p-6">
                <div className="text-xs text-slate-500 mb-3">Generated Message</div>
                <div className="bg-slate-800 rounded-lg p-4 text-sm text-slate-300 leading-relaxed">
                  <p className="mb-2">Hi Sarah,</p>
                  <p className="mb-2">I noticed BrightClean SF has great reviews (4.8 stars!) but your booking system seems to be email-only.</p>
                  <p className="mb-2">We help cleaning companies get 20-30% more bookings with automated scheduling. Worth a quick chat?</p>
                  <p className="text-slate-500">Best,<br/>TimingoFlow Team</p>
                </div>
                <div className="mt-3 text-xs text-green-400"> Personalized  •   Relevant  •   Non-salesy</div>
              </div>
            </div>
            
            {/* Outreach Sending */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-block px-3 py-1 bg-indigo-950 border border-indigo-800 rounded-full text-xs text-indigo-300 font-semibold mb-4">C. Outreach Sending</div>
                <h3 className="text-2xl font-bold mb-3">Send At Scale, Track Everything</h3>
                <p className="text-slate-400 leading-relaxed mb-4">Automatically send outreach and track opens, clicks, and replies in real-time.</p>
                <ul className="space-y-2">
                  {['Auto-send on your schedule', 'Smart follow-ups', 'Reply detection', 'Performance analytics'].map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                      <span className="text-green-400"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold">Today's Sends</span>
                  <span className="text-xs bg-green-900 text-green-300 px-2 py-1 rounded">23 sent</span>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-4">
                  <div className="bg-slate-800 rounded p-3 text-center">
                    <div className="text-2xl font-bold text-indigo-400">67%</div>
                    <div className="text-xs text-slate-500">Open Rate</div>
                  </div>
                  <div className="bg-slate-800 rounded p-3 text-center">
                    <div className="text-2xl font-bold text-amber-400">23%</div>
                    <div className="text-xs text-slate-500">Reply Rate</div>
                  </div>
                  <div className="bg-slate-800 rounded p-3 text-center">
                    <div className="text-2xl font-bold text-green-400">8</div>
                    <div className="text-xs text-slate-500">Interested</div>
                  </div>
                </div>
                <div className="text-xs text-slate-500">Recent Activity</div>
                <div className="space-y-2 mt-2">
                  {['BrightClean SF replied', 'Golden Bay opened email', 'Sparkle clicked link'].map((activity, i) => (
                    <div key={i} className="text-xs text-slate-400 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-green-400"></span>
                      {activity}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Social Proof */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Agencies Are Seeing Real Results</h2>
            <p className="text-slate-400">Predictable pipeline growth within weeks</p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {[
              { quote: 'We struggled with consistent leads — this gave us a predictable pipeline within weeks.', author: 'Daniel H.', role: 'Marketing Agency Owner, London, UK' },
              { quote: 'Finally, a system that actually brings qualified leads without me spending hours on outreach.', author: 'Amanda C.', role: 'Web Design Agency, Toronto, Canada' },
              { quote: 'The response rate is incredible. We\'re booking 3-5 demos per week now.', author: 'Liam P.', role: 'SEO Consultant, Sydney, Australia' },
              { quote: 'Our sales team finally has a predictable list to work from every Monday morning.', author: 'Rachel M.', role: 'B2B Consultant, Austin, USA' },
            ].map((testimonial, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-6">
                <div className="text-indigo-400 text-3xl mb-4">"</div>
                <p className="text-slate-300 mb-4 leading-relaxed">{testimonial.quote}</p>
                <div>
                  <p className="font-semibold text-white text-sm">{testimonial.author}</p>
                  <p className="text-xs text-slate-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Results Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Real Campaign Results</h2>
            <p className="text-slate-400 text-lg">Here's what a typical campaign looks like</p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
              <h3 className="text-xl font-bold mb-6">Sample Leads Found</h3>
              <div className="space-y-4">
                {[
                  { name: 'BrightClean SF', email: 'info@brightcleansf.com', need: 'No online booking' },
                  { name: 'Golden Bay Cleaning', email: 'contact@goldenbay.com', need: 'Manual scheduling only' },
                  { name: 'Sparkle Home Services', email: 'hello@sparklehome.com', need: 'Email booking only' },
                ].map((lead, i) => (
                  <div key={i} className="p-4 bg-slate-800 rounded-lg">
                    <div className="font-semibold text-white mb-1">{lead.name}</div>
                    <div className="text-sm text-slate-400 mb-2">{lead.email}</div>
                    <div className="text-xs text-amber-400">Need: {lead.need}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-6">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
                <h3 className="text-xl font-bold mb-4">Sample Reply</h3>
                <div className="bg-slate-800 rounded-lg p-4 text-sm text-slate-300">
                  <p className="mb-2">"Yes, we're interested — when can we speak?"</p>
                  <p className="text-xs text-slate-500">– BrightClean SF</p>
                </div>
              </div>
              
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-8">
                <h3 className="text-xl font-bold mb-4">Campaign Stats</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Messages Sent</span>
                    <span className="font-bold text-white">142</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Open Rate</span>
                    <span className="font-bold text-indigo-400">68%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Response Rate</span>
                    <span className="font-bold text-green-400">23%</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Interested Replies</span>
                    <span className="font-bold text-amber-400">18</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Benefits Section */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Everything You Need To Get Clients</h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { icon: '', title: 'Consistent Pipeline', desc: 'Predictable flow of leads every week' },
              { icon: '', title: 'Qualified Leads Only', desc: 'Businesses already needing your service' },
              { icon: '', title: 'Automated Outreach', desc: 'Set it and let it run on autopilot' },
              { icon: '', title: 'Saves Time', desc: 'No more manual lead hunting' },
              { icon: '', title: 'Focus on Closing', desc: 'Spend time on deals, not prospecting' },
              { icon: '', title: 'Full Analytics', desc: 'Track every metric that matters' },
            ].map(({ icon, title, desc }, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-6 hover:border-indigo-600 transition">
                <div className="text-4xl mb-3">{icon}</div>
                <h3 className="font-bold text-lg mb-2">{title}</h3>
                <p className="text-sm text-slate-400">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Pricing Preview */}
      <section className="py-20 bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Simple, Transparent Pricing</h2>
            <p className="text-slate-400">Choose the plan that fits your agency</p>
          </div>
          
          <div className="grid md:grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {[
              { 
                name: 'Starter', 
                subtitle: 'Solo agencies',
                price: '$99', 
                outcome: '5-10 clients/month',
                features: ['1,500 leads/month', '30 AI messages/day', '5 pipelines', 'Multi-channel outreach'] 
              },
              { 
                name: 'Growth', 
                subtitle: 'Scaling to 6-7 figures',
                price: '$249', 
                outcome: '15-20 clients/month',
                features: ['3,500 leads/month', '120 AI messages/day', '15 pipelines', 'Advanced automation'], 
                popular: true 
              },
              { 
                name: 'Pro', 
                subtitle: '7-figure agencies',
                price: '$499', 
                outcome: '25-30+ clients/month',
                features: ['7,500 leads/month', '300 AI messages/day', 'Unlimited pipelines', 'Priority support'] 
              },
            ].map((plan, i) => (
              <div key={i} className={`bg-slate-900 border ${plan.popular ? 'border-indigo-600' : 'border-slate-800'} rounded-xl p-8 relative flex flex-col`}>
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 bg-indigo-600 text-white text-xs font-bold rounded-full">Most Popular</div>
                )}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-xs text-slate-500 mb-4">{plan.subtitle}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{plan.price}</span>
                  <span className="text-slate-400">/month</span>
                </div>
                <ul className="space-y-3 mb-6 flex-1">
                  {plan.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                      <span className="text-green-400 mt-0.5"></span>
                      {feature}
                    </li>
                  ))}
                </ul>
                <div className="mb-6 p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Outcome:</p>
                  <p className="text-sm font-semibold text-indigo-400">{plan.outcome}</p>
                </div>
                <button onClick={() => navigate('/signup')} className={`w-full py-3 rounded-lg font-semibold transition ${
                  plan.popular 
                    ? 'bg-indigo-600 hover:bg-indigo-500 text-white' 
                    : 'bg-slate-800 hover:bg-slate-700 text-slate-200'
                }`}>
                  Get Started
                </button>
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <button onClick={() => navigate('/pricing')} className="text-indigo-400 hover:text-indigo-300 font-semibold transition">
              View detailed pricing & add-ons 
            </button>
          </div>
        </div>
      </section>
      
      {/* Risk Reversal */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12">
            <h2 className="text-3xl font-bold mb-4">No Risk. See It Working First.</h2>
            <p className="text-lg text-slate-400 mb-8 leading-relaxed">
              We'll show you exactly how this works for your agency before you commit.<br />
              If it doesn't make sense, you don't continue.
            </p>
            <div className="flex flex-wrap justify-center gap-6 text-sm text-slate-300">
              <div className="flex items-center gap-2">
                <span className="text-green-400"></span>
                No contracts
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400"></span>
                Cancel anytime
              </div>
              <div className="flex items-center gap-2">
                <span className="text-green-400"></span>
                Money-back guarantee
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Final CTA */}
      <section className="py-20 bg-gradient-to-b from-indigo-950/20 to-transparent">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Get More Clients Consistently?</h2>
            <p className="text-lg text-slate-400">See this working for your agency in the next 24 hours</p>
          </div>
          
          <form onSubmit={handleDemoSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 space-y-4">
            <div>
              <input 
                type="text" 
                placeholder="Your Name" 
                value={demoName}
                onChange={(e) => setDemoName(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
              />
            </div>
            <div>
              <input 
                type="email" 
                placeholder="Email Address" 
                value={demoEmail}
                onChange={(e) => setDemoEmail(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
              />
            </div>
            <div>
              <input 
                type="tel" 
                placeholder="Phone Number" 
                value={demoPhone}
                onChange={(e) => setDemoPhone(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
              />
            </div>
            <div>
              <select
                value={demoIndustry}
                onChange={(e) => setDemoIndustry(e.target.value)}
                required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-indigo-600 transition"
              >
                <option value="">Select Your Industry</option>
                <option value="marketing">Marketing Agency</option>
                <option value="webdesign">Web Design Agency</option>
                <option value="seo">SEO Agency</option>
                <option value="consulting">Consulting</option>
                <option value="other">Other</option>
              </select>
            </div>
            <button 
              type="submit"
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-bold text-lg transition shadow-xl shadow-indigo-900/40"
            >
              See This Working For Your Agency 
            </button>
            <p className="text-xs text-center text-slate-500">
              We'll contact you within 24 hours to show you how this works for your specific agency
            </p>
          </form>
        </div>
      </section>
      
      <PublicFooter />
    </div>
  );
}

// Pricing Page Component
function PricingPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "Pricing - Plans for Agencies, Consultants & B2B Teams | TimingoFlow",
    description: "Simple, transparent pricing for verified B2B lead generation and AI-powered outreach. Starter, Growth, and Agency plans with monthly lead limits, AI email credits, and pipeline tracking. Start your free trial today.",
    path: "/pricing",
  });

  const plans = [
    {
      key: 'starter',
      name: 'Starter',
      price: '$99',
      subtitle: 'Best for solo founders, freelancers & small agencies',
      goal: 'Get consistent outreach running.',
      color: 'border-sky-800/60',
      accent: 'text-sky-300',
      btn: 'bg-sky-700 hover:bg-sky-600',
      limits: ['50 leads/day', '50 AI emails/day', '1,500 stored leads', '5 workflows', '5 pipelines', '1 website tracking script', '1 sender identity'],
      included: ['Lead generation search','Saved leads CRM','Basic outreach campaigns','AI email generation','Open + click tracking','Manual follow-ups','Basic analytics dashboard','Kanban pipeline board','Reminders','CSV export/import','Website visitor tracking','AI chat assistant','Demo booking page','Search history'],
      excluded: ['Lead Intel','Auto follow-up sequences','AI auto-replies','Lead leaderboard','Advanced analytics','API access','Smart routing','Visitor identification','Multi-user support'],
    },
    {
      key: 'growth',
      name: 'Growth',
      price: '$249',
      subtitle: 'Best for growing agencies & outbound teams',
      goal: 'Automate lead qualification and follow-ups.',
      color: 'border-violet-600',
      accent: 'text-violet-300',
      btn: 'bg-violet-700 hover:bg-violet-600',
      popular: true,
      limits: ['120 leads/day', '120 AI emails/day', '5,000 stored leads', '20 workflows', '15 pipelines', '3 sender identities', '3 tracking scripts'],
      included: ['Everything in Starter PLUS:','Lead Intel','Decision-maker discovery','LinkedIn profile discovery','AI email personalization from Intel','Auto follow-up sequences','AI reply sentiment detection','CRM auto-stage movement','Automated reminders','Lead leaderboard','Full analytics dashboard','Smart routing','Visitor identification','High-intent visitor tracking','Embedded + popup capture','Multi-channel outreach','API access','Auto-reply toggle','Website activity tracking'],
      excluded: ['AI auto-replies','Webhooks','Advanced reporting exports','Multi-user teams','Priority support'],
    },
    {
      key: 'pro',
      name: 'Pro',
      price: '$499',
      subtitle: 'Best for high-volume agencies & sales operations',
      goal: 'Build a fully automated AI-powered sales system.',
      color: 'border-amber-600',
      accent: 'text-amber-300',
      btn: 'bg-amber-700 hover:bg-amber-600',
      limits: ['300 leads/day', '300 AI emails/day', '15,000 stored leads', 'Unlimited workflows', 'Unlimited pipelines', 'Unlimited tracking scripts', 'Unlimited sender identities'],
      included: ['Everything in Growth PLUS:','AI auto-replies','Advanced automation routing','AI-powered conversation handling','Full lead engagement scoring','Advanced reporting & forecasting','API + webhooks','Team collaboration','Multi-user management','Usage controls','Advanced visitor identification','Full client journey tracking','Priority queue processing','Priority support','Faster AI generation','Organisation AI settings','AI provider switching','Enterprise-grade analytics'],
      excluded: [],
    },
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-12 pb-24">

        {/* Hero */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-indigo-900/40 border border-indigo-700/50 rounded-full text-indigo-300 text-sm font-semibold mb-5">
            AI-powered lead generation for agencies
          </div>
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 leading-tight">
            Simple, transparent pricing
          </h1>
          <p className="text-lg text-slate-400 max-w-xl mx-auto mb-6">
            Pick a plan. Get set up in 15 minutes. Your first leads in 24 hours.
          </p>
          <div className="flex flex-wrap justify-center gap-5 text-sm text-slate-500 mb-4">
            {['No contracts','Cancel anytime','14-day money-back','No setup fees'].map(t => (
              <span key={t} className="flex items-center gap-1.5"><span className="text-emerald-400">✓</span> {t}</span>
            ))}
          </div>
          <p className="text-sm text-slate-400">Start with a 7-day trial. No card required. Cancel anytime.</p>
        </div>

        {/* Plan cards */}
        <div className="grid sm:grid-cols-3 gap-6 mb-16">
          {plans.map((plan) => (
            <div key={plan.key} className={`bg-slate-900 border-2 ${plan.color} rounded-2xl p-6 flex flex-col relative ${plan.popular ? 'shadow-2xl shadow-violet-900/30 scale-[1.02]' : ''}`}>
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-violet-600 text-white text-[11px] font-bold rounded-full uppercase tracking-wider">Most Popular</div>
              )}
              <div className="mb-4">
                <h3 className={`text-lg font-bold ${plan.accent}`}>{plan.name}</h3>
                <p className="text-xs text-slate-500 mt-0.5">{plan.subtitle}</p>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-extrabold text-white">{plan.price}</span>
                <span className="text-slate-400 text-sm">/mo</span>
              </div>
              <p className="text-xs text-slate-500 italic mb-4">{plan.goal}</p>

              <button onClick={() => { if (window.trackEvent) window.trackEvent('cta_pricing_click', { plan: plan.name }); navigate('/signup'); }}
                className={`w-full py-2.5 rounded-xl font-bold mb-5 transition text-sm text-white ${plan.btn}`}>
                Get Started
              </button>

              {/* Limits */}
              <div className="bg-slate-800/60 rounded-xl p-3 mb-4">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-2">Limits</p>
                <div className="space-y-1">
                  {plan.limits.map(l => <div key={l} className="text-xs text-slate-300 flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${plan.btn.split(' ')[0].replace('bg-','bg-').replace('700','500')} shrink-0`}></span>{l}</div>)}
                </div>
              </div>

              {/* Included */}
              <div className="space-y-1.5 flex-1">
                <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Features</p>
                {plan.included.map(f => (
                  <div key={f} className="flex items-start gap-1.5 text-xs">
                    <span className="text-emerald-400 shrink-0 mt-0.5">{f.startsWith('Everything') ? '' : '✅'}</span>
                    <span className={f.startsWith('Everything') ? 'text-slate-400 font-semibold' : 'text-slate-300'}>{f}</span>
                  </div>
                ))}
                {plan.excluded.length > 0 && (
                  <details className="mt-2">
                    <summary className="text-[10px] text-slate-600 cursor-pointer hover:text-slate-400 list-none">Not included ({plan.excluded.length})</summary>
                    <div className="mt-1.5 space-y-1">
                      {plan.excluded.map(f => (
                        <div key={f} className="flex items-start gap-1.5 text-xs">
                          <span className="text-red-700 shrink-0">❌</span>
                          <span className="text-slate-600">{f}</span>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Custom/Enterprise */}
        <div className="bg-gradient-to-br from-slate-800/60 to-slate-900 border border-slate-700 rounded-2xl p-8 mb-16 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Custom / Enterprise</h3>
          <p className="text-slate-400 text-sm mb-4">For large agencies, enterprise sales teams & partnerships</p>
          <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto mb-6 text-xs text-slate-400">
            {['Custom limits & pricing','Dedicated infrastructure','White-labeling','Custom integrations','Slack/CRM integrations','Dedicated onboarding','Reseller systems','Dedicated AI tuning','Custom automations','Dedicated sending infrastructure','Priority dedicated support','Enterprise SLA'].map(f => (
              <div key={f} className="flex items-center gap-1.5"><span className="text-indigo-400">✦</span>{f}</div>
            ))}
          </div>
          <button onClick={() => navigate('/demo')} className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 text-white rounded-xl font-bold text-sm transition">
            Talk to Us
          </button>
        </div>

        {/* FAQ */}
        <div className="max-w-2xl mx-auto mb-16">
          <h2 className="text-2xl font-bold text-center mb-6">Common Questions</h2>
          <div className="space-y-3">
            {[
              { q: 'Do you offer refunds?', a: '14-day money-back guarantee. Not happy? Full refund, no questions asked.' },
              { q: 'Can I cancel anytime?', a: 'Yes. No contracts. Cancel from your settings — access continues until end of billing period.' },
              { q: 'Is there a setup fee?', a: 'None. You pay the monthly price and that\'s it.' },
              { q: 'Can I change plans?', a: 'Upgrade or downgrade anytime. Changes apply on your next billing cycle.' },
              { q: 'What happens to my data if I cancel?', a: 'Your leads and data are yours. You can export everything before cancelling.' },
            ].map(({ q, a }) => (
              <details key={q} className="bg-slate-900 border border-slate-800 rounded-xl group">
                <summary className="px-5 py-4 font-semibold text-sm cursor-pointer list-none flex items-center justify-between">
                  {q}<span className="text-slate-500 group-open:rotate-180 transition-transform text-xs">▼</span>
                </summary>
                <p className="px-5 pb-4 text-sm text-slate-400">{a}</p>
              </details>
            ))}
          </div>
        </div>

        {/* Final CTA */}
        <div className="bg-gradient-to-r from-indigo-950/50 to-purple-950/50 border border-indigo-800/50 rounded-2xl p-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-3">Ready to Start Getting Clients?</h2>
          <p className="text-slate-400 mb-3">Set up in 15 minutes. First leads in 24 hours.</p>
          <p className="text-sm text-slate-500 mb-7 max-w-xl mx-auto">If TimingoFlow doesn't help you build a usable prospect list in your first week, contact us and we'll help you set up your first campaign — or refund you in full.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button onClick={() => navigate('/signup')} className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-base transition shadow-xl shadow-indigo-900/40">Get Started Now</button>
            <button onClick={() => navigate('/demo')} className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 rounded-xl font-bold text-base transition">See It In Action</button>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}

// Demo Booking Page Component
function DemoBookingPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "Book a Demo - See TimingoFlow in Action | TimingoFlow",
    description: "Book a personalized demo of TimingoFlow and see how agencies and B2B service providers find verified leads, generate AI outreach, and track follow-ups in one workspace.",
    path: "/demo",
  });
  const formRef = React.useRef(null);
  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const [formData, setFormData] = useState({
    name: location.state?.name || '',
    email: location.state?.email || '',
    phone: location.state?.phone || '',
    company: location.state?.company || '',
    preferred_date: '',
    preferred_time: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true); setError('');
    try {
      const response = await fetch(`${API}/book-demo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) { setError(data.detail || 'Failed to submit. Please try again.'); return; }
      if (window.trackEvent) window.trackEvent('demo_booking_submit', { value: 1, currency: 'USD' });
      setSubmitted(true);
    } catch (err) {
      setError('Network error — please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  
  if (submitted) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <PublicNav />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
          <div className="bg-slate-900 border border-emerald-700/50 rounded-2xl p-12 text-center">
            <div className="w-20 h-20 bg-emerald-600/20 border-2 border-emerald-500 rounded-full flex items-center justify-center text-4xl mx-auto mb-6">✓</div>
            <h1 className="text-3xl font-bold text-white mb-3">You're booked!</h1>
            <p className="text-slate-400 mb-2">We'll confirm your slot within 24 hours.</p>
            <p className="text-sm text-slate-500 mb-8">A confirmation will be sent to <span className="text-emerald-400">{formData.email}</span></p>
            {formData.preferred_date && (
              <div className="bg-slate-800 rounded-xl px-6 py-4 mb-8 inline-block text-left">
                <p className="text-xs text-slate-500 mb-1">Requested slot</p>
                <p className="text-white font-semibold">{formData.preferred_date} {formData.preferred_time && `at ${formData.preferred_time}`}</p>
              </div>
            )}
            <br/>
            <button onClick={() => navigate('/')} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold transition">
              Back to Home
            </button>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        {/* Video Section - FIRST THING THEY SEE */}
        <div className="mb-12">
          {/* Video with pulsing CTA overlaid at the bottom */}
          <div className="relative rounded-2xl overflow-hidden bg-slate-900 border border-slate-800 shadow-2xl" style={{ paddingBottom: '56.25%' }}>
            <iframe
              className="absolute top-0 left-0 w-full h-full"
              src="https://www.youtube.com/embed/0WgnmYLVBgI?autoplay=1&mute=1&rel=0"
              title="How This System Brings You Clients Automatically (3-Min Demo)"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
            {/* Pulsing Book Demo button — floats over the bottom of the video */}
            <div className="absolute bottom-0 left-0 right-0 flex justify-center pb-5 pointer-events-none"
                 style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, transparent 100%)' }}>
              <button
                onClick={scrollToForm}
                className="pointer-events-auto relative px-7 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-full text-sm shadow-2xl transition-transform hover:scale-105"
                style={{ animation: 'demoPulse 2s ease-in-out infinite' }}
              >
                📅 Book My Demo — It's Free
                {/* Ripple ring */}
                <span className="absolute inset-0 rounded-full border-2 border-indigo-400 opacity-75"
                      style={{ animation: 'demoRipple 2s ease-in-out infinite' }} />
              </button>
            </div>
          </div>

          {/* Keyframes injected via a style tag */}
          <style>{`
            @keyframes demoPulse {
              0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(99,102,241,0.6); }
              50% { transform: scale(1.06); box-shadow: 0 0 0 12px rgba(99,102,241,0); }
            }
            @keyframes demoRipple {
              0% { transform: scale(1); opacity: 0.75; }
              100% { transform: scale(1.5); opacity: 0; }
            }
          `}</style>

          {/* Social Proof Under Video */}
          <p className="text-center text-slate-500 mt-4 italic text-sm">
            "I understood the full workflow in under 3 minutes" – Marcus T., Founder
          </p>
        </div>
        
        {/* Trust Strip */}
        <div className="text-center mb-8 pb-8 border-b border-slate-800">
          <p className="text-sm text-slate-400 uppercase tracking-wide mb-3">Used by Agencies &amp; Marketers at:</p>
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-6">
            <span className="text-slate-500 font-medium">Built for agencies and B2B teams</span>
            <span className="text-slate-600">–</span>
            <span className="text-slate-500 font-medium">Lead search, outreach, and follow-up in one place</span>
            <span className="text-slate-600">–</span>
            <span className="text-slate-500 font-medium">Self-serve setup with demo support</span>
          </div>
        </div>
        
        {/* Hero Headline - Outcome-Focused */}
        <div className="text-center mb-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold mb-4 leading-tight">
            Get <span className="text-indigo-400">Pre-Qualified Leads</span> Without Cold Calling
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 mb-4">
            Automated lead discovery + AI outreach + tracking — watch it work in 3 minutes
          </p>
          <div className="flex justify-center gap-3 mt-6 text-sm text-slate-400">
            <span> No cold calling</span>
            <span>–</span>
            <span> Found daily</span>
            <span>–</span>
            <span> AI contacted</span>
          </div>
        </div>
        
        {/* Quick Stat Proof */}
        <div className="grid grid-cols-3 gap-4 mb-12 text-center">
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-indigo-400">24 hrs</p>
            <p className="text-xs text-slate-500">to first contact</p>
          </div>
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-emerald-400">30%</p>
            <p className="text-xs text-slate-500">avg email open rate</p>
          </div>
          <div className="bg-slate-900/40 border border-slate-800 rounded-lg p-4">
            <p className="text-2xl font-bold text-orange-400">15min</p>
            <p className="text-xs text-slate-500">setup time</p>
          </div>
        </div>
        
        {/* CTA Text */}
        <div className="text-center mb-10">
          <p className="text-lg text-slate-300 mb-6">
            Ready to see how agencies like yours are getting clients on autopilot?
          </p>
          <p className="text-sm text-slate-400 mb-1">
             Book a personalized walkthrough (5 min max)
          </p>
        </div>
        
        {/* Booking Form */}
        <div className="max-w-lg mx-auto" ref={formRef}>
          <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-700 rounded-2xl p-8 space-y-5 shadow-2xl">
            <h2 className="text-xl font-bold text-white text-center mb-2">Book Your Demo</h2>
            <p className="text-sm text-slate-400 text-center mb-4">Takes 2 minutes. We'll confirm within 24 hours.</p>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-sm transition"
                  placeholder="John Smith" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Phone Number</label>
                <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-sm transition"
                  placeholder="+1 555 000 0000" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Work Email *</label>
              <input type="email" name="email" value={formData.email} onChange={handleInputChange} required
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-sm transition"
                placeholder="john@yourcompany.com" />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Company / Agency</label>
              <input type="text" name="company" value={formData.company} onChange={handleInputChange}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-sm transition"
                placeholder="Acme Marketing Co." />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Preferred Date</label>
                <input type="date" name="preferred_date" value={formData.preferred_date} onChange={handleInputChange}
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 text-sm transition" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Preferred Time</label>
                <select name="preferred_time" value={formData.preferred_time} onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white focus:outline-none focus:border-indigo-500 text-sm transition">
                  <option value="">Any time</option>
                  <option>9:00 AM</option><option>10:00 AM</option><option>11:00 AM</option>
                  <option>12:00 PM</option><option>1:00 PM</option><option>2:00 PM</option>
                  <option>3:00 PM</option><option>4:00 PM</option><option>5:00 PM</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wide mb-1.5">Anything you'd like us to know?</label>
              <textarea name="message" value={formData.message} onChange={handleInputChange} rows={3}
                className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 text-sm transition resize-none"
                placeholder="e.g. I have 3 sales reps and want to automate lead generation for IT companies..." />
            </div>

            {error && <p className="text-sm text-red-400 text-center">{error}</p>}

            <button type="submit" disabled={submitting}
              className="w-full py-4 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white rounded-xl font-bold text-base transition shadow-xl shadow-indigo-900/40 active:scale-95 flex items-center justify-center gap-2">
              {submitting ? <><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />Booking...</> : "Book My Demo →"}
            </button>

            <p className="text-xs text-center text-slate-500">No spam. We'll only email you about your demo.</p>
          </form>
          
          {/* Trust + Urgency Footer */}
          <div className="mt-6 text-center text-xs text-slate-500 space-y-2">
            <p> Your data is private. <a href="#" className="text-indigo-400 hover:underline">Privacy policy</a></p>
            <p> Slots limited to 2 per day</p>
          </div>

          {/* Self-service path for ready buyers */}
          <div className="mt-8 pt-8 border-t border-slate-800 text-center">
            <p className="text-sm text-slate-500 mb-3">Already convinced? Skip the call.</p>
            <button
              onClick={() => { if (window.trackEvent) window.trackEvent('cta_start_free_clicked', { location: 'demo_page' }); navigate('/signup'); }}
              className="inline-flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-slate-200 hover:text-white rounded-xl font-semibold text-sm transition"
            >
              Start for free — no card needed 
            </button>
            <p className="text-xs text-slate-600 mt-2">Set up in 15 minutes. Cancel any time.</p>
          </div>
        </div>
      </div>
      
      <PublicFooter />
    </div>
  );
}

// About Page Component
function AboutPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "About TimingoFlow - Why We Built It & Who It's For",
    description: "TimingoFlow was built for agencies and B2B service providers who need consistent prospecting without hours of manual searching, copying, writing, and tracking. Learn our mission and story.",
    path: "/about",
  });
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">We Help Agencies Get Clients. That's It.</h1>
          <p className="text-xl text-slate-400">No fluff. No complexity. Just results.</p>
        </div>
        
        <div className="prose prose-invert prose-lg max-w-none">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 sm:p-12 mb-12">
            <h2 className="text-2xl font-bold mb-4">The Problem We Solve</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              Most marketing, web design, and SEO agencies are incredible at what they do. But getting consistent clients? That's the hard part.
            </p>
            <p className="text-slate-300 leading-relaxed mb-4">
              We built TimingoFlow because we experienced this ourselves. Spending hours finding leads. Crafting personalized messages. Following up manually. It was exhausting and unpredictable.
            </p>
            <p className="text-slate-300 leading-relaxed">
              So we created a workspace that brings lead search, AI outreach, follow-ups, and reply tracking into one place. Now agencies can spend less time stitching tools together and more time speaking with qualified prospects.
            </p>
          </div>
          
          <div className="grid md:grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 mb-12">
            {[
              { number: '1', label: 'Workspace for Search, Outreach, and Tracking' },
              { number: '24h', label: 'Typical Demo Follow-Up Window' },
              { number: '7 days', label: 'Free Trial to Explore the Product' },
            ].map(({ number, label }, i) => (
              <div key={i} className="bg-slate-900 border border-slate-800 rounded-xl p-6 text-center">
                <div className="text-4xl font-extrabold text-indigo-400 mb-2">{number}</div>
                <div className="text-sm text-slate-400">{label}</div>
              </div>
            ))}
          </div>
          
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 sm:p-12 mb-12">
            <h2 className="text-2xl font-bold mb-4">Our Mission</h2>
            <p className="text-slate-300 leading-relaxed mb-4">
              To make client acquisition predictable and scalable for every agency — so you can spend less time prospecting and more time growing.
            </p>
            <p className="text-slate-300 leading-relaxed">
              We believe great agencies shouldn't struggle to find clients. TimingoFlow exists to fix that.
            </p>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 sm:p-12 mb-12">
            <h2 className="text-2xl font-bold mb-4">What We Value</h2>
            <div className="grid sm:grid-cols-2 gap-6 not-prose">
              {[
                { title: 'Speed', desc: 'From search to a booked meeting should take minutes, not days.' },
                { title: 'Simplicity', desc: 'One workspace instead of five disconnected tools.' },
                { title: 'Verified data', desc: 'Leads and email verification you can actually trust before you send.' },
                { title: 'Useful AI', desc: 'AI that writes real, editable outreach — not generic filler.' },
              ].map((v, i) => (
                <div key={i} className="bg-slate-800/50 border border-slate-800 rounded-xl p-5">
                  <h3 className="font-semibold text-white mb-1">{v.title}</h3>
                  <p className="text-sm text-slate-400">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 sm:p-12 mb-12">
            <h2 className="text-2xl font-bold mb-4">Founder</h2>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl font-extrabold text-white shrink-0">RO</div>
              <div>
                <p className="font-semibold text-white text-lg mb-1">Ridwan Oyenuga</p>
                <p className="text-sm text-indigo-400 mb-3">
                  <a href="https://www.linkedin.com/in/ridwanoyenuga" target="_blank" rel="noopener noreferrer" className="hover:text-indigo-300 underline">linkedin.com/in/ridwanoyenuga</a>
                </p>
                <p className="text-slate-300 leading-relaxed mb-3">
                  Ridwan is a serial entrepreneur who has built and shipped products across technology and consumer markets, working with founders, teams, and clients around the world.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  He started TimingoFlow after seeing agencies and B2B service providers everywhere lose hours a week to manual prospecting — searching, copying, writing, and tracking by hand — work that automation could do in minutes.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 sm:p-12 mb-12">
            <h2 className="text-2xl font-bold mb-4">Where We Operate</h2>
            <p className="text-slate-300 leading-relaxed mb-2">
              TimingoFlow is a remote-first company serving agencies, consultants, and B2B service providers worldwide — including teams across the US, UK, Canada, and Australia.
            </p>
            <p className="text-slate-300 leading-relaxed">
              See our <button onClick={() => navigate('/security')} className="text-indigo-400 hover:text-indigo-300 underline">Security page</button> for how we handle your data, or reach us directly at <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300 underline">team@timingoflow.com</a>.
            </p>
          </div>

          <div className="bg-gradient-to-r from-indigo-950/50 to-purple-950/50 border border-indigo-800/50 rounded-2xl p-8 sm:p-12 text-center">
            <h2 className="text-2xl font-bold mb-4">Want to See How It Works?</h2>
            <p className="text-lg text-slate-400 mb-8">Book a demo and we'll show you exactly how TimingoFlow can work for your agency</p>
            <button 
              onClick={() => navigate('/demo')}
              className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-lg transition shadow-xl shadow-indigo-900/40"
            >
              Book a Demo
            </button>
          </div>
        </div>
      </div>
      
      <PublicFooter />
    </div>
  );
}

// Shared layout for legal/trust pages
function LegalPageLayout({ title, updated, children }) {
  const { theme } = useTheme();
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <h1 className="text-3xl sm:text-4xl font-extrabold mb-2">{title}</h1>
        <p className={`text-xs mb-10 ${theme === 'dark' ? 'text-slate-500' : 'text-gray-500'}`}>Last updated: {updated}</p>
        <div className={`space-y-8 text-sm leading-relaxed ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>
          {children}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}

function LegalSection({ heading, children }) {
  const { theme } = useTheme();
  return (
    <div>
      <h2 className={`font-bold text-lg mb-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>{heading}</h2>
      {children}
    </div>
  );
}

function PrivacyPolicyPage() {
  useSEO({
    title: "Privacy Policy - TimingoFlow",
    description: "How TimingoFlow collects, uses, and protects your data. Read our full privacy policy.",
    path: "/privacy",
  });
  return (
    <LegalPageLayout title="Privacy Policy" updated={new Date().toLocaleDateString()}>
      <LegalSection heading="1. Information We Collect">
        <p>We collect information you provide directly to us, including your name, email address, phone number, and company information when you sign up for our service or contact us.</p>
      </LegalSection>
      <LegalSection heading="2. How We Use Your Information">
        <p>We use the information we collect to:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Provide, maintain, and improve our services</li>
          <li>Send you technical notices and support messages</li>
          <li>Respond to your comments and questions</li>
          <li>Monitor and analyze trends and usage</li>
        </ul>
      </LegalSection>
      <LegalSection heading="3. Information Sharing">
        <p>We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following circumstances:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>With your consent</li>
          <li>To comply with legal obligations</li>
          <li>To protect our rights and safety</li>
        </ul>
      </LegalSection>
      <LegalSection heading="4. Data Security">
        <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. See our <a href="/security" className="text-indigo-400 hover:text-indigo-300 underline">Security page</a> for details.</p>
      </LegalSection>
      <LegalSection heading="5. Your Rights">
        <p>You have the right to access, update, or delete your personal information at any time. Contact us at team@timingoflow.com to exercise these rights.</p>
      </LegalSection>
      <LegalSection heading="6. Cookies">
        <p>We use cookies and similar tracking technologies to track activity on our service and hold certain information to improve user experience.</p>
      </LegalSection>
      <LegalSection heading="7. Contact Us">
        <p>If you have questions about this Privacy Policy, please contact us at:</p>
        <p className="mt-2">Email: <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a></p>
      </LegalSection>
    </LegalPageLayout>
  );
}

function TermsPage() {
  useSEO({
    title: "Terms of Service - TimingoFlow",
    description: "The terms and conditions governing your use of TimingoFlow.",
    path: "/terms",
  });
  return (
    <LegalPageLayout title="Terms of Service" updated={new Date().toLocaleDateString()}>
      <LegalSection heading="1. Acceptance of Terms">
        <p>By accessing and using TimingoFlow, you accept and agree to be bound by the terms and provision of this agreement.</p>
      </LegalSection>
      <LegalSection heading="2. Description of Service">
        <p>TimingoFlow provides lead generation and outreach automation services for agencies and B2B service providers. We reserve the right to modify or discontinue the service at any time.</p>
      </LegalSection>
      <LegalSection heading="3. User Accounts">
        <p>You are responsible for:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Maintaining the confidentiality of your account credentials</li>
          <li>All activities that occur under your account</li>
          <li>Notifying us immediately of any unauthorized use</li>
        </ul>
      </LegalSection>
      <LegalSection heading="4. Acceptable Use">
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 mt-2 space-y-1">
          <li>Use the service for any illegal purposes</li>
          <li>Send spam or unsolicited communications</li>
          <li>Attempt to gain unauthorized access to our systems</li>
          <li>Violate any applicable laws or regulations</li>
        </ul>
      </LegalSection>
      <LegalSection heading="5. Subscription and Payment">
        <p>Subscription fees are billed monthly or annually in advance. You may cancel your subscription at any time, and you will continue to have access until the end of your billing period.</p>
      </LegalSection>
      <LegalSection heading="6. Refund Policy">
        <p>See our full <a href="/refund-policy" className="text-indigo-400 hover:text-indigo-300 underline">Refund & Trial Policy</a> for details on our money-back guarantee.</p>
      </LegalSection>
      <LegalSection heading="7. Limitation of Liability">
        <p>TimingoFlow shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.</p>
      </LegalSection>
      <LegalSection heading="8. Termination">
        <p>We reserve the right to suspend or terminate your account if you violate these terms or engage in fraudulent activity.</p>
      </LegalSection>
      <LegalSection heading="9. Contact Us">
        <p>If you have questions about these Terms, please contact us at:</p>
        <p className="mt-2">Email: <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a></p>
      </LegalSection>
    </LegalPageLayout>
  );
}

function RefundPolicyPage() {
  useSEO({
    title: "Refund & Trial Policy - TimingoFlow",
    description: "TimingoFlow's trial length, cancellation terms, and 14-day money-back guarantee explained.",
    path: "/refund-policy",
  });
  return (
    <LegalPageLayout title="Refund & Trial Policy" updated={new Date().toLocaleDateString()}>
      <LegalSection heading="Free Trial">
        <p>New accounts start with a 7-day free trial. No card is required to start a trial. You can explore search, AI outreach, and pipeline features before deciding to subscribe.</p>
      </LegalSection>
      <LegalSection heading="Money-Back Guarantee">
        <p>We offer a 14-day money-back guarantee for first-time subscribers. If TimingoFlow doesn't help you build a usable prospect list in your first week, contact us and we'll help you set up your first campaign — or refund your payment in full.</p>
      </LegalSection>
      <LegalSection heading="Cancellation">
        <p>You can cancel your subscription at any time from your billing settings. There are no long-term contracts. You'll continue to have access until the end of your current billing period.</p>
      </LegalSection>
      <LegalSection heading="How to Request a Refund">
        <p>Email <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a> within 14 days of your initial purchase with your account email and we'll process it within 5 business days.</p>
      </LegalSection>
    </LegalPageLayout>
  );
}

function SecurityPage() {
  useSEO({
    title: "Security - TimingoFlow",
    description: "How TimingoFlow protects your account, your data, and your leads.",
    path: "/security",
  });
  return (
    <LegalPageLayout title="Security" updated={new Date().toLocaleDateString()}>
      <LegalSection heading="Your Workspace Is Private">
        <p>Every organization's leads, campaigns, and outreach data are isolated to that organization's workspace. Team members only see data for organizations they've been added to.</p>
      </LegalSection>
      <LegalSection heading="Data in Transit">
        <p>All traffic between your browser and TimingoFlow is encrypted with HTTPS/TLS.</p>
      </LegalSection>
      <LegalSection heading="Account Access">
        <p>Passwords are never stored in plain text. We recommend using a unique password for your TimingoFlow account and enabling any available account protections.</p>
      </LegalSection>
      <LegalSection heading="Where Lead Data Comes From">
        <p>TimingoFlow surfaces publicly available business information (business name, category, public contact details, and public web presence) gathered through search and enrichment, combined with email verification to flag deliverability risk. We do not sell or share your saved leads or outreach data with other customers or third parties.</p>
      </LegalSection>
      <LegalSection heading="Reporting a Concern">
        <p>If you believe you've found a security issue, email us at <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a> and we'll respond within one business day.</p>
      </LegalSection>
      <LegalSection heading="Third-Party Disclaimer">
        <p>TimingoFlow is an independent product and is not affiliated with, endorsed by, or sponsored by Google, LinkedIn, or any other third-party platform referenced on this site.</p>
      </LegalSection>
    </LegalPageLayout>
  );
}

// Features Page Component
function FeaturesPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "Features - Lead Finder, AI Outreach & Pipeline CRM | TimingoFlow",
    description: "See how TimingoFlow's Lead Finder, AI Outreach Generator, Saved Folders, Pipeline CRM, Auto Follow-ups, and Analytics work together to turn prospecting into booked meetings.",
    path: "/features",
  });

  const features = [
    {
      tag: "Lead Finder",
      title: "Find businesses by niche, city, country, and keyword",
      benefit: "Stop guessing who to contact — search by industry and location and get a targeted list in seconds instead of hours of manual Googling.",
      bullets: ["Search by niche, city, country, or keyword", "Results update in real time", "Works for any country you target"],
      screenshot: "/screenshots/lead-search-dashboard.png",
    },
    {
      tag: "AI Outreach Generator",
      title: "Generate personalized emails based on business type and offer",
      benefit: "Replace generic templates with messages that reference the specific business, so replies come from relevance, not volume.",
      bullets: ["Personalized per business", "Editable before sending", "Multiple tones and offers"],
      screenshot: "/screenshots/ai-outreach-generator.png",
    },
    {
      tag: "Saved Folders",
      title: "Organize prospects by campaign, client, niche, or country",
      benefit: "Keep every client or campaign's prospects separate so nothing gets mixed up when you're running multiple lists at once.",
      bullets: ["Folder per campaign, client, or niche", "Move or copy leads between folders", "Built for agencies managing multiple clients"],
      screenshot: "/screenshots/saved-folders.png",
    },
    {
      tag: "Pipeline CRM",
      title: "Track leads from New → Contacted → Replied → Meeting Booked → Won/Lost",
      benefit: "See exactly where every prospect stands without a separate spreadsheet, so nothing falls through the cracks.",
      bullets: ["Custom pipeline stages", "Drag-and-drop lead status", "Full history per lead"],
      screenshot: "/screenshots/pipeline-tracker.png",
    },
    {
      tag: "Auto Follow-ups",
      title: "Never forget to follow up after first outreach",
      benefit: "Automatic reminders mean a lead who didn't reply on day one still gets a second and third touch — where most deals are actually won.",
      bullets: ["Auto-send follow-ups on a schedule", "Stops automatically once a lead replies", "Pause a sequence anytime without losing progress"],
      screenshot: "/screenshots/auto-followups.png",
    },
    {
      tag: "Analytics",
      title: "Track leads found, emails generated, replies, and booked calls",
      benefit: "Know what's actually working — by campaign, by folder, by outreach message — instead of guessing at your response rate.",
      bullets: ["Leads, emails, replies, and bookings in one view", "Filter by date range or campaign", "Exportable reports"],
      screenshot: "/screenshots/analytics-report.png",
    },
  ];

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />

      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
        <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">Everything you need to prospect, reach out, and follow up</h1>
        <p className="text-lg text-slate-400 max-w-2xl mx-auto">One workspace for finding leads, writing outreach, and tracking every conversation — instead of stitching together a scraper, a mail merge tool, and a spreadsheet.</p>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-20 space-y-6">
        {features.map((f, i) => (
          <div key={i} className={`grid md:grid-cols-2 gap-8 items-center border rounded-2xl p-8 ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
            <div className={i % 2 === 1 ? 'md:order-2' : ''}>
              <div className="inline-block px-3 py-1 bg-indigo-950 border border-indigo-800 rounded-full text-xs text-indigo-300 font-semibold mb-4">{f.tag}</div>
              <h2 className="text-2xl font-bold mb-3">{f.title}</h2>
              <p className={`leading-relaxed mb-4 ${theme === 'dark' ? 'text-slate-400' : 'text-gray-600'}`}>{f.benefit}</p>
              <ul className="space-y-2 mb-6">
                {f.bullets.map((b, j) => (
                  <li key={j} className={`flex items-center gap-2 text-sm ${theme === 'dark' ? 'text-slate-300' : 'text-gray-700'}`}>
                    <span className="text-green-400">✓</span>
                    {b}
                  </li>
                ))}
              </ul>
              <button onClick={() => navigate('/signup')} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold text-sm transition">
                Try it free
              </button>
            </div>
            <div className={i % 2 === 1 ? 'md:order-1' : ''}>
              {f.screenshot ? (
                <img
                  src={f.screenshot}
                  alt={`${f.tag} screen in TimingoFlow — ${f.title}`}
                  loading="lazy"
                  className={`w-full rounded-xl border shadow-lg ${theme === 'dark' ? 'border-slate-800' : 'border-gray-200'}`}
                />
              ) : (
                <div className={`aspect-video rounded-xl border-2 border-dashed flex items-center justify-center text-sm ${theme === 'dark' ? 'border-slate-700 text-slate-500 bg-slate-800/50' : 'border-gray-300 text-gray-400 bg-gray-50'}`}>
                  Screenshot placeholder — {f.tag}
                </div>
              )}
            </div>
          </div>
        ))}
      </section>

      <section className="py-16 bg-gradient-to-r from-indigo-950/50 to-purple-950/50 border-t border-indigo-800/30">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4">See these features working on your niche</h2>
          <button onClick={() => navigate('/demo')} className="px-8 py-4 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-bold text-lg transition shadow-xl shadow-indigo-900/40">
            Book a Demo
          </button>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}

// Contact Page Component
function ContactPage() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "Contact TimingoFlow - Sales & Support",
    description: "Get in touch with TimingoFlow. Book a demo, ask about pricing, or reach our support team. We typically respond within one business day.",
    path: "/contact",
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    company: '',
    website: '',
    use_case: '',
    target_countries: '',
    monthly_volume: '',
  });
  const [submitted, setSubmitted] = useState(false);
  
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${API}/public/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) {
        const error = await response.json();
        alert(error.detail || 'Failed to send message. Please try again.');
        return;
      }
      // Track contact form submission
      if (window.trackEvent) {
        window.trackEvent('contact_form_submit', {
          subject: formData.subject,
          value: 1,
          currency: 'USD'
        });
      }
      setSubmitted(true);
    } catch (err) {
      console.error('Contact form error:', err);
      alert('Failed to send message. Please try again.');
    }
  };
  
  if (submitted) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <PublicNav />
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-20 text-center">
          <div className="bg-green-900/20 border border-green-800 rounded-2xl p-12">
            <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-6"></div>
            <h1 className="text-3xl font-bold mb-4">Message Sent!</h1>
            <p className="text-lg text-slate-400 mb-8">
              Thanks for reaching out. We'll get back to you within 24 hours.
            </p>
            <button 
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition"
            >
              Back to Home
            </button>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }
  
  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <PublicNav />
      
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4">Get In Touch</h1>
          <p className="text-xl text-slate-400">Have questions? We're here to help.</p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left: Contact Info */}
          <div>
            <div className="space-y-8 mb-8">
              <div>
                <h3 className="font-bold text-lg mb-2">Email Us</h3>
                <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300 transition">
                  team@timingoflow.com
                </a>
              </div>
              
              <div>
                <h3 className="font-bold text-lg mb-2">Call Us</h3>
                <a href="tel:+15077282258" className="text-indigo-400 hover:text-indigo-300 transition">
                  +1 (507) 728-2258
                </a>
              </div>
              
              <div>
                <h3 className="font-bold text-lg mb-2">Hours</h3>
                <p className="text-slate-400">Monday - Friday: 9AM - 6PM EST</p>
              </div>
            </div>
            
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
              <h3 className="font-bold mb-3">Prefer a demo instead?</h3>
              <p className="text-sm text-slate-400 mb-4">
                Want us to show you how TimingoFlow can find leads for your niche? Book a demo.
              </p>
              <button
                onClick={() => navigate('/demo')}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition w-full"
              >
                Book a Demo
              </button>
            </div>
          </div>
          
          {/* Right: Contact Form */}
          <div>
            <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 space-y-5">
              <div>
                <label className="block text-sm font-medium mb-2">Name *</label>
                <input 
                  type="text" 
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                  placeholder="Your name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Email *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                  placeholder="your@email.com"
                />
              </div>
              
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Company</label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                    placeholder="Your company or agency"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Website</label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                    placeholder="yourcompany.com"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">What do you sell?</label>
                <select
                  name="use_case"
                  value={formData.use_case}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-indigo-600 transition"
                >
                  <option value="">Select an option</option>
                  <option value="marketing_agency">Marketing agency services</option>
                  <option value="web_design">Web design / development</option>
                  <option value="seo">SEO services</option>
                  <option value="consulting">B2B consulting</option>
                  <option value="saas">SaaS product</option>
                  <option value="recruiting">Recruiting / staffing</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium mb-2">Countries you target</label>
                  <input
                    type="text"
                    name="target_countries"
                    value={formData.target_countries}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                    placeholder="e.g. US, UK, Canada"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Monthly outreach volume</label>
                  <select
                    name="monthly_volume"
                    value={formData.monthly_volume}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white focus:outline-none focus:border-indigo-600 transition"
                  >
                    <option value="">Select a range</option>
                    <option value="<500">Under 500</option>
                    <option value="500-2000">500 - 2,000</option>
                    <option value="2000-5000">2,000 - 5,000</option>
                    <option value="5000+">5,000+</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subject *</label>
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Message *</label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  rows={5}
                  className="w-full px-4 py-3 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-indigo-600 transition resize-none"
                  placeholder="Tell us about your needs..."
                />
              </div>
              
              <button 
                type="submit"
                className="w-full px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-semibold transition"
              >
                Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}

function LandingPage() {
  const navigate = useNavigate();
  useSEO({
    title: "Watch the TimingoFlow Demo - Find Leads, Write Outreach, Book Meetings",
    description: "A 3-minute walkthrough of how TimingoFlow finds verified B2B leads, generates personalized AI outreach, and tracks follow-ups automatically.",
    path: "/landing",
  });
  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      <nav className="flex items-center justify-between px-4 sm:px-8 py-4 border-b border-slate-800/30">
        <div className="flex items-center gap-2.5">
          <img src={TimingoflowLogo} alt="TimingoFlow" className="w-12 h-12" />
        </div>
        <button onClick={() => alert("Demo - video would play here")} className="px-4 sm:px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-sm font-bold transition shadow-lg shadow-indigo-900/30">Watch Demo</button>
      </nav>
      <main className="flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-6 py-12 sm:py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
        <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-950 border border-indigo-800 rounded-full text-xs text-indigo-300 font-semibold mb-6">Watch the AI prospecting workspace in action</div>
        <h1 className="text-4xl sm:text-6xl font-black tracking-tight mb-6 max-w-4xl leading-tight">See how agencies can{" "}<span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-cyan-400 to-blue-400">find leads, personalize outreach, and track replies</span>{" "}from one dashboard</h1>
        <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mb-8 leading-relaxed font-light">This 3-minute demo shows the self-serve workflow from lead search to follow-up tracking.</p>
        <div className="flex flex-col items-center gap-4 mb-10">
          <button onClick={() => alert("Demo - video would play here")} className="px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-white rounded-2xl font-black text-lg sm:text-xl transition transform hover:scale-105 shadow-2xl shadow-cyan-900/50">Watch 3-Min Demo</button>
          <p className="text-slate-400 text-sm sm:text-base">No signup required</p>
        </div>
        <div className="mt-10 sm:mt-12 p-5 sm:p-6 bg-slate-800/40 border border-slate-700/60 rounded-2xl max-w-2xl w-full backdrop-blur-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6">
            <div className="text-center"><div className="text-2xl sm:text-3xl font-black text-emerald-400">142</div><div className="text-xs sm:text-sm text-slate-400 mt-1">Messages sent today</div></div>
            <div className="text-center"><div className="text-2xl sm:text-3xl font-black text-cyan-400">68%</div><div className="text-xs sm:text-sm text-slate-400 mt-1">Open rate</div></div>
            <div className="text-center"><div className="text-2xl sm:text-3xl font-black text-blue-400">23%</div><div className="text-xs sm:text-sm text-slate-400 mt-1">Reply rate</div></div>
            <div className="text-center"><div className="text-2xl sm:text-3xl font-black text-violet-400">8</div><div className="text-xs sm:text-sm text-slate-400 mt-1">Interested</div></div>
          </div>
          <div className="mt-4 pt-4 border-t border-slate-700/40 text-xs text-slate-500 text-center">Example workspace snapshot</div>
        </div>
      </main>
      <div className="px-4 sm:px-6 py-16 sm:py-20 bg-slate-900/50 border-t border-slate-800/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-12 sm:mb-16">How it works</h2>
          <div className="space-y-8 sm:space-y-10">
            {[{num:"1",title:"We find businesses needing your service"},{num:"2",title:"We contact them with personalized outreach"},{num:"3",title:"They reply and book meetings with you"}].map((step) => (<div key={step.num} className="flex items-start gap-5 sm:gap-6"><div className="flex-shrink-0"><div className="flex items-center justify-center h-12 w-12 rounded-full bg-indigo-600 text-white font-black text-lg">{step.num}</div></div><p className="text-lg sm:text-xl font-bold text-white">{step.title}</p></div>))}
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-16 sm:py-20 bg-slate-950 border-t border-slate-800/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-12">Recent Campaign Snapshot</h2>
          <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-10 sm:mb-12">
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 sm:p-6 text-center"><div className="text-3xl sm:text-4xl font-black text-emerald-400 mb-2">142</div><div className="text-sm text-slate-400">Messages sent</div></div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 sm:p-6 text-center"><div className="text-3xl sm:text-4xl font-black text-blue-400 mb-2">18</div><div className="text-sm text-slate-400">Replies received</div></div>
            <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 sm:p-6 text-center"><div className="text-3xl sm:text-4xl font-black text-amber-400 mb-2">8</div><div className="text-sm text-slate-400">Interested leads</div></div>
          </div>
          <div className="bg-slate-800/50 border border-slate-700/60 rounded-2xl p-6 sm:p-8">
            <p className="text-slate-300 text-base sm:text-lg italic mb-4">"Yes, we are interested - when can we speak?"</p>
            <p className="text-slate-500 text-sm">- Real reply from live campaign</p>
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-16 sm:py-20 bg-slate-900/50 border-t border-slate-800/30">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-black text-center mb-10 sm:mb-12">Who this is for</h2>
          <div className="space-y-4 sm:space-y-5">
            {["Agencies that want consistent clients","Agencies tired of manual outreach","Agencies already closing deals but need more pipeline"].map((item, idx) => (<div key={idx} className="flex items-center gap-4 p-4 sm:p-5 bg-slate-800 border border-slate-700 rounded-xl"><span className="text-emerald-400 text-xl font-bold">&#10003;</span><span className="text-base sm:text-lg text-slate-200">{item}</span></div>))}
          </div>
        </div>
      </div>
      <div className="px-4 sm:px-6 py-16 sm:py-20 bg-gradient-to-b from-slate-900 to-slate-950 border-t border-slate-800/30">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-black mb-6">Ready to transform your pipeline?</h2>
          <p className="text-slate-400 text-base sm:text-lg mb-8">Start your demo and see exactly how it works for your agency</p>
          <button onClick={() => navigate("/signup")} className="px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white rounded-2xl font-black text-lg transition transform hover:scale-105 shadow-2xl shadow-indigo-900/50">See how this works for your agency</button>
        </div>
      </div>
      <footer className="text-center py-6 text-xs sm:text-sm text-slate-600 border-t border-slate-800/30 flex flex-col items-center gap-2">
        <span>TimingoFlow - Built for agencies</span>
        <span>Questions? <a href="mailto:team@timingoflow.com" className="text-indigo-500 hover:text-indigo-400 transition">Contact us</a></span>
      </footer>
    </div>
  );
}

// --- Login Page ---------------------------------------------------------------


function LoginPage() {


  const { login } = useAuth();
  const { theme } = useTheme();
  useSEO({
    title: "Log In - TimingoFlow",
    description: "Log in to your TimingoFlow workspace to access your leads, outreach campaigns, and pipeline.",
    path: "/login",
    noindex: true,
  });

  const navigate   = useNavigate();


  const [form, setForm]   = useState({ email: "", password: "" });


  const [error, setError] = useState("");


  const [loading, setLoading] = useState(false);





  const handleSubmit = async (e) => {


    e.preventDefault();


    setLoading(true); setError("");


    try {


      const resp = await fetch(`${API}/auth/login`, {


        method: "POST", headers: { "Content-Type": "application/json" },


        body: JSON.stringify({ email: form.email.trim(), password: form.password }),


      });


      const data = await resp.json();


      if (!resp.ok) {


        if (data.detail === "pending")  { navigate("/pending"); return; }


        if (data.detail === "rejected") { navigate("/rejected"); return; }

        if (data.detail === "suspended") { setError("Your account has been suspended. Contact team@timingoflow.com for assistance."); return; }

        throw new Error(data.detail || "Login failed.");


      }


      login(data.token, data.user);


      navigate(data.user.role === "admin" ? "/admin" : "/app");


    } catch (err) { setError(err.message); }


    finally { setLoading(false); }


  };





  return (


    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">


      <div className="w-full max-w-sm">


        <div className="text-center mb-8">


                     <img src={TimingoflowLogo} alt="TimingoFlow" className="w-16 h-16" />



          <h1 className="text-2xl font-extrabold tracking-tight">Welcome back</h1>


          <p className="text-slate-500 text-sm mt-1">Sign in to your TimingoFlow account</p>


        </div>


        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col gap-4">


          {error && <div className="bg-red-950/60 border border-red-800 text-red-300 text-xs px-3 py-2 rounded-lg">{error}</div>}


          <div className="flex flex-col gap-1.5">


            <label className="text-xs text-slate-400 font-semibold">Email</label>


            <input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} required


              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition"


              placeholder="you@example.com" />


          </div>


          <div className="flex flex-col gap-1.5">


            <label className="text-xs text-slate-400 font-semibold">Password</label>


            <input type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} required


              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition" />


          </div>


          <button type="submit" disabled={loading}


            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg py-2.5 font-semibold text-sm transition mt-1">


            {loading ? "Signing in..." : "Sign In"}


          </button>


        </form>

        <p className="text-center text-xs text-slate-600 mt-3 flex items-center justify-center gap-1">
          <svg className="w-3.5 h-3.5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
          Your workspace data is private.
        </p>

        <p className="text-center text-xs text-slate-600 mt-4">


          No account?{" "}


          <button onClick={() => navigate("/signup")} className="text-indigo-400 hover:text-indigo-300 transition font-semibold">Sign up</button>


          {" ... "}


          <button onClick={() => navigate("/")} className="text-slate-500 hover:text-slate-300 transition">Back to home</button>

          {" ... "}

          <a href="mailto:team@timingoflow.com" className="text-slate-500 hover:text-slate-300 transition">Need help?</a>


        </p>


      </div>


    </div>


    <PublicFooter />


  </div>


  );


}





// --- Signup Page --------------------------------------------------------------


function SignupPage() {


  const navigate = useNavigate();
  const { theme } = useTheme();
  useSEO({
    title: "Start Your Free Trial - TimingoFlow",
    description: "Start finding verified B2B leads in under 5 minutes. Search by niche and location, generate AI-personalized outreach, and track follow-ups. No card required for your trial.",
    path: "/signup",
  });

  const [form, setForm] = useState({ name: "", email: "", password: "", confirm: "" });
  const [error, setError]           = useState("");
  const [success, setSuccess]       = useState(false);
  const [loading, setLoading]       = useState(false);
  const [verifyStep, setVerifyStep] = useState(false);
  const [vCode, setVCode]           = useState("");





  const handleSubmit = async (e) => {


    e.preventDefault();


    if (form.password !== form.confirm) { setError("Passwords do not match."); return; }


    if (form.password.length < 6)       { setError("Password must be at least 6 characters."); return; }


    setLoading(true); setError("");


    try {


      const resp = await fetch(`${API}/auth/signup`, {


        method: "POST", headers: { "Content-Type": "application/json" },


        body: JSON.stringify({ name: form.name.trim(), email: form.email.trim(), password: form.password }),


      });


      const data = await resp.json();


      if (!resp.ok) throw new Error(data.detail || "Signup failed.");
      // Track signup event
      if (window.trackEvent) {
        window.trackEvent('signup_initiated', {
          value: 1,
          currency: 'USD'
        });
      }
      if (data.skip_verify) { setSuccess(true); return; }
      setVerifyStep(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!vCode.trim()) return;
    setLoading(true); setError("");
    try {
      const resp = await fetch(`${API}/auth/verify-signup`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: form.email.trim(), code: vCode.trim() }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Verification failed.");
      setSuccess(true);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };





  if (success) return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm text-center">
        <div className="text-5xl mb-4"></div>
        <h1 className="text-2xl font-extrabold mb-2">Email Verified!</h1>
        <p className="text-slate-400 text-sm leading-relaxed mb-3">Your account is <strong className="text-amber-400">pending admin approval</strong>. You'll receive access once an administrator reviews your request.</p>
        <p className="text-slate-500 text-xs mb-6">Need help? Contact <span className="text-indigo-400">team@timingoflow.com</span></p>
        <button onClick={() => navigate("/login")} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold text-sm transition">Go to Login</button>
      </div>
    </div>
    <PublicFooter />
  </div>
  );

  if (verifyStep) return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <div className="w-12 h-12 rounded-2xl bg-indigo-600 flex items-center justify-center text-xl mx-auto mb-4 shadow-xl shadow-indigo-900/50"></div>
          <h1 className="text-2xl font-extrabold tracking-tight">Check your email</h1>
          <p className="text-slate-500 text-sm mt-1">We sent a 6-digit code to <strong className="text-slate-300">{form.email}</strong></p>
        </div>
        <form onSubmit={handleVerify} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col gap-4">
          {error && <div className="bg-red-950/60 border border-red-800 text-red-300 text-xs px-3 py-2 rounded-lg">{error}</div>}
          <div className="flex flex-col gap-1.5">
            <label className="text-xs text-slate-400 font-semibold">Verification Code</label>
            <input type="text" inputMode="numeric" maxLength={6} value={vCode}
              onChange={e => setVCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000" autoFocus
              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-3 text-xl outline-none transition text-center tracking-[0.5em] font-bold" />
          </div>
          <p className="text-[11px] text-slate-500 leading-relaxed">Check your spam folder if you don't see it. The code expires in 15 minutes.</p>
          <button type="submit" disabled={loading || vCode.length !== 6}
            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg py-2.5 font-semibold text-sm transition">
            {loading ? "Verifying..." : "Verify Email"}
          </button>
        </form>
        <p className="text-center text-xs text-slate-600 mt-4">
          Wrong email?{" "}<button onClick={() => { setVerifyStep(false); setError(""); setVCode(""); }} className="text-indigo-400 hover:text-indigo-300 transition font-semibold">Go back</button>
          {" – "}<button onClick={() => navigate("/login")} className="text-slate-500 hover:text-slate-300 transition">Sign in instead</button>
        </p>
      </div>
    </div>
    <PublicFooter />
  </div>
  );





  return (


    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">

      <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-12 items-center">

      {/* Benefits sidebar */}
      <div className="hidden lg:block">
        <h2 className="text-3xl font-extrabold tracking-tight mb-4">Start finding leads in less than 5 minutes.</h2>
        <div className="space-y-4">
          {[
            { title: 'Search businesses by niche and location', desc: 'Find targeted prospects in seconds instead of hours of manual searching.' },
            { title: 'Generate personalized outreach', desc: 'AI writes messages tailored to each business — you review before sending.' },
            { title: 'Track follow-ups in one dashboard', desc: 'See every lead\'s status and never let a reply go unanswered.' },
          ].map((b, i) => (
            <div key={i} className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-indigo-600 text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">✓</span>
              <div>
                <p className="font-semibold text-white text-sm">{b.title}</p>
                <p className="text-slate-400 text-sm">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="w-full max-w-sm mx-auto lg:mx-0">


        <div className="text-center mb-8">


                   <img src={TimingoflowLogo} alt="TimingoFlow" className="w-16 h-16" />



          <h1 className="text-2xl font-extrabold tracking-tight">Create account</h1>


          <p className="text-slate-500 text-sm mt-1">Start getting consistent client opportunities</p>

          <p className="text-slate-500 text-xs mt-2">No credit card required. 7-day free trial.</p>


        </div>


        <form onSubmit={handleSubmit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 flex flex-col gap-4">


          {error && <div className="bg-red-950/60 border border-red-800 text-red-300 text-xs px-3 py-2 rounded-lg">{error}</div>}


          {[


            { key: "name",     label: "Full Name",        type: "text",     placeholder: "Jane Smith" },


            { key: "email",    label: "Email",            type: "email",    placeholder: "you@example.com" },


            { key: "password", label: "Password",         type: "password", placeholder: "Min. 6 characters" },


            { key: "confirm",  label: "Confirm Password", type: "password", placeholder: "Confirm your password" },


          ].map(({ key, label, type, placeholder }) => (


            <div key={key} className="flex flex-col gap-1.5">


              <label className="text-xs text-slate-400 font-semibold">{label}</label>


              <input type={type} value={form[key]} onChange={(e) => setForm((p) => ({ ...p, [key]: e.target.value }))} required


                placeholder={placeholder}


                className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition" />


            </div>


          ))}


          <button type="submit" disabled={loading}


            className="bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg py-2.5 font-semibold text-sm transition mt-1">


            {loading ? "Creating Account..." : "Create Account"}


          </button>


        </form>


        <p className="text-center text-xs text-slate-600 mt-4">


          Already have an account?{" "}


          <button onClick={() => navigate("/login")} className="text-indigo-400 hover:text-indigo-300 transition font-semibold">Sign in</button>


        </p>


      </div>

      </div>

    </div>
    <PublicFooter />
  </div>
  );


}





// --- Pending / Rejected pages -------------------------------------------------


function PendingPage() {


  const { logout } = useAuth();
  const { theme } = useTheme();
  useSEO({
    title: "Account Pending - TimingoFlow",
    description: "Your TimingoFlow account is awaiting approval.",
    path: "/pending",
    noindex: true,
  });

  const navigate = useNavigate();


  return (


    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">


      <div className="text-center max-w-sm">


        <div className="text-5xl mb-4"></div><h1 className="text-2xl font-extrabold mb-2">Awaiting Approval</h1>


        <p className="text-slate-400 text-sm leading-relaxed mb-4">Your account is pending admin review. You will receive access once an administrator approves your request.</p>


        <p className="text-slate-500 text-xs mb-6">Issues? Contact us at <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a></p>


        <button onClick={() => { logout(); navigate("/login"); }} className="px-5 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-lg text-sm font-semibold transition">Sign Out</button>


      </div>


    </div>


    <PublicFooter />


  </div>


  );


}


function RejectedPage() {


  const { logout } = useAuth();
  const { theme } = useTheme();
  useSEO({
    title: "Account Not Approved - TimingoFlow",
    description: "Your TimingoFlow account request was not approved.",
    path: "/rejected",
    noindex: true,
  });

  const navigate = useNavigate();


  return (


    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>
      <PublicNav />
      <div className="flex-1 flex items-center justify-center px-4 py-12">


      <div className="text-center max-w-sm">


        <div className="text-5xl mb-4"></div><h1 className="text-2xl font-extrabold mb-2">Access Denied</h1>


        <p className="text-slate-400 text-sm leading-relaxed mb-4">Your account request was not approved. If you believe this is a mistake, contact us at <a href="mailto:team@timingoflow.com" className="text-indigo-400 hover:text-indigo-300">team@timingoflow.com</a>.</p>


        <button onClick={() => { logout(); navigate("/login"); }} className="px-5 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-lg text-sm font-semibold transition">Back to Login</button>


      </div>


    </div>


    <PublicFooter />


  </div>


  );


}





// --- Admin Panel --------------------------------------------------------------


// ─── Billing Page ─────────────────────────────────────────────────────────────
function BillingPage() {
  const { authFetch, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard"); // dashboard | customers | invoices | settings
  const [settings, setSettings] = useState({ company_name:"", company_address:"", company_phone:"", company_email:"", logo_url:"", invoice_prefix:"INV" });
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [analytics, setAnalytics] = useState({ by_currency:[], monthly:[], recent:[] });
  const [loading, setLoading] = useState(true);

  // Invoice creation state
  const [creating, setCreating] = useState(false);
  const [invForm, setInvForm] = useState({ customer_id:"", customer_name:"", customer_email:"", customer_phone:"", customer_address:"", customer_company:"", currency:"NGN", tax_rate:0, due_date:"", notes:"", line_items:[{ description:"", quantity:1, unit_price:"" }] });
  const [invSaving, setInvSaving] = useState(false);
  const [preview, setPreview] = useState(null); // invoice object to preview
  const [sending, setSending] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [isLive, setIsLive] = useState(true);
  const [genLink, setGenLink] = useState(null); // { inv_id, url }
  const [generatingLink, setGeneratingLink] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(null);
  const [newCust, setNewCust] = useState(null); // null or form obj
  const [custSaving, setCustSaving] = useState(false);
  const [msg, setMsg] = useState("");

  const inp = "w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-indigo-500 placeholder-slate-600 transition";

  // Handle redirect back from GlobalPay payment page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paid = params.get("paid");
    const ref = params.get("ref");
    if (paid === "1") {
      setMsg(`✅ Payment confirmed! Invoice with ref ${ref || ""} marked as paid.`);
      setTab("invoices");
      window.history.replaceState({}, "", "/billing");
      load();
    } else if (paid === "0") {
      setMsg("Payment could not be automatically verified. Use 'Check Payment' on the invoice.");
      window.history.replaceState({}, "", "/billing");
    }
  }, []);

  const checkPayment = async (inv) => {
    setCheckingPayment(inv.id);
    try {
      const r = await authFetch(`/admin/billing/invoices/${inv.id}/requery`, { method:"POST" });
      const d = await r.json();
      if (!r.ok) { setMsg(d.detail || "Requery failed"); return; }
      if (d.paid) { setMsg(`✅ Payment confirmed! Invoice ${inv.invoice_number} marked as paid.`); load(); }
      else { setMsg(`Payment not yet received (code: ${d.response_code || "unknown"}). Try again in a moment.`); }
    } catch(e) { setMsg(`Error: ${e.message}`); }
    setCheckingPayment(null);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [s, c, inv, an, gp] = await Promise.all([
        authFetch("/admin/billing/settings").then(r => r.json()),
        authFetch("/admin/billing/customers").then(r => r.json()),
        authFetch("/admin/billing/invoices").then(r => r.json()),
        authFetch("/admin/billing/analytics").then(r => r.json()),
        authFetch("/admin/globalpay/settings").then(r => r.json()),
      ]);
      setSettings(s); setCustomers(c); setInvoices(inv); setAnalytics(an);
      setApiKey(gp.api_key || ""); setIsLive(gp.is_live !== false);
    } catch(e) {}
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    await authFetch("/admin/billing/settings", { method:"PUT", body:JSON.stringify(settings) });
    await authFetch("/admin/globalpay/settings", { method:"PUT", body:JSON.stringify({ api_key:apiKey, is_live:isLive }) });
    setSettingsSaved(true); setTimeout(()=>setSettingsSaved(false), 2500);
  };

  const saveCust = async () => {
    setCustSaving(true);
    try {
      if (newCust.id) {
        await authFetch(`/admin/billing/customers/${newCust.id}`, { method:"PUT", body:JSON.stringify(newCust) });
      } else {
        await authFetch("/admin/billing/customers", { method:"POST", body:JSON.stringify(newCust) });
      }
      setNewCust(null); load();
    } catch(e) {}
    setCustSaving(false);
  };

  const deleteCust = async (id) => {
    if (!window.confirm("Delete this customer?")) return;
    await authFetch(`/admin/billing/customers/${id}`, { method:"DELETE" });
    load();
  };

  const selectCustomer = (cid) => {
    const c = customers.find(x => x.id === parseInt(cid));
    if (c) setInvForm(f => ({ ...f, customer_id:c.id, customer_name:c.name, customer_email:c.email, customer_phone:c.phone, customer_address:c.address, customer_company:c.company }));
    else setInvForm(f => ({ ...f, customer_id:"" }));
  };

  const addLine = () => setInvForm(f => ({ ...f, line_items:[...f.line_items, { description:"", quantity:1, unit_price:"" }] }));
  const removeLine = (i) => setInvForm(f => ({ ...f, line_items:f.line_items.filter((_,j)=>j!==i) }));
  const setLine = (i, field, val) => setInvForm(f => { const li=[...f.line_items]; li[i]={...li[i],[field]:val}; return {...f,line_items:li}; });

  const subtotal = invForm.line_items.reduce((s,l) => s + (parseFloat(l.quantity)||0)*(parseFloat(l.unit_price)||0), 0);
  const taxAmt = subtotal * (parseFloat(invForm.tax_rate)||0) / 100;
  const total = subtotal + taxAmt;

  const createInvoice = async () => {
    setInvSaving(true);
    try {
      const r = await authFetch("/admin/billing/invoices", { method:"POST", body:JSON.stringify({ ...invForm, tax_rate: parseFloat(invForm.tax_rate)||0 }) });
      const d = await r.json();
      if (!r.ok) { setMsg(d.detail||"Error"); return; }
      setMsg(`Invoice ${d.invoice_number} created!`);
      setCreating(false);
      setInvForm({ customer_id:"", customer_name:"", customer_email:"", customer_phone:"", customer_address:"", customer_company:"", currency:"NGN", tax_rate:0, due_date:"", notes:"", line_items:[{ description:"", quantity:1, unit_price:"" }] });
      load();
    } catch(e) { setMsg(e.message); }
    setInvSaving(false);
  };

  const sendInvoice = async (id) => {
    setSending(id);
    try {
      const r = await authFetch(`/admin/billing/invoices/${id}/send`, { method:"POST" });
      const d = await r.json();
      if (!r.ok) { setMsg(d.detail||"Send failed"); } else { setMsg("Invoice sent!"); load(); }
    } catch(e) { setMsg(e.message); }
    setSending(null);
  };

  const markPaid = async (id) => {
    await authFetch(`/admin/billing/invoices/${id}`, { method:"PATCH", body:JSON.stringify({ status:"paid", paid_at: new Date().toISOString() }) });
    load();
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice?")) return;
    await authFetch(`/admin/billing/invoices/${id}`, { method:"DELETE" });
    load();
  };

  const generatePaymentLink = async (inv) => {
    if (!apiKey) { setMsg("Add your GlobalPay API key in Settings first."); return; }
    setGeneratingLink(inv.id);
    try {
      const endpoint = isLive
        ? "https://paygw.globalpay.com.ng/globalpay-paymentgateway/api/paymentgateway/generate-payment-link"
        : "https://newwebservicetest.zenithbank.com:8443/new-globalpay-paymentgateway-external/api/paymentgateway/generate-payment-link";
      const ref = `TF-${Date.now().toString(36).toUpperCase()}`;
      const resp = await fetch(endpoint, {
        method:"POST",
        headers:{ apiKey, "Content-Type":"application/json", Language:"en" },
        body:JSON.stringify({ amount:parseFloat(inv.total), merchantTransactionReference:ref, redirectUrl:"https://leadgen-1087946781393.europe-west1.run.app/billing/payment-return",
          customer:{ firstName:inv.customer_name.split(" ")[0]||inv.customer_name, lastName:inv.customer_name.split(" ").slice(1).join(" ")||"-",
            emailAddress:inv.customer_email, phoneNumber:inv.customer_phone||"00000000000", currency:inv.currency, address:inv.customer_address||"" } })
      });
      const data = await resp.json();
      const url = data?.data?.checkoutUrl;
      if (!url) { setMsg(`GlobalPay error: ${JSON.stringify(data)}`); return; }
      await authFetch(`/admin/billing/invoices/${inv.id}`, { method:"PATCH", body:JSON.stringify({ checkout_url:url, reference:ref }) });
      setMsg("Payment link generated!"); setGenLink({ inv_id:inv.id, url }); load();
    } catch(e) { setMsg(`Error: ${e.message}`); }
    setGeneratingLink(null);
  };

  const statusBadge = { draft:"bg-slate-700 text-slate-300", sent:"bg-blue-900/50 text-blue-300", paid:"bg-emerald-900/50 text-emerald-300", cancelled:"bg-red-900/50 text-red-400" };
  const currSymbol = { NGN:"₦", USD:"$", GBP:"£", EUR:"€" };

  return (
    <div className="min-h-screen bg-slate-950 text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 border-b border-slate-800 bg-slate-900">
        <div className="flex items-center gap-3">
          <img src={TimingoflowLogo} alt="Logo" className="w-10 h-10" />
          <span className="font-bold text-sm">TimingoFlow</span>
          <span className="text-slate-600">|</span>
          <span className="text-xs text-emerald-400 font-bold uppercase tracking-widest">Billing</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/admin")} className="text-xs px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-400 hover:text-white rounded-lg transition">← Admin</button>
          <button onClick={() => navigate("/")} className="text-xs px-3 py-1.5 bg-indigo-950 border border-indigo-800 text-indigo-400 rounded-lg transition">App</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="text-xs px-3 py-1.5 border border-slate-700 text-slate-500 hover:text-red-400 rounded-lg transition">Sign Out</button>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex gap-1 px-6 pt-5 border-b border-slate-800">
        {[["dashboard","📊 Dashboard"],["invoices","🧾 Invoices"],["customers","👥 Customers"],["settings","⚙ Settings"]].map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition ${tab===id ? "border-indigo-500 text-white bg-slate-900" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {msg && <div className="mx-6 mt-4 px-4 py-2 bg-slate-800 border border-slate-700 text-slate-300 text-sm rounded-xl flex items-center justify-between"><span>{msg}</span><button onClick={()=>setMsg("")} className="text-slate-500 hover:text-white ml-4">×</button></div>}

      <div className="flex-1 p-6 overflow-auto">
        {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"/></div> : (<>

        {/* DASHBOARD */}
        {tab==="dashboard" && (
          <div className="space-y-6 max-w-5xl">
            {/* Summary cards by currency */}
            {analytics.by_currency.length === 0
              ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500">No invoice data yet. Create your first invoice.</div>
              : <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {analytics.by_currency.map(row => (
                  <div key={row.currency} className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                    <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">{row.currency}</p>
                    <p className="text-3xl font-extrabold text-emerald-400">{currSymbol[row.currency]||""}{Number(row.total_paid||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</p>
                    <p className="text-xs text-slate-500 mt-1">Collected · {row.paid_count} invoice{row.paid_count!==1?"s":""}</p>
                    <p className="text-sm text-amber-300 mt-2 font-semibold">{currSymbol[row.currency]||""}{Number(row.total_pending||0).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})} pending</p>
                  </div>
                ))}
              </div>}

            {/* Recent invoices */}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-800 font-semibold text-slate-300 text-sm">Recent Invoices</div>
              {analytics.recent.length === 0
                ? <p className="px-5 py-8 text-slate-600 text-sm text-center">No invoices yet</p>
                : <div className="divide-y divide-slate-800/60">
                  {analytics.recent.map(inv => (
                    <div key={inv.id} className="flex items-center gap-4 px-5 py-3 hover:bg-slate-800/30 transition">
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-white truncate">{inv.customer_name}</p>
                        <p className="text-xs text-slate-500">{inv.invoice_number} · {new Date(inv.created_at).toLocaleDateString()}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-white">{currSymbol[inv.currency]||""}{Number(inv.total).toLocaleString()}</p>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold capitalize ${statusBadge[inv.status]||"bg-slate-700 text-slate-400"}`}>{inv.status}</span>
                      </div>
                    </div>
                  ))}
                </div>}
            </div>
          </div>
        )}

        {/* INVOICES */}
        {tab==="invoices" && (
          <div className="space-y-5 max-w-5xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Invoices</h2>
              <button onClick={() => setCreating(true)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition">+ New Invoice</button>
            </div>

            {/* Create invoice form */}
            {creating && (
              <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-white text-lg">New Invoice</h3>
                  <button onClick={() => setCreating(false)} className="text-slate-500 hover:text-white text-xl">×</button>
                </div>

                {/* Customer select */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Select Saved Customer</label>
                    <select onChange={e=>selectCustomer(e.target.value)} className={inp}>
                      <option value="">— Enter manually below —</option>
                      {customers.map(c => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Currency</label>
                    <select value={invForm.currency} onChange={e=>setInvForm(f=>({...f,currency:e.target.value}))} className={inp}>
                      <option value="NGN">NGN — Nigerian Naira</option>
                      <option value="USD">USD — US Dollar</option>
                      <option value="GBP">GBP — British Pound</option>
                      <option value="EUR">EUR — Euro</option>
                    </select>
                  </div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Customer Name *</label><input value={invForm.customer_name} onChange={e=>setInvForm(f=>({...f,customer_name:e.target.value}))} placeholder="John Doe" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Customer Email *</label><input value={invForm.customer_email} onChange={e=>setInvForm(f=>({...f,customer_email:e.target.value}))} placeholder="client@email.com" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Phone</label><input value={invForm.customer_phone} onChange={e=>setInvForm(f=>({...f,customer_phone:e.target.value}))} placeholder="08100000000" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Company</label><input value={invForm.customer_company} onChange={e=>setInvForm(f=>({...f,customer_company:e.target.value}))} placeholder="Acme Ltd" className={inp}/></div>
                  <div className="sm:col-span-2"><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Address</label><input value={invForm.customer_address} onChange={e=>setInvForm(f=>({...f,customer_address:e.target.value}))} placeholder="123 Main St, Lagos" className={inp}/></div>
                </div>

                {/* Line items */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-[11px] text-slate-500 uppercase tracking-wider">Line Items</label>
                    <button onClick={addLine} className="text-xs text-indigo-400 hover:text-indigo-300">+ Add Line</button>
                  </div>
                  <div className="space-y-2">
                    {invForm.line_items.map((line,i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center">
                        <input value={line.description} onChange={e=>setLine(i,"description",e.target.value)} placeholder="Description" className={`col-span-6 ${inp}`}/>
                        <input type="number" value={line.quantity} onChange={e=>setLine(i,"quantity",e.target.value)} placeholder="Qty" min="1" className={`col-span-2 ${inp}`}/>
                        <input type="number" value={line.unit_price} onChange={e=>setLine(i,"unit_price",e.target.value)} placeholder="Price" className={`col-span-3 ${inp}`}/>
                        <button onClick={()=>removeLine(i)} className="col-span-1 text-slate-600 hover:text-red-400 text-lg transition">×</button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Totals + extras */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Tax Rate (%)</label><input type="number" value={invForm.tax_rate} onChange={e=>setInvForm(f=>({...f,tax_rate:e.target.value}))} placeholder="0" min="0" max="100" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Due Date</label><input type="date" value={invForm.due_date} onChange={e=>setInvForm(f=>({...f,due_date:e.target.value}))} className={inp}/></div>
                  <div className="flex flex-col justify-end">
                    <p className="text-xs text-slate-500">Subtotal: {invForm.currency} {subtotal.toFixed(2)}</p>
                    {taxAmt > 0 && <p className="text-xs text-slate-500">Tax: {invForm.currency} {taxAmt.toFixed(2)}</p>}
                    <p className="text-lg font-bold text-white">Total: {invForm.currency} {total.toFixed(2)}</p>
                  </div>
                </div>
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Notes</label><textarea value={invForm.notes} onChange={e=>setInvForm(f=>({...f,notes:e.target.value}))} rows={2} placeholder="Payment terms, thank you notes..." className={`${inp} resize-none`}/></div>

                <div className="flex gap-3">
                  <button onClick={createInvoice} disabled={invSaving || !invForm.customer_name || !invForm.customer_email || !total}
                    className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-xl transition">
                    {invSaving ? "Creating..." : "Create Invoice"}
                  </button>
                  <button onClick={() => setCreating(false)} className="px-6 py-3 bg-slate-800 border border-slate-700 text-slate-400 rounded-xl transition hover:text-white">Cancel</button>
                </div>
              </div>
            )}

            {/* Invoice list */}
            {invoices.length === 0 && !creating
              ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500">No invoices yet. Click "New Invoice" to create one.</div>
              : <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[700px]">
                    <thead><tr className="bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-wider border-b border-slate-800">
                      <th className="px-4 py-3 text-left">Invoice #</th>
                      <th className="px-4 py-3 text-left">Customer</th>
                      <th className="px-4 py-3 text-left">Amount</th>
                      <th className="px-4 py-3 text-left">Status</th>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Actions</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {invoices.map(inv => (
                        <tr key={inv.id} className="hover:bg-slate-800/20 transition">
                          <td className="px-4 py-3 font-mono text-indigo-300 font-semibold">{inv.invoice_number}</td>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-white">{inv.customer_name}</p>
                            <p className="text-xs text-slate-500">{inv.customer_email}</p>
                          </td>
                          <td className="px-4 py-3 font-bold text-white">{currSymbol[inv.currency]||""}{Number(inv.total).toLocaleString(undefined,{minimumFractionDigits:2,maximumFractionDigits:2})}</td>
                          <td className="px-4 py-3"><span className={`text-[10px] px-2 py-1 rounded-full font-bold capitalize ${statusBadge[inv.status]||"bg-slate-700 text-slate-400"}`}>{inv.status}</span></td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{new Date(inv.created_at).toLocaleDateString()}</td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1 flex-wrap">
                              <button onClick={() => setPreview(inv)} className="text-[10px] px-2 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition">Preview</button>
                              {inv.status !== "paid" && <button onClick={() => sendInvoice(inv.id)} disabled={sending===inv.id} className="text-[10px] px-2 py-1 bg-indigo-900/50 hover:bg-indigo-700 text-indigo-300 rounded-lg transition">{sending===inv.id ? "..." : "Send Email"}</button>}
                              {inv.status !== "paid" && inv.currency === "NGN" && <button onClick={() => generatePaymentLink(inv)} disabled={generatingLink===inv.id} className="text-[10px] px-2 py-1 bg-emerald-900/50 hover:bg-emerald-700 text-emerald-300 rounded-lg transition">{generatingLink===inv.id ? "..." : inv.checkout_url ? "Regen Link" : "Pay Link"}</button>}
                              {inv.checkout_url && <button onClick={() => navigator.clipboard.writeText(inv.checkout_url)} className="text-[10px] px-2 py-1 bg-violet-900/50 hover:bg-violet-700 text-violet-300 rounded-lg transition">Copy Link</button>}
                              {inv.checkout_url && inv.status !== "paid" && <button onClick={() => checkPayment(inv)} disabled={checkingPayment===inv.id} className="text-[10px] px-2 py-1 bg-sky-900/50 hover:bg-sky-700 text-sky-300 rounded-lg transition">{checkingPayment===inv.id ? "Checking..." : "Check Payment"}</button>}
                              {inv.status !== "paid" && <button onClick={() => markPaid(inv.id)} className="text-[10px] px-2 py-1 bg-emerald-900/30 hover:bg-emerald-700 text-emerald-400 rounded-lg transition">Mark Paid</button>}
                              <button onClick={() => deleteInvoice(inv.id)} className="text-[10px] px-2 py-1 text-slate-600 hover:text-red-400 rounded-lg transition">Delete</button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>}
          </div>
        )}

        {/* CUSTOMERS */}
        {tab==="customers" && (
          <div className="space-y-5 max-w-3xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">Customers</h2>
              <button onClick={() => setNewCust({ name:"", email:"", phone:"", company:"", address:"", notes:"" })} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition">+ Add Customer</button>
            </div>

            {newCust && (
              <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 space-y-4">
                <h3 className="font-bold text-white">{newCust.id ? "Edit Customer" : "New Customer"}</h3>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Name *</label><input value={newCust.name} onChange={e=>setNewCust(c=>({...c,name:e.target.value}))} placeholder="Full name" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Email</label><input value={newCust.email} onChange={e=>setNewCust(c=>({...c,email:e.target.value}))} placeholder="email@domain.com" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Phone</label><input value={newCust.phone} onChange={e=>setNewCust(c=>({...c,phone:e.target.value}))} placeholder="08100000000" className={inp}/></div>
                  <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Company</label><input value={newCust.company} onChange={e=>setNewCust(c=>({...c,company:e.target.value}))} placeholder="Company name" className={inp}/></div>
                  <div className="sm:col-span-2"><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Address</label><input value={newCust.address} onChange={e=>setNewCust(c=>({...c,address:e.target.value}))} placeholder="Address" className={inp}/></div>
                  <div className="sm:col-span-2"><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Notes</label><textarea value={newCust.notes} onChange={e=>setNewCust(c=>({...c,notes:e.target.value}))} rows={2} className={`${inp} resize-none`}/></div>
                </div>
                <div className="flex gap-3">
                  <button onClick={saveCust} disabled={custSaving||!newCust.name} className="px-5 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold rounded-xl text-sm transition">{custSaving?"Saving...":"Save Customer"}</button>
                  <button onClick={()=>setNewCust(null)} className="px-5 py-2 bg-slate-800 border border-slate-700 text-slate-400 rounded-xl text-sm transition">Cancel</button>
                </div>
              </div>
            )}

            {customers.length === 0 && !newCust
              ? <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center text-slate-500">No customers yet. Add your first client.</div>
              : <div className="bg-slate-900 border border-slate-800 rounded-2xl divide-y divide-slate-800/60">
                {customers.map(c => (
                  <div key={c.id} className="flex items-center gap-4 px-5 py-4 hover:bg-slate-800/20 transition">
                    <div className="w-9 h-9 rounded-full bg-indigo-900/50 border border-indigo-700/40 flex items-center justify-center text-sm font-bold text-indigo-300 shrink-0">{c.name[0]?.toUpperCase()}</div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-white">{c.name} {c.company && <span className="text-slate-500 font-normal text-xs">· {c.company}</span>}</p>
                      <p className="text-xs text-slate-500">{c.email}{c.phone ? ` · ${c.phone}` : ""}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setNewCust({...c})} className="text-xs px-3 py-1 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg transition">Edit</button>
                      <button onClick={() => deleteCust(c.id)} className="text-xs px-3 py-1 text-slate-600 hover:text-red-400 rounded-lg transition">Delete</button>
                    </div>
                  </div>
                ))}
              </div>}
          </div>
        )}

        {/* SETTINGS */}
        {tab==="settings" && (
          <div className="space-y-5 max-w-2xl">
            <h2 className="text-xl font-bold">Billing Settings</h2>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Your Company Info (appears on all invoices)</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Company Name</label><input value={settings.company_name} onChange={e=>setSettings(s=>({...s,company_name:e.target.value}))} placeholder="TimingoFlow" className={inp}/></div>
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Company Email</label><input value={settings.company_email} onChange={e=>setSettings(s=>({...s,company_email:e.target.value}))} placeholder="team@timingoflow.com" className={inp}/></div>
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Phone</label><input value={settings.company_phone} onChange={e=>setSettings(s=>({...s,company_phone:e.target.value}))} placeholder="+234..." className={inp}/></div>
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Invoice Prefix</label><input value={settings.invoice_prefix} onChange={e=>setSettings(s=>({...s,invoice_prefix:e.target.value}))} placeholder="INV" className={inp}/></div>
                <div className="sm:col-span-2"><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Address</label><textarea value={settings.company_address} onChange={e=>setSettings(s=>({...s,company_address:e.target.value}))} rows={2} placeholder="Your full address..." className={`${inp} resize-none`}/></div>
              </div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">GlobalPay Settings</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">API Key</label><input type="password" value={apiKey} onChange={e=>setApiKey(e.target.value)} placeholder="Your GlobalPay API key" className={inp}/></div>
                <div><label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Mode</label>
                  <select value={isLive?"live":"test"} onChange={e=>setIsLive(e.target.value==="live")} className={inp}>
                    <option value="live">Live (Production)</option>
                    <option value="test">Test (Sandbox)</option>
                  </select>
                </div>
              </div>
            </div>
            <button onClick={saveSettings} className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-xl transition">
              {settingsSaved ? "✓ Saved!" : "Save All Settings"}
            </button>
          </div>
        )}

        </>)}
      </div>

      {/* Invoice Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={()=>setPreview(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e=>e.stopPropagation()}>
            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">
              <span className="font-bold text-white">Invoice Preview — #{preview.invoice_number}</span>
              <div className="flex gap-2">
                <button onClick={() => window.print()} className="text-xs px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition">🖨 Print / Save PDF</button>
                <button onClick={()=>setPreview(null)} className="text-slate-500 hover:text-white text-xl leading-none">×</button>
              </div>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <img src={TimingoflowLogo} alt="Logo" className="h-12 mb-2"/>
                  <p className="font-bold text-white text-lg">{settings.company_name || "TimingoFlow"}</p>
                  {settings.company_address && <p className="text-xs text-slate-500 whitespace-pre-line">{settings.company_address}</p>}
                  {settings.company_phone && <p className="text-xs text-slate-500">{settings.company_phone}</p>}
                  {settings.company_email && <p className="text-xs text-slate-500">{settings.company_email}</p>}
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-indigo-400">INVOICE</p>
                  <p className="text-slate-400 font-mono text-sm">#{preview.invoice_number}</p>
                  <p className="text-xs text-slate-500 mt-1">Date: {new Date(preview.created_at).toLocaleDateString()}</p>
                  {preview.due_date && <p className="text-xs text-slate-500">Due: {preview.due_date}</p>}
                </div>
              </div>
              <div className="bg-slate-800/50 rounded-xl p-4">
                <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-1">Bill To</p>
                <p className="font-bold text-white">{preview.customer_name}</p>
                {preview.customer_company && <p className="text-sm text-slate-400">{preview.customer_company}</p>}
                <p className="text-sm text-slate-400">{preview.customer_email}</p>
                {preview.customer_phone && <p className="text-sm text-slate-400">{preview.customer_phone}</p>}
                {preview.customer_address && <p className="text-sm text-slate-400">{preview.customer_address}</p>}
              </div>
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-800 text-slate-500 text-[11px] uppercase tracking-wider">
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-right">Unit Price</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr></thead>
                <tbody className="divide-y divide-slate-800">
                  {(typeof preview.line_items === "string" ? JSON.parse(preview.line_items) : preview.line_items||[]).map((l,i)=>(
                    <tr key={i}>
                      <td className="px-3 py-2 text-white">{l.description}</td>
                      <td className="px-3 py-2 text-slate-400 text-center">{l.quantity}</td>
                      <td className="px-3 py-2 text-slate-400 text-right">{preview.currency} {Number(l.unit_price).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                      <td className="px-3 py-2 text-white text-right">{preview.currency} {(Number(l.quantity)*Number(l.unit_price)).toLocaleString(undefined,{minimumFractionDigits:2})}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-right space-y-1">
                <p className="text-sm text-slate-400">Subtotal: {preview.currency} {Number(preview.subtotal).toLocaleString(undefined,{minimumFractionDigits:2})}</p>
                {Number(preview.tax_rate)>0 && <p className="text-sm text-slate-400">Tax ({preview.tax_rate}%): {preview.currency} {Number(preview.tax_amount).toLocaleString(undefined,{minimumFractionDigits:2})}</p>}
                <p className="text-xl font-black text-white">Total: {preview.currency} {Number(preview.total).toLocaleString(undefined,{minimumFractionDigits:2})}</p>
              </div>
              {preview.checkout_url && (
                <div className="bg-indigo-900/30 border border-indigo-700/40 rounded-xl p-4 text-center">
                  <p className="text-xs text-slate-400 mb-2">Payment Link</p>
                  <p className="font-mono text-xs text-indigo-300 break-all mb-3">{preview.checkout_url}</p>
                  <button onClick={() => navigator.clipboard.writeText(preview.checkout_url)} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition">Copy Payment Link</button>
                </div>
              )}
              {preview.notes && <p className="text-xs text-slate-500 border-t border-slate-800 pt-3">{preview.notes}</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PaymentLinksPanel({ authFetch }) {
  const [apiKey, setApiKey] = useState("");
  const [isLive, setIsLive] = useState(true);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [form, setForm] = useState({ firstName: "", lastName: "", emailAddress: "", phoneNumber: "", amount: "", currency: "NGN", address: "", description: "", redirectUrl: "https://timingoflow.com" });
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // { checkout_url, reference }
  const [error, setError] = useState("");
  const [links, setLinks] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    authFetch("/admin/globalpay/settings").then(r => r.json()).then(d => {
      setApiKey(d.api_key || "");
      setIsLive(d.is_live !== false);
    }).catch(() => {});
  }, [authFetch]);

  const saveSettings = async () => {
    await authFetch("/admin/globalpay/settings", { method: "PUT", body: JSON.stringify({ api_key: apiKey, is_live: isLive }) });
    setSettingsSaved(true);
    setTimeout(() => setSettingsSaved(false), 2500);
  };

  const loadHistory = async () => {
    const r = await authFetch("/admin/globalpay/links");
    if (r.ok) setLinks(await r.json());
    setShowHistory(true);
  };

  const generate = async () => {
    setError(""); setResult(null); setGenerating(true);
    try {
      if (!apiKey) { setError("API key not set. Open Settings and add your GlobalPay API key."); return; }

      const endpoint = isLive
        ? "https://paygw.globalpay.com.ng/globalpay-paymentgateway/api/paymentgateway/generate-payment-link"
        : "https://newwebservicetest.zenithbank.com:8443/new-globalpay-paymentgateway-external/api/paymentgateway/generate-payment-link";

      const ref = `TF-${Date.now().toString(36).toUpperCase()}`;
      const payload = {
        amount: parseFloat(form.amount),
        merchantTransactionReference: ref,
        redirectUrl: form.redirectUrl || "https://timingoflow.com",
        customer: {
          firstName: form.firstName,
          lastName: form.lastName,
          emailAddress: form.emailAddress,
          phoneNumber: form.phoneNumber,
          currency: form.currency || "NGN",
          address: form.address || "",
        },
      };

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "apiKey": apiKey, "Content-Type": "application/json", "Language": "en" },
        body: JSON.stringify(payload),
      });
      const data = await resp.json();
      const checkoutUrl = data?.data?.checkoutUrl || "";
      if (!checkoutUrl) { setError(`GlobalPay error: ${JSON.stringify(data)}`); return; }

      setResult({ checkout_url: checkoutUrl, reference: ref });

      // Save record to backend for history (fire and forget)
      authFetch("/admin/globalpay/save-link", {
        method: "POST",
        body: JSON.stringify({ ...form, amount: parseFloat(form.amount), reference: ref, checkout_url: checkoutUrl }),
      }).catch(() => {});

      loadHistory();
    } catch (e) { setError(`Failed to reach GlobalPay: ${e.message}`); }
    finally { setGenerating(false); }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(result.checkout_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const f = (field, val) => setForm(p => ({ ...p, [field]: val }));
  const inp = "w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-indigo-500 placeholder-slate-600 transition";

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">💳 Payment Links <span className="text-[10px] px-2 py-0.5 bg-emerald-900/40 text-emerald-400 border border-emerald-700/40 rounded-full font-bold">GlobalPay</span></h2>
          <p className="text-xs text-slate-500 mt-0.5">Generate a payment link and send it directly to your client</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSettings(s => !s)} className="text-xs px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-400 hover:text-white rounded-lg transition">⚙ Settings</button>
          <button onClick={loadHistory} className="text-xs px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-400 hover:text-white rounded-lg transition">📋 History</button>
        </div>
      </div>

      {/* Settings panel */}
      {showSettings && (
        <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">GlobalPay Settings</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">API Key</label>
              <input type="password" value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Your GlobalPay API key" className={inp} />
            </div>
            <div>
              <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Mode</label>
              <select value={isLive ? "live" : "test"} onChange={e => setIsLive(e.target.value === "live")} className={inp}>
                <option value="live">Live (Production)</option>
                <option value="test">Test (Sandbox)</option>
              </select>
            </div>
          </div>
          <button onClick={saveSettings} className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition">
            {settingsSaved ? "✓ Saved" : "Save Settings"}
          </button>
        </div>
      )}

      {/* Generate form */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">First Name</label>
          <input value={form.firstName} onChange={e => f("firstName", e.target.value)} placeholder="John" className={inp} />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Last Name</label>
          <input value={form.lastName} onChange={e => f("lastName", e.target.value)} placeholder="Doe" className={inp} />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Email</label>
          <input type="email" value={form.emailAddress} onChange={e => f("emailAddress", e.target.value)} placeholder="client@example.com" className={inp} />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Phone Number</label>
          <input value={form.phoneNumber} onChange={e => f("phoneNumber", e.target.value)} placeholder="08100000000" className={inp} />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Amount (NGN)</label>
          <input type="number" value={form.amount} onChange={e => f("amount", e.target.value)} placeholder="50000" min="1" className={inp} />
        </div>
        <div>
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Description (optional)</label>
          <input value={form.description} onChange={e => f("description", e.target.value)} placeholder="e.g. Starter plan - 1 month" className={inp} />
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] text-slate-500 uppercase tracking-wider block mb-1">Redirect URL (after payment)</label>
          <input value={form.redirectUrl} onChange={e => f("redirectUrl", e.target.value)} className={inp} />
        </div>
      </div>

      {error && <div className="bg-red-900/30 border border-red-700/50 text-red-300 text-sm px-4 py-3 rounded-xl">{error}</div>}

      <button onClick={generate} disabled={generating || !form.amount || !form.firstName || !form.emailAddress}
        className="w-full py-3 bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 disabled:text-slate-500 text-white font-bold rounded-xl transition text-sm flex items-center justify-center gap-2">
        {generating ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating...</> : "⚡ Generate Payment Link"}
      </button>

      {/* Result */}
      {result && (
        <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl p-4 space-y-3">
          <p className="text-emerald-400 font-bold text-sm">✓ Payment link generated!</p>
          <p className="text-[11px] text-slate-500">Reference: <span className="font-mono text-slate-300">{result.reference}</span></p>
          <div className="flex gap-2">
            <input readOnly value={result.checkout_url} className="flex-1 bg-slate-800 border border-slate-700 text-slate-300 text-xs px-3 py-2 rounded-lg font-mono" />
            <button onClick={copyLink} className={`px-4 py-2 text-xs font-bold rounded-lg transition ${copied ? "bg-emerald-600 text-white" : "bg-indigo-600 hover:bg-indigo-500 text-white"}`}>
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
          <a href={result.checkout_url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:text-indigo-300 underline">Open link →</a>
        </div>
      )}

      {/* History */}
      {showHistory && links.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-bold text-slate-400 uppercase tracking-widest">Recent Payment Links</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {links.map(l => (
              <div key={l.id} className="bg-slate-800/60 border border-slate-700/50 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white truncate">{l.customer_name}</p>
                  <p className="text-xs text-slate-500">{l.customer_email} · ₦{Number(l.amount).toLocaleString()} · {new Date(l.created_at).toLocaleDateString()}</p>
                </div>
                <button onClick={() => { navigator.clipboard.writeText(l.checkout_url); }} className="text-xs px-3 py-1 bg-indigo-900/40 text-indigo-300 border border-indigo-700/40 rounded-lg hover:bg-indigo-700 transition">Copy</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Enterprise Intelligence Dashboard ───────────────────────────────────────
function EnterpriseAdminPage() {
  const { authFetch, user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("command");
  const [cmdData, setCmdData] = useState(null);
  const [liveFeed, setLiveFeed] = useState([]);
  const [users, setUsers] = useState([]);
  const [userProfile, setUserProfile] = useState(null);
  const [emailIntel, setEmailIntel] = useState(null);
  const [revenue, setRevenue] = useState(null);
  const [product, setProduct] = useState(null);
  const [infraData, setInfraData] = useState(null);
  const [fraud, setFraud] = useState(null);
  const [leadIntel, setLeadIntel] = useState(null);
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [adminUsers, setAdminUsers] = useState([]);
  const [emailFilterUser, setEmailFilterUser] = useState("");
  const [emailFilterDays, setEmailFilterDays] = useState("30");
  const [emailFilterStart, setEmailFilterStart] = useState("");
  const [emailFilterEnd, setEmailFilterEnd] = useState("");
  const [customMrrUser, setCustomMrrUser] = useState(null);
  const [customMrrVal, setCustomMrrVal] = useState("");
  // Report generator state
  const [rptUser, setRptUser] = useState("");
  const [rptDays, setRptDays] = useState("30");
  const [rptLoading, setRptLoading] = useState(false);
  const [rptResult, setRptResult] = useState(null);
  const [rptType, setRptType] = useState("brief");
  // AI chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  // Command center filters
  const [cmdDays, setCmdDays] = useState(1);
  const [activeUsersPopup, setActiveUsersPopup] = useState(null); // null | "now" | "today"
  const [activeUsersList, setActiveUsersList] = useState([]);
  const [healthPopup, setHealthPopup] = useState(false);
  const [queuePopup, setQueuePopup] = useState(false);
  // Lead intel filters
  const [leadFilterUser, setLeadFilterUser] = useState("");
  const [leadFilterDays, setLeadFilterDays] = useState("");

  const PLAN_COLORS = { starter: "#f59e0b", growth: "#6366f1", pro: "#10b981", custom: "#64748b" };
  const PLAN_BADGES = { starter: "bg-amber-900/40 text-amber-300 border-amber-700/40", growth: "bg-indigo-900/40 text-indigo-300 border-indigo-700/40", pro: "bg-emerald-900/40 text-emerald-300 border-emerald-700/40", custom: "bg-slate-700 text-slate-300 border-slate-600" };
  const DOW_LABELS = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  const load = async (section) => {
    setLoading(true);
    try {
      if (section === "command" || !section) {
        const [cmd, feed] = await Promise.all([
          authFetch(`/admin/enterprise/command-center?days=${cmdDays}`).then(r=>r.json()),
          authFetch("/admin/enterprise/live-feed?limit=40").then(r=>r.json()),
        ]);
        setCmdData(cmd); setLiveFeed(Array.isArray(feed) ? feed : []);
      } else if (section === "users") {
        const u = await authFetch("/admin/enterprise/users").then(r=>r.json());
        setUsers(Array.isArray(u) ? u : []);
        const au = await authFetch("/admin/users").then(r=>r.json());
        setAdminUsers(Array.isArray(au) ? au : []);
      } else if (section === "email") {
        const params = new URLSearchParams();
        if (emailFilterUser) params.set("user_email", emailFilterUser);
        if (emailFilterStart) params.set("start_date", emailFilterStart);
        if (emailFilterEnd) params.set("end_date", emailFilterEnd);
        if (emailFilterDays && !emailFilterStart && !emailFilterEnd) params.set("days", emailFilterDays);
        const e = await authFetch(`/admin/enterprise/email-intelligence?${params}`).then(r=>r.json());
        setEmailIntel(e);
      } else if (section === "revenue") {
        const rv = await authFetch("/admin/enterprise/revenue").then(r=>r.json());
        setRevenue(rv);
      } else if (section === "product") {
        const p = await authFetch("/admin/enterprise/product-analytics").then(r=>r.json());
        setProduct(p);
      } else if (section === "infra") {
        const inf = await authFetch("/admin/enterprise/infrastructure").then(r=>r.json());
        setInfraData(inf);
      } else if (section === "fraud") {
        const fr = await authFetch("/admin/enterprise/fraud-signals").then(r=>r.json());
        setFraud(fr);
      } else if (section === "leads") {
        const p = new URLSearchParams();
        if (leadFilterUser) p.set("user_email", leadFilterUser);
        if (leadFilterDays) p.set("days", leadFilterDays);
        const li = await authFetch(`/admin/enterprise/lead-intelligence?${p}`).then(r=>r.json());
        setLeadIntel(li);
      }
    } catch(e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(tab); }, [tab]);
  // Always load users list so brief + revenue tabs have user dropdown
  useEffect(() => {
    authFetch("/admin/users").then(r=>r.json()).then(u=>{ if(Array.isArray(u)) setAdminUsers(u); }).catch(()=>{});
  }, []);
  // Reload command center when days filter changes — clear stale data first
  useEffect(() => {
    if (tab === "command") {
      setCmdData(null);
      load("command");
    }
  }, [cmdDays]);

  // Auto-refresh command center every 30s
  useEffect(() => {
    // No auto-refresh — user controls when to reload via filter pills
  }, [tab]);

  const loadBrief = async () => {
    setBriefLoading(true);
    try {
      const b = await authFetch("/admin/enterprise/daily-brief").then(r=>r.json());
      setBrief(b);
    } catch(e) {}
    setBriefLoading(false);
  };

  const loadUserProfile = async (email) => {
    try {
      const p = await authFetch(`/admin/enterprise/user/${encodeURIComponent(email)}`).then(r=>r.json());
      setUserProfile(p);
    } catch(e) {}
  };

  const statCard = (label, value, sub, color="text-white") => (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
      <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">{label}</p>
      <p className={`text-2xl font-extrabold ${color}`}>{value ?? "–"}</p>
      {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
    </div>
  );

  const tabs = [
    ["command","🌐 Command Center"],
    ["users","👥 Users"],
    ["email","📧 Email Intel"],
    ["revenue","💰 Revenue"],
    ["product","🧪 Product"],
    ["leads","🎯 Leads"],
    ["infra","⚙ Infra"],
    ["fraud","🛡 Security"],
    ["brief","🧠 AI Brief"],
  ];

  return (
    <div className="min-h-screen bg-[#080c14] text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-3 border-b border-slate-800/80 bg-slate-950/80 backdrop-blur sticky top-0 z-40">
        <div className="flex items-center gap-3">
          <img src={TimingoflowLogo} alt="Logo" className="w-8 h-8"/>
          <span className="font-bold text-sm text-white">TimingoFlow</span>
          <span className="text-slate-700 text-xs">|</span>
          <span className="text-[10px] font-bold uppercase tracking-widest text-indigo-400">Enterprise Intelligence</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate("/admin")} className="text-xs px-3 py-1.5 bg-slate-800 border border-slate-700 text-slate-400 hover:text-white rounded-lg transition">Admin Classic</button>
          <button onClick={() => navigate("/billing")} className="text-xs px-3 py-1.5 bg-emerald-950 border border-emerald-800 text-emerald-400 rounded-lg transition">💳 Billing</button>
          <button onClick={() => navigate("/")} className="text-xs px-3 py-1.5 bg-indigo-950 border border-indigo-800 text-indigo-400 rounded-lg transition">App</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="text-xs px-3 py-1.5 border border-slate-700 text-slate-500 hover:text-red-400 rounded-lg transition">Sign Out</button>
        </div>
      </header>

      {/* Tab bar */}
      <div className="flex gap-0.5 px-4 pt-3 border-b border-slate-800/60 overflow-x-auto">
        {tabs.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg whitespace-nowrap border-b-2 transition ${tab===id ? "border-indigo-500 text-white bg-slate-900" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="flex-1 p-5 overflow-auto">
        {loading && <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"/></div>}

        {/* ─── COMMAND CENTER ─── */}
        {tab === "command" && !cmdData && <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"/></div>}
        {!loading && tab === "command" && cmdData && (
          <div className="space-y-5 max-w-[1400px]">
            {/* Date filter pills */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500 font-semibold uppercase tracking-wider">Period:</span>
              {[[1,"Today"],[7,"7 Days"],[30,"30 Days"],[90,"90 Days"]].map(([d,label]) => (
                <button key={d} onClick={()=>setCmdDays(d)}
                  className={`px-3 py-1 text-xs font-bold rounded-full border transition ${cmdDays===d ? "bg-indigo-600 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:text-white"}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* KPI bar */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
              {/* Clickable active users */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-emerald-700 transition"
                onClick={async()=>{
                  const r = await authFetch("/admin/enterprise/active-users?window=now").then(r=>r.json());
                  setActiveUsersList(Array.isArray(r)?r:[]); setActiveUsersPopup("now");
                }}>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Active Now</p>
                <p className="text-2xl font-extrabold text-emerald-400">{cmdData.users?.active_now || 0}</p>
                <p className="text-[10px] text-slate-600 mt-1">right now · tap to see</p>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-indigo-700 transition"
                onClick={async()=>{
                  const r = await authFetch("/admin/enterprise/active-users?window=today").then(r=>r.json());
                  setActiveUsersList(Array.isArray(r)?r:[]); setActiveUsersPopup("today");
                }}>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Active Today</p>
                <p className="text-2xl font-extrabold text-indigo-400">{cmdData.users?.active_today || 0}</p>
                <p className="text-[10px] text-slate-600 mt-1">last 24h · tap to see</p>
              </div>
              {statCard("Emails", cmdData.emails?.emails_today?.toLocaleString() || 0, "sent")}
              {statCard("Opens", cmdData.emails?.opens_today?.toLocaleString() || 0, `${cmdData.emails?.emails_today > 0 ? Math.round((cmdData.emails?.opens_today||0)*100/(cmdData.emails?.emails_today||1)) : 0}% rate`, "text-blue-300")}
              {statCard("Replies", cmdData.emails?.replies_today || 0, "inbound", "text-violet-300")}
              {statCard("Revenue", `$${Number(cmdData.revenue?.period_revenue||cmdData.revenue?.month_revenue||0).toLocaleString()}`, "this period", "text-emerald-400")}
              {/* Queue - clickable */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-slate-600 transition"
                onClick={()=>setQueuePopup(true)}>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Queue</p>
                <p className={`text-2xl font-extrabold ${cmdData.queue?.failed>0?"text-red-400":"text-white"}`}>{cmdData.queue?.pending||0}</p>
                <p className="text-[10px] text-slate-600 mt-1">{cmdData.queue?.failed||0} failed · tap</p>
              </div>
              {/* Health - clickable */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 cursor-pointer hover:border-slate-600 transition"
                onClick={()=>setHealthPopup(true)}>
                <p className="text-xs text-slate-500 uppercase tracking-widest mb-1">Health</p>
                <p className={`text-2xl font-extrabold ${cmdData.health_score>70?"text-emerald-400":cmdData.health_score>40?"text-amber-400":"text-red-400"}`}>{cmdData.health_score}/100</p>
                <div className="w-full bg-slate-800 rounded-full h-1.5 mt-2">
                  <div className={`h-1.5 rounded-full ${cmdData.health_score>70?"bg-emerald-500":cmdData.health_score>40?"bg-amber-500":"bg-red-500"}`} style={{width:`${cmdData.health_score}%`}}/>
                </div>
              </div>
            </div>

            {/* Popups */}
            {activeUsersPopup && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setActiveUsersPopup(null)}>
                <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6 shadow-2xl" onClick={e=>e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white">{activeUsersPopup==="now"?"Active Right Now":"Active Today"}</h3>
                    <button onClick={()=>setActiveUsersPopup(null)} className="text-slate-500 hover:text-white text-xl">×</button>
                  </div>
                  {activeUsersList.length===0 ? <p className="text-slate-500 text-sm text-center py-4">No users active in this window</p> : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {activeUsersList.map(u=>(
                        <div key={u.email} className="flex items-center justify-between px-3 py-2 bg-slate-800 rounded-xl">
                          <div>
                            <p className="text-sm font-semibold text-white">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </div>
                          <div className="text-right">
                            <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${PLAN_BADGES[u.plan]||PLAN_BADGES.custom}`}>{u.plan}</span>
                            <p className="text-[10px] text-slate-500 mt-0.5">{Math.round(u.minutes_ago||0)}m ago</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            {healthPopup && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setHealthPopup(false)}>
                <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white">Platform Health Breakdown</h3>
                    <button onClick={()=>setHealthPopup(false)} className="text-slate-500 hover:text-white text-xl">×</button>
                  </div>
                  <div className="space-y-3">
                    {[
                      ["📧 Email Open Rate","40% weight","Open rate × 2.5 — 30%+ open rate = 75+ score"],
                      ["⚙️ Queue Health","35% weight","100 if no failures, -10 per failed job"],
                      ["👥 User Engagement","15% weight","% of users active today × 5"],
                      ["💬 Reply Rate Bonus","10% weight","Reply rate bonus (max 20 points)"],
                    ].map(([label, weight, desc])=>(
                      <div key={label} className="bg-slate-800 rounded-xl p-3">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-semibold text-white">{label}</p>
                          <span className="text-xs text-slate-500">{weight}</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{desc}</p>
                      </div>
                    ))}
                    <div className={`text-center py-2 rounded-xl font-bold text-lg ${cmdData.health_score>70?"text-emerald-400 bg-emerald-900/20":cmdData.health_score>40?"text-amber-400 bg-amber-900/20":"text-red-400 bg-red-900/20"}`}>
                      Overall: {cmdData.health_score}/100
                    </div>
                  </div>
                </div>
              </div>
            )}
            {queuePopup && (
              <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={()=>setQueuePopup(false)}>
                <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-md w-full p-6" onClick={e=>e.stopPropagation()}>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-white">Email Queue Status</h3>
                    <button onClick={()=>setQueuePopup(false)} className="text-slate-500 hover:text-white text-xl">×</button>
                  </div>
                  <div className="space-y-2">
                    {[["Pending",cmdData.queue?.pending,"bg-blue-900/30 text-blue-300"],["Completed",cmdData.queue?.completed,"bg-emerald-900/30 text-emerald-300"],["Failed",cmdData.queue?.failed,"bg-red-900/30 text-red-400"]].map(([label,val,cls])=>(
                      <div key={label} className={`flex items-center justify-between px-4 py-3 rounded-xl ${cls}`}>
                        <span className="font-semibold">{label}</span>
                        <span className="text-2xl font-black">{val||0}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="grid lg:grid-cols-3 gap-5">
              {/* 24h Email trend */}
              <div className="lg:col-span-2 bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold text-white mb-4">📈 Email Activity — Last {cmdDays === 1 ? "24 Hours" : cmdDays + " Days"}</p>
                {cmdData.email_24h?.length > 0 ? (
                  <div className="h-48">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={cmdData.email_24h.map(r => {
                        const d = new Date(r.hour);
                        const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                        const label = cmdDays <= 1
                          ? (d.getHours() % 12 || 12) + (d.getHours() < 12 ? "am" : "pm")
                          : MONTHS[d.getMonth()] + " " + d.getDate();
                        return {...r, hour: label};
                      })}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                        <XAxis dataKey="hour" tick={{fontSize:10,fill:"#64748b"}}/>
                        <YAxis tick={{fontSize:10,fill:"#64748b"}}/>
                        <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                        <Line type="monotone" dataKey="sent" stroke="#6366f1" strokeWidth={2} dot={false} name="Sent"/>
                        <Line type="monotone" dataKey="opened" stroke="#10b981" strokeWidth={2} dot={false} name="Opened"/>
                        <Line type="monotone" dataKey="clicked" stroke="#f59e0b" strokeWidth={2} dot={false} name="Clicked"/>
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                ) : <p className="text-slate-500 text-sm text-center py-16">No data yet</p>}
              </div>

              {/* Live feed */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold text-white mb-3">⚡ Live Feed</p>
                <div className="space-y-2 max-h-56 overflow-y-auto">
                  {liveFeed.length === 0 ? <p className="text-slate-500 text-xs text-center py-4">No recent activity</p> : liveFeed.map((e,i) => {
                    const icons = {email_opened:"📧",reply_received:"💬",new_signup:"🆕",bounced:"⚠️",spam_complaint:"🚨",email_complained:"🚨"};
                    const colors = {email_opened:"text-blue-300",reply_received:"text-emerald-300",new_signup:"text-indigo-300",bounced:"text-amber-300",spam_complaint:"text-red-300",email_complained:"text-red-300"};
                    return (
                      <div key={i} className="flex items-start gap-2 text-xs">
                        <span>{icons[e.type] || "·"}</span>
                        <div>
                          <span className={`font-medium ${colors[e.type] || "text-slate-300"}`}>{(e.entity || "").slice(0,28)}</span>
                          {e.country && <span className="text-slate-600 ml-1">({e.country})</span>}
                          <div className="text-slate-600">{e.ts ? new Date(e.ts).toLocaleTimeString() : ""}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Geo breakdown */}
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold text-white mb-4">🌍 Performance by Country — {cmdDays === 1 ? "Today" : `Last ${cmdDays} Days`}</p>
                <div className="space-y-2">
                  {(cmdData.geo_data || []).length === 0
                    ? <p className="text-slate-500 text-xs text-center py-4">No country data for this period</p>
                    : (() => {
                        const gd = (cmdData.geo_data||[]).slice(0,10);
                        const maxOpen = Math.max(...gd.map(r=>r.open_rate||0), 1);
                        const maxClick = Math.max(...gd.map(r=>r.click_rate||0), 1);
                        const maxReply = Math.max(...gd.map(r=>r.reply_rate||0), 1);
                        return gd.map(r => (
                          <div key={r.country} className="space-y-1">
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-white font-medium truncate max-w-[140px]">{r.country}</span>
                              <span className="text-[10px] text-slate-500">{r.sent?.toLocaleString()} sent</span>
                            </div>
                            <div className="flex gap-1.5 items-center">
                              <div className="flex-1 bg-slate-800 rounded-full h-1.5"><div className="h-1.5 bg-blue-500 rounded-full" style={{width:`${((r.open_rate||0)/maxOpen*100).toFixed(1)}%`}}/></div>
                              <span className="text-[10px] text-blue-300 w-12 text-right">{r.open_rate}% open</span>
                            </div>
                            <div className="flex gap-1.5 items-center">
                              <div className="flex-1 bg-slate-800 rounded-full h-1.5"><div className="h-1.5 bg-amber-500 rounded-full" style={{width:`${((r.click_rate||0)/maxClick*100).toFixed(1)}%`}}/></div>
                              <span className="text-[10px] text-amber-300 w-12 text-right">{r.click_rate}% click</span>
                            </div>
                            <div className="flex gap-1.5 items-center">
                              <div className="flex-1 bg-slate-800 rounded-full h-1.5"><div className="h-1.5 bg-emerald-500 rounded-full" style={{width:`${((r.reply_rate||0)/maxReply*100).toFixed(1)}%`}}/></div>
                              <span className="text-[10px] text-emerald-300 w-12 text-right">{r.reply_rate}% reply</span>
                            </div>
                          </div>
                        ));
                      })()}
                </div>
              </div>

              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold text-white mb-4">👥 User Distribution</p>
                <div className="grid grid-cols-2 gap-3">
                  {statCard("Total Users", cmdData.users?.total, "all time")}
                  {statCard("Approved", cmdData.users?.approved, "can log in", "text-emerald-400")}
                  {statCard("Pending", cmdData.users?.pending, "need review", cmdData.users?.pending > 0 ? "text-amber-400" : "text-white")}
                  {statCard("New This Week", cmdData.users?.new_week, "signups", "text-indigo-400")}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── USERS ─── */}
        {!loading && tab === "users" && (
          <div className="space-y-5 max-w-[1400px]">
            <h2 className="text-xl font-bold">👥 User Intelligence</h2>
            {userProfile ? (
              <div className="space-y-5">
                <button onClick={() => setUserProfile(null)} className="text-sm text-indigo-400 hover:text-indigo-300">← Back to users</button>
                <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-bold text-white">{userProfile.user?.name}</h3>
                      <p className="text-slate-400 text-sm">{userProfile.user?.email}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${PLAN_BADGES[userProfile.user?.plan] || PLAN_BADGES.custom}`}>{userProfile.user?.plan || "custom"}</span>
                        <span className="text-xs text-slate-500">Joined {userProfile.user?.created_at ? new Date(userProfile.user.created_at).toLocaleDateString() : "–"}</span>
                        {userProfile.user?.last_active_at && <span className="text-xs text-emerald-400">Last active {new Date(userProfile.user.last_active_at).toLocaleString()}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5">
                    {statCard("Leads", userProfile.features?.total_leads || 0, "generated")}
                    {statCard("Emails", userProfile.features?.emails_sent || 0, "sent")}
                    {statCard("Replies", userProfile.features?.replies_received || 0, "received", "text-emerald-400")}
                    {statCard("Intel Runs", userProfile.features?.intel_used || 0, "leads with Intel", "text-indigo-400")}
                  </div>
                </div>
                {/* Email trend chart */}
                {userProfile.email_trend?.length > 0 && (
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                    <p className="text-sm font-bold mb-4">📈 Email Activity (30 days)</p>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={userProfile.email_trend.map(r=>({...r,day:r.day?.slice(5)}))}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                          <XAxis dataKey="day" tick={{fontSize:9,fill:"#64748b"}}/>
                          <YAxis tick={{fontSize:9,fill:"#64748b"}}/>
                          <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                          <Area type="monotone" dataKey="sent" stroke="#6366f1" fill="#6366f120" name="Sent"/>
                          <Area type="monotone" dataKey="opened" stroke="#10b981" fill="#10b98120" name="Opened"/>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}
                {/* Pipeline distribution */}
                {userProfile.pipeline?.length > 0 && (
                  <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                    <p className="text-sm font-bold mb-4">🎯 Pipeline Distribution</p>
                    <div className="space-y-2">
                      {userProfile.pipeline.map(p => (
                        <div key={p.pipeline_stage} className="flex items-center gap-3">
                          <span className="text-xs text-slate-400 w-24 capitalize">{p.pipeline_stage}</span>
                          <div className="flex-1 bg-slate-800 rounded-full h-2">
                            <div className="h-2 bg-indigo-500 rounded-full" style={{width:`${Math.min(100, (p.count / Math.max(...(userProfile.pipeline||[]).map(x=>x.count||1),1)) * 100)}%`}}/>
                          </div>
                          <span className="text-xs text-slate-300 w-8 text-right">{p.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm min-w-[900px]">
                    <thead><tr className="bg-slate-800/50 text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-800">
                      <th className="px-4 py-3 text-left">User</th>
                      <th className="px-4 py-3 text-left">Plan</th>
                      <th className="px-4 py-3 text-left">Engagement</th>
                      <th className="px-4 py-3 text-left">Churn Risk</th>
                      <th className="px-4 py-3 text-left">Emails Sent</th>
                      <th className="px-4 py-3 text-left">Leads</th>
                      <th className="px-4 py-3 text-left">Revenue</th>
                      <th className="px-4 py-3 text-left">Last Active</th>
                      <th className="px-4 py-3"/>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-800/60">
                      {users.map(u => (
                        <tr key={u.email} className="hover:bg-slate-800/20 transition">
                          <td className="px-4 py-3">
                            <p className="font-semibold text-white text-sm">{u.name}</p>
                            <p className="text-xs text-slate-500">{u.email}</p>
                          </td>
                          <td className="px-4 py-3"><span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${PLAN_BADGES[u.plan] || PLAN_BADGES.custom}`}>{u.plan || "custom"}</span></td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-16 bg-slate-800 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${u.engagement_score > 60 ? "bg-emerald-500" : u.engagement_score > 30 ? "bg-amber-500" : "bg-red-500"}`} style={{width:`${u.engagement_score}%`}}/></div>
                              <span className="text-xs text-slate-400">{u.engagement_score}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3"><span className={`text-xs font-bold ${u.churn_risk > 60 ? "text-red-400" : u.churn_risk > 30 ? "text-amber-400" : "text-emerald-400"}`}>{u.churn_risk}%</span></td>
                          <td className="px-4 py-3 text-slate-300 text-xs">{(u.total_emails||0).toLocaleString()}</td>
                          <td className="px-4 py-3 text-slate-300 text-xs">{(u.total_leads||0).toLocaleString()}</td>
                          <td className="px-4 py-3">
                            <span className="text-emerald-400 text-xs font-semibold">$${Number(u.lifetime_revenue||0).toLocaleString()}</span>
                            {u.plan === "custom" && (
                              customMrrUser === u.email
                                ? <span className="flex items-center gap-1 mt-1">
                                    <input type="number" value={customMrrVal} onChange={e=>setCustomMrrVal(e.target.value)} placeholder="MRR $" className="w-20 bg-slate-700 border border-slate-600 text-white text-xs px-1.5 py-0.5 rounded"/>
                                    <button onClick={async()=>{await authFetch(`/admin/users/${encodeURIComponent(u.email)}/custom-mrr`,{method:"PUT",body:JSON.stringify({custom_mrr:parseFloat(customMrrVal)||0})});setCustomMrrUser(null);load("users");}} className="text-[10px] px-1.5 py-0.5 bg-emerald-700 text-white rounded">Save</button>
                                    <button onClick={()=>setCustomMrrUser(null)} className="text-[10px] px-1.5 py-0.5 bg-slate-600 text-slate-300 rounded">✕</button>
                                  </span>
                                : <button onClick={()=>{setCustomMrrUser(u.email);setCustomMrrVal(u.custom_mrr||"");}} className="block text-[10px] text-indigo-400 hover:text-indigo-300 mt-0.5">Set MRR</button>
                            )}
                          </td>
                          <td className="px-4 py-3 text-slate-500 text-xs">{u.last_active_at ? new Date(u.last_active_at).toLocaleString() : "Never"}</td>
                          <td className="px-4 py-3"><button onClick={() => loadUserProfile(u.email)} className="text-xs px-2 py-1 bg-indigo-900/40 text-indigo-300 border border-indigo-700/40 rounded-lg hover:bg-indigo-700 transition">View</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── EMAIL INTELLIGENCE ─── */}
        {!loading && tab === "email" && emailIntel && (
          <div className="space-y-5 max-w-[1400px]">
            <div className="flex flex-wrap items-end gap-3">
              <h2 className="text-xl font-bold">📧 Email Intelligence Center</h2>
              <div className="flex flex-wrap gap-2 ml-auto items-end">
                {/* User filter */}
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Filter by User</label>
                  <select value={emailFilterUser} onChange={e=>{setEmailFilterUser(e.target.value);}} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none">
                    <option value="">All Users</option>
                    {(emailIntel.user_list||[]).map(u => <option key={u.user_email} value={u.user_email}>{u.user_email} ({u.sent})</option>)}
                  </select>
                </div>
                {/* Days preset */}
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Period</label>
                  <select value={emailFilterDays} onChange={e=>setEmailFilterDays(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none">
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                    <option value="">All time</option>
                  </select>
                </div>
                {/* Custom date range */}
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">From</label>
                  <input type="date" value={emailFilterStart} onChange={e=>setEmailFilterStart(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none"/>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">To</label>
                  <input type="date" value={emailFilterEnd} onChange={e=>setEmailFilterEnd(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none"/>
                </div>
                <button onClick={()=>load("email")} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition">Apply</button>
                <button onClick={()=>{setEmailFilterUser("");setEmailFilterDays("30");setEmailFilterStart("");setEmailFilterEnd("");}} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs rounded-lg transition">Reset</button>
              </div>
            </div>
            <div className="grid lg:grid-cols-2 gap-5">
              {/* Volume trend */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">📈 Volume Trend (30 days)</p>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={(emailIntel.volume_trend||[]).map(r=>({...r,day:r.day?.slice(5)}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                      <XAxis dataKey="day" tick={{fontSize:9,fill:"#64748b"}}/>
                      <YAxis tick={{fontSize:9,fill:"#64748b"}}/>
                      <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                      <Area type="monotone" dataKey="sent" stroke="#6366f1" fill="#6366f120" name="Sent"/>
                      <Area type="monotone" dataKey="opened" stroke="#10b981" fill="#10b98120" name="Opened"/>
                      <Area type="monotone" dataKey="replied" stroke="#f59e0b" fill="#f59e0b20" name="Replied"/>
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              {/* By hour heatmap */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">⏰ Best Send Hours (open rate %)</p>
                <div className="grid grid-cols-12 gap-1">
                  {Array.from({length:24},(_,h) => {
                    const d = (emailIntel.by_hour||[]).find(r=>r.hour===h);
                    const rate = d ? parseFloat(d.open_rate||0) : 0;
                    const intensity = Math.min(100, rate * 2);
                    return (
                      <div key={h} className="text-center">
                        <div className="h-8 rounded text-[8px] flex items-end justify-center pb-0.5 text-slate-400" style={{background:`rgba(99,102,241,${intensity/100})`}}>{rate>0?`${rate}%`:""}</div>
                        <div className="text-[8px] text-slate-600 mt-0.5">{h}</div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-slate-500 mt-2">Hour of day (UTC) — color intensity = open rate</p>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {/* By industry */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🏭 Performance by Industry</p>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {(emailIntel.by_industry||[]).map(r => (
                    <div key={r.industry} className="flex items-center gap-3">
                      <span className="text-xs text-slate-400 w-36 truncate" title={r.industry}>{r.industry}</span>
                      <div className="flex-1 bg-slate-800 rounded-full h-2"><div className="h-2 bg-indigo-500 rounded-full" style={{width:`${Math.min(100,r.open_rate||0)}%`}}/></div>
                      <span className="text-[10px] text-slate-300 w-12 text-right">{r.open_rate}% open</span>
                      <span className="text-[10px] text-emerald-400 w-14 text-right">{r.reply_rate}% reply</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* By day of week */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">📅 Performance by Day of Week</p>
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={(emailIntel.by_dow||[]).map(r=>({...r,day:DOW_LABELS[r.dow]||r.dow}))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                      <XAxis dataKey="day" tick={{fontSize:10,fill:"#64748b"}}/>
                      <YAxis tick={{fontSize:10,fill:"#64748b"}}/>
                      <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                      <Bar dataKey="open_rate" fill="#6366f1" name="Open %" radius={[4,4,0,0]}/>
                      <Bar dataKey="reply_rate" fill="#10b981" name="Reply %" radius={[4,4,0,0]}/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top subjects */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <p className="text-sm font-bold mb-4">🏆 Top Subject Lines by Open Rate</p>
              <div className="space-y-2">
                {(emailIntel.top_subjects||[]).slice(0,10).map((s,i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs text-slate-600 w-4">{i+1}</span>
                    <span className="text-xs text-slate-300 flex-1 truncate">{s.subject}</span>
                    <span className="text-xs text-slate-500 w-12 text-right">{s.sent} sent</span>
                    <div className="w-20 bg-slate-800 rounded-full h-1.5"><div className="h-1.5 bg-indigo-500 rounded-full" style={{width:`${s.open_rate||0}%`}}/></div>
                    <span className="text-xs text-emerald-400 w-12 text-right">{s.open_rate}%</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Deliverability */}
            {(emailIntel.deliverability||[]).length > 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">📬 Deliverability Events (30 days)</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {(emailIntel.deliverability||[]).map(d => (
                    <div key={d.event_type} className={`rounded-xl p-3 border ${d.event_type.includes("spam")||d.event_type.includes("complaint") ? "bg-red-900/20 border-red-700/40" : d.event_type.includes("bounce") ? "bg-amber-900/20 border-amber-700/40" : "bg-slate-800 border-slate-700"}`}>
                      <p className="text-xs text-slate-400 capitalize">{d.event_type.replace(/_/g," ")}</p>
                      <p className="text-xl font-bold text-white mt-1">{d.count}</p>
                      <p className="text-[10px] text-slate-500">Last: {d.last_seen ? new Date(d.last_seen).toLocaleDateString() : "–"}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ─── REVENUE ─── */}
        {!loading && tab === "revenue" && revenue && (
          <div className="space-y-5 max-w-[1400px]">
            <h2 className="text-xl font-bold">💰 Revenue Intelligence</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
              {statCard("MRR", `$${(revenue.mrr||0).toLocaleString()}`, "monthly recurring", "text-emerald-400")}
              {statCard("ARR", `$${(revenue.arr||0).toLocaleString()}`, "annual run rate", "text-indigo-400")}
              {(() => {
                const totalRevenue = (revenue.by_plan||[]).reduce((s,r)=>s+Number(r.total_revenue||0),0);
                const paidCustomers = (revenue.by_plan||[]).reduce((s,r)=>s+Number(r.users||0),0);
                const ltv = paidCustomers > 0 ? Math.round(totalRevenue / paidCustomers) : 0;
                return statCard("LTV (avg)", `$${ltv.toLocaleString()}`, "per customer", "text-violet-400");
              })()}
              {statCard("Total Revenue", `$${(revenue.by_plan||[]).reduce((s,r)=>s+Number(r.total_revenue||0),0).toLocaleString()}`, "all time", "text-amber-400")}
              {statCard("Paid Invoices", revenue.monthly_breakdown?.[0]?.paid_count || 0, "this month")}
              {statCard("Pending", revenue.monthly_breakdown?.[0]?.pending || 0, "awaiting payment", "text-slate-300")}
            </div>

            {/* Monthly trend */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <p className="text-sm font-bold mb-4">📈 Monthly Revenue Trend</p>
              <div className="h-52">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(revenue.monthly_breakdown||[]).slice(0,12).reverse()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                    <XAxis dataKey="month" tick={{fontSize:9,fill:"#64748b"}}/>
                    <YAxis tick={{fontSize:9,fill:"#64748b"}}/>
                    <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                    <Bar dataKey="collected" fill="#10b981" name="Collected ($)" radius={[4,4,0,0]}/>
                    <Bar dataKey="pending" fill="#6366f150" name="Pending ($)" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {/* Upsell candidates */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🎯 Upsell Opportunities</p>
                <div className="space-y-3">
                  {(revenue.upsell_candidates||[]).length === 0 ? <p className="text-slate-500 text-xs">No strong upsell signals right now</p>
                  : (revenue.upsell_candidates||[]).map(u => (
                    <div key={u.email} className="bg-slate-800/40 rounded-xl p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-semibold text-white text-sm">{u.name || u.email}</p>
                          <p className="text-xs text-slate-500">{u.email}</p>
                        </div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border font-bold uppercase ${PLAN_BADGES[u.plan] || PLAN_BADGES.custom}`}>{u.plan}</span>
                      </div>
                      <div className="flex gap-3 mt-2 text-xs text-slate-400">
                        <span>📧 {u.emails_7d || 0} emails this week</span>
                        <span>🎯 {u.total_leads || 0} leads</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Churn risk */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🚨 Churn Risk Users</p>
                <div className="space-y-2">
                  {(revenue.churn_risk_users||[]).length === 0 ? <p className="text-slate-500 text-xs">No churn signals detected</p>
                  : (revenue.churn_risk_users||[]).map(u => (
                    <div key={u.email} className="flex items-center justify-between py-2 border-b border-slate-800">
                      <div>
                        <p className="text-sm text-white font-medium">{u.name || u.email}</p>
                        <p className="text-xs text-slate-500">{u.email} · {u.plan}</p>
                      </div>
                      <span className="text-xs text-red-400 font-bold">{Math.round(u.days_inactive||0)}d inactive</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ─── PRODUCT ─── */}
        {!loading && tab === "product" && product && (
          <div className="space-y-5 max-w-[1400px]">
            <h2 className="text-xl font-bold">🧪 Product Analytics Lab</h2>
            {/* Activation funnel */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <p className="text-sm font-bold mb-5">🔽 Activation Funnel</p>
              <div className="space-y-3">
                {(product.funnel||[]).map((step, i) => (
                  <div key={step.step} className="flex items-center gap-4">
                    <span className="text-xs text-slate-500 w-5">{i+1}</span>
                    <span className="text-sm text-slate-300 w-44">{step.step}</span>
                    <div className="flex-1 bg-slate-800 rounded-full h-5 relative">
                      <div className={`h-5 rounded-full flex items-center pl-2 transition-all ${i===0?"bg-indigo-600":i===1?"bg-indigo-500":i===2?"bg-violet-500":i===3?"bg-blue-500":"bg-emerald-600"}`} style={{width:`${step.pct||0}%`}}>
                        <span className="text-[10px] text-white font-bold">{step.pct}%</span>
                      </div>
                    </div>
                    <span className="text-sm text-white font-bold w-8 text-right">{step.count}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-4">Avg time to first email sent: <span className="text-white font-semibold">{product.avg_hours_to_first_email}h</span></p>
            </div>

            {/* Feature adoption */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
              <p className="text-sm font-bold mb-4">📊 Feature Adoption ({product.total_users} total users)</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {Object.entries(product.feature_adoption||{}).map(([feat, data]) => (
                  <div key={feat} className="flex items-center gap-3">
                    <span className="text-xs text-slate-400 w-36 capitalize">{feat.replace(/_/g," ")}</span>
                    <div className="flex-1 bg-slate-800 rounded-full h-3"><div className="h-3 bg-indigo-500 rounded-full" style={{width:`${Math.min(100,data.pct||0)}%`}}/></div>
                    <span className="text-xs text-slate-300 w-16 text-right">{data.pct}% ({data.count})</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─── LEADS ─── */}
        {!loading && tab === "leads" && leadIntel && (
          <div className="space-y-5 max-w-[1400px]">
            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3">
              <h2 className="text-xl font-bold">🎯 Lead Intelligence</h2>
              <div className="flex flex-wrap gap-2 ml-auto items-end">
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Filter by User</label>
                  <select value={leadFilterUser} onChange={e=>setLeadFilterUser(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none">
                    <option value="">All Users</option>
                    {(leadIntel.user_list||[]).map(u=><option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Period</label>
                  <select value={leadFilterDays} onChange={e=>setLeadFilterDays(e.target.value)} className="bg-slate-800 border border-slate-700 text-white text-xs px-2 py-1.5 rounded-lg outline-none">
                    <option value="">All Time</option>
                    <option value="7">Last 7 days</option>
                    <option value="30">Last 30 days</option>
                    <option value="90">Last 90 days</option>
                  </select>
                </div>
                <button onClick={()=>load("leads")} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg transition">Apply</button>
                <button onClick={()=>{setLeadFilterUser("");setLeadFilterDays("");}} className="px-3 py-1.5 bg-slate-700 text-slate-300 text-xs rounded-lg transition">Reset</button>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {statCard("Total Leads", (leadIntel.pipeline_distribution||[]).reduce((s,r)=>s+(r.count||0),0).toLocaleString(), "in database")}
              {statCard("Untapped", leadIntel.untapped_leads?.toLocaleString() || 0, "never emailed", "text-amber-400")}
              {statCard("Industries", leadIntel.industry_performance?.length || 0, "tracked")}
              {statCard("Countries", leadIntel.geo_leads?.length || 0, "represented")}
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              {/* Industry performance — open + click + reply */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🏭 Industry Performance</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs min-w-[400px]">
                    <thead><tr className="text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-800">
                      <th className="pb-2 text-left">Industry</th>
                      <th className="pb-2 text-right">Leads</th>
                      <th className="pb-2 text-right">Open%</th>
                      <th className="pb-2 text-right">Click%</th>
                      <th className="pb-2 text-right">Reply%</th>
                    </tr></thead>
                    <tbody className="divide-y divide-slate-800/50">
                      {(leadIntel.industry_performance||[]).map(r=>(
                        <tr key={r.industry} className="hover:bg-slate-800/20 transition">
                          <td className="py-2 text-slate-300 max-w-[160px] truncate" title={r.industry}>{r.industry}</td>
                          <td className="py-2 text-right text-slate-500">{r.total_leads}</td>
                          <td className="py-2 text-right text-blue-400">{r.open_rate||0}%</td>
                          <td className="py-2 text-right text-amber-400">{r.click_rate||0}%</td>
                          <td className="py-2 text-right text-emerald-400 font-semibold">{r.reply_rate||0}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pipeline */}
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🎯 Pipeline Distribution</p>
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart layout="vertical" data={leadIntel.pipeline_distribution||[]}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#1e293b"/>
                      <XAxis type="number" tick={{fontSize:9,fill:"#64748b"}}/>
                      <YAxis type="category" dataKey="pipeline_stage" tick={{fontSize:9,fill:"#64748b"}} width={70}/>
                      <Tooltip contentStyle={{background:"#0f172a",border:"1px solid #334155",borderRadius:"8px"}}/>
                      <Bar dataKey="count" fill="#6366f1" radius={[0,4,4,0]} name="Leads"/>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            {/* Top leads table */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-5 py-3 border-b border-slate-800 font-semibold text-sm">🏆 Top Leads by Engagement {(leadIntel.top_leads||[]).length === 0 && <span className="text-slate-500 font-normal text-xs">— no emails sent yet</span>}</div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead><tr className="bg-slate-800/50 text-slate-500 text-[10px] uppercase tracking-wider border-b border-slate-800">
                    <th className="px-4 py-2 text-left">Company</th>
                    <th className="px-4 py-2 text-left">Industry</th>
                    <th className="px-4 py-2 text-left">Health</th>
                    <th className="px-4 py-2 text-left">Emails</th>
                    <th className="px-4 py-2 text-left">Opens</th>
                    <th className="px-4 py-2 text-left">Pipeline</th>
                  </tr></thead>
                  <tbody className="divide-y divide-slate-800/60">
                    {(leadIntel.top_leads||[]).slice(0,15).map(l => (
                      <tr key={l.id} className="hover:bg-slate-800/20">
                        <td className="px-4 py-2">
                          <p className="font-medium text-white text-xs">{l.name}</p>
                          <p className="text-[10px] text-slate-500">{l.country}</p>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-400 truncate max-w-[120px]">{l.category}</td>
                        <td className="px-4 py-2">
                          <div className="flex items-center gap-1">
                            <div className="w-12 bg-slate-800 rounded-full h-1.5"><div className={`h-1.5 rounded-full ${(l.health_score||0)>70?"bg-emerald-500":(l.health_score||0)>40?"bg-amber-500":"bg-red-500"}`} style={{width:`${l.health_score||0}%`}}/></div>
                            <span className="text-[10px] text-slate-400">{l.health_score||"–"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-2 text-xs text-slate-400">{l.email_count||0}</td>
                        <td className="px-4 py-2 text-xs text-slate-400">{l.max_opens||0}</td>
                        <td className="px-4 py-2"><span className="text-[10px] px-1.5 py-0.5 bg-slate-700 text-slate-300 rounded capitalize">{l.pipeline_stage}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ─── INFRASTRUCTURE ─── */}
        {!loading && tab === "infra" && infraData && (
          <div className="space-y-5 max-w-[1400px]">
            <h2 className="text-xl font-bold">⚙ Infrastructure Observability</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              {(infraData.queue||[]).map(q => statCard(`Queue: ${q.status}`, q.count, `avg retries: ${parseFloat(q.avg_retries||0).toFixed(1)}`, q.status==="failed"?"text-red-400":q.status==="processing"?"text-amber-400":"text-white"))}
            </div>
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🗄 Database Size</p>
                <div className="space-y-2">
                  {Object.entries(infraData.db_size||{}).map(([k,v]) => (
                    <div key={k} className="flex items-center justify-between text-sm"><span className="text-slate-400 capitalize">{k.replace(/_/g," ")}</span><span className="text-white font-semibold">{Number(v||0).toLocaleString()}</span></div>
                  ))}
                </div>
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">📬 IMAP Health (24h)</p>
                <div className="space-y-2">
                  <div className="flex justify-between"><span className="text-slate-400 text-sm">Replies found</span><span className="text-white font-semibold">{infraData.imap?.replies_found||0}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400 text-sm">Last reply</span><span className="text-white text-sm">{infraData.imap?.last_reply ? new Date(infraData.imap.last_reply).toLocaleString() : "–"}</span></div>
                  <div className="flex justify-between"><span className="text-slate-400 text-sm">Enrichment cache</span><span className="text-white font-semibold">{infraData.scraping?.total_cached||0} domains</span></div>
                </div>
              </div>
            </div>
            {(infraData.recent_errors||[]).length > 0 && (
              <div className="bg-red-900/20 border border-red-700/40 rounded-xl p-5">
                <p className="text-sm font-bold text-red-300 mb-3">🚨 Recent Deliverability Errors</p>
                {infraData.recent_errors.map(e => (
                  <div key={e.event_type} className="flex justify-between text-sm">
                    <span className="text-red-300 capitalize">{e.event_type.replace(/_/g," ")}</span>
                    <span className="text-red-400 font-bold">{e.count} in 24h</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── FRAUD & SECURITY ─── */}
        {!loading && tab === "fraud" && fraud && (
          <div className="space-y-5 max-w-[1400px]">
            <h2 className="text-xl font-bold">🛡 Security & Fraud Intelligence</h2>
            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">⚠️ Pending Approvals</p>
                {(fraud.pending_users||[]).length === 0 ? <p className="text-slate-500 text-sm">No pending users</p>
                : <div className="space-y-2">{(fraud.pending_users||[]).map(u => (
                  <div key={u.email} className="flex items-center justify-between py-2 border-b border-slate-800">
                    <div><p className="text-sm text-white">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></div>
                    <span className="text-xs text-amber-400">{Math.round(u.days_pending||0)}d waiting</span>
                  </div>
                ))}</div>}
              </div>
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <p className="text-sm font-bold mb-4">🚨 Spam Complaints (30 days)</p>
                {(fraud.spam_complaints||[]).length === 0 ? <p className="text-slate-500 text-sm text-center py-4">✅ No spam complaints</p>
                : <div className="space-y-2">{(fraud.spam_complaints||[]).map(u => (
                  <div key={u.user_email} className="flex justify-between text-sm">
                    <span className="text-white">{u.user_email}</span>
                    <span className="text-red-400 font-bold">{u.complaints} complaints</span>
                  </div>
                ))}</div>}
              </div>
            </div>
            {(fraud.volume_spikes||[]).length > 0 && (
              <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl p-5">
                <p className="text-sm font-bold text-amber-300 mb-3">⚡ Volume Spikes (last hour)</p>
                {fraud.volume_spikes.map(u => (
                  <div key={u.user_email} className="flex justify-between text-sm">
                    <span className="text-amber-300">{u.user_email}</span>
                    <span className="text-white">{u.last_hour} emails/hour · {u.last_day} today</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ─── AI BRIEF ─── */}
        {!loading && tab === "brief" && (
          <div className="space-y-5 max-w-3xl">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold">🧠 AI Intelligence Reports</h2>
            </div>

            {/* Report Controls */}
            {(() => {
              const generate = async () => {
                setRptLoading(true); setRptResult(null);
                try {
                  const url = rptType === "brief"
                    ? `/admin/enterprise/daily-brief?days=${rptDays}${rptUser ? `&user_email=${encodeURIComponent(rptUser)}` : ""}`
                    : "/admin/enterprise/generate-report";
                  const opts = rptType === "brief"
                    ? {}
                    : { method: "POST", body: JSON.stringify({ user_email: rptUser || null, days: parseInt(rptDays), title: rptUser ? `Performance Report — ${rptUser}` : "Platform Intelligence Report" }) };
                  const r = await authFetch(url, opts).then(r => r.json());
                  setRptResult(r);
                } catch(e) { console.error(e); }
                setRptLoading(false);
              };

              const downloadPDF = () => {
                if (!rptResult?.html) return;
                const win = window.open("", "_blank");
                win.document.write(rptResult.html);
                win.document.close();
                setTimeout(() => win.print(), 500);
              };

              const downloadWord = () => {
                if (!rptResult?.report_text && !rptResult?.bullets) return;
                const text = rptResult.report_text || (rptResult.bullets||[]).join("\n\n");
                const blob = new Blob([text], { type: "application/msword" });
                const a = document.createElement("a");
                a.href = URL.createObjectURL(blob);
                a.download = `${(rptResult.title||"report").replace(/[^a-z0-9]/gi,"_")}.doc`;
                a.click();
              };

              return (
                <div className="space-y-5">
                  {/* Config panel */}
                  <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 space-y-4">
                    <div className="grid sm:grid-cols-4 gap-3">
                      <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Report Type</label>
                        <select value={rptType} onChange={e=>setRptType(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none">
                          <option value="brief">Quick AI Brief (bullets)</option>
                          <option value="full">Full Report (PDF/Word)</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Scope</label>
                        <select value={rptUser} onChange={e=>setRptUser(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none">
                          <option value="">Entire Platform</option>
                          {(adminUsers||[]).map(u=><option key={u.email} value={u.email}>{u.name || u.email}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="text-[10px] text-slate-500 uppercase tracking-wider block mb-1">Period</label>
                        <select value={rptDays} onChange={e=>setRptDays(e.target.value)} className="w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none">
                          <option value="7">Last 7 Days</option>
                          <option value="30">Last 30 Days</option>
                          <option value="60">Last 60 Days</option>
                          <option value="90">Last 90 Days</option>
                        </select>
                      </div>
                      <div className="flex flex-col justify-end">
                        <button onClick={generate} disabled={rptLoading}
                          className="w-full py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white text-sm font-bold rounded-lg transition flex items-center justify-center gap-2">
                          {rptLoading ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>Generating...</> : "🧠 Generate"}
                        </button>
                      </div>
                    </div>
                    {rptUser && <p className="text-xs text-indigo-300">Scope: Report for <strong>{rptUser}</strong> — last {rptDays} days</p>}
                    {!rptUser && <p className="text-xs text-slate-500">Scope: Full platform summary — last {rptDays} days</p>}
                  </div>

                  {/* Result */}
                  {rptLoading && (
                    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center">
                      <div className="w-10 h-10 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"/>
                      <p className="text-slate-400">Analyzing data and generating {rptType === "brief" ? "brief" : "full report"}...</p>
                      <p className="text-slate-600 text-xs mt-2">Powered by Gemini · usually takes 5–15 seconds</p>
                    </div>
                  )}

                  {rptResult && !rptLoading && (
                    <div className="bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden">
                      {/* Header */}
                      <div className="flex items-center justify-between px-5 py-4 bg-indigo-900/20 border-b border-indigo-700/30">
                        <div>
                          <p className="font-bold text-white">{rptResult.title || (rptType === "brief" ? "AI Intelligence Brief" : "Full Report")}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{rptResult.scope} · Generated {rptResult.generated_at ? new Date(rptResult.generated_at).toLocaleString() : "just now"}</p>
                        </div>
                        {rptType === "full" && rptResult.html && (
                          <div className="flex gap-2">
                            <button onClick={downloadPDF} className="px-4 py-2 bg-red-900/50 hover:bg-red-700 border border-red-700/50 text-red-300 text-xs font-bold rounded-lg transition flex items-center gap-1">
                              📄 Download PDF
                            </button>
                            <button onClick={downloadWord} className="px-4 py-2 bg-blue-900/50 hover:bg-blue-700 border border-blue-700/50 text-blue-300 text-xs font-bold rounded-lg transition flex items-center gap-1">
                              📝 Download Word
                            </button>
                          </div>
                        )}
                      </div>
                      {/* Brief bullets */}
                      {rptType === "brief" && (
                        <div className="p-5 space-y-4">
                          {/* Key metrics strip */}
                          {rptResult.data && (
                            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-2">
                              {[
                                ["Sent", rptResult.data.emails?.sent?.toLocaleString()],
                                ["Open %", rptResult.data.emails?.open_rate + "%"],
                                ["Click %", rptResult.data.emails?.click_rate + "%"],
                                ["Reply %", rptResult.data.emails?.reply_rate + "%"],
                                ["Leads", rptResult.data.leads?.total?.toLocaleString()],
                                ["Interested", rptResult.data.leads?.interested],
                              ].map(([label, val]) => (
                                <div key={label} className="bg-slate-800/60 rounded-xl p-2 text-center">
                                  <p className="text-xs font-bold text-white">{val ?? "–"}</p>
                                  <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* AI bullets - strip markdown */}
                          {(rptResult.bullets||[]).map((b,i) => {
                            const clean = b.replace(/\*\*/g,"").replace(/\*/g,"").replace(/^#+\s*/,"").replace(/^-\s+/,"").trim();
                            return (
                              <div key={i} className="flex items-start gap-3 bg-slate-800/40 rounded-xl px-4 py-3">
                                <p className="text-sm text-slate-200 leading-relaxed">{clean}</p>
                              </div>
                            );
                          })}
                          {/* Interested leads */}
                          {(rptResult.data?.interested_leads||[]).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-bold text-emerald-400 mb-2">🔥 Interested Leads ({(rptResult.data.interested_leads||[]).length})</p>
                              <div className="space-y-1">
                                {(rptResult.data.interested_leads||[]).map((l,i) => (
                                  <div key={i} className="bg-emerald-900/20 border border-emerald-800/30 rounded-lg px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-xs font-semibold text-white">{l.name}</p>
                                      {l.phone && <span className="text-[10px] text-emerald-300">📞 {l.phone}</span>}
                                      <span className="text-[10px] text-indigo-300">✉ {l.email}</span>
                                    </div>
                                    <div className="flex gap-2 mt-0.5">
                                      <span className="text-[10px] text-slate-400">{l.category}</span>
                                      {l.country && <span className="text-[10px] text-slate-500">· {l.country}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Clicked leads */}
                          {(rptResult.data?.clicked_leads||[]).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-bold text-amber-400 mb-2">🖱️ Leads Who Clicked ({(rptResult.data.clicked_leads||[]).length})</p>
                              <div className="space-y-1">
                                {(rptResult.data.clicked_leads||[]).map((l,i) => (
                                  <div key={i} className="bg-amber-900/20 border border-amber-800/30 rounded-lg px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-xs font-semibold text-white">{l.name}</p>
                                      <span className="text-[10px] text-amber-300 font-bold">{l.click_count}× clicks</span>
                                      {l.phone && <span className="text-[10px] text-amber-200">📞 {l.phone}</span>}
                                      <span className="text-[10px] text-indigo-300">✉ {l.email}</span>
                                    </div>
                                    <div className="flex gap-2 mt-0.5">
                                      <span className="text-[10px] text-slate-400">{l.category}</span>
                                      {l.country && <span className="text-[10px] text-slate-500">· {l.country}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Opened leads */}
                          {(rptResult.data?.opened_leads||[]).length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs font-bold text-blue-400 mb-2">📧 Leads Who Opened ({(rptResult.data.opened_leads||[]).length})</p>
                              <div className="space-y-1">
                                {(rptResult.data.opened_leads||[]).map((l,i) => (
                                  <div key={i} className="bg-blue-900/20 border border-blue-800/30 rounded-lg px-3 py-2">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <p className="text-xs font-semibold text-white">{l.name}</p>
                                      <span className="text-[10px] text-blue-300 font-bold">{l.open_count}× opens</span>
                                      {l.phone && <span className="text-[10px] text-blue-200">📞 {l.phone}</span>}
                                      <span className="text-[10px] text-indigo-300">✉ {l.email}</span>
                                    </div>
                                    <div className="flex gap-2 mt-0.5">
                                      <span className="text-[10px] text-slate-400">{l.category}</span>
                                      {l.country && <span className="text-[10px] text-slate-500">· {l.country}</span>}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Best send time */}
                          {(rptResult.data?.best_send_hour||[]).length > 0 && (
                            <div className="grid sm:grid-cols-2 gap-3 mt-2">
                              <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                                <p className="text-xs font-bold text-slate-400 mb-1">⏰ Best Send Hours</p>
                                {(rptResult.data.best_send_hour||[]).map(h => (
                                  <p key={h.hour} className="text-xs text-slate-300">{h.hour}:00 — {h.open_rate}% open rate ({h.sent} sent)</p>
                                ))}
                              </div>
                              <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                                <p className="text-xs font-bold text-slate-400 mb-1">📅 Best Send Days</p>
                                {(rptResult.data.best_send_day||[]).map(d => (
                                  <p key={d.dow} className="text-xs text-slate-300">{d.day_name?.trim()} — {d.open_rate}% open rate</p>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Top subjects */}
                          {(rptResult.data?.top_subjects||[]).length > 0 && (
                            <div className="bg-slate-800/40 rounded-xl px-4 py-3">
                              <p className="text-xs font-bold text-slate-400 mb-2">✉️ Top Subject Lines</p>
                              {(rptResult.data.top_subjects||[]).map((s,i) => (
                                <div key={i} className="flex items-center justify-between py-1 border-b border-slate-700/50 last:border-0">
                                  <p className="text-xs text-slate-300 truncate flex-1 mr-3">"{s.subject}"</p>
                                  <span className="text-xs text-blue-400 shrink-0">{s.open_rate}% open</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Full report preview */}
                      {rptType === "full" && rptResult.html && (
                        <div className="p-5 space-y-3">
                          <p className="text-xs text-slate-500">Preview below · use Download PDF for the full formatted version</p>
                          <div className="bg-white rounded-xl p-4 max-h-[500px] overflow-y-auto text-slate-900 text-sm"
                            dangerouslySetInnerHTML={{__html: rptResult.html}}/>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {/* ── AI Chat Assistant ── */}
            {(() => {
              const sendChat = async () => {
                const q = chatInput.trim();
                if (!q || chatLoading) return;
                setChatInput("");
                setChatMessages(prev => [...prev, { role: "user", text: q }]);
                setChatLoading(true);
                try {
                  const res = await authFetch("/admin/enterprise/ai-chat", {
                    method: "POST",
                    body: JSON.stringify({ user_email: rptUser || null, question: q, days: parseInt(rptDays) })
                  }).then(r => r.json());
                  setChatMessages(prev => [...prev, { role: "ai", text: res.answer || "No response" }]);
                } catch {
                  setChatMessages(prev => [...prev, { role: "ai", text: "Error getting response. Please try again." }]);
                }
                setChatLoading(false);
              };

              return (
                <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
                  {/* Header */}
                  <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-indigo-600 flex items-center justify-center text-sm font-bold text-white">AI</div>
                      <div>
                        <p className="font-bold text-white text-sm">Ask the Data</p>
                        <p className="text-[10px] text-slate-500">
                          Has full access to {rptUser ? <strong className="text-indigo-300">{rptUser}</strong> : "all users"}'s data · last {rptDays} days
                        </p>
                      </div>
                    </div>
                    {chatMessages.length > 0 && (
                      <button onClick={() => setChatMessages([])} className="text-[10px] text-slate-500 hover:text-slate-300 transition">Clear chat</button>
                    )}
                  </div>

                  {/* Messages */}
                  {chatMessages.length > 0 && (
                    <div className="max-h-[450px] overflow-y-auto p-4 space-y-3">
                      {chatMessages.map((m, i) => {
                        const renderAI = (text) => text.split('\n').map((line, li) => {
                          if (!line.trim()) return <div key={li} className="h-1" />;
                          const indent = (line.match(/^(\s*)/) || ['',''])[1].length;
                          const pl = Math.min(indent * 3, 56);
                          const stripped = line.trim();
                          const fmt = (s) => {
                            const parts = s.split(/\*\*(.+?)\*\*/g);
                            return parts.map((p, j) => j % 2 === 1 ? <strong key={j} className="text-white font-semibold">{p}</strong> : p);
                          };
                          const isBullet = /^[•*]\s/.test(stripped);
                          const content = isBullet ? stripped.slice(stripped.search(/\s/) + 1) : stripped;
                          if (isBullet) return (
                            <div key={li} className="flex gap-2 my-0.5" style={{paddingLeft: pl}}>
                              <span className="text-indigo-400 shrink-0 mt-0.5 text-xs">•</span>
                              <span className="text-slate-200">{fmt(content)}</span>
                            </div>
                          );
                          return <div key={li} className="my-0.5 text-slate-200" style={{paddingLeft: pl}}>{fmt(stripped)}</div>;
                        });
                        return (
                        <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                          <div className={`max-w-[88%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                            m.role === "user"
                              ? "bg-indigo-600 text-white rounded-br-sm whitespace-pre-wrap"
                              : "bg-slate-800/80 rounded-bl-sm"
                          }`}>
                            {m.role === "ai" ? <div className="space-y-0.5">{renderAI(m.text)}</div> : m.text}
                          </div>
                        </div>
                        );
                      })}
                      {chatLoading && (
                        <div className="flex justify-start">
                          <div className="bg-slate-800 rounded-2xl rounded-bl-sm px-4 py-3 flex items-center gap-1.5">
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay:"0ms"}}/>
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay:"160ms"}}/>
                            <div className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{animationDelay:"320ms"}}/>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Suggested prompts when empty */}
                  {chatMessages.length === 0 && (
                    <div className="p-4 grid grid-cols-2 gap-2">
                      {[
                        "Give me all interested leads with emails, phone numbers and decision-maker names",
                        "Find WhatsApp numbers for the top clicked leads",
                        "Who are the top 10 warmest prospects with full contact details?",
                        "What are the open rate and click rate numbers?",
                        "Which industry has the best open rate?",
                        "Find LinkedIn profiles for the interested leads",
                      ].map(s => (
                        <button key={s} onClick={() => setChatInput(s)}
                          className="text-left text-xs text-slate-400 bg-slate-800/60 hover:bg-slate-700 border border-slate-700/50 rounded-xl px-3 py-2.5 transition leading-relaxed">
                          {s}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Input */}
                  <div className="p-4 border-t border-slate-800 flex gap-2">
                    <input
                      value={chatInput}
                      onChange={e => setChatInput(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendChat(); } }}
                      placeholder="e.g. Give me all interested leads with their phone numbers and emails..."
                      className="flex-1 bg-slate-800 border border-slate-700 text-white text-sm px-4 py-2.5 rounded-xl outline-none focus:border-indigo-500 placeholder-slate-500 transition"
                    />
                    <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()}
                      className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-bold rounded-xl transition flex items-center gap-2 shrink-0">
                      {chatLoading
                        ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"/>
                        : "Send"}
                    </button>
                  </div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Classic Admin Page ───────────────────────────────────────────────────────
function AdminPage() {

  const { authFetch, user, logout } = useAuth();

  const { theme } = useTheme();

  const navigate = useNavigate();

  const [users, setUsers]   = useState([]);

  const [loading, setLoading] = useState(true);

  const [tab, setTab]       = useState("pending"); // pending|all

  const [msg, setMsg]       = useState("");

  const [limitsModal, setLimitsModal] = useState(null); // { email, limits, usage }

  const [limitsLoading, setLimitsLoading] = useState(false);

  const [limitsSaving, setLimitsSaving] = useState(false);

  const [limitsForm, setLimitsForm] = useState({});

  // Global AI Provider Settings
  const [globalAiProvider, setGlobalAiProvider] = useState("claude");
  const [switchingAi, setSwitchingAi] = useState(false);
  const [aiSwitched, setAiSwitched] = useState(false);



  const fetchUsers = useCallback(async () => {

    setLoading(true);

    try {

      const resp = await authFetch("/admin/users");

      if (!resp.ok) throw new Error("Failed to load users.");

      setUsers(await resp.json());

    } catch (err) { setMsg(err.message); }

    finally { setLoading(false); }

  }, [authFetch]);



  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // Load current global AI provider setting
  useEffect(() => {
    const loadAiProvider = async () => {
      try {
        const resp = await authFetch("/admin/ai-provider");
        if (resp.ok) {
          const data = await resp.json();
          setGlobalAiProvider(data.ai_provider);
        }
      } catch (err) {
        console.error("Failed to load AI provider setting:", err);
      }
    };
    loadAiProvider();
  }, [authFetch]);

  const openLimits = async (email) => {

    setLimitsLoading(true);

    setLimitsModal({ email, limits: {}, usage: {} });

    try {

      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}/limits`);

      if (!resp.ok) throw new Error("Failed to load limits.");

      const data = await resp.json();

      setLimitsModal({ email, limits: data.limits, usage: data.usage });

      setLimitsForm(data.limits);

    } catch (err) { setMsg(err.message); setLimitsModal(null); }

    finally { setLimitsLoading(false); }

  };



  const saveLimits = async () => {

    if (!limitsModal) return;

    setLimitsSaving(true);

    try {

      const resp = await authFetch(`/admin/users/${encodeURIComponent(limitsModal.email)}/limits`, {

        method: "PUT", body: JSON.stringify(limitsForm),

      });

      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }

      setMsg(`Limits updated for ${limitsModal.email}.`);

      setLimitsModal(null);

      fetchUsers();

    } catch (err) { setMsg(`Error: ${err.message}`); }

    finally { setLimitsSaving(false); }

  };



  const toggleSuspend = async (email, suspended) => {

    try {

      const action = suspended ? "unsuspend" : "suspend";

      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}/${action}`, { method: "POST" });

      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }

      setMsg(`${email} ${suspended ? "unsuspended" : "suspended"}.`);

      if (limitsModal && limitsModal.email === email) {

        setLimitsForm(f => ({ ...f, suspended: !suspended }));

        setLimitsModal(m => ({ ...m, limits: { ...m.limits, suspended: !suspended } }));

      }

      fetchUsers();

    } catch (err) { setMsg(`Error: ${err.message}`); }

  };



  const doAction = async (email, action) => {

    setMsg("");

    try {

      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}/${action}`, { method: "POST" });

      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }

      setMsg(action === "approve" ? `${email} approved.` : `${email} rejected.`);

      fetchUsers();

    } catch (err) { setMsg(`Error: ${err.message}`); }

  };



  const doDelete = async (email) => {

    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;

    setMsg("");

    try {

      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}`, { method: "DELETE" });

      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }

      setMsg(`Deleted ${email}.`);

      fetchUsers();

    } catch (err) { setMsg(`Error: ${err.message}`); }

  };



  const PLAN_COLORS = {
    starter: "bg-sky-900/40 text-sky-300 border-sky-700/50",
    growth:  "bg-violet-900/40 text-violet-300 border-violet-700/50",
    pro:     "bg-amber-900/40 text-amber-300 border-amber-700/50",
    custom:  "bg-slate-800 text-slate-400 border-slate-700",
  };

  const PLAN_LIMITS_PRESETS = {
    starter: { leads_per_day: 50, leads_per_month: 1500, emails_per_day: 50, max_saved_leads: 1500, max_folders: 5, drip_sequences_enabled: false },
    growth:  { leads_per_day: 120, leads_per_month: 5000, emails_per_day: 120, max_saved_leads: 5000, max_folders: 20, drip_sequences_enabled: true },
    pro:     { leads_per_day: 300, leads_per_month: 15000, emails_per_day: 300, max_saved_leads: 15000, max_folders: null, drip_sequences_enabled: true },
  };

  const assignPlan = async (email, plan) => {
    try {
      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}/assign-plan`, {
        method: "POST", body: JSON.stringify({ plan }),
      });
      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }
      setMsg(`Plan '${plan}' assigned to ${email}.`);
      // If custom plan, prompt for MRR amount
      if (plan === "custom") {
        const mrrInput = window.prompt(`Set monthly price for ${email} (custom plan):\nEnter amount in $ or leave blank to skip:`, "");
        if (mrrInput && !isNaN(parseFloat(mrrInput))) {
          await authFetch(`/admin/users/${encodeURIComponent(email)}/custom-mrr`, {
            method: "PUT", body: JSON.stringify({ custom_mrr: parseFloat(mrrInput) }),
          });
          setMsg(`Plan 'custom' assigned to ${email} at $${mrrInput}/mo.`);
        }
      }
      fetchUsers();
      if (limitsModal && limitsModal.email === email && plan !== "custom") {
        setLimitsForm(f => ({ ...f, ...PLAN_LIMITS_PRESETS[plan] }));
      }
    } catch (err) { setMsg(`Error: ${err.message}`); }
  };

  const doRoleChange = async (email, role) => {

    try {

      const resp = await authFetch(`/admin/users/${encodeURIComponent(email)}/role`, {

        method: "PATCH", body: JSON.stringify({ role }),

      });

      if (!resp.ok) { const d = await resp.json(); throw new Error(d.detail); }

      setMsg(`${email} role set to ${role}.`);

      fetchUsers();

    } catch (err) { setMsg(`Error: ${err.message}`); }

  };



  const setLimitField = (field, value) => {

    setLimitsForm(f => ({ ...f, [field]: value }));

  };



  const LimitInput = ({ label, field, type = "number", usage, usageLabel, max }) => (

    <div className="flex flex-col gap-1.5">

      <label className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>

      <div className="flex items-center gap-2">

        <input

          type={type}

          value={limitsForm[field] === null || limitsForm[field] === undefined ? "" : limitsForm[field]}

          onChange={e => setLimitField(field, e.target.value === "" ? null : parseInt(e.target.value, 10))}

          placeholder="Unlimited"

          min={0}

          className="w-full bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-indigo-500 transition placeholder:text-slate-600"

        />

      </div>

      {usage !== undefined && (

        <p className="text-[11px] text-slate-500">Used: <span className="text-indigo-400 font-semibold">{usage}</span>{max !== null && max !== undefined ? ` / ${max}` : " (unlimited)"}{usageLabel ? ` ${usageLabel}` : ""}</p>

      )}

    </div>

  );



  const LimitToggle = ({ label, field, description }) => (

    <div className="flex items-center justify-between gap-3 py-2">

      <div>

        <p className="text-sm text-white font-medium">{label}</p>

        {description && <p className="text-[11px] text-slate-500">{description}</p>}

      </div>

      <button

        onClick={() => setLimitField(field, !limitsForm[field])}

        className={`relative w-11 h-6 rounded-full transition-colors ${limitsForm[field] ? "bg-emerald-600" : "bg-slate-700"}`}

      >

        <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${limitsForm[field] ? "translate-x-5" : ""}`} />

      </button>

    </div>

  );



  const statusColor = { pending: "text-amber-400", approved: "text-emerald-400", rejected: "text-red-400" };

  const filtered = tab === "pending" ? users.filter((u) => u.status === "pending") : users;



  return (

    <div className={`min-h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-50 text-gray-900'} flex flex-col`}>

      {/* Top bar */}

      <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-4 sm:px-7 py-4 border-b border-slate-800 bg-slate-900">

        <div className="flex items-center gap-3">

                   <img src={TimingoflowLogo} alt="TimingoFlow" className="w-16 h-16" />

          <span className="font-bold text-sm tracking-tight">TimingoFlow</span>

          <span className="text-slate-700 hidden sm:inline">|</span>

          <span className="text-xs text-amber-400 font-bold uppercase tracking-widest">Admin</span>

        </div>

        <div className="flex items-center gap-2 sm:gap-3 flex-wrap">

          <span className="text-xs text-slate-500 hidden sm:inline">{user?.name} ({user?.email})</span>

          <button onClick={() => navigate("/intelligence")} className="text-xs text-purple-300 hover:text-purple-200 transition px-3 py-1.5 bg-purple-950 border border-purple-800 rounded-lg font-bold">🧠 Intelligence</button>
          <button onClick={() => navigate("/billing")} className="text-xs text-emerald-400 hover:text-emerald-300 transition px-3 py-1.5 bg-emerald-950 border border-emerald-800 rounded-lg font-bold">💳 Billing</button>
          <button onClick={() => navigate("/")} className="text-xs text-indigo-400 hover:text-indigo-300 transition px-3 py-1.5 bg-indigo-950 border border-indigo-800 rounded-lg">App</button>
          <button onClick={() => { logout(); navigate("/login"); }} className="text-xs text-slate-500 hover:text-red-400 transition px-3 py-1.5 border border-slate-700 rounded-lg">Sign Out</button>

        </div>

      </header>



      <main className="flex-1 p-4 sm:p-7 flex flex-col gap-6">

        {/* Global AI Provider Control */}
        <div className="bg-gradient-to-br from-indigo-950/40 to-purple-950/30 border border-indigo-800/50 rounded-2xl p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <h2 className="text-lg font-bold text-white">AI Message Generator</h2>
              </div>
              <p className="text-sm text-slate-400 leading-relaxed">Choose which AI model generates outreach messages for <strong className="text-white">all users</strong>. Claude Haiku is faster and more cost-effective, while Gemini offers different writing styles.</p>
            </div>
            <div className="shrink-0 flex flex-col items-end gap-2">
              <select 
                value={globalAiProvider} 
                onChange={async (e) => {
                  const newProvider = e.target.value;
                  setSwitchingAi(true);
                  setAiSwitched(false);
                  try {
                    const resp = await authFetch(`/admin/ai-provider?provider=${newProvider}`, {
                      method: "PUT"
                    });
                    if (resp.ok) {
                      setGlobalAiProvider(newProvider);
                      setAiSwitched(true);
                      setTimeout(() => setAiSwitched(false), 3000);
                    } else {
                      const err = await resp.json().catch(() => ({}));
                      alert(err.detail || "Failed to switch AI provider");
                    }
                  } catch (e) {
                    alert("Failed to switch AI provider: " + e.message);
                  } finally {
                    setSwitchingAi(false);
                  }
                }}
                disabled={switchingAi}
                className="bg-slate-800 border border-slate-600 text-white rounded-lg px-4 py-2.5 text-sm font-medium outline-none focus:border-indigo-500 transition cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed min-w-[220px]"
              >
                <option value="gemini"> Gemini (Google)</option>
                <option value="claude"> Claude Haiku (Anthropic)</option>
              </select>
              {aiSwitched && (
                <div className="flex items-center gap-2 bg-emerald-900/30 border border-emerald-700/50 rounded-lg px-3 py-1.5">
                  <svg className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-xs text-emerald-300 font-medium">Switched to {globalAiProvider === "claude" ? "Claude Haiku" : "Gemini"}</span>
                </div>
              )}
              {switchingAi && (
                <div className="flex items-center gap-2 text-slate-400 text-xs">
                  <div className="w-3.5 h-3.5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" />
                  <span>Switching...</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>

          <h1 className="text-xl font-bold tracking-tight">User Management</h1>

          <p className="text-slate-500 text-sm mt-0.5">Review signup requests, manage users, and set per-user limits</p>

        </div>



        {/* Stats row */}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">

          {[

            { label: "Total Users",   value: users.length,                                             color: "text-indigo-300",  ring: "border-indigo-800/50" },

            { label: "Pending",       value: users.filter((u) => u.status === "pending").length,       color: "text-amber-300",   ring: "border-amber-800/50" },

            { label: "Approved",      value: users.filter((u) => u.status === "approved").length,      color: "text-emerald-300", ring: "border-emerald-900/50" },

            { label: "Rejected",      value: users.filter((u) => u.status === "rejected").length,      color: "text-red-300",     ring: "border-red-900/50" },

          ].map(({ label, value, color, ring }) => (

            <div key={label} className={`bg-slate-900 border ${ring} rounded-xl p-4`}>

              <p className={`text-3xl font-bold ${color}`}>{value}</p>

              <p className="text-xs text-slate-600 mt-0.5">{label}</p>

            </div>

          ))}

        </div>



        {msg && <div className="bg-slate-800 border border-slate-700 text-slate-300 text-xs px-4 py-2 rounded-lg">{msg}</div>}



        {/* Tabs */}

        <div className="flex gap-2">

          <button onClick={() => setTab("pending")} className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${tab==="pending"?"bg-amber-950 border-amber-800 text-amber-300":"bg-transparent border-slate-700 text-slate-500 hover:text-slate-300"}`}>

            Pending ({users.filter((u) => u.status === "pending").length})

          </button>

          <button onClick={() => setTab("all")} className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${tab==="all"?"bg-slate-800 border-slate-600 text-white":"bg-transparent border-slate-700 text-slate-500 hover:text-slate-300"}`}>

            All Users ({users.length})

          </button>

        </div>



        {/* User table */}

        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">

          {loading ? (

            <div className="p-10 text-center text-slate-600 text-sm">Loading users...</div>

          ) : filtered.length === 0 ? (

            <div className="p-10 text-center text-slate-600 text-sm">{tab === "pending" ? "No pending signup requests." : "No users found."}</div>

          ) : (

            <div className="overflow-x-auto">

              <table className="w-full text-sm min-w-[780px]">

                <thead>

                  <tr className="bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-widest border-b border-slate-800">

                    <th className="px-5 py-3 text-left">Name</th>
                    <th className="px-5 py-3 text-left">Email</th>
                    <th className="px-5 py-3 text-left">Plan</th>
                    <th className="px-5 py-3 text-left">Role</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Joined</th>
                    <th className="px-5 py-3 text-left">Actions</th>

                  </tr>

                </thead>

                <tbody className="divide-y divide-slate-800/60">

                  {filtered.map((u) => (

                    <tr key={u.email} className="hover:bg-slate-800/25 transition">

                      <td className="px-5 py-3 font-semibold text-white">{u.name}</td>
                      <td className="px-5 py-3 text-slate-400 text-xs font-mono">{u.email}</td>
                      <td className="px-5 py-3">
                        <select
                          value={u.plan || "custom"}
                          onChange={(e) => assignPlan(u.email, e.target.value)}
                          className={`text-xs px-2 py-1 rounded-lg border outline-none cursor-pointer font-semibold capitalize ${PLAN_COLORS[u.plan || "custom"]}`}
                        >
                          <option value="starter">Starter — $99/mo</option>
                          <option value="growth">Growth — $249/mo</option>
                          <option value="pro">Pro — $499/mo</option>
                          <option value="custom">Custom</option>
                        </select>
                      </td>
                      <td className="px-5 py-3">
                        <select value={u.role} onChange={(e) => doRoleChange(u.email, e.target.value)}

                          disabled={u.email === user?.email}

                          className="bg-slate-800 border border-slate-700 text-slate-300 text-xs px-2 py-1 rounded-lg outline-none cursor-pointer disabled:opacity-40">

                          <option value="user">User</option>

                          <option value="admin">Admin</option>

                        </select>

                      </td>

                      <td className="px-5 py-3">

                        <span className={`text-xs font-semibold capitalize ${statusColor[u.status] || "text-slate-400"}`}>

                          {u.status}

                        </span>

                      </td>

                      <td className="px-5 py-3 text-slate-500 text-xs whitespace-nowrap">

                        {u.created_at ? new Date(u.created_at).toLocaleDateString() : "..."}

                      </td>

                      <td className="px-5 py-3">

                        <div className="flex gap-2 items-center flex-wrap">

                          {u.status === "pending" && (

                            <>

                              <button onClick={() => doAction(u.email, "approve")} className="px-3 py-1 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition">Approve</button>

                              <button onClick={() => doAction(u.email, "reject")}  className="px-3 py-1 bg-red-900 hover:bg-red-800 text-red-300 text-xs font-semibold rounded-lg transition">Reject</button>

                            </>

                          )}

                          {u.status === "rejected" && (

                            <button onClick={() => doAction(u.email, "approve")} className="px-3 py-1 bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold rounded-lg transition">Approve</button>

                          )}

                          {u.status === "approved" && u.email !== user?.email && (

                            <button onClick={() => doAction(u.email, "reject")} className="px-3 py-1 bg-slate-700 hover:bg-red-900 text-slate-400 hover:text-red-300 text-xs font-semibold rounded-lg transition">Revoke</button>

                          )}

                          {u.status === "approved" && u.email !== user?.email && (

                            <button onClick={() => openLimits(u.email)} className="px-3 py-1 bg-indigo-900 hover:bg-indigo-800 text-indigo-300 text-xs font-semibold rounded-lg transition">Manage</button>

                          )}

                          {u.email !== user?.email && (

                            <button onClick={() => doDelete(u.email)} className="text-slate-600 hover:text-red-400 transition text-sm px-1">Del</button>

                          )}

                        </div>

                      </td>

                    </tr>

                  ))}

                </tbody>

              </table>

            </div>

          )}

        </div>

        {/* GlobalPay Payment Links */}
        <PaymentLinksPanel authFetch={authFetch} />

      </main>



      {/* Limits Modal */}

      {limitsModal && (

        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setLimitsModal(null)}>

          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>

            <div className="sticky top-0 bg-slate-900 border-b border-slate-800 px-6 py-4 flex items-center justify-between z-10">

              <div>

                <h2 className="text-lg font-bold text-white">User Limits</h2>

                <p className="text-xs text-slate-500 font-mono mt-0.5">{limitsModal.email}</p>

              </div>

              <button onClick={() => setLimitsModal(null)} className="text-slate-500 hover:text-white text-xl leading-none">&times;</button>

            </div>

            {limitsLoading ? (

              <div className="p-10 text-center text-slate-600 text-sm">Loading limits...</div>

            ) : (

              <div className="p-6 flex flex-col gap-5">

                {/* Plan quick-assign */}
                <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
                  <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest mb-3">Quick Apply Plan</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { key: "starter", label: "Starter", price: "$99/mo", color: "border-sky-700/60 text-sky-300 hover:bg-sky-900/30" },
                      { key: "growth",  label: "Growth",  price: "$249/mo", color: "border-violet-700/60 text-violet-300 hover:bg-violet-900/30" },
                      { key: "pro",     label: "Pro",     price: "$499/mo", color: "border-amber-700/60 text-amber-300 hover:bg-amber-900/30" },
                      { key: "custom",  label: "Custom",  price: "Manual",  color: "border-slate-600 text-slate-400 hover:bg-slate-700/30" },
                    ].map(({ key, label, price, color }) => (
                      <button key={key} onClick={() => {
                        assignPlan(limitsModal.email, key);
                        if (key !== "custom") setLimitsForm(f => ({ ...f, ...PLAN_LIMITS_PRESETS[key] }));
                      }} className={`border rounded-lg px-3 py-2 text-xs font-semibold transition flex flex-col items-start gap-0.5 ${color}`}>
                        <span>{label}</span>
                        <span className="text-[10px] opacity-60">{price}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-600 mt-2">Selecting a plan auto-fills limits below. Custom = manual control.</p>
                </div>

                {/* Suspension */}

                <div className={`p-4 rounded-xl border ${limitsForm.suspended ? "bg-red-950/50 border-red-800/50" : "bg-slate-800/50 border-slate-700/50"}`}>

                  <div className="flex items-center justify-between gap-3">

                    <div>

                      <p className="text-sm font-semibold text-white">Account Suspended</p>

                      <p className="text-[11px] text-slate-500">Suspended users cannot access the platform</p>

                    </div>

                    <button

                      onClick={() => toggleSuspend(limitsModal.email, limitsForm.suspended)}

                      className={`relative w-11 h-6 rounded-full transition-colors ${limitsForm.suspended ? "bg-red-600" : "bg-slate-700"}`}

                    >

                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${limitsForm.suspended ? "translate-x-5" : ""}`} />

                    </button>

                  </div>

                </div>



                {/* Search Limits */}

                <div>

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Search Limits</h3>

                  <div className="grid grid-cols-2 gap-3">

          					<LimitInput label="Per Day" field="leads_per_day" usage={limitsModal.usage.leads_today} max={limitsForm.leads_per_day} />

          					<LimitInput label="Per Month" field="leads_per_month" usage={limitsModal.usage.leads_month} max={limitsForm.leads_per_month} />

          					<LimitInput label="Lifetime" field="leads_lifetime" usage={limitsModal.usage.leads_lifetime} max={limitsForm.leads_lifetime} />

                    <LimitInput label="Max Results/Search" field="max_results_per_search" usage={undefined} />

                  </div>

                </div>



                {/* Export */}

                <div className="border-t border-slate-800 pt-4">

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Export</h3>

                  <LimitToggle label="Can Export CSV" field="can_export" description="Allow user to export leads as CSV" />

                </div>



                {/* Saved Leads & Folders */}

                <div className="border-t border-slate-800 pt-4">

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Storage Limits</h3>

                  <div className="grid grid-cols-2 gap-3">

                    <LimitInput label="Max Saved Leads" field="max_saved_leads" usage={limitsModal.usage.saved_leads} max={limitsForm.max_saved_leads} />

                    <LimitInput label="Max Folders" field="max_folders" usage={limitsModal.usage.folders} max={limitsForm.max_folders} />

                  </div>

                </div>



                {/* Reminders */}

                <div className="border-t border-slate-800 pt-4">

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Reminders</h3>

                  <LimitToggle label="Reminders Enabled" field="reminders_enabled" description="Allow user to use the reminder system" />

                  <LimitInput label="Max Active Reminders" field="max_reminders" usage={limitsModal.usage.active_reminders} max={limitsForm.max_reminders} />

                </div>

                <div className="border-t border-slate-800 pt-4">

                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Email Outreach</h3>

                  <LimitInput label="Emails Per Day" field="emails_per_day" usage={limitsModal.usage.emails_sent_today} max={limitsForm.emails_per_day} />

                  <LimitToggle label="Require Approval" field="require_approval" description="Require admin approval before sending emails" />

                </div>

                {/* Organizations */}
                <div className="border-t border-slate-800 pt-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Organizations</h3>
                  <LimitInput label="Max Organizations" field="max_organizations" usage={undefined} max={limitsForm.max_organizations} />
                  <LimitInput label="Max Docs Per Org" field="max_org_docs" usage={undefined} max={limitsForm.max_org_docs} />
                </div>

                {/* AI Scripts */}
                <div className="border-t border-slate-800 pt-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">AI Scripts</h3>
                  <LimitInput label="Scripts Per Lead" field="ai_scripts_per_lead" usage={undefined} max={limitsForm.ai_scripts_per_lead} />
                  <LimitInput label="Scripts Per Day" field="ai_scripts_per_day" usage={undefined} max={limitsForm.ai_scripts_per_day} />
                </div>

                {/* AI Chatbot */}
                <div className="border-t border-slate-800 pt-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">AI Assistant</h3>
                  <LimitToggle label="AI Assistant Enabled" field="ai_chatbot_enabled" description="Allow user to use the AI chatbot assistant" />
                  <LimitInput label="Prompts Per Day" field="ai_chatbot_prompts_per_day" usage={undefined} max={limitsForm.ai_chatbot_prompts_per_day} />
                  <LimitInput label="Max Chats" field="ai_chatbot_max_chats" usage={undefined} max={limitsForm.ai_chatbot_max_chats} />
                </div>

                {/* Auto Follow-ups */}
                <div className="border-t border-slate-800 pt-4">
                  <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3">Auto Follow-ups</h3>
                  <LimitToggle label="Auto Follow-ups Enabled" field="drip_sequences_enabled" description="Allow user to create automated drip follow-up sequences (Pro feature)" />
                </div>



                {/* Save */}

                <div className="border-t border-slate-800 pt-4 flex gap-3">

                  <button

                    onClick={saveLimits}

                    disabled={limitsSaving}

                    className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white text-sm font-bold rounded-xl transition"

                  >

                    {limitsSaving ? "Saving..." : "Save Limits"}

                  </button>

                  <button onClick={() => setLimitsModal(null)} className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold rounded-xl transition">Cancel</button>

                </div>

              </div>

            )}

          </div>

        </div>

      )}

    </div>

  );

}




// --- Sidebar ------------------------------------------------------------------


function Sidebar({ page, setPage, open, onClose }) {


  const { user, logout } = useAuth();
  const { theme } = useTheme();


  const navigate = useNavigate();


  const navItems = [
    { id: "dashboard",    label: "Dashboard",       icon: "" },
    { id: "search",       label: "Search",          icon: "" },
    { id: "saved",        label: "Saved Leads",     icon: "" },
    { id: "pipeline",     label: "Pipeline",        icon: "" },
    { id: "outreach",     label: "AI Outreach",     icon: "" },
    { id: "followups",    label: "Follow-ups",      icon: "" },
    { id: "drip",         label: "Auto Follow-ups",  icon: "" },
    { id: "leaderboard",  label: "Lead Leaderboard",icon: "" },
    { id: "tracking",     label: "Email Tracking",  icon: "" },
    { id: "linkanalytics", label: "Link Analytics", icon: "" },
    { id: "siteactivity", label: "Site Activity", icon: "" },
    { id: "organization", label: "Organizations", icon: "" },
    { id: "campaign",     label: "Campaign Settings", icon: "" },
    { id: "linkedin",     label: "LinkedIn Outreach", icon: "" },
    { id: "history",      label: "History",         icon: "" },
    { id: "analytics",    label: "Analytics",       icon: "" },
    { id: "reminders",    label: "Reminders",       icon: "" },
    { id: "assistant",    label: "AI Assistant",    icon: "" },
    { id: "settings",     label: "Settings",        icon: "" },
  ];

  const navSections = [
    { id: "workspace", label: "Workspace", items: ["dashboard", "search", "saved", "pipeline"] },
    { id: "outreach", label: "Outreach", items: ["outreach", "followups", "drip", "campaign", "linkedin", "reminders"] },
    { id: "insights", label: "Insights", items: ["leaderboard", "tracking", "linkanalytics", "siteactivity", "history", "analytics"] },
    { id: "account", label: "Account", items: ["organization", "assistant", "settings"] },
  ];

  const navItemMap = navItems.reduce((acc, item) => {
    acc[item.id] = item;
    return acc;
  }, {});

  const currentSection = navSections.find((section) => section.items.includes(page))?.id || "workspace";
  const sidebarOpenSectionKey = `sidebar_open_section:${user?.email || "anon"}`;
  const [expandedSections, setExpandedSections] = useState(() => ({
    workspace: loadLS(sidebarOpenSectionKey, currentSection) === "workspace",
    outreach: loadLS(sidebarOpenSectionKey, currentSection) === "outreach",
    insights: loadLS(sidebarOpenSectionKey, currentSection) === "insights",
    account: loadLS(sidebarOpenSectionKey, currentSection) === "account",
    [loadLS(sidebarOpenSectionKey, currentSection) || currentSection]: true,
  }));

  useEffect(() => {
    setExpandedSections((prev) => {
      if (prev[currentSection] && Object.values(prev).filter(Boolean).length === 1) return prev;
      return {
        workspace: false,
        outreach: false,
        insights: false,
        account: false,
        [currentSection]: true,
      };
    });
  }, [currentSection]);

  useEffect(() => {
    const openSection = Object.entries(expandedSections).find(([, isOpen]) => isOpen)?.[0] || null;
    saveLS(sidebarOpenSectionKey, openSection);
  }, [expandedSections, sidebarOpenSectionKey]);

  const handleNav = (id) => { setPage(id); onClose(); };
  const toggleSection = (id) => setExpandedSections((prev) => {
    const nextOpen = !prev[id];
    return {
      workspace: false,
      outreach: false,
      insights: false,
      account: false,
      [id]: nextOpen,
    };
  });

  return (
    <>
      {/* Mobile overlay backdrop */}
      {open && <div className="fixed inset-0 bg-black/60 z-40 md:hidden" onClick={onClose} />}

      <aside className={`fixed md:sticky top-0 left-0 z-50 w-64 md:w-56 shrink-0 border-r flex flex-col h-screen min-h-0 transition-transform duration-200 ease-in-out ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'} ${
        open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
      }`}>


      <div className={`px-5 py-4 border-b flex items-center justify-between ${theme === 'dark' ? 'border-slate-800/60' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center font-bold text-xs shadow-lg shadow-indigo-900/50">T</div>
          <span className={`font-bold text-sm tracking-tight ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>TimingoFlow</span>
        </div>
        <button onClick={onClose} className={`md:hidden p-1 ${theme === 'dark' ? 'text-slate-500 hover:text-white' : 'text-gray-500 hover:text-gray-900'}`}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>





      <nav className="flex-1 min-h-0 py-3 flex flex-col gap-2 px-2 overflow-y-auto">


        {navSections.map((section) => (
          <div key={section.id} className={`rounded-2xl border overflow-hidden ${theme === 'dark' ? 'border-slate-800/70 bg-slate-950/35' : 'border-gray-200 bg-gray-50'}`}>
            <button
              onClick={() => toggleSection(section.id)}
              className={`w-full flex items-center justify-between px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-[0.14em] transition ${theme === 'dark' ? 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/40' : 'text-gray-600 hover:text-gray-800 hover:bg-gray-100'}`}
            >
              <span>{section.label}</span>
              <svg
                className={`w-4 h-4 transition-transform ${expandedSections[section.id] ? "rotate-180" : "rotate-0"}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {expandedSections[section.id] && (
              <div className="px-2 pb-2 flex flex-col gap-0.5">
                {section.items.map((itemId) => {
                  const item = navItemMap[itemId];
                  if (!item) return null;
                  return (
                    <button
                      key={item.id}
                      onClick={() => handleNav(item.id)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium w-full text-left transition ${
                        page === item.id ? "bg-indigo-900/60 text-indigo-200 shadow-sm" : "text-slate-500 hover:text-slate-200 hover:bg-slate-800/50"
                      }`}
                    >
                      <span className="text-base">{item.icon}</span><span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}





        {user?.role === "admin" && (


          <button onClick={() => { navigate("/admin"); onClose(); }}


            className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium w-full text-left transition text-amber-500/80 hover:text-amber-300 hover:bg-amber-950/40 mt-2">


            <span className="text-base">AD</span><span>Admin Panel</span>


          </button>


        )}


      </nav>





      <div className="px-3 py-3 border-t border-slate-800/60">


        <div className="flex items-center gap-2.5 px-3 py-2 mb-1">


          <div className="w-7 h-7 rounded-full bg-indigo-800 flex items-center justify-center text-xs font-bold text-indigo-200 uppercase">


            {user?.name?.[0] || "-"}


          </div>


          <div className="flex-1 min-w-0">


            <p className="text-xs font-semibold text-white truncate">{user?.name || "User"}</p>


            <p className="text-[10px] text-slate-600 truncate">{user?.email}</p>


          </div>


        </div>


        <button onClick={() => { logout(); navigate("/login"); onClose(); }}


          className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs text-slate-500 hover:text-red-400 hover:bg-red-950/30 transition font-medium">


          <span>OUT</span> Sign Out


        </button>


      </div>


    </aside>
    </>


  );


}





// --- Search Page --------------------------------------------------------------


// ─── Plan gating ─────────────────────────────────────────────────────────────
const PLAN_RANK = { starter: 1, growth: 2, pro: 3, custom: 99 };
const PLAN_DISPLAY = { starter: 'Starter', growth: 'Growth', pro: 'Pro', custom: 'Custom' };
const PLAN_COLOR = { starter: 'text-sky-300', growth: 'text-violet-300', pro: 'text-amber-300', custom: 'text-slate-300' };

function usePlan() {
  const { user } = useAuth();
  const plan = (user?.plan || 'custom').toLowerCase();
  const rank = PLAN_RANK[plan] ?? 99;
  return {
    plan,
    canUse: (requiredPlan) => rank >= (PLAN_RANK[requiredPlan] ?? 0),
    planLabel: PLAN_DISPLAY[plan] || plan,
  };
}

function UpgradeBanner({ requiredPlan, featureName }) {
  const navigate = useNavigate();
  const { plan, planLabel } = usePlan();
  const reqLabel = PLAN_DISPLAY[requiredPlan] || requiredPlan;
  const reqColor = PLAN_COLOR[requiredPlan] || 'text-white';
  return (
    <div className="mb-5 bg-gradient-to-r from-indigo-950/60 to-violet-950/60 border border-indigo-700/50 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
      <div className="text-3xl">🔒</div>
      <div className="flex-1">
        <p className="font-bold text-white text-sm mb-0.5">
          {featureName} is not available on the <span className={PLAN_COLOR[plan] || 'text-slate-300'}>{planLabel}</span> plan
        </p>
        <p className="text-xs text-slate-400">
          Upgrade to <span className={`font-bold ${reqColor}`}>{reqLabel}</span> or higher to unlock this feature.
          Contact <span className="text-indigo-400">team@timingoflow.com</span> to upgrade.
        </p>
      </div>
      <button
        onClick={() => navigate('/pricing')}
        className="shrink-0 px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition"
      >
        View Plans
      </button>
    </div>
  );
}

function SearchPage({ saved, saveLead, bulkSaveLeads, rerunQuery, clearRerun, folders, createFolder, userLimits, refreshLimits }) {


  const { authFetch } = useAuth();


  const [query, setQuery]       = useState({ category: "", location: "", max: 20, noWebsite: false, requireEmail: false, requirePhone: false, requireWebsite: false, worldwide: false, aiAssist: false });


  const [results, setResults]   = useState([]);


  const [running, setRunning]   = useState(false);


  const [progress, setProgress] = useState([]);


  const [error, setError]       = useState("");


  const [cached, setCached]     = useState(false);


  const [sort, setSort]         = useState({ col: "priority", dir: "desc" });


  const [selected, setSelected] = useState(new Set());


  const [searchDone, setSearchDone] = useState(false);
  const [scrapeCount, setScrapeCount] = useState(0);
  const [scrapeStartTime, setScrapeStartTime] = useState(null);
  const [isRanked, setIsRanked] = useState(false);


  const [expandedLead, setExpandedLead] = useState(null);
  const [aiScripts, setAiScripts] = useState({}); // { leadName: { scripts: [], loading: bool } }
  const [savingLeads, setSavingLeads] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const selectedOrg = orgs.find(o => o.id === selectedOrgId) || null;

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch("/organizations");
        if (resp.ok) {
          const data = await resp.json();
          setOrgs(data);
          if (data.length > 0) setSelectedOrgId(data[0].id);
        }
      } catch (e) { console.error(e); }
    })();
  }, [authFetch]);

  const fetchAiScripts = async (lead) => {
    if (aiScripts[lead.name]?.scripts?.length) return;
    setAiScripts(prev => ({ ...prev, [lead.name]: { scripts: [], loading: true } }));
    try {
      const resp = await authFetch("/ai/call-script", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_name: lead.name, business_category: lead.category, website: lead.website, detected_issues: lead.detected_issues || [], org_id: selectedOrgId })
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiScripts(prev => ({ ...prev, [lead.name]: { scripts: data.scripts, loading: false } }));
      } else if (resp.status === 429) {
        const err = await resp.json().catch(() => ({}));
        setAiScripts(prev => ({ ...prev, [lead.name]: { scripts: [err.detail || "AI script limit reached. Contact team@timingoflow.com to upgrade."], loading: false, exhausted: true } }));
      } else {
        setAiScripts(prev => ({ ...prev, [lead.name]: { scripts: getScripts(lead), loading: false } }));
      }
    } catch {
      setAiScripts(prev => ({ ...prev, [lead.name]: { scripts: getScripts(lead), loading: false } }));
    }
  };

  useEffect(() => {
    if (rerunQuery) {
      setQuery((q) => ({ ...q, category: rerunQuery.category, location: rerunQuery.location, max: rerunQuery.max }));
      clearRerun();
    }
  }, [rerunQuery, clearRerun]);


  const [bulkStatus, setBulkStatus] = useState("new");
  const [saveFolderPicker, setSaveFolderPicker] = useState(null); // null | {lead} | "bulk"
  const [saveFolderId, setSaveFolderId] = useState("");
  const [newFolderName, setNewFolderName] = useState("");


  const esRef = useRef(null);
  const [showStopConfirm, setShowStopConfirm] = useState(false);





  const cacheKey = query.worldwide
    ? null
    : `cache_${query.category}_${query.location}_${query.max}_${query.noWebsite}_${query.requireEmail}_${query.requirePhone}_${query.requireWebsite}_${query.aiAssist}_${selectedOrgId || "none"}`;

  const stopScrape = async () => {
    setShowStopConfirm(false);
    try { await authFetch("/scrape/stop", { method: "POST" }); } catch {}
    if (esRef.current) { esRef.current.close(); esRef.current = null; }
    setRunning(false); setSearchDone(true);
  };

  const runSearch = async () => {


    if (!query.category || (!query.location && !query.worldwide)) return setError("Please fill in category and location (or enable Worldwide).");


    // Check search limits before running
    if (userLimits?.limits) {
      const u = userLimits.usage || {};
      const l = userLimits.limits;
      if (l.leads_per_day !== null && l.leads_per_day !== undefined && u.leads_today >= l.leads_per_day)
        return setError(`Daily lead generation limit reached (${l.leads_per_day}). Try again tomorrow.`);
      if (l.leads_per_month !== null && l.leads_per_month !== undefined && u.leads_month >= l.leads_per_month)
        return setError(`Monthly lead generation limit reached (${l.leads_per_month}).`);
      if (l.leads_lifetime !== null && l.leads_lifetime !== undefined && u.leads_lifetime >= l.leads_lifetime)
        return setError(`Lifetime lead generation limit reached (${l.leads_lifetime}).`);
    }

    setError(""); setResults([]); setProgress([]); setRunning(true); setCached(false); setSelected(new Set()); setSearchDone(false); setScrapeCount(0); setScrapeStartTime(Date.now()); setIsRanked(false);





    const prev = cacheKey ? loadLS(`results_${cacheKey}`, null) : null;


    if (prev && prev.length > 0) { setResults(prev); setCached(true); setRunning(false); setSearchDone(true); return; }





    if (esRef.current) esRef.current.close();


    try {


      const token = loadLS("ls_token", "");


      const url = `${API}/scrape/stream?category=${encodeURIComponent(query.category)}&location=${encodeURIComponent(query.worldwide ? "worldwide" : query.location)}&max_results=${query.max}&no_website_only=${query.noWebsite}&require_email=${query.requireEmail}&require_phone=${query.requirePhone}&require_website=${query.requireWebsite}&ai_assist=${query.aiAssist}&worldwide=${query.worldwide}${selectedOrgId ? `&org_id=${selectedOrgId}` : ""}`;


      const es = new window.EventSource(url + (token ? `&token=${encodeURIComponent(token)}` : ""));


      esRef.current = es;


      es.addEventListener("progress", (e) => { try { const d = JSON.parse(e.data); setProgress((p) => [...p.slice(-49), d.msg || d.message || ""]); if (typeof d.count === "number" && d.count > 0) setScrapeCount(d.count); } catch {} });


      es.addEventListener("result",   (e) => { try { const d = JSON.parse(e.data); setResults((p) => [...p, d.business || d]); setScrapeCount(c => c + 1); } catch {} });

      es.addEventListener("ranked_results", (e) => {
        try {
          const d = JSON.parse(e.data);
          if (d.leads && Array.isArray(d.leads)) {
            setResults(d.leads);
            setIsRanked(true);
          }
        } catch {}
      });

      es.addEventListener("done",     (e) => {


        es.close();


        setRunning(false); setSearchDone(true);


        setResults((current) => { if (cacheKey && current.length > 0) saveLS(`results_${cacheKey}`, current); return current; });

        if (refreshLimits) refreshLimits();

      });


      es.addEventListener("error_event", (e) => {


        try { const d = JSON.parse(e.data); setError(d.message); } catch { setError("Stream error."); }


        es.close(); setRunning(false);


      });


      es.onerror = () => { setRunning(false); es.close(); };


    } catch (err) { setError(err.message); setRunning(false); }


  };





  const handleSaveLead = (lead) => {
    const key = lead.name + "|" + lead.address;
    if (saved.some((s) => s.name + "|" + s.address === key)) return;
    setSaveFolderPicker({ lead }); setSaveFolderId(""); setNewFolderName("");
  };

  const confirmSaveLead = async () => {
    if (!saveFolderPicker) return;
    setSavingLeads(true);
    try {
      if (saveFolderPicker === "bulk") {
        const toSave = results
          .filter((r) => selected.has(r.name))
          .filter((lead) => {
            const key = lead.name + "|" + lead.address;
            return !saved.some((s) => s.name + "|" + s.address === key);
          });
        let folderId = saveFolderId ? parseInt(saveFolderId) : null;
        if (newFolderName.trim()) {
          const f = await createFolder(newFolderName.trim(), "#6366f1");
          if (f) folderId = f.id;
        }
        if (toSave.length > 0) {
          await bulkSaveLeads(toSave, folderId);
        }
        setSelected(new Set());
      } else if (saveFolderPicker?.lead) {
        let folderId = saveFolderId ? parseInt(saveFolderId) : null;
        if (newFolderName.trim()) {
          const f = await createFolder(newFolderName.trim(), "#6366f1");
          if (f) folderId = f.id;
        }
        await saveLead(saveFolderPicker.lead, folderId);
      }
      setSaveFolderPicker(null); setSaveFolderId(""); setNewFolderName("");
    } finally {
      setSavingLeads(false);
    }
  };


  const toggleSelect = (name) => setSelected((p) => { const n = new Set(p); n.has(name) ? n.delete(name) : n.add(name); return n; });


  const toggleAll    = () => setSelected(selected.size === results.length ? new Set() : new Set(results.map((r) => r.name)));


  const bulkSave = () => {
    setSaveFolderPicker("bulk"); setSaveFolderId(""); setNewFolderName("");
  };





  const sorted = [...results].sort((a, b) => {


    if (sort.col === "priority") {
      const av = computePriority(a).score, bv = computePriority(b).score;
      return sort.dir === "asc" ? av - bv : bv - av;
    }
    let av = a[sort.col] ?? "-", bv = b[sort.col] ?? "-";


    if (sort.col === "rating" || sort.col === "reviews") { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }


    if (av < bv) return sort.dir === "asc" ? -1 : 1;


    if (av > bv) return sort.dir === "asc" ? 1 : -1;


    return 0;


  });


  const doSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));





  return (


    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-5">


      <div>


        <h2 className="text-lg font-bold text-white">Search Leads</h2>


        <p className="text-xs text-slate-500 mt-0.5">Find businesses by category and location</p>

        {userLimits?.limits && (userLimits.limits.leads_per_day !== null || userLimits.limits.leads_per_month !== null || userLimits.limits.leads_lifetime !== null) && (
          <div className="flex gap-3 mt-2 flex-wrap">
            {userLimits.limits.leads_per_day !== null && userLimits.limits.leads_per_day !== undefined && (
              <span className="text-[11px] px-2 py-0.5 bg-slate-800 rounded-md text-slate-400">Today: <span className="text-indigo-400 font-semibold">{userLimits.usage?.leads_today || 0}</span> / {userLimits.limits.leads_per_day}</span>
            )}
            {userLimits.limits.leads_per_month !== null && userLimits.limits.leads_per_month !== undefined && (
              <span className="text-[11px] px-2 py-0.5 bg-slate-800 rounded-md text-slate-400">Month: <span className="text-indigo-400 font-semibold">{userLimits.usage?.leads_month || 0}</span> / {userLimits.limits.leads_per_month}</span>
            )}
            {userLimits.limits.leads_lifetime !== null && userLimits.limits.leads_lifetime !== undefined && (
              <span className="text-[11px] px-2 py-0.5 bg-slate-800 rounded-md text-slate-400">Lifetime: <span className="text-indigo-400 font-semibold">{userLimits.usage?.leads_lifetime || 0}</span> / {userLimits.limits.leads_lifetime}</span>
            )}
          </div>
        )}


      </div>





      {error && <div className="bg-red-950/60 border border-red-800 text-red-300 text-xs px-4 py-2.5 rounded-xl">{error}</div>}





      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">

        {/* Organization selector for AI issue detection */}
        {orgs.length > 1 && (
          <div>
            <label className="text-xs font-semibold text-slate-400 mb-1.5 block">Search as Organization</label>
            <div className="flex flex-wrap gap-2">
              {orgs.map(o => (
                <button key={o.id} onClick={() => setSelectedOrgId(o.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${selectedOrgId === o.id ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                  {o.business_name || "Untitled"}
                </button>
              ))}
            </div>
          </div>
        )}


        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">


          <div className="flex flex-col gap-1.5">


            <label className="text-xs font-semibold text-slate-400">Category</label>


            <input list="cats" value={query.category} onChange={(e) => setQuery((q) => ({ ...q, category: e.target.value }))}


              placeholder='e.g. "dentist" or "coffee shop"'


              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition" />


            <datalist id="cats">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
            {(() => {
              const FUNDRAISING_KW = ["invest","investor","funding","fund","vc","venture","angel","raise","capital","seed","pitch","round"];
              const GENERIC_INV = ["investor","investors","investment","investments","investing","funding"];
              const goalLower = (selectedOrg?.org_goal || "").toLowerCase();
              const catLower = (query.category || "").trim().toLowerCase();
              const isFundraising = FUNDRAISING_KW.some(kw => goalLower.includes(kw));
              const isGeneric = GENERIC_INV.includes(catLower);
              if (isFundraising && isGeneric) return (
                <p className="text-[11px] text-amber-400 mt-1 leading-tight">
                   For startup investors, try <button className="underline font-semibold" onClick={() => setQuery(q => ({...q, category: "venture capital firm"}))}>venture capital firm</button> or <button className="underline font-semibold" onClick={() => setQuery(q => ({...q, category: "angel investor"}))}>angel investor</button>. Worldwide search auto-cycles all types.
                </p>
              );
              return null;
            })()}


          </div>


          <div className="flex flex-col gap-1.5">


            <label className="text-xs font-semibold text-slate-400">Location</label>

            {query.worldwide ? (
              <div className="bg-slate-800/50 border border-indigo-700/60 rounded-lg px-3 py-2.5 flex items-center gap-2">
                <span className="text-lg"></span>
                <span className="text-sm text-indigo-300 font-medium">Worldwide</span>
              </div>
            ) : (
              <input list="locs" value={query.location} onChange={(e) => setQuery((q) => ({ ...q, location: e.target.value }))}


                placeholder="City, state or country"


                className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition" />
            )}


            <datalist id="locs">{LOCATIONS.map((c) => <option key={c} value={c} />)}</datalist>


          </div>


          <div className="flex flex-col gap-1.5">


            <label className="text-xs font-semibold text-slate-400">Max Results</label>


            <input type="number" min="1" max="500" value={query.max}

              onChange={(e) => { const v = e.target.value; setQuery((q) => ({ ...q, max: v === "" ? "" : Math.max(1, parseInt(v, 10) || 1) })); }}

              onBlur={() => { if (!query.max) setQuery((q) => ({ ...q, max: 20 })); }}

              placeholder="e.g. 25"

              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white placeholder-slate-600 rounded-lg px-3 py-2.5 text-sm outline-none transition" />


          </div>


        </div>


        <div className="flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={query.worldwide} onChange={(e) => setQuery((q) => ({ ...q, worldwide: e.target.checked, location: e.target.checked ? "" : q.location }))}
              className="accent-indigo-600 w-4 h-4 cursor-pointer rounded" />
            <span className="text-sm text-white font-semibold"> Worldwide</span>
          </label>
          <span className="text-slate-700 text-xs">|</span>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={query.noWebsite} onChange={(e) => setQuery((q) => ({ ...q, noWebsite: e.target.checked, requireWebsite: false }))}
              className="accent-indigo-600 w-4 h-4 cursor-pointer rounded" />
            <span className="text-sm text-slate-300">Only without website</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={query.requireWebsite} onChange={(e) => setQuery((q) => ({ ...q, requireWebsite: e.target.checked, noWebsite: false }))}
              className="accent-indigo-600 w-4 h-4 cursor-pointer rounded" />
            <span className="text-sm text-slate-300">Only with website</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={query.requireEmail} onChange={(e) => setQuery((q) => ({ ...q, requireEmail: e.target.checked }))}
              className="accent-emerald-600 w-4 h-4 cursor-pointer rounded" />
            <span className="text-sm text-slate-300">Must have email</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input type="checkbox" checked={query.requirePhone} onChange={(e) => setQuery((q) => ({ ...q, requirePhone: e.target.checked }))}
              className="accent-amber-600 w-4 h-4 cursor-pointer rounded" />
            <span className="text-sm text-slate-300">Must have phone</span>
          </label>
        </div>

        <div className={`rounded-xl border px-4 py-3 ${query.aiAssist ? "bg-emerald-950/30 border-emerald-700/50" : "bg-slate-900/60 border-slate-800"}`}>
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input type="checkbox" checked={query.aiAssist} onChange={(e) => setQuery((q) => ({ ...q, aiAssist: e.target.checked }))}
              className="accent-emerald-500 w-4 h-4 cursor-pointer rounded mt-0.5" />
            <div className="flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-white">AI Assist Search</span>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">Best for larger searches</span>
              </div>
              <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                AI widens your search into nearby business types first, then expands into nearby cities only if volume is still low.
              </p>
              {query.aiAssist && (
                <p className="text-[11px] text-emerald-300 mt-2 leading-relaxed">
                  Example: Dentist -&gt; dental clinic, cosmetic dentist, orthodontist. Marketing agency -&gt; PR firm, SEO agency, branding agency.
                </p>
              )}
            </div>
          </label>
        </div>


        <button onClick={runSearch} disabled={running}


          className="self-start px-6 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition shadow-lg shadow-indigo-900/30">


          {running ? "Searching..." : "Search"}


        </button>


      </div>


      {!running && results.length === 0 && !searchDone && (
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-slate-500">Try an example search:</p>
          <div className="flex flex-wrap gap-2">
            {[
              { category: "Dentist", location: "New York", max: 20 },
              { category: "Restaurant", location: "London", max: 15 },
              { category: "Plumber", location: "Los Angeles", max: 25 },
              { category: "Real Estate Agent", location: "Dubai", max: 20 },
              { category: "Coffee Shop", location: "Paris", max: 10 },
              { category: "Gym", location: "Sydney", max: 15 },
            ].map((ex, i) => (
              <button key={i} onClick={() => setQuery((q) => ({ ...q, category: ex.category, location: ex.location, max: ex.max }))}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 hover:border-indigo-500 text-slate-300 hover:text-white rounded-lg text-xs font-medium transition cursor-pointer">
                {ex.category} in {ex.location}
              </button>
            ))}
          </div>
        </div>
      )}





      {running && (


        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-3">

          {/* Progress bar + ETA */}
          {(() => {
            const pct = query.max > 0 ? Math.min(100, Math.round((scrapeCount / query.max) * 100)) : 0;
            let eta = "";
            if (scrapeCount > 0 && scrapeStartTime) {
              const elapsed = (Date.now() - scrapeStartTime) / 1000;
              const perLead = elapsed / scrapeCount;
              const remaining = Math.max(0, Math.round(perLead * (query.max - scrapeCount)));
              if (remaining > 60) eta = `~${Math.round(remaining / 60)}m ${remaining % 60}s remaining`;
              else eta = `~${remaining}s remaining`;
            }
            return (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <p className="text-xs font-bold text-indigo-400">{query.aiAssist ? "AI-Assisted Search" : "Generating Leads"}</p>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-semibold text-slate-300">{scrapeCount > 0 ? `${scrapeCount} found so far` : (query.aiAssist ? "Planning search" : "Starting search")}</span>
                    {eta && <span className="text-[11px] text-slate-500">{eta}</span>}
                    <button onClick={() => setShowStopConfirm(true)} className="px-2.5 py-1 text-xs font-semibold bg-red-600/20 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-600/40 transition-colors">Stop</button>
                  </div>
                </div>
                <div className="w-full bg-slate-800 rounded-full h-2.5 overflow-hidden">
                  <div className="bg-gradient-to-r from-indigo-600 to-indigo-400 h-full rounded-full transition-all duration-500 ease-out" style={{ width: `${pct}%` }} />
                </div>
                <p className="text-[10px] text-slate-600 mt-1">{query.aiAssist ? "AI is widening categories and nearby areas while it keeps the leads relevant." : `${pct}% of your target collected so far`}</p>
              </div>
            );
          })()}

          <div className="max-h-20 overflow-auto flex flex-col gap-0.5">


            {progress.length === 0
              ? <p className="text-xs text-slate-500 animate-pulse">{query.aiAssist ? "Preparing AI-assisted search plan..." : "Starting search..."}</p>
              : progress.slice(-4).map((msg, i) => <p key={i} className="text-xs text-slate-400">{msg}</p>)
            }


          </div>


        </div>


      )}

      {/* Stop Confirmation Modal */}
      {showStopConfirm && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold text-white mb-2">Stop Generating?</h3>
            <p className="text-sm text-slate-400 mb-5">Are you sure you want to stop? You'll receive the <span className="text-white font-semibold">{scrapeCount} lead{scrapeCount !== 1 ? "s" : ""}</span> found so far.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowStopConfirm(false)} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-slate-800 text-slate-300 rounded-lg hover:bg-slate-700 transition-colors">Keep Going</button>
              <button onClick={stopScrape} className="flex-1 px-4 py-2.5 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">Stop & Get Results</button>
            </div>
          </div>
        </div>
      )}



      {!running && searchDone && results.length === 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 flex flex-col items-center gap-3">
          <div className="text-4xl"></div>
          <p className="text-sm font-semibold text-slate-300">No results found</p>
          <p className="text-xs text-slate-500 text-center max-w-md">Try a different category or location, increase the max results, or uncheck the "without website" filter.</p>
          <button onClick={() => setSearchDone(false)} className="mt-1 px-4 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 rounded-lg text-xs font-medium transition">Back to suggestions</button>
        </div>
      )}

      {/* Folder picker modal */}
      {saveFolderPicker && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setSaveFolderPicker(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-sm flex flex-col gap-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white">Save to Folder</h3>
            <p className="text-xs text-slate-500">
              {saveFolderPicker === "bulk" ? `Save ${selected.size} selected leads` : `Save "${saveFolderPicker.lead?.name}"`}
            </p>
            <select value={saveFolderId} onChange={e => { setSaveFolderId(e.target.value); setNewFolderName(""); }}
              className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-xs outline-none">
              <option value="">No folder (Unfiled)</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <div className="flex items-center gap-2">
              <span className="text-[10px] text-slate-500">or create:</span>
              <input value={newFolderName} onChange={e => { setNewFolderName(e.target.value); setSaveFolderId(""); }} placeholder="New folder name..."
                onKeyDown={e => e.key === "Enter" && confirmSaveLead()}
                className="flex-1 bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-2 py-1.5 text-xs outline-none" />
            </div>
            <div className="flex gap-2 justify-end mt-1">
              <button onClick={() => setSaveFolderPicker(null)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 rounded-lg text-xs transition">Cancel</button>
              <button onClick={confirmSaveLead} disabled={savingLeads}
                className="px-4 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-60 text-white rounded-lg text-xs font-bold transition flex items-center gap-2">
                {savingLeads && <div className="w-3.5 h-3.5 border-2 border-white/80 border-t-transparent rounded-full animate-spin" />}
                {savingLeads ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}


      {results.length > 0 && (


        <>

          <div className="flex items-center justify-between flex-wrap gap-3">


            <p className="text-xs text-slate-500">


              {results.length} results {cached && <span className="text-indigo-400 ml-1">(cached)</span>}
              {isRanked && <span className="ml-2 text-emerald-400 font-semibold"> Ranked by goal match</span>}


              {selected.size > 0 && <span className="ml-2 text-emerald-400">{selected.size} selected</span>}


            </p>


            <div className="flex items-center gap-2 flex-wrap">


              {selected.size > 0 && (


                <>


                  <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}


                    className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none">


                    {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}


                  </select>


                  <button onClick={bulkSave} className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 text-white rounded-lg text-xs font-bold transition">


                    Save {selected.size} Selected


                  </button>


                </>


              )}


              {(!userLimits?.limits || userLimits.limits.can_export !== false) && (
              <button onClick={() => {


                const csv = Papa.unparse(results.map((r) => ({ Name: r.name, Category: r.category, Address: r.address, Phone: r.phone, Email: r.email, Rating: r.rating, Reviews: r.reviews, Website: r.website })));


                const b = new Blob([csv], { type: "text/csv" }); const u = URL.createObjectURL(b);


                const a = document.createElement("a"); a.href = u; a.download = `leads-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(u);


              }} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-semibold transition">


                Export CSV


              </button>
              )}


            </div>


          </div>

          {/* If we finished and found fewer leads than requested, explain it */}
          {!running && searchDone && results.length < (query.max || 0) && (
            <p className="mt-1 text-[11px] text-amber-300 max-w-xl">
              Found {results.length} of {query.max} requested leads in this area.
              {" "}
              These are the strongest matches we could find based on your filters and goal.
            </p>
          )}


          <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">


            <div className="overflow-x-auto">


              <table className="w-full text-sm min-w-[900px]">


                <thead>


                  <tr className="bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-widest border-b border-slate-800">


                    <th className="w-9 px-4 py-3">


                      <input type="checkbox" checked={selected.size === results.length && results.length > 0} onChange={toggleAll}


                        className="accent-indigo-600 w-3.5 h-3.5 cursor-pointer" />


                    </th>


                    <SortTh col="priority" sort={sort} doSort={doSort}>Priority</SortTh>


                    {[["name","Name"],["phone","Phone"],["rating","Rating"]].map(([col,lbl]) => (


                      <SortTh key={col} col={col} sort={sort} doSort={doSort}>{lbl}</SortTh>


                    ))}

                    {isRanked && <th className="px-4 py-3 text-left">Match</th>}

                    <th className="px-4 py-3 text-left">Signals</th>


                    <th className="px-4 py-3 text-left">Call Time</th>


                    <th className="px-4 py-3 text-left">Email</th>


                    <th className="px-4 py-3 text-left">Website</th>


                    <th className="px-4 py-3 text-left">Actions</th>


                  </tr>


                </thead>


                <tbody className="divide-y divide-slate-800/60">


                  {sorted.map((r) => {
                    const pri = computePriority(r);
                    const { signals: issues, type: signalType } = getDisplaySignals(r);
                    const tz = getLeadTimezone(r);
                    const isExpanded = expandedLead === r.name;
                    return (
                    <React.Fragment key={r.name}>
                    <tr className={`hover:bg-slate-800/25 transition cursor-pointer ${selected.has(r.name) ? "bg-indigo-950/20" : ""}`}
                      onClick={() => { const next = isExpanded ? null : r.name; setExpandedLead(next); if (next) fetchAiScripts(r); }}>


                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>


                        <input type="checkbox" checked={selected.has(r.name)} onChange={() => toggleSelect(r.name)}


                          className="accent-indigo-600 w-3.5 h-3.5 cursor-pointer" />


                      </td>


                      <td className="px-4 py-3">
                        <span className={`text-xs font-bold ${pri.color}`}>{pri.label}</span>
                        <span className="text-[10px] text-slate-600 ml-1">({pri.score})</span>
                      </td>


                      <td className="px-4 py-3 font-semibold text-white max-w-[180px] truncate">{r.name}</td>


                      <td className="px-4 py-3 text-slate-300 text-xs font-mono">{r.phone || <span className="text-slate-600">-</span>}</td>


                      <td className="px-4 py-3"><StarRating rating={r.rating} /></td>

                      {isRanked && (
                        <td className="px-4 py-3">
                          {r.quick_score != null ? (
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              r.quick_score >= 70 ? "bg-emerald-900/50 text-emerald-400 border border-emerald-800" :
                              r.quick_score >= 40 ? "bg-amber-900/40 text-amber-400 border border-amber-800" :
                              "bg-slate-800 text-slate-500 border border-slate-700"
                            }`}>{r.quick_score}%</span>
                          ) : <span className="text-[10px] text-slate-600">–</span>}
                        </td>
                      )}

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1.5">
                          {issues.length > 0 ? issues.slice(0, isExpanded ? issues.length : 2).map((iss, ii) => (
                            <span key={ii} className={`text-xs px-2 py-1 rounded-lg font-semibold ${
                              signalType === "ai"
                                ? "bg-teal-950/60 border border-teal-700 text-teal-300"
                                : "bg-red-950/60 border border-red-800 text-red-300"
                            }`}>{iss}</span>
                          )) : <span className="text-xs text-slate-600">–</span>}
                          {!isExpanded && issues.length > 2 && <span className={`text-xs font-semibold ${signalType === "ai" ? "text-teal-400" : "text-red-400"}`}>+{issues.length - 2}</span>}
                        </div>
                      </td>


                      <td className="px-4 py-3 text-xs">
                        {tz ? (
                          <div className="flex flex-col">
                            <span className={`text-[10px] font-semibold ${tz.callStatus.color}`}>{tz.callStatus.label}</span>
                            <span className="text-[10px] text-slate-500">{tz.localTime}</span>
                          </div>
                        ) : <span className="text-[10px] text-slate-600">-</span>}
                      </td>


                      <td className="px-4 py-3 text-indigo-400 text-xs font-mono max-w-[160px]">


                        {r.email ? <a href={`mailto:${r.email}`} onClick={(e) => e.stopPropagation()} className="hover:underline break-all">{r.email}</a> : <span className="text-slate-600">N/A</span>}


                      </td>

                      <td className="px-4 py-3 text-xs max-w-[120px]">
                        {r.website ? <a href={r.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-cyan-400 hover:underline truncate block">{r.website.replace(/https?:\/\//, "").substring(0, 22)}</a> : <span className="text-slate-600">N/A</span>}
                      </td>


                      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>


                        {saved.some((s) => s.name + "|" + s.address === r.name + "|" + r.address)
                          ? <span className="text-xs px-3 py-1 bg-emerald-900/40 border border-emerald-800 text-emerald-400 rounded-lg font-semibold">Saved</span>
                          : <button onClick={() => handleSaveLead(r)} className="text-xs px-3 py-1 bg-indigo-900/60 hover:bg-indigo-700 border border-indigo-800 text-indigo-300 rounded-lg transition font-semibold">
                              Save
                            </button>
                        }


                      </td>


                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-800/40">
                        <td colSpan={isRanked ? 11 : 10} className="px-6 py-4">
                          <div className="flex flex-col gap-3">
                            <div className="flex gap-6 flex-wrap text-xs">
                              <div><span className="text-slate-500">Category:</span> <span className="text-slate-300">{r.category || "N/A"}</span></div>
                              <div><span className="text-slate-500">Address:</span> <span className="text-slate-300">{r.address || "N/A"}</span></div>
                              <div><span className="text-slate-500">Reviews:</span> <span className="text-slate-300">{r.reviews || "N/A"}</span></div>
                            </div>
                            {issues.length > 0 && (
                            <div>
                              <p className="text-xs font-semibold text-slate-400 mb-1.5">{signalType === "ai" ? " Lead Insights" : " Issues"}</p>
                              <div className="flex flex-wrap gap-2">
                                {issues.map((iss, ii) => (
                                  <span key={ii} className={`text-xs px-3 py-1.5 rounded-lg font-bold ${
                                    signalType === "ai"
                                      ? "bg-teal-950/50 border border-teal-700 text-teal-300"
                                      : "bg-red-950/50 border border-red-800 text-red-300"
                                  }`}>{iss}</span>
                                ))}
                              </div>
                            </div>
                            )}
                            <div>
                              <p className="text-xs font-semibold text-slate-400 mb-1.5"> AI Call Scripts</p>
                              {aiScripts[r.name]?.loading ? (
                                <div className="flex items-center gap-2 py-2">
                                  <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                  <span className="text-xs text-slate-500">Generating personalized scripts...</span>
                                </div>
                              ) : (
                              <div className="flex flex-col gap-1.5">
                                {(aiScripts[r.name]?.scripts || getScripts(r)).map((script, si) => (
                                  <div key={si} className="flex items-start gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
                                    <p className="text-xs text-slate-300 flex-1 italic">"{script}"</p>
                                    <button onClick={() => navigator.clipboard.writeText(script)}
                                      className="text-[10px] px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md shrink-0 transition">Copy</button>
                                  </div>
                                ))}
                              </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}


                </tbody>


              </table>


            </div>


          </div>


        </>


      )}


    </div>


  );


}





// --- Saved Leads Page ---------------------------------------------------------


function SavedPage({ saved, updateLead, deleteLead, bulkUpdateLeads, bulkDeleteLeads, folders, createFolder, updateFolder, deleteFolder, bulkMoveLeads, userLimits, leadsLoaded }) {

  const { authFetch } = useAuth();
  const [sort, setSort]         = useState({ col: "priority", dir: "desc" });
  const [filter, setFilter]     = useState("All");
  const [selected, setSelected] = useState(new Set());
  const [bulkStatus, setBulkStatus] = useState("new");
  const [expandedLead, setExpandedLead] = useState(null);
  const [aiScripts, setAiScripts] = useState({});

  const fetchAiScripts = async (lead) => {
    const key = lead.id || lead.name;
    if (aiScripts[key]?.scripts?.length) return;
    setAiScripts(prev => ({ ...prev, [key]: { scripts: [], loading: true } }));
    try {
      const resp = await authFetch("/ai/call-script", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ business_name: lead.name, business_category: lead.category, website: lead.website, detected_issues: lead.detected_issues || [] })
      });
      if (resp.ok) {
        const data = await resp.json();
        setAiScripts(prev => ({ ...prev, [key]: { scripts: data.scripts, loading: false } }));
      } else if (resp.status === 429) {
        const err = await resp.json().catch(() => ({}));
        setAiScripts(prev => ({ ...prev, [key]: { scripts: [err.detail || "AI script limit reached. Contact team@timingoflow.com to upgrade."], loading: false, exhausted: true } }));
      } else {
        setAiScripts(prev => ({ ...prev, [key]: { scripts: getScripts(lead), loading: false } }));
      }
    } catch {
      setAiScripts(prev => ({ ...prev, [key]: { scripts: getScripts(lead), loading: false } }));
    }
  };
  const [localNotes, setLocalNotes] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [folderSearch, setFolderSearch] = useState("");
  const [activeFolder, setActiveFolder] = useState("all");
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderColor, setNewFolderColor] = useState("#6366f1");
  const [editingFolder, setEditingFolder] = useState(null);
  const [editFolderName, setEditFolderName] = useState("");
  const [advFilter, setAdvFilter] = useState("none");
  const [moveToFolder, setMoveToFolder] = useState(null);
  const [deleteFolderModal, setDeleteFolderModal] = useState(null);
  const [deleteFolderAction, setDeleteFolderAction] = useState("unfiled");
  const [deleteFolderTarget, setDeleteFolderTarget] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeProgress, setAnalyzeProgress] = useState(null);

  const handleUpdateStatus = (lead, status) => updateLead(lead.id, { status });
  const handleDeleteLead = (lead) => deleteLead(lead.id);
  const handleNoteBlur = (lead) => { const val = localNotes[lead.id]; if (val !== undefined && val !== (lead.notes || "")) updateLead(lead.id, { notes: val }); };
  const toggleSelect = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const applyBulk    = () => { bulkUpdateLeads([...selected], bulkStatus); setSelected(new Set()); };
  const deleteSelected = () => { bulkDeleteLeads([...selected]); setSelected(new Set()); };

  const handleCreateFolder = () => {
    if (!newFolderName.trim()) return;
    createFolder(newFolderName.trim(), newFolderColor);
    setNewFolderName(""); setNewFolderColor("#6366f1"); setShowNewFolder(false);
  };

  const handleRenameFolder = (id) => {
    if (!editFolderName.trim()) return;
    updateFolder(id, { name: editFolderName.trim() });
    setEditingFolder(null); setEditFolderName("");
  };

  const handleBulkMove = (folderId) => {
    bulkMoveLeads([...selected], folderId === "unfiled" ? null : folderId);
    setSelected(new Set()); setMoveToFolder(null);
  };

  const handleAnalyzeSelected = async () => {
    if (!selected.size || analyzing) return;
    const ids = [...selected];
    setAnalyzing(true);
    setAnalyzeProgress(`Analyzing ${ids.length} lead${ids.length > 1 ? "s" : ""}–`);
    try {
      const resp = await authFetch("/leads/analyze-compatibility", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ lead_ids: ids })
      });
      if (resp.ok) {
        const data = await resp.json();
        setAnalyzeProgress(`Done – ${data.results?.length || 0} leads scored.`);
        // Refresh leads from parent — use a custom event trigger
        window.dispatchEvent(new CustomEvent("refreshLeads"));
      } else {
        const err = await resp.json().catch(() => ({}));
        setAnalyzeProgress(err.detail || "Analysis failed.");
      }
    } catch (e) { setAnalyzeProgress("Error: " + e.message); }
    finally { setAnalyzing(false); setTimeout(() => setAnalyzeProgress(null), 5000); }
  };

  const doSort = (col) => setSort((s) => ({ col, dir: s.col === col && s.dir === "asc" ? "desc" : "asc" }));

  // Filtering pipeline: folder -> status -> advanced -> search
  let filtered = saved;
  if (activeFolder === "unfiled") filtered = filtered.filter(l => !l.folder_id);
  else if (activeFolder !== "all") filtered = filtered.filter(l => l.folder_id === activeFolder);

  if (filter !== "All") filtered = filtered.filter(l => l.status === filter);

  if (advFilter === "no_website") filtered = filtered.filter(l => !l.website);
  else if (advFilter === "has_issues") filtered = filtered.filter(l => (l.detected_issues || []).length > 0);
  else if (advFilter === "no_phone") filtered = filtered.filter(l => !l.phone);
  else if (advFilter === "has_email") filtered = filtered.filter(l => l.email);
  else if (advFilter === "no_email") filtered = filtered.filter(l => !l.email);
  else if (advFilter === "high_compat") filtered = filtered.filter(l => l.ai_compat_score != null && l.ai_compat_score >= 70);
  else if (advFilter === "low_compat") filtered = filtered.filter(l => l.ai_compat_score != null && l.ai_compat_score < 40);

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(l =>
      (l.name || "").toLowerCase().includes(q) ||
      (l.phone || "").toLowerCase().includes(q) ||
      (l.email || "").toLowerCase().includes(q) ||
      (l.address || "").toLowerCase().includes(q) ||
      (l.category || "").toLowerCase().includes(q) ||
      (l.notes || "").toLowerCase().includes(q) ||
      (l.website || "").toLowerCase().includes(q)
    );
  }

  const toggleAll = () => setSelected(selected.size === filtered.length ? new Set() : new Set(filtered.map(l => l.id)));

  const sorted = [...filtered].sort((a, b) => {
    if (sort.col === "priority") {
      const av = computePriority(a).score, bv = computePriority(b).score;
      return sort.dir === "asc" ? av - bv : bv - av;
    }
    let av = a[sort.col] ?? "-", bv = b[sort.col] ?? "-";
    if (sort.col === "rating" || sort.col === "reviews" || sort.col === "savedAt") { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
    if (av < bv) return sort.dir === "asc" ? -1 : 1;
    if (av > bv) return sort.dir === "asc" ? 1 : -1;
    return 0;
  });

  const FOLDER_COLORS = ["#6366f1","#ec4899","#10b981","#f59e0b","#ef4444","#06b6d4","#8b5cf6","#f97316"];

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold text-white">Saved Leads</h2>
          <p className="text-xs text-slate-500 mt-0.5">{saved.length} leads in your database</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNewFolder(true)} className="px-3 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition">
            + New Folder
          </button>
          {saved.length > 0 && (!userLimits?.limits || userLimits.limits.can_export !== false) && (
            <button onClick={() => {
              const exp = activeFolder === "all" ? saved : filtered;
              const csv = Papa.unparse(exp.map((r) => ({ Name: r.name, Category: r.category, Address: r.address, Phone: r.phone, Email: r.email, Rating: r.rating, Reviews: r.reviews, Status: r.status })));
              const b = new Blob([csv], { type: "text/csv" }); const u = URL.createObjectURL(b);
              const a = document.createElement("a"); a.href = u; a.download = `saved-leads-${Date.now()}.csv`; a.click(); URL.revokeObjectURL(u);
            }} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-semibold transition">
              Export CSV
            </button>
          )}
        </div>
      </div>

      {/* New folder form */}
      {showNewFolder && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl px-4 py-3 flex items-center gap-3 flex-wrap">
          <input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Folder name..." maxLength={50}
            onKeyDown={e => e.key === "Enter" && handleCreateFolder()}
            className="bg-slate-900 border border-slate-600 text-slate-200 rounded-lg px-3 py-1.5 text-xs outline-none focus:border-indigo-500 flex-1 min-w-[140px]" />
          <div className="flex gap-1.5">
            {FOLDER_COLORS.map(c => (
              <button key={c} onClick={() => setNewFolderColor(c)}
                className={`w-5 h-5 rounded-full transition ${newFolderColor === c ? "ring-2 ring-white ring-offset-1 ring-offset-slate-800" : "opacity-60 hover:opacity-100"}`}
                style={{ backgroundColor: c }} />
            ))}
          </div>
          <button onClick={handleCreateFolder} className="px-3 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition">Create</button>
          <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); }} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 rounded-lg text-xs transition">Cancel</button>
        </div>
      )}

      {/* Folder tabs */}
      <div className="flex gap-2 flex-wrap items-center">
        <button onClick={() => { setActiveFolder("all"); setSelected(new Set()); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${activeFolder === "all" ? "bg-indigo-900/60 border-indigo-700 text-indigo-300" : "bg-transparent border-slate-700 text-slate-500 hover:text-slate-300"}`}>
          All ({saved.length})
        </button>
        <button onClick={() => { setActiveFolder("unfiled"); setSelected(new Set()); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${activeFolder === "unfiled" ? "bg-slate-700 border-slate-600 text-slate-300" : "bg-transparent border-slate-700 text-slate-500 hover:text-slate-300"}`}>
          Unfiled ({saved.filter(l => !l.folder_id).length})
        </button>
        {/* Folder search — filters tabs and provides a dropdown for quick-jump */}
        {folders.length > 3 && (
          <div className="relative">
            <input
              value={folderSearch}
              onChange={e => setFolderSearch(e.target.value)}
              placeholder="Find folder..."
              className="bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-300 rounded-lg px-2 py-1 text-xs outline-none w-32 placeholder:text-slate-600"
            />
            {folderSearch && (
              <div className="absolute top-full left-0 mt-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-30 min-w-[180px] max-h-52 overflow-y-auto">
                {folders.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())).length === 0 && (
                  <div className="px-3 py-2 text-xs text-slate-500">No folders match</div>
                )}
                {folders.filter(f => f.name.toLowerCase().includes(folderSearch.toLowerCase())).map(f => (
                  <button key={f.id} onClick={() => { setActiveFolder(f.id); setSelected(new Set()); setFolderSearch(""); }}
                    className={`w-full text-left px-3 py-2 text-xs flex items-center gap-2 hover:bg-slate-800 transition ${activeFolder === f.id ? "text-white font-semibold" : "text-slate-300"}`}>
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.color }}></span>
                    {f.name} ({saved.filter(l => l.folder_id === f.id).length})
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {folders.filter(f => !folderSearch || f.name.toLowerCase().includes(folderSearch.toLowerCase())).map(f => (
          <div key={f.id} className="flex items-center group relative">
            {editingFolder === f.id ? (
              <div className="flex items-center gap-1">
                <input value={editFolderName} onChange={e => setEditFolderName(e.target.value)} autoFocus
                  onKeyDown={e => { if (e.key === "Enter") handleRenameFolder(f.id); if (e.key === "Escape") setEditingFolder(null); }}
                  className="bg-slate-900 border border-indigo-500 text-slate-200 rounded-lg px-2 py-1 text-xs outline-none min-w-[120px]" />
                <button onClick={() => handleRenameFolder(f.id)} className="text-emerald-400 text-xs px-1">OK</button>
                <button onClick={() => setEditingFolder(null)} className="text-slate-500 text-xs px-1">X</button>
              </div>
            ) : (
              <button onClick={() => { setActiveFolder(f.id); setSelected(new Set()); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition flex items-center gap-1.5 whitespace-nowrap ${activeFolder === f.id ? "border-opacity-80 text-white" : "bg-transparent border-slate-700 text-slate-400 hover:text-slate-300"}`}
                style={activeFolder === f.id ? { backgroundColor: f.color + "22", borderColor: f.color } : {}}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.color }}></span>
                {f.name} ({saved.filter(l => l.folder_id === f.id).length})
              </button>
            )}
            {activeFolder === f.id && !editingFolder && (
              <div className="flex ml-1 gap-0.5">
                <button onClick={() => { setEditingFolder(f.id); setEditFolderName(f.name); }} className="text-slate-600 hover:text-slate-300 text-[10px] px-1 transition" title="Rename">&#9998;</button>
                <button onClick={() => { setDeleteFolderModal({ id: f.id, name: f.name }); setDeleteFolderAction("unfiled"); setDeleteFolderTarget(""); }} className="text-slate-600 hover:text-red-400 text-[10px] px-1 transition" title="Delete folder">&times;</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Delete folder modal */}
      {deleteFolderModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setDeleteFolderModal(null)}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-sm flex flex-col gap-3" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-white">Delete Folder</h3>
            <p className="text-xs text-slate-400">What should happen to the leads inside <strong className="text-white">"{deleteFolderModal.name}"</strong>?</p>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delAction" checked={deleteFolderAction === "unfiled"} onChange={() => setDeleteFolderAction("unfiled")} className="accent-indigo-600" />
                <span className="text-xs text-slate-300">Move leads to Unfiled</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delAction" checked={deleteFolderAction === "move"} onChange={() => setDeleteFolderAction("move")} className="accent-indigo-600" />
                <span className="text-xs text-slate-300">Move leads to another folder</span>
              </label>
              {deleteFolderAction === "move" && (
                <select value={deleteFolderTarget} onChange={e => setDeleteFolderTarget(e.target.value)}
                  className="bg-slate-800 border border-slate-700 text-slate-300 rounded-lg px-2 py-1.5 text-xs outline-none ml-5">
                  <option value="">Select folder...</option>
                  {folders.filter(f => f.id !== deleteFolderModal.id).map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              )}
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="radio" name="delAction" checked={deleteFolderAction === "delete_leads"} onChange={() => setDeleteFolderAction("delete_leads")} className="accent-red-600" />
                <span className="text-xs text-red-400">Delete all leads in this folder</span>
              </label>
            </div>
            <div className="flex gap-2 justify-end mt-1">
              <button onClick={() => setDeleteFolderModal(null)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 rounded-lg text-xs transition">Cancel</button>
              <button onClick={() => {
                const action = deleteFolderAction;
                const target = deleteFolderAction === "move" && deleteFolderTarget ? parseInt(deleteFolderTarget) : null;
                deleteFolder(deleteFolderModal.id, action, target);
                if (activeFolder === deleteFolderModal.id) setActiveFolder("all");
                setDeleteFolderModal(null);
              }}
                disabled={deleteFolderAction === "move" && !deleteFolderTarget}
                className="px-4 py-1.5 bg-red-700 hover:bg-red-600 disabled:opacity-40 text-white rounded-lg text-xs font-bold transition">Delete Folder</button>
            </div>
          </div>
        </div>
      )}

      {/* Search + Filters */}
      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search leads by name, phone, email, address..."
            className="w-full bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none pl-8" />
          <svg className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
        </div>
        <select value={advFilter} onChange={e => setAdvFilter(e.target.value)}
          className="bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-2 py-2 text-xs outline-none">
          <option value="none">All filters</option>
          <option value="no_website">No Website</option>
          <option value="has_issues">Has Issues</option>
          <option value="no_phone">No Phone</option>
          <option value="has_email">Has Email</option>
          <option value="no_email">No Email</option>
          <option value="high_compat">Strong Goal Match (70+)</option>
          <option value="low_compat">Weak Goal Match (&lt;40)</option>
        </select>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {[{ value: "All", label: "All" }, ...LEAD_STATUSES].map((s) => (
          <button key={s.value} onClick={() => setFilter(s.value)}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${filter === s.value ? "bg-indigo-900/60 border-indigo-700 text-indigo-300" : "bg-transparent border-slate-700 text-slate-500 hover:text-slate-300"}`}>
            {s.label} ({s.value === "All" ? (activeFolder === "all" ? saved.length : filtered.length) : filtered.filter((l) => l.status === s.value).length})
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 flex-wrap">
          <span className="text-xs text-slate-400">{selected.size} selected</span>
          <select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}
            className="bg-slate-700 border border-slate-600 text-slate-300 rounded-lg px-2 py-1 text-xs outline-none">
            {LEAD_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <button onClick={applyBulk} className="px-3 py-1 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg text-xs font-semibold transition">Apply Status</button>
          <div className="flex items-center gap-1">
            <select value={moveToFolder || ""} onChange={e => setMoveToFolder(e.target.value)}
              className="bg-slate-700 border border-slate-600 text-slate-300 rounded-lg px-2 py-1 text-xs outline-none">
              <option value="">Move to folder...</option>
              <option value="unfiled">Unfiled</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            {moveToFolder && <button onClick={() => handleBulkMove(moveToFolder === "unfiled" ? "unfiled" : parseInt(moveToFolder))} className="px-3 py-1 bg-cyan-700 hover:bg-cyan-600 text-white rounded-lg text-xs font-semibold transition">Move</button>}
          </div>
          <button onClick={deleteSelected} className="px-3 py-1 bg-red-900/60 hover:bg-red-800 text-red-300 rounded-lg text-xs font-semibold transition">Delete</button>
          <button onClick={handleAnalyzeSelected} disabled={analyzing}
            className={`px-3 py-1 rounded-lg text-xs font-semibold transition flex items-center gap-1.5 ${analyzing ? "bg-indigo-900 text-indigo-400" : "bg-indigo-700 hover:bg-indigo-600 text-white"}`}>
            {analyzing ? (<><div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" /> Analyzing–</>) : " Re-analyze Match"}
          </button>
          {analyzeProgress && <span className="text-[11px] text-slate-400">{analyzeProgress}</span>}
        </div>
      )}

      {!leadsLoaded ? (
        <div className="flex-1 flex items-center justify-center"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /><p className="text-xs text-slate-500">Loading leads...</p></div></div>
      ) : saved.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-600 text-sm">No saved leads yet. Run a search and save some!</div>
      ) : sorted.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-slate-600 text-sm">No leads match your current filters.</div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[950px]">
              <thead>
                <tr className="bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-widest border-b border-slate-800">
                  <th className="w-9 px-4 py-3"><input type="checkbox" checked={selected.size === sorted.length && sorted.length > 0} onChange={toggleAll} className="accent-indigo-600 w-3.5 h-3.5 cursor-pointer" /></th>
                  <SortTh col="priority" sort={sort} doSort={doSort}>Priority</SortTh>
                  {[["name","Name"],["phone","Phone"],["rating","Rating"]].map(([c,l]) => (
                    <SortTh key={c} col={c} sort={sort} doSort={doSort}>{l}</SortTh>
                  ))}
                  <SortTh col="ai_compat_score" sort={sort} doSort={doSort}>Goal Match</SortTh>
                  <th className="px-4 py-3 text-left">Signals</th>
                  <th className="px-4 py-3 text-left">Call Time</th>
                  <th className="px-4 py-3 text-left">Notes</th>
                  <th className="px-4 py-3 text-left">Folder</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {sorted.map((r) => {
                  const pri = computePriority(r);
                  const { signals: issues, type: signalType } = getDisplaySignals(r);
                  const tz = getLeadTimezone(r);
                  const isExpanded = expandedLead === (r.name + "|" + r.address);
                  const leadFolder = folders.find(f => f.id === r.folder_id);
                  return (
                  <React.Fragment key={r.id}>
                  <tr className={`hover:bg-slate-800/25 transition cursor-pointer ${selected.has(r.id) ? "bg-indigo-950/20" : ""}`}
                    onClick={() => { const next = isExpanded ? null : r.name + "|" + r.address; setExpandedLead(next); if (next) fetchAiScripts(r); }}>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selected.has(r.id)} onChange={() => toggleSelect(r.id)} className="accent-indigo-600 w-3.5 h-3.5 cursor-pointer" /></td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-bold ${pri.color}`}>{pri.label}</span>
                      <span className="text-[10px] text-slate-600 ml-1">({pri.score})</span>
                    </td>
                    <td className="px-4 py-3 font-semibold text-white max-w-[160px] truncate">{r.name}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs font-mono">{r.phone || "-"}</td>
                    <td className="px-4 py-3"><StarRating rating={r.rating} /></td>
                    <td className="px-4 py-3">
                      {r.ai_compat_score != null ? (
                        <div className="flex items-center gap-1" title={r.ai_compat_reason || ""}>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            r.ai_compat_score >= 70 ? "bg-emerald-900/50 text-emerald-400 border border-emerald-800" :
                            r.ai_compat_score >= 40 ? "bg-amber-900/40 text-amber-400 border border-amber-800" :
                            "bg-slate-800 text-slate-500 border border-slate-700"
                          }`}>{r.ai_compat_score}%</span>
                        </div>
                      ) : <span className="text-[10px] text-slate-700">-</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {issues.length > 0 ? issues.map((iss, ii) => (
                          <span key={ii} className={`text-[10px] px-1.5 py-0.5 rounded-md ${
                            signalType === "ai"
                              ? "bg-teal-950/60 border border-teal-800 text-teal-300"
                              : "bg-red-950/60 border border-red-900 text-red-400"
                          }`}>{iss}</span>
                        )) : <span className="text-[10px] text-slate-600">–</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {tz ? (
                        <div className="flex flex-col">
                          <span className={`text-[10px] font-semibold ${tz.callStatus.color}`}>{tz.callStatus.label}</span>
                          <span className="text-[10px] text-slate-500">{tz.localTime}</span>
                        </div>
                      ) : <span className="text-[10px] text-slate-600">-</span>}
                    </td>
                    <td className="px-4 py-3 max-w-[200px]" onClick={(e) => e.stopPropagation()}>
                      <textarea value={localNotes[r.id] !== undefined ? localNotes[r.id] : (r.notes || "")} onChange={(e) => setLocalNotes(p => ({...p, [r.id]: e.target.value}))}
                        onBlur={() => handleNoteBlur(r)}
                        placeholder="Add note..."
                        rows="3"
                        className="w-full bg-transparent border border-slate-700 focus:border-indigo-500 text-slate-300 placeholder-slate-600 rounded-md px-2 py-1.5 text-xs outline-none resize-y min-h-[60px]" />
                    </td>
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      {leadFolder ? (
                        <span className="text-[10px] px-1.5 py-0.5 rounded-md border font-semibold" style={{ color: leadFolder.color, borderColor: leadFolder.color + "60", backgroundColor: leadFolder.color + "15" }}>{leadFolder.name}</span>
                      ) : <span className="text-[10px] text-slate-600">-</span>}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <LeadStatusSelect value={r.status} onChange={(v) => handleUpdateStatus(r, v)} />
                      {r.status_changed_at && (
                        <p className="text-[9px] text-slate-600 mt-0.5">{new Date(r.status_changed_at).toLocaleDateString()} {new Date(r.status_changed_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</p>
                      )}
                    </td>
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <button onClick={() => handleDeleteLead(r)} className="text-slate-600 hover:text-red-400 transition text-sm">Del</button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr className="bg-slate-800/40">
                      <td colSpan="11" className="px-6 py-4">
                        <div className="flex flex-col gap-3">
                          <div className="flex gap-6 flex-wrap text-xs">
                            <div><span className="text-slate-500">Category:</span> <span className="text-slate-300">{r.category || "N/A"}</span></div>
                            <div><span className="text-slate-500">Address:</span> <span className="text-slate-300">{r.address || "N/A"}</span></div>
                            <div><span className="text-slate-500">Email:</span> <span className="text-indigo-400">{r.email || "N/A"}</span></div>
                            <div><span className="text-slate-500">Website:</span> <span className="text-cyan-400">{r.website || "N/A"}</span></div>
                            <div><span className="text-slate-500">Reviews:</span> <span className="text-slate-300">{r.reviews || "N/A"}</span></div>
                            {r.status_changed_at && <div><span className="text-slate-500">Status changed:</span> <span className="text-slate-300">{new Date(r.status_changed_at).toLocaleString()}</span></div>}
                            {r.saved_at && <div><span className="text-slate-500">Saved:</span> <span className="text-slate-300">{new Date(r.saved_at).toLocaleString()}</span></div>}
                          </div>
                          {r.ai_compat_score != null && (
                            <div className={`rounded-xl px-4 py-3 flex items-start gap-3 ${
                              r.ai_compat_score >= 70 ? "bg-emerald-950/40 border border-emerald-800/50" :
                              r.ai_compat_score >= 40 ? "bg-amber-950/30 border border-amber-800/40" :
                              "bg-slate-800/50 border border-slate-700"}`}>
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-sm shrink-0 ${
                                r.ai_compat_score >= 70 ? "bg-emerald-900 text-emerald-300" :
                                r.ai_compat_score >= 40 ? "bg-amber-900 text-amber-300" :
                                "bg-slate-700 text-slate-400"}`}>{r.ai_compat_score}</div>
                              <div>
                                <p className={`text-xs font-bold ${r.ai_compat_score >= 70 ? "text-emerald-400" : r.ai_compat_score >= 40 ? "text-amber-400" : "text-slate-400"}`}>
                                  {r.ai_compat_score >= 70 ? "Strong match" : r.ai_compat_score >= 40 ? "Moderate match" : "Weak match"} – Goal Alignment Score
                                </p>
                                <p className="text-[11px] text-slate-400 mt-0.5">{r.ai_compat_reason}</p>
                              </div>
                            </div>
                          )}
                          {/* Move to folder inline */}
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] text-slate-500">Move to:</span>
                            <select value={r.folder_id || ""} onChange={e => updateLead(r.id, { folder_id: e.target.value ? parseInt(e.target.value) : null })}
                              className="bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-2 py-1 text-[10px] outline-none">
                              <option value="">Unfiled</option>
                              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                            </select>
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-400 mb-1.5">{"📞"} AI Call Scripts</p>
                            {aiScripts[r.id || r.name]?.loading ? (
                              <div className="flex items-center gap-2 py-2">
                                <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                                <span className="text-xs text-slate-500">Generating personalized scripts...</span>
                              </div>
                            ) : (
                            <div className="flex flex-col gap-1.5">
                              {(aiScripts[r.id || r.name]?.scripts || getScripts(r)).map((script, si) => (
                                <div key={si} className="flex items-start gap-2 bg-slate-900 border border-slate-700 rounded-lg px-3 py-2">
                                  <p className="text-xs text-slate-300 flex-1 italic">"{script}"</p>
                                  <button onClick={() => navigator.clipboard.writeText(script)}
                                    className="text-[10px] px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-md shrink-0 transition">Copy</button>
                                </div>
                              ))}
                            </div>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// --- Pipeline / Kanban Page ---------------------------------------------------


function PipelinePage({ saved, updateLead, leadsLoaded }) {
  const { authFetch } = useAuth();
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [movingId, setMovingId] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [colPages, setColPages] = useState({});
  const COL_LIMIT = 50;
  const [dragItem, setDragItem] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [stageLabels, setStageLabels] = useState(() => {
    try { return JSON.parse(localStorage.getItem('pipelineLabels') || '{}'); } catch { return {}; }
  });
  const [editingLabel, setEditingLabel] = useState(null);
  const [editLabelVal, setEditLabelVal] = useState("");

  const STAGES = [
    { id: "new",          label: "New",          color: "text-slate-400",   bg: "bg-slate-800/60",   border: "border-slate-700" },
    { id: "sent",         label: "Sent",          color: "text-blue-400",    bg: "bg-blue-950/30",    border: "border-blue-800/50" },
    { id: "opened",       label: "Opened",        color: "text-indigo-400",  bg: "bg-indigo-950/30",  border: "border-indigo-800/50" },
    { id: "clicked",      label: "Clicked",       color: "text-amber-400",   bg: "bg-amber-950/30",   border: "border-amber-800/50" },
    { id: "replied",      label: "Replied",       color: "text-purple-400",  bg: "bg-purple-950/30",  border: "border-purple-800/50" },
    { id: "interested",   label: "Interested",    color: "text-emerald-400", bg: "bg-emerald-950/30", border: "border-emerald-800/50" },
    { id: "not_interested",label:"Not Interested",color: "text-red-400",     bg: "bg-red-950/30",     border: "border-red-800/50" },
    { id: "closed",       label: "Closed",        color: "text-teal-400",    bg: "bg-teal-950/30",    border: "border-teal-800/50" },
  ];

  const load = async () => {
    setLoading(true);
    try {
      const r = await authFetch("/pipeline");
      if (r.ok) setPipeline(await r.json());
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const moveStage = async (lead, stage) => {
    setMovingId(lead.id);
    try {
      await authFetch(`/pipeline/${lead.id}/stage`, { method: "PATCH", headers: {"Content-Type":"application/json"}, body: JSON.stringify({stage}) });
      await load();
    } catch(e) { console.error(e); }
    finally { setMovingId(null); }
  };

  const updateStatus = (lead, status) => updateLead(lead.id, { status });


  const handleDragStart = (e, lead) => {
    setDragItem(lead);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, colId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOver(colId);
  };

  const handleDrop = (e, colId) => {
    e.preventDefault();
    if (dragItem && dragItem.pipeline_stage !== colId) moveStage(dragItem, colId);
    setDragItem(null);
    setDragOver(null);
  };

  const handleDragEnd = () => { setDragItem(null); setDragOver(null); };

  const saveLabel = (stageId) => {
    const val = editLabelVal.trim();
    const updated = val ? { ...stageLabels, [stageId]: val } : (() => { const c = {...stageLabels}; delete c[stageId]; return c; })();
    setStageLabels(updated);
    localStorage.setItem('pipelineLabels', JSON.stringify(updated));
    setEditingLabel(null);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin"/></div>
  );

  return (
    <div className="p-4 sm:p-6 flex flex-col gap-5 min-h-0">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">📋 Pipeline</h2>
          <p className="text-xs text-slate-500 mt-0.5">Auto-advances on email activity — close deals manually</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowNew(v => !v)}
            className={`text-xs px-3 py-1.5 rounded-lg border transition ${showNew ? "bg-slate-700 border-slate-600 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
            {showNew ? "Hide New" : `Show New (${pipeline?.totals?.new || 0})`}
          </button>
          <button onClick={load} className="text-xs text-slate-500 hover:text-white px-3 py-1.5 bg-slate-800 rounded-lg border border-slate-700">Refresh</button>
        </div>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4">
        {STAGES.filter(s => s.id !== "new" || showNew).map(stage => {
          const allLeads = (pipeline?.leads?.[stage.id] || []);
          const page = colPages[stage.id] || 1;
          const leads = allLeads.slice(0, page * COL_LIMIT);
          return (
            <div key={stage.id}
              className={`min-w-[220px] w-56 flex-shrink-0 flex flex-col gap-2 rounded-2xl p-3 border transition-all ${stage.bg} ${dragOver===stage.id ? "border-indigo-500 ring-1 ring-indigo-500/40" : stage.border}`}
              onDragOver={e => handleDragOver(e, stage.id)}
              onDrop={e => handleDrop(e, stage.id)}
              onDragLeave={() => setDragOver(null)}>
              <div className="flex items-center justify-between mb-1">
                {editingLabel === stage.id ? (
                  <form onSubmit={e => { e.preventDefault(); saveLabel(stage.id); }} className="flex gap-1 flex-1">
                    <input autoFocus value={editLabelVal} onChange={e => setEditLabelVal(e.target.value)}
                      className="text-xs font-bold uppercase tracking-widest bg-transparent border-b border-indigo-500 outline-none flex-1 text-white"/>
                    <button type="submit" className="text-emerald-400 text-[10px]">✓</button>
                    <button type="button" onClick={() => setEditingLabel(null)} className="text-slate-500 text-[10px]">✕</button>
                  </form>
                ) : (
                  <span className={`text-xs font-bold uppercase tracking-widest ${stage.color} cursor-pointer hover:opacity-70`}
                    title="Click to rename"
                    onClick={() => { setEditingLabel(stage.id); setEditLabelVal(stageLabels[stage.id] || stage.label); }}>
                    {stageLabels[stage.id] || stage.label}
                  </span>
                )}
                <span className="text-xs text-slate-600 bg-slate-900/60 px-2 py-0.5 rounded-full">{allLeads.length}</span>
              </div>
              <div className="flex flex-col gap-2 min-h-[40px]">
                {leads.map(lead => (
                  <div key={lead.id}
                    draggable
                    onDragStart={e => handleDragStart(e, lead)}
                    onDragEnd={handleDragEnd}
                    className={`bg-slate-900 border border-slate-800 rounded-xl p-3 hover:border-slate-600 transition cursor-grab active:cursor-grabbing ${dragItem?.id===lead.id ? "opacity-50" : ""}`}>
                    <p className="font-semibold text-white text-xs truncate">{lead.name}</p>
                    {lead.email && <p className="text-[10px] text-slate-500 truncate font-mono mt-0.5">{lead.email}</p>}
                    {lead.phone && <p className="text-[10px] text-indigo-400 font-mono">{lead.phone}</p>}
                    <div className="flex gap-2 mt-1.5 text-[10px] text-slate-600">
                      {lead.emails_sent > 0 && <span>📤 {lead.emails_sent}</span>}
                      {lead.total_opens > 0 && <span>👁 {lead.total_opens}</span>}
                      {lead.total_clicks > 0 && <span>🖱 {lead.total_clicks}</span>}
                      {lead.total_replies > 0 && <span className="text-emerald-400">💬 {lead.total_replies}</span>}
                    </div>
                    {/* Quick move buttons */}
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {stage.id !== "interested" && (
                        <button onClick={() => moveStage(lead, "interested")} disabled={movingId===lead.id}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-900/40 text-emerald-400 hover:bg-emerald-900/60 border border-emerald-800/40">Interested</button>
                      )}
                      {stage.id !== "not_interested" && (
                        <button onClick={() => moveStage(lead, "not_interested")} disabled={movingId===lead.id}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-red-900/40 text-red-400 hover:bg-red-900/60 border border-red-800/40">Not Interested</button>
                      )}
                      {stage.id !== "closed" && (
                        <button onClick={() => moveStage(lead, "closed")} disabled={movingId===lead.id}
                          className="text-[10px] px-1.5 py-0.5 rounded bg-teal-900/40 text-teal-400 hover:bg-teal-900/60 border border-teal-800/40">Closed ✓</button>
                      )}
                    </div>
                  </div>
                ))}
                {leads.length === 0 && <p className="text-center text-slate-700 text-[10px] py-3">Empty</p>}
                {allLeads.length > leads.length && (
                  <button onClick={() => setColPages(p => ({...p, [stage.id]: (p[stage.id]||1)+1}))}
                    className="w-full text-xs text-slate-500 hover:text-slate-300 py-2 border border-dashed border-slate-700 rounded-lg mt-1">
                    Load more ({allLeads.length - leads.length} more)
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}





// --- History Page -------------------------------------------------------------


function HistoryPage({ onRerun }) {


  const { authFetch } = useAuth();


  const [history, setHistory] = useState([]);


  const [loading, setLoading] = useState(true);





  useEffect(() => {


    authFetch("/scrape/history")
      .then((r) => r.ok ? r.json() : Promise.reject(new Error("Failed")))
      .then((d) => { setHistory(d.history || []); setLoading(false); })
      .catch(() => setLoading(false));


  }, [authFetch]);


  const formatTime = (ts) => {
    if (!ts) return "...";
    const d = new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    if (diff < 604800000) return `${Math.floor(diff / 86400000)}d ago`;
    return d.toLocaleDateString();
  };

  return (


    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-5">


      <div>


        <h2 className="text-lg font-bold text-white">Search History</h2>


        <p className="text-xs text-slate-500 mt-0.5">Your previous searches</p>


      </div>


      {loading ? (


        <div className="flex flex-col gap-2">{[...Array(5)].map((_, i) => <SkeletonRow key={i} cols={6} />)}</div>


      ) : history.length === 0 ? (


        <div className="text-center text-slate-600 text-sm mt-10">No history yet. Run a search to get started.</div>


      ) : (


        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">


            <thead>


              <tr className="bg-slate-800/50 text-slate-500 text-[11px] uppercase tracking-widest border-b border-slate-800">


                <th className="px-5 py-3 text-left">#</th>


                <th className="px-5 py-3 text-left">Category</th>


                <th className="px-5 py-3 text-left">Location</th>

                <th className="px-5 py-3 text-left">Max</th>

                <th className="px-5 py-3 text-left">Results</th>


                <th className="px-5 py-3 text-left">Time</th>

                <th className="px-5 py-3 text-left">Actions</th>


              </tr>


            </thead>


            <tbody className="divide-y divide-slate-800/60">


              {history.map((h, idx) => (


                <tr key={idx} className="hover:bg-slate-800/25 transition">


                  <td className="px-5 py-3 text-slate-600 text-xs">{history.length - idx}</td>


                  <td className="px-5 py-3 font-semibold text-white">{h.category}</td>


                  <td className="px-5 py-3 text-slate-400">{h.location}</td>

                  <td className="px-5 py-3 text-slate-500 text-xs">{h.max_results ?? "-"}</td>

                  <td className="px-5 py-3 text-emerald-400 font-bold">{h.result_count ?? "-"}</td>


                  <td className="px-5 py-3 text-slate-500 text-xs" title={h.timestamp ? new Date(h.timestamp).toLocaleString() : ""}>{formatTime(h.timestamp)}</td>

                  <td className="px-5 py-3">
                    <button onClick={() => onRerun(h.category, h.location, h.max_results)}
                      className="text-xs px-3 py-1 bg-indigo-900/60 hover:bg-indigo-700 border border-indigo-800 text-indigo-300 rounded-lg transition font-semibold">
                      Rerun
                    </button>
                  </td>

                </tr>


              ))}


            </tbody>


          </table>
          </div>


        </div>


      )}


    </div>


  );


}





// --- Analytics Page -----------------------------------------------------------


function AnalyticsPage({ saved, leadsLoaded }) {

  const { authFetch } = useAuth();
  const [senderStats, setSenderStats] = useState([]);
  const [senderLoading, setSenderLoading] = useState(true);
  const [insights, setInsights] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [sResp, iResp] = await Promise.all([
          authFetch("/analytics/email-senders"),
          authFetch("/analytics/user-insights"),
        ]);
        if (sResp.ok) setSenderStats(await sResp.json());
        if (iResp.ok) setInsights(await iResp.json());
      } catch (e) { console.error(e); }
      finally { setSenderLoading(false); }
    })();
  }, [authFetch]);

  const total   = saved.length;


  const withPh  = saved.filter((l) => l.phone).length;


  const withEm  = saved.filter((l) => l.email).length;

  const withWeb = saved.filter((l) => l.website).length;

  const withAddr = saved.filter((l) => l.address).length;


  const avgRat  = total ? (saved.reduce((a, l) => a + (parseFloat(l.rating) || 0), 0) / total).toFixed(1) : "0";

  const totalReviews = saved.reduce((a, l) => a + (parseInt(l.reviews) || 0), 0);

  const highRated = saved.filter((l) => parseFloat(l.rating) >= 4.0).length;


  const byStatus = LEAD_STATUSES.map((s) => ({ name: s.label, value: saved.filter((l) => l.status === s.value).length }));


  const byCat    = Object.entries(


    saved.reduce((acc, l) => { const k = l.category || "Unknown"; acc[k] = (acc[k] || 0) + 1; return acc; }, {})


  ).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 8);


  const byRating = [
    { name: "5★", value: saved.filter((l) => { const r = parseFloat(l.rating); return r >= 4.5; }).length },
    { name: "4★", value: saved.filter((l) => { const r = parseFloat(l.rating); return r >= 3.5 && r < 4.5; }).length },
    { name: "3★", value: saved.filter((l) => { const r = parseFloat(l.rating); return r >= 2.5 && r < 3.5; }).length },
    { name: "2★", value: saved.filter((l) => { const r = parseFloat(l.rating); return r >= 1.5 && r < 2.5; }).length },
    { name: "1★", value: saved.filter((l) => { const r = parseFloat(l.rating); return r > 0 && r < 1.5; }).length },
    { name: "N/A", value: saved.filter((l) => !l.rating).length },
  ];

  const contactability = [
    { name: "Email + Phone", value: saved.filter((l) => l.email && l.phone).length },
    { name: "Email Only", value: saved.filter((l) => l.email && !l.phone).length },
    { name: "Phone Only", value: saved.filter((l) => !l.email && l.phone).length },
    { name: "No Contact", value: saved.filter((l) => !l.email && !l.phone).length },
  ];

  const byLocation = Object.entries(
    saved.reduce((acc, l) => {
      const addr = l.address || "";
      const parts = addr.split(",").map((s) => s.trim()).filter(Boolean);
      const place = parts.length >= 2 ? parts.slice(-2).join(", ") : parts[parts.length - 1] || "Unknown";
      acc[place] = (acc[place] || 0) + 1;
      return acc;
    }, {})
  ).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value).slice(0, 10);

  const LOCATION_COLORS = ["#6366f1","#10b981","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#ec4899","#14b8a6","#f97316","#a855f7"];

  const hotLeads = saved.filter((l) => l.phone && computePriority(l).score >= 8).length;
  const byPriority = [
    { name: "🔥 Hot", value: saved.filter((l) => l.phone && computePriority(l).score >= 8).length },
    { name: " Medium", value: saved.filter((l) => { const s = computePriority(l).score; return l.phone && s >= 5 && s < 8; }).length },
    { name: "Cold", value: saved.filter((l) => { const s = computePriority(l); return !l.phone || s.score < 5; }).length },
  ];
  const PRIORITY_COLORS = ["#f97316", "#eab308", "#64748b"];

  return (


    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-6">


      <div>


        <h2 className="text-lg font-bold text-white">Analytics</h2>


        <p className="text-xs text-slate-500 mt-0.5">Insights from your lead database</p>


      </div>

      {!leadsLoaded ? (
        <div className="flex-1 flex items-center justify-center py-20"><div className="flex flex-col items-center gap-3"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /><p className="text-xs text-slate-500">Loading analytics...</p></div></div>
      ) : <>

      {/*  Smart Insights (from actual email performance data)  */}
      {insights !== null && (
        <div className="bg-slate-900 border border-indigo-800/40 rounded-2xl p-5 flex flex-col gap-5">
          <div className="flex items-center gap-2">
            <span className="text-base"></span>
            <h3 className="text-sm font-bold text-white">Smart Insights{insights.total_sent > 0 ? ` – Based on Your ${insights.total_sent} Sent Emails` : ""}</h3>
          </div>
          {!insights.has_enough_data && (
            <div className="text-center py-6 text-slate-500 text-sm">
              {insights.total_sent === 0
                ? "No emails sent yet. Once you start sending outreach, this panel will show your best times, days, niches, and subject style based on your real results."
                : `Only ${insights.total_sent} emails sent so far. Keep sending — insights will appear once you have more data.`}
            </div>
          )}
          {insights.has_enough_data && (

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Best Send Times */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">⏰ Best Hours to Send</p>
              {insights.best_send_hours.length === 0 ? <p className="text-[11px] text-slate-600">Not enough data yet</p> : (
                <div className="flex flex-col gap-1">
                  {insights.best_send_hours.slice(0,3).map((h) => (
                    <div key={h.hour_utc} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-2.5 py-1.5">
                      <span className="text-xs text-white font-semibold">{String(h.hour_utc).padStart(2,"0")}:00 UTC</span>
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-emerald-400">{h.open_rate}% open</span>
                        <span className="text-blue-400">{h.click_rate}% click</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Best Days */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">📅 Best Days to Send</p>
              {insights.best_days.length === 0 ? <p className="text-[11px] text-slate-600">Not enough data yet</p> : (
                <div className="flex flex-col gap-1">
                  {insights.best_days.slice(0,3).map((d) => (
                    <div key={d.day_name} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-2.5 py-1.5">
                      <span className="text-xs text-white font-semibold">{d.day_name}</span>
                      <div className="flex gap-2 text-[10px]">
                        <span className="text-emerald-400">{d.open_rate}% open</span>
                        <span className="text-blue-400">{d.click_rate}% click</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Countries */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">🌍 Best Countries</p>
              <p className="text-[10px] text-slate-600 mb-1.5">Which countries open your emails most</p>
              {(insights.top_countries||[]).length === 0 ? <p className="text-[11px] text-slate-600">No country data yet</p> : (
                <div className="flex flex-col gap-1">
                  {(insights.top_countries||[]).slice(0,4).map((c) => (
                    <div key={c.country} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-2.5 py-1.5">
                      <span className="text-[11px] text-white font-semibold truncate max-w-[110px]">{c.country}</span>
                      <div className="flex gap-2 text-[10px] shrink-0">
                        <span className="text-emerald-400">{c.open_rate}%</span>
                        <span className="text-slate-500">{c.sent} sent</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Top Categories / Niches */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">🎯 Niches That Respond</p>
              <p className="text-[10px] text-slate-600 mb-1.5">Business types with best open rates from your sent emails</p>
              {insights.top_categories.length === 0 ? <p className="text-[11px] text-slate-600">No category data yet</p> : (
                <div className="flex flex-col gap-1">
                  {insights.top_categories.slice(0,4).map((c) => (
                    <div key={c.category} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-2.5 py-1.5">
                      <span className="text-[11px] text-white font-semibold truncate max-w-[110px]" title={c.category}>{c.category}</span>
                      <div className="flex gap-2 text-[10px] shrink-0 items-center">
                        <span className="text-emerald-400">{c.open_rate}%</span>
                        {c.sent < 10 && <span className="text-amber-600 text-[9px]">({c.sent})</span>}
                      </div>
                    </div>
                  ))}
                  {insights.top_categories.length === 1 && (
                    <p className="text-[10px] text-amber-600/70 mt-1">Send to more niches to compare</p>
                  )}
                </div>
              )}
            </div>

            {/* Rating Band */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">⭐ Best Lead Rating</p>
              <p className="text-[10px] text-slate-600 mb-1.5">Which star-rating band responds best</p>
              {insights.rating_bands.length === 0 ? <p className="text-[11px] text-slate-600">Not enough data yet</p> : (
                <div className="flex flex-col gap-1">
                  {insights.rating_bands.slice(0,3).map(r => (
                    <div key={r.band} className="flex items-center justify-between bg-slate-800/60 rounded-lg px-2.5 py-1.5">
                      <span className="text-xs text-amber-400 font-bold">{r.band}★</span>
                      <div className="text-[10px] text-slate-400">{r.open_rate}% open · {r.sent} sent</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* A/B Subject */}
            <div>
              <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wide mb-1.5">✍ Subject Style Winning</p>
              <p className="text-[10px] text-slate-600 mb-1.5">Which subject line approach gets more opens</p>
              <div className="bg-slate-800/60 rounded-lg px-2.5 py-2">
                {insights.ab_preference.preferred_variant === "auto" ? (
                  <span className="text-xs text-slate-400">Still learning — keep sending</span>
                ) : (
                  <>
                    <span className={`text-xs font-bold ${insights.ab_preference.preferred_variant === "b" ? "text-emerald-400" : "text-indigo-400"}`}>
                      Subject {insights.ab_preference.preferred_variant.toUpperCase()} ({insights.ab_preference.preferred_variant === "b" ? "Benefit-led" : "Curiosity-led"})
                    </span>
                    <div className="text-[10px] text-slate-400 mt-0.5">A: {insights.ab_preference.a.open_rate}% · B: {insights.ab_preference.b.open_rate}% open rate</div>
                  </>
                )}
              </div>
            </div>
          </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">


        <StatCard label="Total Leads" value={total} subText={`${highRated} rated 4+`} />


        <StatCard label="With Email"  value={withEm} subText={total ? `${Math.round(withEm*100/total)}% coverage` : "0%"} color="text-emerald-300" ring="border-emerald-800/40" />


        <StatCard label="With Phone"  value={withPh} subText={total ? `${Math.round(withPh*100/total)}% coverage` : "0%"} color="text-blue-300" ring="border-blue-800/40" />


        <StatCard label="Avg Rating"  value={avgRat} subText="out of 5.0" color="text-amber-300" ring="border-amber-800/40" />


      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="With Website" value={withWeb} subText={total ? `${Math.round(withWeb*100/total)}%` : "0%"} color="text-purple-300" ring="border-purple-800/40" />
        <StatCard label="With Address" value={withAddr} subText={total ? `${Math.round(withAddr*100/total)}%` : "0%"} color="text-cyan-300" ring="border-cyan-800/40" />
        <StatCard label="Total Reviews" value={totalReviews.toLocaleString()} subText={total ? `avg ${Math.round(totalReviews / total)} per lead` : "0"} color="text-pink-300" ring="border-pink-800/40" />
        <StatCard label="🔥 Hot Leads" value={hotLeads} subText={total ? `${Math.round(hotLeads*100/total)}% of total` : "0%"} color="text-orange-300" ring="border-orange-800/40" />
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">


        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">


          <h3 className="text-sm font-bold text-slate-300 mb-4">Leads by Status</h3>

          <div className="w-full" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
                <Pie data={byStatus} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}>


              {byStatus.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}


            </Pie>


            <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />


              </RePieChart>
            </ResponsiveContainer>
          </div>


        </div>


        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">


          <h3 className="text-sm font-bold text-slate-300 mb-4">Top Categories</h3>


          {byCat.length === 0 ? <p className="text-slate-600 text-xs">No data yet.</p> : (

            <div className="w-full" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ReBarChart data={byCat}>


              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />


              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 9 }} />


              <YAxis tick={{ fill: "#64748b", fontSize: 9 }} />


              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />


              <Bar dataKey="value" fill="#6366f1" radius={[3,3,0,0]} />


                </ReBarChart>
              </ResponsiveContainer>
            </div>


          )}


        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">
          <h3 className="text-sm font-bold text-slate-300 mb-4">Rating Distribution</h3>
          {total === 0 ? <p className="text-slate-600 text-xs">No data yet.</p> : (
            <div className="w-full" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ReBarChart data={byRating}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 10 }} />
              <YAxis tick={{ fill: "#64748b", fontSize: 9 }} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />
              <Bar dataKey="value" fill="#f59e0b" radius={[3,3,0,0]} />
                </ReBarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">
          <h3 className="text-sm font-bold text-slate-300 mb-3">Email Senders Usage</h3>
          {senderLoading ? (
            <p className="text-slate-600 text-xs">Loading sender stats...</p>
          ) : senderStats.length === 0 ? (
            <p className="text-slate-600 text-xs">No emails sent yet.</p>
          ) : (
            <div className="flex flex-col gap-2 text-xs max-h-52 overflow-auto pr-1">
              {senderStats.map((s) => (
                <div key={s.from_email} className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-300 truncate" title={s.from_email}>{s.from_email}</span>
                    <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-500">
                      {s.sent_today} today 
                      <span className="mx-1 text-slate-700">–</span>
                      {s.total_sent} total
                    </span>
                  </div>
                  {s.last_sent && (
                    <span className="text-[10px] text-slate-600">
                      Last sent: {new Date(s.last_sent).toLocaleString()}
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">
          <h3 className="text-sm font-bold text-slate-300 mb-4">Contactability</h3>
          <div className="w-full" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
            <Pie data={contactability} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}>
              {contactability.map((_, i) => <Cell key={i} fill={["#10b981","#6366f1","#f59e0b","#ef4444"][i]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5">
          <h3 className="text-sm font-bold text-slate-300 mb-4">Call Priority</h3>
          <div className="w-full" style={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RePieChart>
            <Pie data={byPriority} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={75} label={({ name, value }) => value > 0 ? `${name}: ${value}` : ""}>
              {byPriority.map((_, i) => <Cell key={i} fill={PRIORITY_COLORS[i]} />)}
            </Pie>
            <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />
              </RePieChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 sm:p-5 lg:col-span-2">
          <h3 className="text-sm font-bold text-slate-300 mb-4">Leads by Location</h3>
          {byLocation.length === 0 ? <p className="text-slate-600 text-xs">No data yet.</p> : (
            <div className="flex flex-col lg:flex-row items-start gap-4">
              <div className="w-full lg:w-auto" style={{ height: 240, minWidth: 200 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <RePieChart>
                <Pie data={byLocation} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, value }) => value > 0 ? `${value}` : ""}>
                  {byLocation.map((_, i) => <Cell key={i} fill={LOCATION_COLORS[i % LOCATION_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 8 }} />
                  </RePieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-col gap-1.5 text-xs">
                {byLocation.map((loc, i) => (
                  <div key={loc.name} className="flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full inline-block flex-shrink-0" style={{ background: LOCATION_COLORS[i % LOCATION_COLORS.length] }} />
                    <span className="text-slate-400">{loc.name}</span>
                    <span className="text-slate-600 ml-auto">{loc.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>


      </div>

      </>}

    </div>


  );


}










// --- Settings Page (Email Senders + Password Gate) ----------------------------

function SettingsPage() {
  const { authFetch, user } = useAuth();

  // Password gate
  const [unlocked, setUnlocked]       = useState(false);
  const [pwInput, setPwInput]         = useState("");
  const [pwError, setPwError]         = useState("");
  const [pwLoading, setPwLoading]     = useState(false);

  // Senders
  const [senders, setSenders]         = useState([]);
  const [loadingSenders, setLoadingSenders] = useState(false);
  const [deleteConfirm, setDeleteConfirm]   = useState(null); // sender id

  // Edit API key
  const [editingApiKey, setEditingApiKey] = useState(null); // sender id being edited
  const [newApiKey, setNewApiKey]         = useState("");
  const [newSmtpPass, setNewSmtpPass]     = useState("");
  const [apiKeyError, setApiKeyError]     = useState("");
  const [apiKeySaving, setApiKeySaving]   = useState(false);

  // Test sender
  const [testingSender, setTestingSender] = useState(null); // sender id being tested
  const [testResult, setTestResult]       = useState(null); // {ok, message}

  // Add-sender form
  const [showForm, setShowForm]       = useState(false);
  const [provider, setProvider]       = useState("resend");
  const [displayName, setDisplayName] = useState("");
  const [fromEmail, setFromEmail]     = useState("");
  const [apiKey, setApiKey]           = useState("");
  const [smtpUser, setSmtpUser]       = useState("");
  const [smtpPass, setSmtpPass]       = useState("");
  const [smtpHost, setSmtpHost]       = useState("smtp.zoho.com");
  const [smtpPort, setSmtpPort]       = useState(587);
  const [formError, setFormError]     = useState("");
  const [formSaving, setFormSaving]   = useState(false);

  // Change password
  const [cpOld, setCpOld]         = useState("");
  const [cpNew, setCpNew]         = useState("");
  const [cpNew2, setCpNew2]       = useState("");
  const [cpError, setCpError]     = useState("");
  const [cpSuccess, setCpSuccess] = useState("");
  const [cpSaving, setCpSaving]   = useState(false);

  const verifyPassword = async () => {
    if (!pwInput) return;
    setPwLoading(true); setPwError("");
    try {
      const r = await authFetch("/settings/verify-password", {
        method: "POST", body: JSON.stringify({ password: pwInput })
      });
      if (r.ok) { setUnlocked(true); loadSenders(); }
      else { const d = await r.json().catch(() => ({})); setPwError(d.detail || "Incorrect password."); }
    } catch { setPwError("Connection error."); }
    setPwLoading(false);
  };

  const loadSenders = async () => {
    setLoadingSenders(true);
    try {
      const r = await authFetch("/settings/email-senders");
      if (r.ok) setSenders(await r.json());
    } catch {}
    setLoadingSenders(false);
  };

  const deleteSender = async (id) => {
    await authFetch(`/settings/email-senders/${id}`, { method: "DELETE" });
    setSenders(s => s.filter(x => x.id !== id));
    setDeleteConfirm(null);
  };

  const toggleSender = async (id) => {
    const r = await authFetch(`/settings/email-senders/${id}`, { method: "PATCH" });
    if (r.ok) { const d = await r.json(); setSenders(s => s.map(x => x.id === id ? { ...x, is_active: d.is_active } : x)); }
  };

  const updateApiKey = async (senderId) => {
    setApiKeyError(""); setApiKeySaving(true);
    const sender = senders.find(s => s.id === senderId);
    if (!sender) return;

    const body = sender.provider === "resend" 
      ? { api_key: newApiKey }
      : { api_key: "", smtp_pass: newSmtpPass };

    try {
      const r = await authFetch(`/settings/email-senders/${senderId}/api-key`, {
        method: "PUT", body: JSON.stringify(body)
      });
      if (r.ok) {
        setEditingApiKey(null);
        setNewApiKey("");
        setNewSmtpPass("");
        alert("API key updated successfully!");
      } else {
        const d = await r.json().catch(() => ({}));
        setApiKeyError(d.detail || "Failed to update.");
      }
    } catch {
      setApiKeyError("Connection error.");
    }
    setApiKeySaving(false);
  };

  const testSender = async (senderId) => {
    setTestingSender(senderId);
    setTestResult(null);
    try {
      const r = await authFetch(`/settings/email-senders/${senderId}/test`, { method: "POST" });
      const data = await r.json();
      setTestResult({ ...data, senderId });
    } catch {
      setTestResult({ ok: false, error: "Connection error", senderId });
    }
    setTimeout(() => setTestResult(null), 5000); // Clear after 5s
    setTestingSender(null);
  };

  const addSender = async () => {
    setFormError(""); setFormSaving(true);
    const body = { provider, display_name: displayName, from_email: fromEmail };
    if (provider === "resend") body.api_key = apiKey;
    if (provider === "zoho")   { body.smtp_user = smtpUser; body.smtp_pass = smtpPass; body.smtp_host = smtpHost; body.smtp_port = smtpPort; }
    try {
      const r = await authFetch("/settings/email-senders", { method: "POST", body: JSON.stringify(body) });
      if (r.ok) {
        setShowForm(false); setDisplayName(""); setFromEmail(""); setApiKey(""); setSmtpUser(""); setSmtpPass("");
        loadSenders();
      } else { const d = await r.json().catch(() => ({})); setFormError(d.detail || "Failed to save."); }
    } catch { setFormError("Connection error."); }
    setFormSaving(false);
  };

  const changePassword = async () => {
    if (!cpOld || !cpNew || !cpNew2) { setCpError("All fields are required."); return; }
    if (cpNew !== cpNew2) { setCpError("New passwords do not match."); return; }
    if (cpNew.length < 6) { setCpError("New password must be at least 6 characters."); return; }
    setCpError(""); setCpSuccess(""); setCpSaving(true);
    try {
      const r = await authFetch("/settings/change-password", {
        method: "POST", body: JSON.stringify({ current_password: cpOld, new_password: cpNew })
      });
      if (r.ok) { setCpSuccess("Password updated successfully."); setCpOld(""); setCpNew(""); setCpNew2(""); }
      else { const d = await r.json().catch(() => ({})); setCpError(d.detail || "Failed to update password."); }
    } catch { setCpError("Connection error."); }
    setCpSaving(false);
  };

  const providerBadge = (p) => p === "zoho"
    ? <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-orange-900/50 text-orange-300 border border-orange-700/40">Zoho</span>
    : <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-900/50 text-indigo-300 border border-indigo-700/40">Resend</span>;

  if (!unlocked) {
    return (
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-8 w-full max-w-sm flex flex-col gap-5">
          <div className="text-center">
            <div className="w-12 h-12 bg-indigo-900/40 border border-indigo-700/40 rounded-2xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            </div>
            <h2 className="text-lg font-bold text-white">Settings</h2>
            <p className="text-xs text-slate-500 mt-1">Enter your account password to access settings</p>
          </div>
          <div className="flex flex-col gap-3">
            <input
              type="password" value={pwInput} onChange={e => setPwInput(e.target.value)}
              onKeyDown={e => e.key === "Enter" && verifyPassword()}
              placeholder="Your account password"
              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none"
            />
            {pwError && <p className="text-xs text-red-400">{pwError}</p>}
            <button onClick={verifyPassword} disabled={pwLoading || !pwInput}
              className="py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition">
              {pwLoading ? "Verifying..." : "Unlock Settings"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-bold text-white">Settings</h2>
        <p className="text-xs text-slate-500 mt-0.5">Manage your email sending accounts</p>
      </div>

      {/* Email Senders Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-white">Email Senders</h3>
            <p className="text-[11px] text-slate-500 mt-0.5">
              Add your own Resend or Zoho accounts. Emails are sent from these, rotating randomly across all active senders.
              If none are configured, the platform default is used.
            </p>
          </div>
          <button onClick={() => { setShowForm(s => !s); setFormError(""); }}
            className="shrink-0 px-3 py-1.5 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg text-xs font-bold transition">
            {showForm ? "Cancel" : "+ Add Sender"}
          </button>
        </div>

        {/* Add form */}
        {showForm && (
          <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4 flex flex-col gap-3">
            <div className="flex gap-2">
              {["resend", "zoho"].map(p => (
                <button key={p} onClick={() => setProvider(p)}
                  className={`flex-1 py-2 rounded-lg text-xs font-bold transition border ${provider === p ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                  {p === "resend" ? " Resend" : " Zoho"}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input value={displayName} onChange={e => setDisplayName(e.target.value)} placeholder="Display name (e.g. TimingoFlow Support)"
                className="col-span-2 bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
              <input value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder={provider === "resend" ? "sender@yourdomain.com" : "yourname@zoho.com"}
                className="col-span-2 bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
              {provider === "resend" && (
                <input value={apiKey} onChange={e => setApiKey(e.target.value)} placeholder="Resend API key (re_–)"
                  className="col-span-2 bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none font-mono" />
              )}
              {provider === "zoho" && (<>
                <input value={smtpHost} onChange={e => setSmtpHost(e.target.value)} placeholder="SMTP host (smtp.zoho.com)"
                  className="bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
                <input type="number" value={smtpPort} onChange={e => setSmtpPort(Number(e.target.value))} placeholder="Port (587)"
                  className="bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
                <input value={smtpUser} onChange={e => setSmtpUser(e.target.value)} placeholder="Zoho login email"
                  className="bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
                <input type="password" value={smtpPass} onChange={e => setSmtpPass(e.target.value)} placeholder="Zoho app password"
                  className="bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-xs outline-none" />
              </>)}
            </div>
            {formError && <p className="text-xs text-red-400">{formError}</p>}
            <button onClick={addSender} disabled={formSaving}
              className="self-start px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition">
              {formSaving ? "Saving..." : "Save Sender"}
            </button>
          </div>
        )}

        {/* Senders list */}
        {loadingSenders ? (
          <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : senders.length === 0 ? (
          <div className="text-center py-6 text-slate-600 text-xs">
            No senders configured yet.<br />
            <span className="text-slate-500">Emails will use the platform default until you add your own.</span>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {senders.map(s => (
              <div key={s.id} className={`flex flex-col rounded-xl px-4 py-3 border transition ${s.is_active ? "bg-slate-800 border-slate-700" : "bg-slate-900 border-slate-800 opacity-50"}`}>
                <div className="flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {providerBadge(s.provider)}
                      <span className="text-sm font-semibold text-white truncate">{s.display_name || s.from_email}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">{s.from_email}{s.smtp_user && s.smtp_user !== s.from_email ? ` – login: ${s.smtp_user}` : ""}</p>
                  </div>
                  <button onClick={() => toggleSender(s.id)}
                    className={`shrink-0 px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${s.is_active ? "bg-emerald-900/40 border-emerald-700/40 text-emerald-400 hover:bg-emerald-900/60" : "bg-slate-800 border-slate-700 text-slate-500 hover:border-slate-500"}`}>
                    {s.is_active ? "Active" : "Off"}
                  </button>
                  <button onClick={() => { setEditingApiKey(s.id); setNewApiKey(""); setNewSmtpPass(""); setApiKeyError(""); }}
                    className="shrink-0 px-2.5 py-1 bg-indigo-700 hover:bg-indigo-600 text-white rounded-lg text-[10px] font-bold transition">
                     Edit Key
                  </button>
                  <button onClick={() => testSender(s.id)} disabled={testingSender === s.id}
                    className="shrink-0 px-2.5 py-1 bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-white rounded-lg text-[10px] font-bold transition">
                    {testingSender === s.id ? "Testing..." : " Test"}
                  </button>
                  {deleteConfirm === s.id ? (
                    <div className="flex gap-1 shrink-0">
                      <button onClick={() => deleteSender(s.id)} className="px-2.5 py-1 bg-red-700 hover:bg-red-600 text-white rounded-lg text-[10px] font-bold transition">Delete</button>
                      <button onClick={() => setDeleteConfirm(null)} className="px-2.5 py-1 bg-slate-700 text-slate-300 rounded-lg text-[10px] font-bold transition">Cancel</button>
                    </div>
                  ) : (
                    <button onClick={() => setDeleteConfirm(s.id)} className="shrink-0 text-slate-600 hover:text-red-400 transition p-1">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                    </button>
                  )}
                </div>
                {testResult && testResult.senderId === s.id && (
                  <div className={`mt-2 px-3 py-2 rounded-lg text-xs ${testResult.ok ? "bg-emerald-900/40 border border-emerald-700/40 text-emerald-300" : "bg-red-900/40 border border-red-700/40 text-red-300"}`}>
                    {testResult.ok ? ` ${testResult.message}` : ` ${testResult.error}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="bg-slate-800/40 border border-slate-700/40 rounded-xl px-4 py-3 mt-1">
          <p className="text-[11px] text-slate-500 leading-relaxed">
            <span className="text-slate-400 font-semibold">How it works:</span> When you send outreach emails, the system randomly picks one of your active senders so emails come from different addresses — reducing the chance of being blocked. Add as many sender addresses as you like. For Resend, you need a verified domain. For Zoho, use an app-specific password.
          </p>
        </div>

        {/* Edit API Key Modal */}
        {editingApiKey && (
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={() => setEditingApiKey(null)}>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
              {(() => {
                const sender = senders.find(s => s.id === editingApiKey);
                if (!sender) return null;
                return (<>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-white">Update Credentials</h3>
                    <button onClick={() => setEditingApiKey(null)} className="text-slate-500 hover:text-slate-300 transition">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="mb-4">
                    <div className="flex items-center gap-2 mb-2">
                      {providerBadge(sender.provider)}
                      <span className="text-sm font-semibold text-white">{sender.display_name || sender.from_email}</span>
                    </div>
                    <p className="text-xs text-slate-500">{sender.from_email}</p>
                  </div>
                  <div className="flex flex-col gap-3">
                    {sender.provider === "resend" ? (
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5 font-semibold">Resend API Key</label>
                        <input
                          type="password"
                          value={newApiKey}
                          onChange={e => setNewApiKey(e.target.value)}
                          placeholder="re_your_new_api_key"
                          className="w-full bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none font-mono"
                        />
                      </div>
                    ) : (
                      <div>
                        <label className="block text-xs text-slate-400 mb-1.5 font-semibold">Zoho App Password</label>
                        <input
                          type="password"
                          value={newSmtpPass}
                          onChange={e => setNewSmtpPass(e.target.value)}
                          placeholder="Enter new SMTP password"
                          className="w-full bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none"
                        />
                      </div>
                    )}
                    {apiKeyError && <p className="text-xs text-red-400">{apiKeyError}</p>}
                    <div className="flex gap-2 justify-end pt-2">
                      <button onClick={() => setEditingApiKey(null)}
                        className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-sm font-semibold transition">
                        Cancel
                      </button>
                      <button onClick={() => updateApiKey(editingApiKey)} disabled={apiKeySaving || (sender.provider === "resend" ? !newApiKey : !newSmtpPass)}
                        className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-lg text-sm font-semibold transition">
                        {apiKeySaving ? "Saving..." : "Update"}
                      </button>
                    </div>
                  </div>
                </>);
              })()}
            </div>
          </div>
        )}
      </div>

      {/* Change Password Section */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">
        <div>
          <h3 className="text-sm font-bold text-white">Change Password</h3>
          <p className="text-[11px] text-slate-500 mt-0.5">Update your account password. You'll need your current password to confirm.</p>
        </div>
        <div className="flex flex-col gap-2">
          <input type="password" value={cpOld} onChange={e => setCpOld(e.target.value)} placeholder="Current password"
            className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none" />
          <input type="password" value={cpNew} onChange={e => setCpNew(e.target.value)} placeholder="New password (min. 6 characters)"
            className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none" />
          <input type="password" value={cpNew2} onChange={e => setCpNew2(e.target.value)} placeholder="Confirm new password"
            className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none" />
        </div>
        {cpError && <p className="text-xs text-red-400">{cpError}</p>}
        {cpSuccess && <p className="text-xs text-emerald-400">{cpSuccess}</p>}
        <button onClick={changePassword} disabled={cpSaving || !cpOld || !cpNew || !cpNew2}
          className="self-start px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition">
          {cpSaving ? "Updating..." : "Update Password"}
        </button>
      </div>

      {/* Reply Tracking Section */}
      <ReplyTrackingSettings authFetch={authFetch} />
    </div>
  );
}

function ReplyTrackingSettings({ authFetch }) {
  const WEBHOOK_URL = "https://leadgen-1087946781393.europe-west1.run.app/webhooks/resend-inbound";
  const [settings, setSettings] = useState(null);
  const [customUrl, setCustomUrl] = useState("");
  const [usePlatform, setUsePlatform] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    authFetch("/settings/reply-tracking").then(r => r.ok ? r.json() : null).then(d => {
      if (d) { setSettings(d); setCustomUrl(d.custom_webhook_url || ""); setUsePlatform(d.use_platform_tracking !== false); }
    }).catch(() => {});
  }, [authFetch]);

  const save = async () => {
    setSaving(true); setSaved(false);
    try {
      const r = await authFetch("/settings/reply-tracking", { method: "PUT", body: JSON.stringify({ custom_webhook_url: customUrl || null, use_platform_tracking: usePlatform }) });
      if (r.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    } catch {} finally { setSaving(false); }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-4">
      <div>
        <h3 className="text-sm font-bold text-white">Reply Tracking</h3>
        <p className="text-[11px] text-slate-500 mt-0.5">Automatically capture replies to your outreach emails and see them in the Reply Analytics tab.</p>
      </div>

      {/* Default / Platform tracking */}
      <div className="bg-slate-800/50 border border-slate-700/60 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-lg bg-emerald-900/40 border border-emerald-700/40 flex items-center justify-center shrink-0 mt-0.5">
            <span className="text-sm"></span>
          </div>
          <div>
            <p className="text-sm font-semibold text-white">TimingoFlow Auto-Tracking (Default)</p>
            <p className="text-[11px] text-slate-400 mt-0.5">When you send via the platform, replies are automatically routed through <span className="text-indigo-400 font-mono">reply+&#123;token&#125;@timingoflow.com</span> and tracked in your account. No setup needed.</p>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">Webhook URL (for Resend Inbound setup)</p>
          <div className="flex gap-2">
            <input readOnly value={WEBHOOK_URL} className="flex-1 bg-slate-900 border border-slate-700 text-slate-300 rounded-lg px-3 py-2 text-xs font-mono outline-none" />
            <button onClick={() => navigator.clipboard.writeText(WEBHOOK_URL)}
              className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-semibold transition">Copy</button>
          </div>
          <p className="text-[10px] text-slate-500 mt-1">In Resend  Email  Inbound: add a route for <span className="text-slate-300 font-mono">reply+*@timingoflow.com</span> pointing to this URL.</p>
        </div>
      </div>

      {/* Custom webhook */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <input type="checkbox" id="customWh" checked={!usePlatform} onChange={e => setUsePlatform(!e.target.checked)} className="accent-indigo-500 w-4 h-4" />
          <label htmlFor="customWh" className="text-sm text-slate-300 cursor-pointer">Use my own Resend Inbound webhook instead</label>
        </div>
        {!usePlatform && (
          <div className="flex flex-col gap-2">
            <p className="text-[11px] text-slate-500">Paste your own endpoint URL. Resend will call it when replies arrive.</p>
            <input
              type="url"
              value={customUrl}
              onChange={e => setCustomUrl(e.target.value)}
              placeholder="https://your-domain.com/webhooks/resend-inbound"
              className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none"
            />
          </div>
        )}
      </div>

      {saved && <p className="text-xs text-emerald-400">Saved!</p>}
      <button onClick={save} disabled={saving}
        className="self-start px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-50 text-white rounded-xl text-sm font-bold transition">
        {saving ? "Saving..." : "Save Reply Tracking Settings"}
      </button>
    </div>
  );
}

// --- Reminders & Settings Page ------------------------------------------------

function RemindersPage({ userLimits }) {
  const { authFetch } = useAuth();
  const remindersDisabledByAdmin = userLimits?.limits?.reminders_enabled === false;
  const [settings, setSettings] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingDue, setSendingDue] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  const STATUS_LABELS = {
    new: "New", contacted_no_answer: "No Answer", contacted_answered: "Answered",
    interested: "Interested", closed: "Closed", not_interested: "Not Interested"
  };

  const fetchSettings = useCallback(async () => {
    try {
      const resp = await authFetch("/reminder-settings");
      if (resp.ok) setSettings(await resp.json());
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const fetchReminders = useCallback(async () => {
    try {
      const resp = await authFetch("/reminders");
      if (resp.ok) { const data = await resp.json(); setReminders(Array.isArray(data) ? data : []); }
    } catch (e) { console.error(e); }
  }, [authFetch]);

  useEffect(() => {
    Promise.all([fetchSettings(), fetchReminders()]).finally(() => setLoading(false));
  }, [fetchSettings, fetchReminders]);

  const saveSettings = async (updates) => {
    setSaving(true);
    try {
      const body = { ...settings, ...updates };
      await authFetch("/reminder-settings", { method: "PUT", body: JSON.stringify(body) });
      setSettings(prev => ({ ...prev, ...updates }));
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const sendDueReminders = async () => {
    setSendingDue(true); setSendResult(null);
    try {
      const resp = await authFetch("/reminders/send-due", { method: "POST" });
      if (resp.ok) { setSendResult(await resp.json()); } else { setSendResult({ error: "Failed to send reminders." }); }
      fetchReminders();
    } catch (e) { setSendResult({ error: e.message }); }
    setSendingDue(false);
  };

  if (loading) return <div className="flex-1 flex items-center justify-center text-slate-600 text-sm">Loading...</div>;

  const defaultDays = settings?.default_days || {};

  return (
    <div className="flex-1 overflow-auto p-4 sm:p-6 flex flex-col gap-5 max-w-3xl">
      <div>
        <h2 className="text-lg font-bold text-white">Reminders & Notifications</h2>
        <p className="text-xs text-slate-500 mt-0.5">Set up automatic follow-up reminders sent to your email</p>
      </div>

      {remindersDisabledByAdmin && (
        <div className="bg-red-950/50 border border-red-800/50 rounded-xl px-4 py-3">
          <p className="text-sm text-red-300 font-semibold">Reminders Disabled</p>
          <p className="text-xs text-red-400/70 mt-0.5">Reminders have been disabled for your account by the administrator.</p>
        </div>
      )}

      {/* Email toggle */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-3">
        <h3 className="text-sm font-bold text-white">Email Reminders</h3>
        <p className="text-[11px] text-slate-500">Reminder emails are sent to your account email when follow-ups are due.</p>
        <label className="flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={settings?.enabled ?? true} onChange={e => saveSettings({ enabled: e.target.checked })}
            className="accent-indigo-600 w-4 h-4 cursor-pointer" />
          <span className="text-xs text-slate-300">Enable email reminders</span>
        </label>
      </div>

      {/* Default reminder days per status */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-3">
        <h3 className="text-sm font-bold text-white">Reminder Days per Status</h3>
        <p className="text-[11px] text-slate-500">When a lead moves to a status, how many days until you get reminded to follow up? Leave empty to disable.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(STATUS_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2.5">
              <span className="text-xs text-slate-300 font-medium">{label}</span>
              <input type="number" min="0" max="365" placeholder="-"
                value={defaultDays[key] ?? ""}
                onChange={e => {
                  const v = e.target.value === "" ? null : parseInt(e.target.value);
                  const updated = { ...defaultDays, [key]: v };
                  setSettings(prev => ({ ...prev, default_days: updated }));
                }}
                className="w-16 bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-2 py-1 text-xs outline-none text-center" />
            </div>
          ))}
        </div>
        <button onClick={() => saveSettings({ default_days: settings.default_days })} disabled={saving}
          className="self-start px-4 py-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition">
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>

      {/* Send due reminders */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-white">Send Due Reminders</h3>
            <p className="text-[11px] text-slate-500">Check and send any reminders that are due now</p>
          </div>
          <button onClick={sendDueReminders} disabled={sendingDue}
            className="px-4 py-2 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 text-white rounded-lg text-xs font-bold transition">
            {sendingDue ? "Sending..." : "Send Now"}
          </button>
        </div>
        {sendResult && (
          <div className={`text-xs px-3 py-2 rounded-lg ${sendResult.error ? "bg-red-950/60 text-red-300" : "bg-emerald-950/60 text-emerald-300"}`}>
            {sendResult.error || `${sendResult.sent} reminder(s) sent!`}
          </div>
        )}
      </div>

      {/* Upcoming reminders */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5 flex flex-col gap-3">
        <h3 className="text-sm font-bold text-white">Upcoming Reminders ({reminders.filter(r => !r.sent).length})</h3>
        {reminders.filter(r => !r.sent).length === 0 ? (
          <p className="text-xs text-slate-600">No pending reminders. Move leads between statuses to create them.</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-[300px] overflow-auto">
            {reminders.filter(r => !r.sent).map(r => (
              <div key={r.id} className="flex items-center justify-between bg-slate-800 rounded-lg px-3 py-2.5">
                <div className="flex flex-col gap-0.5">
                  <span className="text-xs text-white font-semibold">{r.lead_name}</span>
                  <span className="text-[10px] text-slate-500">{r.lead_phone || "No phone"} &middot; {r.lead_status}</span>
                </div>
                <div className="text-right">
                  <p className="text-xs text-indigo-400">{new Date(r.remind_at).toLocaleDateString()}</p>
                  <p className="text-[10px] text-slate-600">{new Date(r.remind_at).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

//  DashboardPage 
function DashboardPage() {
  const { authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch("/dashboard/stats");
        if (resp.ok) setStats(await resp.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [authFetch]);

  const cards = stats ? [
    { label: "Leads Generated Today", value: stats.leads_generated_today, icon: "M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z", color: "indigo" },
    { label: "Leads Saved", value: stats.leads_saved, icon: "M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z", color: "emerald" },
    { label: "Emails Generated", value: stats.emails_generated, icon: "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z", color: "purple" },
    { label: "Emails Sent", value: stats.emails_sent, icon: "M12 19l9 2-9-18-9 18 9-2zm0 0v-8", color: "rose" },
    { label: "Emails Sent Today", value: stats.emails_sent_today, icon: "M13 10V3L4 14h7v7l9-11h-7z", color: "amber" },
  ] : [];

  const colorMap = {
    indigo: "from-indigo-600/20 to-indigo-800/10 border-indigo-700/50 text-indigo-300",
    emerald: "from-emerald-600/20 to-emerald-800/10 border-emerald-700/50 text-emerald-300",
    purple: "from-purple-600/20 to-purple-800/10 border-purple-700/50 text-purple-300",
    rose: "from-rose-600/20 to-rose-800/10 border-rose-700/50 text-rose-300",
    amber: "from-amber-600/20 to-amber-800/10 border-amber-700/50 text-amber-300",
  };

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <h1 className="text-2xl font-bold text-white">Dashboard</h1>
      {loading ? (
        <div className="flex items-center justify-center py-24">
          <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          {cards.map((c, i) => (
            <div key={i} className={`bg-gradient-to-br ${colorMap[c.color]} border rounded-2xl p-5 flex flex-col gap-3`}>
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={c.icon} />
                </svg>
                <span className="text-xs font-semibold uppercase tracking-wider opacity-70">{c.label}</span>
              </div>
              <span className="text-3xl font-black">{c.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

//  OrganizationPage 
function OrganizationPage() {
  const { authFetch, user } = useAuth();
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingOrg, setEditingOrg] = useState(null);
  const [form, setForm] = useState({ business_name: "", business_bio: "", website: "", org_goal: "" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  // Docs state
  const [docs, setDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(false);
  const [docsError, setDocsError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [maxDocs, setMaxDocs] = useState(3);
  const [pendingUpload, setPendingUpload] = useState({ file: null, name: "", description: "" });
  const [docTab, setDocTab] = useState("file"); // "file" | "text"
  const [textDoc, setTextDoc] = useState({ name: "", description: "", content: "" });
  const [savingText, setSavingText] = useState(false);

  // Links state
  const [links, setLinks] = useState([]);
  const [linksLoading, setLinksLoading] = useState(false);
  const [newLink, setNewLink] = useState({ name: "", url: "" });

  const fetchOrgs = useCallback(async () => {
    try {
      const resp = await authFetch("/organizations");
      if (resp.ok) setOrgs(await resp.json());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleNew = () => {
    setEditingOrg("new");
    setForm({ business_name: "", business_bio: "", website: "", org_goal: "" });
    setError(""); setSaved(false);
  };

  const handleEdit = (org) => {
    setEditingOrg(org);
    setForm({ business_name: org.business_name, business_bio: org.business_bio, website: org.website || "", org_goal: org.org_goal || "" });
    setError(""); setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true); setSaved(false); setError("");
    try {
      if (editingOrg === "new") {
        const resp = await authFetch("/organizations", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
        if (resp.ok) { setSaved(true); fetchOrgs(); setTimeout(() => { setEditingOrg(null); setSaved(false); }, 1500); }
        else { const err = await resp.json().catch(() => ({})); setError(err.detail || "Failed to create organization."); }
      } else {
        const resp = await authFetch(`/organizations/${editingOrg.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
        if (resp.ok) { setSaved(true); fetchOrgs(); setTimeout(() => setSaved(false), 3000); }
        else { const err = await resp.json().catch(() => ({})); setError(err.detail || "Failed to update organization."); }
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleDelete = async (org) => {
    if (!window.confirm(`Delete "${org.business_name || "Untitled"}"? This will also delete its campaign settings.`)) return;
    try {
      const resp = await authFetch(`/organizations/${org.id}`, { method: "DELETE" });
      if (resp.ok) fetchOrgs();
    } catch (e) { console.error(e); }
  };

  const loadDocs = useCallback(async (orgId) => {
    if (!orgId || orgId === "new") return;
    setDocsLoading(true); setDocsError("");
    try {
      const r = await authFetch(`/organizations/${orgId}/documents`);
      if (r.ok) {
        const data = await r.json();
        setDocs(data.docs || []);
        setMaxDocs(data.max_docs || 3);
      } else {
        const err = await r.json().catch(() => ({}));
        setDocsError(err.detail || `Failed to load documents (${r.status}).`);
      }
    } catch (e) { setDocsError("Network error loading documents."); }
    finally { setDocsLoading(false); }
  }, [authFetch]);

  const loadLinks = useCallback(async (orgId) => {
    if (!orgId || orgId === "new") return;
    setLinksLoading(true);
    try {
      const r = await authFetch(`/organizations/${orgId}/links`);
      if (r.ok) setLinks(await r.json());
    } catch (e) { console.error(e); }
    finally { setLinksLoading(false); }
  }, [authFetch]);

  useEffect(() => {
    if (editingOrg && editingOrg !== "new") {
      loadDocs(editingOrg.id);
      loadLinks(editingOrg.id);
    } else {
      setDocs([]); setLinks([]);
    }
  }, [editingOrg, loadDocs, loadLinks]);

  const handleFileSelected = (e) => {
    const file = e.target.files[0];
    if (file) setPendingUpload(p => ({ ...p, file }));
    e.target.value = "";
  };

  const handleSaveTextDoc = async () => {
    if (!textDoc.content.trim() || !editingOrg || editingOrg === "new") return;
    setSavingText(true); setUploadError("");
    try {
      const r = await authFetch(`/organizations/${editingOrg.id}/documents/text`, {
        method: "POST",
        body: JSON.stringify({ doc_name: textDoc.name, description: textDoc.description, content_text: textDoc.content }),
      });
      if (r.ok) {
        setTextDoc({ name: "", description: "", content: "" });
        setDocTab("file");
        await loadDocs(editingOrg.id);
      } else {
        const err = await r.json().catch(() => ({}));
        setUploadError(err.detail || `Failed to save (${r.status}).`);
      }
    } catch (e) { setUploadError(e.message || "Network error."); }
    finally { setSavingText(false); }
  };

  const handleUploadConfirm = async () => {
    if (!pendingUpload.file || !editingOrg || editingOrg === "new") return;
    setUploading(true); setUploadError("");
    const fd = new FormData();
    fd.append("file", pendingUpload.file);
    fd.append("doc_name", pendingUpload.name);
    fd.append("description", pendingUpload.description);
    try {
      const r = await authFetch(`/organizations/${editingOrg.id}/documents`, { method: "POST", body: fd });
      if (r.ok) {
        setPendingUpload({ file: null, name: "", description: "" });
        await loadDocs(editingOrg.id);
      } else {
        const err = await r.json().catch(() => ({}));
        setUploadError(err.detail || `Upload failed (${r.status}).`);
      }
    } catch (err) { setUploadError(err.message || "Network error — please try again."); }
    finally { setUploading(false); }
  };

  const deleteDoc = async (docId) => {
    try {
      await authFetch(`/organizations/${editingOrg.id}/documents/${docId}`, { method: "DELETE" });
      setDocs(d => d.filter(x => x.id !== docId));
    } catch (e) { console.error(e); }
  };

  const addLink = async () => {
    if (!newLink.url.trim() || !editingOrg || editingOrg === "new") return;
    try {
      const r = await authFetch(`/organizations/${editingOrg.id}/links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newLink),
      });
      if (r.ok) { setNewLink({ name: "", url: "" }); await loadLinks(editingOrg.id); }
    } catch (e) { console.error(e); }
  };

  const deleteLink = async (linkId) => {
    try {
      await authFetch(`/organizations/${editingOrg.id}/links/${linkId}`, { method: "DELETE" });
      setLinks(l => l.filter(x => x.id !== linkId));
    } catch (e) { console.error(e); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  // Edit / Create view
  if (editingOrg) {
    return (
      <div className="p-3 sm:p-6 max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button onClick={() => setEditingOrg(null)} className="text-slate-400 hover:text-white transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
          </button>
          <h1 className="text-2xl font-bold text-white">{editingOrg === "new" ? "New Organization" : "Edit Organization"}</h1>
        </div>
        <p className="text-sm text-slate-400">This information is used by the AI to write personalized outreach emails on your behalf.</p>

        {error && <div className="bg-red-900/20 border border-red-700/40 rounded-xl px-4 py-3 text-sm text-red-300">{error}</div>}
        {saved && (
          <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl px-4 py-3 flex items-center gap-2 animate-pulse">
            <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
            <span className="text-sm text-emerald-300 font-semibold">{editingOrg === "new" ? "Organization created!" : "Profile saved successfully!"}</span>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Your Goal <span className="text-indigo-400 font-normal">(what are you trying to achieve with this software?)</span></label>
            <textarea value={form.org_goal} onChange={e => setForm(f => ({ ...f, org_goal: e.target.value }))}
              rows={2}
              className="w-full bg-slate-900 border border-indigo-800/60 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition resize-none"
              placeholder="e.g. Find investors aligned with rune-based products  •  Sell custom booking software to local service businesses  •  Generate leads for a web design agency targeting restaurants • " />
            <p className="text-[11px] text-slate-600 mt-1">The AI uses this to score how well each lead matches what you're trying to do before writing outreach.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Business Name</label>
            <input value={form.business_name} onChange={e => setForm(f => ({ ...f, business_name: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition"
              placeholder="e.g. Acme Marketing Agency" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">What We Do (Business Bio)</label>
            <textarea value={form.business_bio} onChange={e => setForm(f => ({ ...f, business_bio: e.target.value }))}
              rows={4}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition resize-none"
              placeholder="e.g. We help local businesses get more customers through Google Ads and SEO..." />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-400 mb-1">Website</label>
            <input value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))}
              className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition"
              placeholder="https://yourwebsite.com" />
          </div>
        </div>

        {editingOrg !== "new" && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 flex flex-col gap-3">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="text-sm font-bold text-white">Company Documents</h3>
                <p className="text-[11px] text-slate-500 mt-0.5">Upload any file type (PDF, Word, Excel, CSV, TXT–). The AI reads these when writing outreach emails. Name each doc and explain what it's for.</p>
              </div>
              <span className="text-[10px] text-slate-500 shrink-0 mt-0.5">{docs.length}/{maxDocs}</span>
            </div>
            {uploadError && <p className="text-xs text-red-400">{uploadError}</p>}
            {docsError && (
              <div className="flex items-center gap-2">
                <p className="text-xs text-red-400 flex-1">{docsError}</p>
                <button onClick={() => loadDocs(editingOrg.id)} className="text-xs text-indigo-400 hover:text-indigo-300 underline shrink-0">Retry</button>
              </div>
            )}
            {docsLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-xs"><div className="w-3.5 h-3.5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" /> Loading...</div>
            ) : docs.length > 0 && (
              <div className="flex flex-col gap-2">
                {docs.map(doc => (
                  <div key={doc.id} className="bg-slate-800 border border-slate-700 rounded-xl p-3">
                    <div className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-indigo-400 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold text-white truncate">{doc.doc_name || doc.filename}</span>
                          <span className="text-[10px] text-slate-500 shrink-0">{(doc.file_size / 1024).toFixed(1)} KB</span>
                        </div>
                        {doc.doc_name && doc.doc_name !== doc.filename && (
                          <p className="text-[10px] text-slate-500 truncate">{doc.filename}</p>
                        )}
                        {doc.description && (
                          <p className="text-[11px] text-slate-400 mt-0.5">{doc.description}</p>
                        )}
                      </div>
                      <button onClick={() => deleteDoc(doc.id)} className="shrink-0 text-slate-600 hover:text-red-400 transition p-0.5" title="Delete">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {docs.length < maxDocs ? (
              <div className="flex flex-col gap-2">
                <div className="flex gap-1 border-b border-slate-700 mb-1">
                  <button onClick={() => setDocTab("file")} className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg transition ${docTab === "file" ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300"}`}> Upload File</button>
                  <button onClick={() => setDocTab("text")} className={`px-3 py-1.5 text-xs font-semibold rounded-t-lg transition ${docTab === "text" ? "bg-slate-700 text-white" : "text-slate-500 hover:text-slate-300"}`}> Paste Text</button>
                </div>
                {docTab === "file" ? (
                  pendingUpload.file ? (
                    <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <svg className="w-4 h-4 text-indigo-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        <span className="text-xs text-slate-300 font-medium truncate">{pendingUpload.file.name}</span>
                      </div>
                      <input value={pendingUpload.name} onChange={e => setPendingUpload(p => ({ ...p, name: e.target.value }))} placeholder="Document name (e.g. Company Overview)" className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 transition" />
                      <textarea value={pendingUpload.description} onChange={e => setPendingUpload(p => ({ ...p, description: e.target.value }))} placeholder="What is this document? (e.g. 'Our pricing sheet')" rows={2} className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 transition resize-none" />
                      <div className="flex gap-2">
                        <button onClick={handleUploadConfirm} disabled={uploading} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${uploading ? "bg-indigo-800 text-indigo-300" : "bg-indigo-600 hover:bg-indigo-500 text-white"}`}>
                          {uploading ? (<><div className="w-3 h-3 border-2 border-indigo-300 border-t-transparent rounded-full animate-spin" /> Uploading–</>) : "Upload"}
                        </button>
                        <button onClick={() => { setPendingUpload({ file: null, name: "", description: "" }); setUploadError(""); }} className="px-4 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-lg transition">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <label className="flex items-center gap-2 self-start px-4 py-2 rounded-xl text-xs font-bold cursor-pointer transition bg-indigo-700 hover:bg-indigo-600 text-white">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                      Choose File
                      <input type="file" onChange={handleFileSelected} className="hidden" />
                    </label>
                  )
                ) : (
                  <div className="bg-slate-800/60 border border-slate-700 rounded-xl p-3 flex flex-col gap-2">
                    <input value={textDoc.name} onChange={e => setTextDoc(d => ({ ...d, name: e.target.value }))} placeholder="Document name (e.g. Company Overview)" className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 transition" />
                    <textarea value={textDoc.description} onChange={e => setTextDoc(d => ({ ...d, description: e.target.value }))} placeholder="What is this? (e.g. 'Pitch deck summary — mention key stats')" rows={2} className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 transition resize-none" />
                    <textarea value={textDoc.content} onChange={e => setTextDoc(d => ({ ...d, content: e.target.value }))} placeholder="Paste your document content here–" rows={6} className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 transition resize-none" />
                    <div className="flex gap-2">
                      <button onClick={handleSaveTextDoc} disabled={savingText || !textDoc.content.trim()} className={`px-4 py-1.5 rounded-lg text-xs font-bold transition flex items-center gap-1.5 ${savingText ? "bg-indigo-800 text-indigo-300" : "bg-indigo-600 hover:bg-indigo-500 text-white"}`}>
                        {savingText ? (<><div className="w-3 h-3 border-2 border-indigo-300 border-t-transparent rounded-full animate-spin" /> Saving–</>) : "Save Text"}
                      </button>
                      <button onClick={() => { setTextDoc({ name: "", description: "", content: "" }); setUploadError(""); }} className="px-4 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-300 text-xs font-semibold rounded-lg transition">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-amber-500">Document limit reached ({maxDocs}). Delete a document to upload another, or contact your admin.</p>
            )}
          </div>
        )}

        {editingOrg !== "new" && (
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-4 flex flex-col gap-3">
            <div>
              <h3 className="text-sm font-bold text-white">Company Links</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Add LinkedIn, portfolio, case studies, or any relevant links. The AI references these when writing outreach emails.</p>
            </div>
            {linksLoading ? (
              <div className="flex items-center gap-2 text-slate-500 text-xs"><div className="w-3.5 h-3.5 border-2 border-slate-600 border-t-transparent rounded-full animate-spin" /> Loading...</div>
            ) : links.length > 0 && (
              <div className="flex flex-col gap-1.5">
                {links.map(link => (
                  <div key={link.id} className="flex items-center gap-2 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
                    <svg className="w-4 h-4 text-indigo-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" /></svg>
                    <div className="flex-1 min-w-0">
                      {link.name && <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{link.name}</p>}
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="text-xs text-indigo-400 hover:text-indigo-300 truncate block transition">{link.url}</a>
                    </div>
                    <button onClick={() => deleteLink(link.id)} className="shrink-0 text-slate-600 hover:text-red-400 transition p-0.5" title="Delete">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex flex-col gap-2">
              <div className="flex gap-2">
                <input
                  value={newLink.name}
                  onChange={e => setNewLink(l => ({ ...l, name: e.target.value }))}
                  placeholder="Label (e.g. LinkedIn)"
                  className="w-32 bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500 transition"
                />
                <input
                  value={newLink.url}
                  onChange={e => setNewLink(l => ({ ...l, url: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && addLink()}
                  placeholder="https://..."
                  className="flex-1 bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500 transition"
                />
                <button onClick={addLink}
                  className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 text-white text-xs font-bold rounded-xl transition shrink-0">
                  Add
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center gap-3">
          <button onClick={handleSave} disabled={saving}
            className={`px-6 py-2.5 text-white text-sm font-bold rounded-xl transition flex items-center gap-2 ${saving ? "bg-indigo-800" : saved ? "bg-emerald-600" : "bg-indigo-600 hover:bg-indigo-500"}`}>
            {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>) : saved ? (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Saved!</>) : editingOrg === "new" ? "Create Organization" : "Save Profile"}
          </button>
          <button onClick={() => setEditingOrg(null)} className="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-sm font-semibold rounded-xl transition">Cancel</button>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="p-3 sm:p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">My Organizations</h1>
          <p className="text-sm text-slate-400 mt-1">Manage your organizations. Each org can have its own campaign settings.</p>
        </div>
        <button onClick={handleNew}
          className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition flex items-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          New Organization
        </button>
      </div>

      {orgs.length === 0 ? (
        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-10 text-center">
          <svg className="w-12 h-12 text-slate-700 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
          <p className="text-slate-400 text-sm">No organizations yet. Create your first one to start generating personalized outreach.</p>
          <button onClick={handleNew} className="mt-4 px-5 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition">Create Organization</button>
        </div>
      ) : (
        <div className="space-y-3">
          {orgs.map(org => (
            <div key={org.id} className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 flex items-center justify-between group hover:border-slate-700 transition">
              <div className="flex-1 min-w-0">
                <h3 className="text-white font-semibold text-sm truncate">{org.business_name || "Untitled Organization"}</h3>
                <p className="text-slate-500 text-xs mt-0.5 truncate">{org.business_bio ? org.business_bio.slice(0, 80) + (org.business_bio.length > 80 ? "..." : "") : "No description"}</p>
                {org.website && <p className="text-indigo-400/60 text-[11px] mt-0.5 truncate">{org.website}</p>}
              </div>
              <div className="flex items-center gap-2 ml-3">
                <button onClick={() => handleEdit(org)} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-semibold rounded-lg transition">Edit</button>
                <button onClick={() => handleDelete(org)} className="px-3 py-1.5 bg-slate-800 hover:bg-red-900/50 text-slate-400 hover:text-red-400 text-xs font-semibold rounded-lg transition">Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

//  CampaignSettingsPage 
function CampaignSettingsPage() {
  const { authFetch } = useAuth();
  const { canUse } = usePlan();
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [form, setForm] = useState({ objective: "get_reply", custom_objective: "", tone: ["direct", "friendly"], custom_instructions: "", cta_type: "ask_question", custom_cta: "", word_count_range: "100-200", paragraph_range: "2-3", auto_reply_enabled: false, auto_reply_instructions: "", max_auto_replies: 3 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch("/organizations");
        if (resp.ok) {
          const data = await resp.json();
          setOrgs(data);
          if (data.length > 0) setSelectedOrgId(data[0].id);
        }
      } catch (e) { console.error(e); }
    })();
  }, [authFetch]);

  useEffect(() => {
    if (selectedOrgId === null) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const resp = await authFetch(`/campaign-settings?org_id=${selectedOrgId}`);
        if (resp.ok) setForm(await resp.json());
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [authFetch, selectedOrgId]);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      const resp = await authFetch("/campaign-settings", { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...form, org_id: selectedOrgId }) });
      if (resp.ok) { setSaved(true); setTimeout(() => setSaved(false), 3000); }
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const objectives = [
    { value: "book_meeting", label: "Book a Meeting" },
    { value: "show_demo", label: "Show a Demo" },
    { value: "get_reply", label: "Get a Reply" },
    { value: "close_sale", label: "Close a Sale" },
    { value: "custom", label: "Custom Objective" },
  ];
  const tones = ["casual", "professional", "direct", "friendly", "witty", "formal"];
  const ctaTypes = [
    { value: "quick_call", label: "Ask for a Quick Call" },
    { value: "see_demo", label: "Invite to See a Demo" },
    { value: "ask_question", label: "Ask a Question" },
    { value: "custom", label: "Custom CTA" },
  ];

  const toggleTone = (t) => setForm(f => ({ ...f, tone: f.tone.includes(t) ? f.tone.filter(x => x !== t) : [...f.tone, t] }));

  if (loading) return <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-3 sm:p-6 max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-white">Campaign Settings</h1>
      <p className="text-sm text-slate-400">Configure how the AI generates your outreach messages.</p>

      {/* Organization Selector */}
      {orgs.length > 1 && (
        <div>
          <label className="block text-xs font-semibold text-slate-400 mb-2">Organization</label>
          <div className="flex flex-wrap gap-2">
            {orgs.map(o => (
              <button key={o.id} onClick={() => setSelectedOrgId(o.id)}
                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${selectedOrgId === o.id ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                {o.business_name || "Untitled"}
              </button>
            ))}
          </div>
        </div>
      )}
      {orgs.length === 0 && (
        <div className="bg-amber-900/20 border border-amber-700/40 rounded-xl px-4 py-3 text-sm text-amber-300">
          Please create an organization profile first to configure campaign settings.
        </div>
      )}

      {/* Objective */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Campaign Objective</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {objectives.map(o => (
            <button key={o.value} onClick={() => setForm(f => ({ ...f, objective: o.value }))}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${form.objective === o.value ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {o.label}
            </button>
          ))}
        </div>
        {form.objective === "custom" && (
          <input value={form.custom_objective} onChange={e => setForm(f => ({ ...f, custom_objective: e.target.value }))}
            className="mt-2 w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500"
            placeholder="Describe your custom objective..." />
        )}
      </div>

      {/* Tone */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Tone (select multiple)</label>
        <div className="flex flex-wrap gap-2">
          {tones.map(t => (
            <button key={t} onClick={() => toggleTone(t)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition capitalize ${form.tone.includes(t) ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Call to Action</label>
        <div className="grid grid-cols-2 gap-2">
          {ctaTypes.map(c => (
            <button key={c.value} onClick={() => setForm(f => ({ ...f, cta_type: c.value }))}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${form.cta_type === c.value ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {c.label}
            </button>
          ))}
        </div>
        {form.cta_type === "custom" && (
          <input value={form.custom_cta} onChange={e => setForm(f => ({ ...f, custom_cta: e.target.value }))}
            className="mt-2 w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500"
            placeholder="e.g. Would you be open to a 10-min chat this week?" />
        )}
      </div>

      {/* Custom Instructions */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1">Custom Instructions (optional)</label>
        <textarea value={form.custom_instructions} onChange={e => setForm(f => ({ ...f, custom_instructions: e.target.value }))}
          rows={3}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition resize-none"
          placeholder="Any extra instructions for the AI, e.g. mention a specific service, avoid certain topics..." />
      </div>

      {/* Word Count Range */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Email Word Count</label>
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
          {["50-100","100-200","200-300","300-400","400-600","600-800","800-1000","1000-2000"].map(r => (
            <button key={r} onClick={() => setForm(f => ({ ...f, word_count_range: r }))}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${form.word_count_range === r ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {r} words
            </button>
          ))}
        </div>
      </div>

      {/* Paragraph Range */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Number of Paragraphs</label>
        <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
          {["1-2","2-3","3-4","4-5","5-6","6-7","7-8"].map(r => (
            <button key={r} onClick={() => setForm(f => ({ ...f, paragraph_range: r }))}
              className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${form.paragraph_range === r ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {r} paragraphs
            </button>
          ))}
        </div>
      </div>

      {/* Auto Reply */}
      <div className={`bg-slate-900 border border-slate-700 rounded-2xl p-5 space-y-4 ${!canUse('pro') ? 'opacity-70' : ''}`}>
        {!canUse('pro') && <UpgradeBanner requiredPlan="pro" featureName="AI Auto Reply" />}
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-bold text-white text-sm">⚡ Auto Reply</h3>
            <p className="text-xs text-slate-500 mt-0.5">When a lead replies, AI automatically sends a follow-up reply on your behalf.</p>
          </div>
          <button onClick={() => canUse('pro') && setForm(f => ({ ...f, auto_reply_enabled: !f.auto_reply_enabled }))}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.auto_reply_enabled && canUse('pro') ? "bg-indigo-600" : "bg-slate-700"} ${!canUse('pro') ? 'cursor-not-allowed' : ''}`}>
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.auto_reply_enabled && canUse('pro') ? "translate-x-6" : "translate-x-1"}`}/>
          </button>
        </div>
        {form.auto_reply_enabled && (
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Goal / Instructions for Auto Reply</label>
              <textarea
                value={form.auto_reply_instructions}
                onChange={e => setForm(f => ({ ...f, auto_reply_instructions: e.target.value }))}
                rows={3}
                placeholder="e.g. 'Try to book a 15-minute demo call. If they ask about pricing, share that we offer a free 7-day trial at timingoflow.com/demo'"
                className="w-full bg-slate-800 border border-slate-700 text-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 placeholder-slate-600 resize-none mt-1"
              />
            </div>
            <div className="flex items-center gap-4">
              <div className="flex-1">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Max AI Replies per Lead</label>
                <input type="number" min={1} max={10} value={form.max_auto_replies}
                  onChange={e => setForm(f => ({ ...f, max_auto_replies: parseInt(e.target.value) || 3 }))}
                  className="w-24 bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 rounded-lg outline-none focus:border-indigo-500" />
                <p className="text-[11px] text-slate-600 mt-1">AI stops after this many exchanges and notifies you to take over.</p>
              </div>
            </div>
            <div className="bg-amber-950/30 border border-amber-800/40 rounded-xl px-4 py-3 text-xs text-amber-300 space-y-1">
              <p className="font-bold">🛑 Auto Reply stops automatically when:</p>
              <p>• Max replies reached (you get an email notification)</p>
              <p>• Lead shows booking intent — e.g. "can we book a call?", "what's your pricing?" (lead moves to Interested)</p>
              <p>• Lead replies "not interested" or unsubscribes</p>
            </div>
            <p className="text-[11px] text-slate-600">The AI will read their reply and your original email, then craft a personalised response. When it stops, you get a "Hot lead" alert email.</p>
          </div>
        )}
      </div>

      {saved && (
        <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl px-4 py-3 flex items-center gap-2 animate-pulse">
          <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          <span className="text-sm text-emerald-300 font-semibold">Settings saved successfully!</span>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={handleSave} disabled={saving}
          className={`px-6 py-2.5 text-white text-sm font-bold rounded-xl transition flex items-center gap-2 ${saving ? "bg-indigo-800" : saved ? "bg-emerald-600" : "bg-indigo-600 hover:bg-indigo-500"}`}>
          {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>) : saved ? (<><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Saved!</>) : "Save Settings"}
        </button>
      </div>
    </div>
  );
}

// ─── LinkedInOutreachPage ────────────────────────────────────────────────────
function LinkedInOutreachPage() {
  const { authFetch } = useAuth();
  const [form, setForm] = useState({
    linkedin_founder_name: "",
    linkedin_demo_link: "",
    linkedin_pitch: "",
    linkedin_tone: "friendly",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    authFetch("/linkedin/settings")
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setForm(f => ({
          ...f,
          linkedin_founder_name: data.linkedin_founder_name || "",
          linkedin_demo_link: data.linkedin_demo_link || "",
          linkedin_pitch: data.linkedin_pitch || "",
          linkedin_tone: data.linkedin_tone || "friendly",
        }));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [authFetch]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authFetch("/linkedin/settings", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center py-24"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>;

  const tones = ["friendly", "professional", "direct", "casual"];

  return (
    <div className="p-3 sm:p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white">LinkedIn Outreach</h1>
        <p className="text-sm text-slate-400 mt-1">Configure the Walk Free Chrome extension — tells the AI how to write your personalised LinkedIn messages.</p>
      </div>

      {/* Extension install banner */}
      <div className="bg-indigo-900/20 border border-indigo-700/40 rounded-xl p-4 flex items-start gap-3">
        <div className="text-2xl">🔗</div>
        <div>
          <p className="text-sm font-semibold text-indigo-300">Walk Free Chrome Extension</p>
          <p className="text-xs text-slate-400 mt-0.5">Install the extension, sign in with your TimingoFlow account, then visit any LinkedIn profile to generate a personalised outreach message instantly.</p>
        </div>
      </div>

      {/* Founder name */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1">Your name (as it appears in messages)</label>
        <input
          value={form.linkedin_founder_name}
          onChange={e => setForm(f => ({ ...f, linkedin_founder_name: e.target.value }))}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500 transition"
          placeholder="e.g. Ridwan Oyenuga" />
        <p className="text-[11px] text-slate-500 mt-0.5">The AI will sign off messages with this name.</p>
      </div>

      {/* Demo link */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1">Demo / product link (optional)</label>
        <input
          value={form.linkedin_demo_link}
          onChange={e => setForm(f => ({ ...f, linkedin_demo_link: e.target.value }))}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-2.5 text-sm outline-none focus:border-indigo-500 transition"
          placeholder="https://..." />
        <p className="text-[11px] text-slate-500 mt-0.5">When present, the AI will naturally include this link in the outreach message.</p>
      </div>

      {/* Pitch */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-1">Your pitch / value proposition</label>
        <textarea
          value={form.linkedin_pitch}
          onChange={e => setForm(f => ({ ...f, linkedin_pitch: e.target.value }))}
          rows={4}
          className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-4 py-3 text-sm outline-none focus:border-indigo-500 transition resize-none"
          placeholder="e.g. We help B2B SaaS companies find and reach 500+ qualified leads per month with AI-personalised email outreach — without any manual prospecting." />
        <p className="text-[11px] text-slate-500 mt-0.5">This is the core message the AI weaves into every LinkedIn outreach. Be specific about who you help and the outcome you deliver.</p>
      </div>

      {/* Default tone */}
      <div>
        <label className="block text-xs font-semibold text-slate-400 mb-2">Default tone</label>
        <div className="flex flex-wrap gap-2">
          {tones.map(t => (
            <button key={t} onClick={() => setForm(f => ({ ...f, linkedin_tone: t }))}
              className={`px-4 py-1.5 rounded-lg text-xs font-semibold border transition capitalize ${form.linkedin_tone === t ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {t}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-slate-500 mt-1">You can override this per-message in the extension popup.</p>
      </div>

      {saved && (
        <div className="bg-emerald-900/20 border border-emerald-700/40 rounded-xl px-4 py-3 flex items-center gap-2">
          <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          <span className="text-sm text-emerald-300 font-semibold">Settings saved!</span>
        </div>
      )}

      <button onClick={handleSave} disabled={saving}
        className={`px-6 py-2.5 text-white text-sm font-bold rounded-xl transition flex items-center gap-2 ${saving ? "bg-indigo-800" : "bg-indigo-600 hover:bg-indigo-500"}`}>
        {saving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>) : "Save Settings"}
      </button>
    </div>
  );
}

//  OutreachPage
function OutreachPage({ folders, refreshFolders }) {
  const { authFetch, user } = useAuth();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [selectedFolders, setSelectedFolders] = useState(new Set());
  const [messages, setMessages] = useState([]);
  const [folderStats, setFolderStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [queueStatus, setQueueStatus] = useState(null);
  const [queueErrors, setQueueErrors] = useState([]);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ subject: "", body: "", cc: "", bcc: "", reply_to: "" });
  const [error, setError] = useState("");
  const [genResult, setGenResult] = useState(null);
  const [tab, setTab] = useState("messages"); // "messages" | "leads"
  const [selectedMsgs, setSelectedMsgs] = useState(new Set());
  const [queuing, setQueuing] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNoSenderConfirm, setShowNoSenderConfirm] = useState(false);
  const [bulkFieldsModal, setBulkFieldsModal] = useState(null); // null | "cc" | "bcc" | "reply_to"
  const [bulkFieldValue, setBulkFieldValue] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [senders, setSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [selectedSenderIds, setSelectedSenderIds] = useState(new Set()); // For multi-sender auto-retry
  const [previewTrackedHtml, setPreviewTrackedHtml] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [autoRetrySaving, setAutoRetrySaving] = useState(false);
  const [sentSearch, setSentSearch] = useState("");
  const [sentCountry, setSentCountry] = useState("");
  const [sentEngagement, setSentEngagement] = useState("all");
  const [generatingFollowups, setGeneratingFollowups] = useState(false);
  const [followupInstructions, setFollowupInstructions] = useState("");
  const [recalling, setRecalling] = useState(false);
  const [selectedQueuedMsgs, setSelectedQueuedMsgs] = useState(new Set());

  // Excel import state
  const [viewSentEmail, setViewSentEmail] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importContacts, setImportContacts] = useState([]);
  const [importParsing, setImportParsing] = useState(false);
  const [importGenerating, setImportGenerating] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState("");
  const [importTone, setImportTone] = useState("friendly");
  const [importLength, setImportLength] = useState("100-150");
  const [importCta, setImportCta] = useState("book_meeting");
  const [importBookingLink, setImportBookingLink] = useState("");
  const [importExtraLink, setImportExtraLink] = useState("");
  const [importInstructions, setImportInstructions] = useState("");
  const [importFolderName, setImportFolderName] = useState("");

  useEffect(() => {
    authFetch("/settings/email-senders")
      .then(r => r.ok ? r.json() : [])
      .then(data => setSenders(Array.isArray(data) ? data.filter(s => s.is_active) : []))
      .catch(() => {});
  }, [authFetch]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await authFetch("/organizations");
        if (resp.ok) {
          const data = await resp.json();
          setOrgs(data);
          if (data.length > 0) setSelectedOrgId(data[0].id);
        }
      } catch (e) { console.error(e); }
    })();
  }, [authFetch]);


  const handleBulkUpdateFields = async (field) => {
    const ids = [...selectedMsgs];
    if (!ids.length) return;
    try {
      await authFetch("/outreach/messages/bulk-update-fields", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_ids: ids, [field]: bulkFieldValue })
      });
      fetchMessages(selectedFolder);
      setBulkFieldsModal(null);
      setBulkFieldValue("");
    } catch (e) { setError(e.message); }
  };

  const fetchMessages = useCallback(async (folderId, { showLoader = true } = {}) => {
    if (showLoader) setLoading(true);
    try {
      const url = folderId ? `/outreach/messages?folder_id=${folderId}` : "/outreach/messages";
      const resp = await authFetch(url);
      if (resp.ok) { setMessages(await resp.json()); setSelectedMsgs(new Set()); }
    } catch (e) { console.error(e); }
    finally { if (showLoader) setLoading(false); }
  }, [authFetch]);

  const fetchFolderStats = useCallback(async (folderId) => {
    if (!folderId) { setFolderStats(null); return; }
    try {
      const resp = await authFetch(`/outreach/folder-stats/${folderId}`);
      if (resp.ok) setFolderStats(await resp.json());
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const fetchQueueStatus = useCallback(async () => {
    try {
      const resp = await authFetch("/outreach/queue-status");
      if (resp.ok) setQueueStatus(await resp.json());
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const handleToggleAutoRetry = async () => {
    if (!queueStatus || autoRetrySaving) return;
    const nextEnabled = !Boolean(queueStatus.auto_retry_failed_emails);
    setAutoRetrySaving(true);
    try {
      const resp = await authFetch("/outreach/queue-auto-retry", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: nextEnabled }),
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => ({}));
        setQueueStatus((prev) => prev ? { ...prev, auto_retry_failed_emails: Boolean(data.enabled) } : prev);
        await fetchQueueStatus();
        await fetchQueueErrors();
        await fetchMessages(selectedFolder);
      }
    } catch (e) {
      setError(e.message || "Failed to update auto-retry setting");
    } finally {
      setAutoRetrySaving(false);
    }
  };

  const fetchQueueErrors = useCallback(async () => {
    try {
      const resp = await authFetch("/outreach/queue-errors");
      if (resp.ok) setQueueErrors(await resp.json());
    } catch (e) { console.error(e); }
  }, [authFetch]);

  useEffect(() => {
    fetchMessages(selectedFolder, { showLoader: true });
    fetchFolderStats(selectedFolder);
    fetchQueueStatus();
    fetchQueueErrors();
    setGenResult(null); setError("");
  }, [selectedFolder, fetchMessages, fetchFolderStats, fetchQueueStatus, fetchQueueErrors]);

  // Auto-refresh outreach data every 10 seconds without full-page flicker
  useEffect(() => {
    const interval = setInterval(() => {
      fetchMessages(selectedFolder, { showLoader: false });
      fetchFolderStats(selectedFolder);
      fetchQueueStatus();
      fetchQueueErrors();
    }, 10000);
    return () => clearInterval(interval);
  }, [selectedFolder, fetchMessages, fetchFolderStats, fetchQueueStatus, fetchQueueErrors]);

  const handleGenerate = async () => {
    const folderIds = [...selectedFolders];
    if (folderIds.length === 0) return;
    setGenerating(true); setError(""); setGenResult(null);
    try {
      const resp = await authFetch("/outreach/generate", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ folder_ids: folderIds, org_id: selectedOrgId })
      });
      if (resp.ok) {
        const data = await resp.json();
        setGenResult(data);
        if (selectedFolder) {
          await fetchMessages(selectedFolder);
          await fetchFolderStats(selectedFolder);
        }
        if (refreshFolders) refreshFolders();
      } else {
        const err = await resp.json().catch(() => ({}));
        setError(err.detail || "Generation failed.");
      }
    } catch (e) { setError(e.message); }
    finally { setGenerating(false); }
  };

  const handleApprove = async (id) => {
    await authFetch(`/outreach/messages/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ approved: true }) });
    fetchMessages(selectedFolder);
  };
  const handleDelete = async (id) => {
    await authFetch(`/outreach/messages/${id}`, { method: "DELETE" });
    fetchMessages(selectedFolder);
    fetchFolderStats(selectedFolder);
  };
  const handleEditSave = async (id) => {
    await authFetch(`/outreach/messages/${id}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify(editForm) });
    setEditingId(null);
    fetchMessages(selectedFolder);
  };
  const handleQueueSelected = async () => {
    const approvedIds = messages.filter(m => m.approved && m.status === "approved").map(m => m.id);
    if (!approvedIds.length) { setError("Approve messages before queuing."); return; }
    setError(""); setQueuing(true);
    try {
      // Pass sender_ids if multi-select enabled (for auto-retry rotation)
      const payload = { message_ids: approvedIds };
      if (selectedSenderIds.size > 0) {
        payload.sender_ids_to_try = Array.from(selectedSenderIds);
      }
      const resp = await authFetch("/outreach/queue", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (resp.ok) { fetchMessages(selectedFolder); fetchQueueStatus(); }
      else { const err = await resp.json().catch(() => ({})); setError(err.detail || "Queue failed."); }
    } catch (e) { setError(e.message); }
    finally { setQueuing(false); }
  };
  const handleSendQueued = async (forceProceed = false) => {
    if (senders.length === 0 && !forceProceed) { setShowNoSenderConfirm(true); return; }
    setShowNoSenderConfirm(false);
    setError(""); setSending(true);
    try {
      const payload = {};
      if (selectedSenderId) payload.sender_id = selectedSenderId;
      if (selectedSenderIds.size > 0) payload.sender_ids = Array.from(selectedSenderIds);
      
      const resp = await authFetch("/outreach/send-queued", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload)
      });
      const data = await resp.json();
      if (data.errors && data.errors.length > 0) {
        setError(`Sent: ${data.sent || 0}. Issues: ${data.errors.join(" | ")}`);
      }
      fetchMessages(selectedFolder); fetchQueueStatus(); fetchQueueErrors();
    } catch (e) { setError(e.message); }
    finally { setSending(false); }
  };

  const toggleSelectMsg = (id) => {
    setSelectedMsgs(prev => { const s = new Set(prev); if (s.has(id)) s.delete(id); else s.add(id); return s; });
  };
  const toggleSelectAll = () => {
    if (selectedMsgs.size === messages.length) setSelectedMsgs(new Set());
    else setSelectedMsgs(new Set(messages.map(m => m.id)));
  };
  const handleBulkApprove = async (ids) => {
    if (!ids.length) return;
    try {
      await authFetch("/outreach/messages/bulk-approve", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message_ids: ids }) });
      fetchMessages(selectedFolder);
    } catch (e) { setError(e.message); }
  };
  const handleBulkDelete = async (ids) => {
    if (!ids.length) return;
    try {
      await authFetch("/outreach/messages/bulk-delete", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message_ids: ids }) });
      fetchMessages(selectedFolder); fetchFolderStats(selectedFolder);
    } catch (e) { setError(e.message); }
  };

  const statusBadge = (status) => {
    const map = { draft: "bg-slate-700 text-slate-300", approved: "bg-emerald-900/60 text-emerald-300", queued: "bg-amber-900/60 text-amber-300", sent: "bg-indigo-900/60 text-indigo-300" };
    return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${map[status] || map.draft}`}>{status}</span>;
  };

  const pendingMessages = messages.filter(m => m.status !== "sent");
  const queuedMessages = messages.filter(m => m.status === "queued");
  const sentMessages = [...messages.filter(m => m.status === "sent")].sort((a, b) => {
    const clickDelta = (b.click_count || 0) - (a.click_count || 0);
    if (clickDelta !== 0) return clickDelta;
    const openDelta = (b.open_count || 0) - (a.open_count || 0);
    if (openDelta !== 0) return openDelta;
    return new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0);
  });
  const filteredSentMessages = sentMessages.filter((m) => {
    const search = sentSearch.trim().toLowerCase();
    const country = sentCountry.trim().toLowerCase();
    if (search) {
      const hay = `${m.lead_name || ""} ${m.lead_email || ""}`.toLowerCase();
      if (!hay.includes(search)) return false;
    }
    if (country && !String(m.lead_country || "").toLowerCase().includes(country)) return false;

    if (sentEngagement === "opened" && (m.open_count || 0) <= 0) return false;
    if (sentEngagement === "clicked" && (m.click_count || 0) <= 0) return false;
    if (sentEngagement === "not_opened" && (m.open_count || 0) > 0) return false;
    if (sentEngagement === "not_clicked" && (m.click_count || 0) > 0) return false;
    return true;
  });
  const openedSentCount = filteredSentMessages.filter(m => (m.open_count || 0) > 0).length;
  const clickedSentCount = filteredSentMessages.filter(m => (m.click_count || 0) > 0).length;

  const formatTrackingTime = (value) => (value ? new Date(value).toLocaleString() : null);
  const compactTrackingUrl = (value) => {
    try {
      const parsed = new URL(value);
      return `${parsed.hostname}${parsed.pathname && parsed.pathname !== "/" ? parsed.pathname : ""}`;
    } catch {
      return value;
    }
  };

  const handlePreviewTrackedHtml = async (messageId) => {
    try {
      const resp = await authFetch(`/outreach/messages/${messageId}/preview-tracked-html`);
      if (resp.ok) {
        const data = await resp.json();
        setPreviewTrackedHtml(data);
        setShowPreviewModal(true);
      } else {
        setError("Failed to load preview");
      }
    } catch (e) {
      setError("Error loading preview: " + e.message);
    }
  };

  const handleGenerateFollowups = async () => {
    if (!filteredSentMessages.length) {
      setError("No sent messages match your follow-up filters.");
      return;
    }
    setGeneratingFollowups(true);
    setError("");
    try {
      const resp = await authFetch("/outreach/followups/generate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message_ids: filteredSentMessages.map(m => m.id),
          org_id: selectedOrgId || null,
          custom_instructions: followupInstructions || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        setError(data.detail || "Failed to generate follow-ups.");
        return;
      }
      const errText = (data.errors || []).slice(0, 3).join(" | ");
      setError(`Follow-ups generated: ${data.generated || 0}. Skipped: ${data.skipped || 0}.${errText ? ` Issues: ${errText}` : ""}`);
      await fetchMessages(selectedFolder);
      await fetchFolderStats(selectedFolder);
      setTab("messages");
    } catch (e) {
      setError(e.message || "Failed to generate follow-ups.");
    } finally {
      setGeneratingFollowups(false);
    }
  };

  const handleGenerateFromImport = async () => {
    setImportGenerating(true); setImportError("");
    try {
      const resp = await authFetch("/outreach/generate-from-import", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          contacts: importContacts,
          org_id: selectedOrgId || null,
          tone: importTone,
          word_count_range: importLength,
          cta_type: importCta,
          custom_instructions: importInstructions || "",
          booking_link: importBookingLink || "",
          extra_link: importExtraLink || "",
          folder_name: importFolderName || "",
        })
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.detail || "Generation failed");
      setImportResult(data);
    } catch (e) { setImportError(e.message); }
    finally { setImportGenerating(false); }
  };

  return (
    <div className="p-3 sm:p-6 space-y-5">
      {/* No-sender confirmation modal */}
      {showNoSenderConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-sm flex flex-col gap-4 shadow-2xl">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-amber-900/40 border border-amber-700/40 flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-amber-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-bold text-white">No email sender configured</h3>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">
                  Emails will go out from the <span className="text-amber-300 font-semibold">TimingoFlow platform address</span>. You will automatically be <span className="font-semibold text-slate-200">CC'd and set as reply-to</span> at <span className="text-indigo-300 font-semibold">{user?.email}</span> – so all replies and copies come straight to you.
                </p>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                  Want to send from your own address? <span className="text-indigo-400 underline cursor-pointer" onClick={() => setShowNoSenderConfirm(false)}>Configure a sender in Settings</span>. Need help? Contact <span className="text-indigo-400">team@timingoflow.com</span>.
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleSendQueued(true)}
                className="flex-1 py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl text-sm font-bold transition">
                Proceed Anyway
              </button>
              <button onClick={() => setShowNoSenderConfirm(false)}
                className="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-sm font-bold transition">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">AI Outreach</h1>
          <button onClick={() => { setShowImportModal(true); setImportResult(null); setImportContacts([]); setImportError(""); }}
            className="px-3 py-1.5 bg-emerald-900/40 hover:bg-emerald-800/50 border border-emerald-700/50 text-emerald-300 text-xs font-bold rounded-lg transition flex items-center gap-1.5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
            Import Excel
          </button>
        </div>
        {queueStatus && (
          <div className="flex gap-3 text-xs items-center flex-wrap">
            <span className="text-slate-400">Sent today: <span className="text-white font-bold">{queueStatus.sent_today}/{queueStatus.daily_limit}</span></span>
            <span className="text-slate-400">Pending: <span className="text-amber-400 font-bold">{queueStatus.pending}</span></span>
            <button
              onClick={handleToggleAutoRetry}
              disabled={autoRetrySaving}
              className={`px-2 py-0.5 rounded text-[10px] font-bold transition border ${queueStatus.auto_retry_failed_emails ? "bg-emerald-900/30 border-emerald-700/50 text-emerald-300 hover:bg-emerald-800/35" : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"} ${autoRetrySaving ? "opacity-60 cursor-not-allowed" : ""}`}
              title="Automatically retry failed emails before marking them as failed"
            >
              Auto Retry: {queueStatus.auto_retry_failed_emails ? "On" : "Off"}
            </button>
            {queueStatus.failed > 0 && <span className="text-red-400">Failed: <span className="font-bold">{queueStatus.failed}</span>
              <button onClick={() => setShowErrorDetails(!showErrorDetails)}
                className="ml-1.5 px-2 py-0.5 bg-blue-900/30 hover:bg-blue-800/40 text-blue-300 rounded text-[10px] font-bold transition">{showErrorDetails ? 'Hide' : 'Show'} Errors</button>
              <button onClick={async () => { await authFetch("/outreach/queue-retry", { method: "POST" }); fetchMessages(selectedFolder); fetchQueueStatus(); fetchQueueErrors(); }}
                className="ml-1.5 px-2 py-0.5 bg-red-900/30 hover:bg-red-800/40 text-red-300 rounded text-[10px] font-bold transition">Retry All</button>
            </span>}
          </div>
        )}
      </div>

      {/* Error Details Panel */}
      {showErrorDetails && queueErrors.length > 0 && (
        <div className="bg-red-950/30 border border-red-900/50 rounded-xl p-4">
          <h3 className="text-sm font-bold text-red-400 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd"/>
            </svg>
            Failed Email Details ({queueErrors.length})
          </h3>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {queueErrors.map((err, idx) => (
              <div key={err.id || idx} className="bg-slate-900/60 border border-red-900/30 rounded-lg p-3">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-white">{err.lead_name || 'Unknown Lead'}</p>
                    <p className="text-xs text-slate-400">{err.lead_email}</p>
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await authFetch(`/outreach/queue-retry-single/${err.id}`, { method: "POST" });
                        fetchQueueStatus();
                        fetchQueueErrors();
                        fetchMessages(selectedFolder);
                      } catch (e) { console.error(e); }
                    }}
                    className="px-2 py-1 bg-amber-900/30 hover:bg-amber-800/40 text-amber-300 rounded text-xs font-semibold transition">
                    Retry
                  </button>
                </div>
                <div className="bg-red-950/40 border border-red-900/20 rounded p-2">
                  <p className="text-xs font-mono text-red-200">{err.error}</p>
                </div>
                {err.scheduled_at && (
                  <p className="text-[10px] text-slate-500 mt-1">Failed at: {new Date(err.scheduled_at).toLocaleString()}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Folder Selection (multi-select for generation) */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-xs text-slate-500 font-semibold mr-1">Folders:</span>
        {(folders || []).map(f => {
          const isSelected = selectedFolders.has(f.id);
          const gen = f.generated_count || 0;
          const total = f.email_leads || 0;
          const allDone = total > 0 && gen >= total;
          const noneDone = gen === 0;
          return (
            <button key={f.id} onClick={() => {
              setSelectedFolders(prev => {
                const next = new Set(prev);
                if (next.has(f.id)) { next.delete(f.id); } else { next.add(f.id); }
                if (next.size === 1) { setSelectedFolder([...next][0]); }
                else if (next.size === 0) { setSelectedFolder(null); }
                else { setSelectedFolder(null); }
                return next;
              });
            }}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition flex items-center gap-1.5 ${isSelected ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: f.color }} />
              {isSelected && <span></span>}
              <span>{f.name}</span>
              {total > 0 && (
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${allDone ? "bg-emerald-900/60 text-emerald-300" : noneDone ? "bg-red-900/40 text-red-400" : "bg-amber-900/40 text-amber-300"}`}>
                  {gen}/{total}
                </span>
              )}
            </button>
          );
        })}
        <button onClick={() => { setSelectedFolders(new Set()); setSelectedFolder(null); }}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${selectedFolders.size === 0 ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
          All Messages
        </button>
        {selectedFolders.size > 1 && (
          <span className="text-[11px] text-indigo-400 font-semibold ml-1">{selectedFolders.size} folders selected</span>
        )}
      </div>

      {/* Organization Selector for generation */}
      {orgs.length > 1 && (
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-xs text-slate-500 font-semibold mr-1">Generate as:</span>
          {orgs.map(o => (
            <button key={o.id} onClick={() => setSelectedOrgId(o.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${selectedOrgId === o.id ? "bg-purple-900/60 border-purple-600 text-purple-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
              {o.business_name || "Untitled"}
            </button>
          ))}
        </div>
      )}

      {/* Folder Stats Card - only when a specific folder is selected */}
      {selectedFolder && folderStats && (
        <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div className="text-center">
              <p className="text-xl font-black text-white">{folderStats.total_leads}</p>
              <p className="text-[10px] text-slate-500 font-semibold">Total Leads</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-emerald-400">{folderStats.with_email}</p>
              <p className="text-[10px] text-slate-500 font-semibold">Have Email</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-red-400">{folderStats.without_email}</p>
              <p className="text-[10px] text-slate-500 font-semibold">No Email</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-indigo-400">{folderStats.already_generated}</p>
              <p className="text-[10px] text-slate-500 font-semibold">Generated</p>
            </div>
            <div className="text-center">
              <p className="text-xl font-black text-amber-400">{folderStats.ready_to_generate}</p>
              <p className="text-[10px] text-slate-500 font-semibold">Ready to Generate</p>
            </div>
          </div>
          {folderStats.with_email === 0 && (
            <div className="mt-3 bg-red-900/20 border border-red-800/40 rounded-xl px-3 py-2 text-xs text-red-300">
              No leads in this folder have email addresses. AI outreach requires leads with emails. Go to <strong>Saved Leads</strong> and add leads with emails to this folder.
            </div>
          )}
          {folderStats.with_email > 0 && folderStats.ready_to_generate === 0 && folderStats.already_generated > 0 && (
            <div className="mt-3 bg-indigo-900/20 border border-indigo-800/40 rounded-xl px-3 py-2 text-xs text-indigo-300">
              All leads with emails already have generated messages. Check the Messages tab below.
            </div>
          )}
        </div>
      )}

      {error && <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-2.5 text-sm">{error}</div>}
      {genResult && (
        <div className={`${genResult.generated > 0 ? "bg-emerald-900/20 border-emerald-700/40 text-emerald-300" : "bg-amber-900/20 border-amber-700/40 text-amber-300"} border rounded-xl px-4 py-2.5 text-sm`}>
          Generated {genResult.generated} message{genResult.generated !== 1 ? "s" : ""} out of {genResult.total_leads} lead{genResult.total_leads !== 1 ? "s" : ""}.
          {genResult.skipped_no_email > 0 && ` ${genResult.skipped_no_email} skipped (no email).`}
          {genResult.skipped_existing > 0 && ` ${genResult.skipped_existing} skipped (already generated).`}
          {genResult.messages.filter(m => m.error).length > 0 && ` ${genResult.messages.filter(m => m.error).length} failed.`}
        </div>
      )}

      {/* Sender picker / default notice */}
      {senders.length > 0 ? (
        <div className="space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500 shrink-0">Single send (each email):</span>
            <button onClick={() => setSelectedSenderId(null)}
              className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${!selectedSenderId ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
               Auto (rotate)
            </button>
            {senders.map(s => (
              <button key={s.id} onClick={() => setSelectedSenderId(s.id)}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${selectedSenderId === s.id ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                {s.from_email}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[11px] text-slate-500 shrink-0">Or: Auto-retry with multiple senders (click to toggle):</span>
            {senders.map(s => (
              <button key={`multi_${s.id}`} onClick={() => {
                setSelectedSenderIds(prev => {
                  const newSet = new Set(prev);
                  newSet.has(s.id) ? newSet.delete(s.id) : newSet.add(s.id);
                  return newSet;
                });
              }}
                className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${selectedSenderIds.has(s.id) ? "bg-emerald-700 border-emerald-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}
                title="Click to select for auto-retry rotation">
                {selectedSenderIds.has(s.id) ? "" : ""} {s.from_email}
              </button>
            ))}
            {selectedSenderIds.size > 0 && (
              <p className="text-[10px] text-emerald-400 w-full">Selected {selectedSenderIds.size} sender{selectedSenderIds.size > 1 ? "s" : ""} for auto-retry rotation</p>
            )}
          </div>
        </div>
      ) : (
        queueStatus && queueStatus.pending > 0 && (
          <div className="bg-amber-900/25 border border-amber-700/35 rounded-xl px-4 py-3 flex flex-col gap-1 max-w-xl">
            <p className="text-xs font-bold text-amber-300"> No email sender configured</p>
            <p className="text-xs text-amber-200/70 leading-relaxed">
              Emails will be sent from <span className="font-semibold">TimingoFlow</span> by default. Replies will go to <span className="font-semibold">{user?.email}</span> so you won't miss them.
            </p>
            <p className="text-[11px] text-amber-200/50 mt-0.5">
              To send from your own email, go to <span className="font-semibold"> Settings  Email Senders</span> and add a Resend or Zoho account.
              Or contact <span className="font-semibold">team@timingoflow.com</span> for help.
            </p>
          </div>
        )
      )}

      {/* Action Buttons */}
      <div className="flex gap-3 flex-wrap">
        <button onClick={handleGenerate} disabled={selectedFolders.size === 0 || generating || (selectedFolders.size === 1 && folderStats && folderStats.ready_to_generate === 0)}
          className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-bold rounded-xl transition flex items-center gap-2">
          {generating ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating...</> : <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> Generate Messages {selectedFolders.size > 0 ? `(${selectedFolders.size} folder${selectedFolders.size > 1 ? "s" : ""})` : ""}{selectedFolders.size === 1 && folderStats ? ` – ${folderStats.ready_to_generate} leads` : ""}</>}
        </button>
        {messages.length > 0 && (
          <>
            <button onClick={handleQueueSelected} disabled={queuing || messages.filter(m => m.status === "approved").length === 0}
              className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-bold rounded-xl transition flex items-center gap-2">
              {queuing ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Queuing...</> : <>Queue Approved ({messages.filter(m => m.status === "approved").length})</>}
            </button>
            <button onClick={handleSendQueued} disabled={sending || !queueStatus || queueStatus.pending === 0}
              className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-bold rounded-xl transition flex items-center gap-2">
              {sending ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Sending...</> : <>Send Queued {queueStatus && queueStatus.pending > 0 ? `(${queueStatus.pending} pending)` : ""}</>}
            </button>
            {queueStatus && queueStatus.pending > 0 && (
              <button onClick={async () => {
                if (!window.confirm(`Stop and cancel ${queueStatus.pending} queued email(s)? Already-sent emails will not be affected.`)) return;
                try {
                  const r = await authFetch("/outreach/cancel-queue", { method: "POST" }).then(r => r.json());
                  await fetchQueueStatus();
                  await fetchMessages(selectedFolder);
                  alert(r.message || "Queue cancelled.");
                } catch { alert("Failed to cancel queue."); }
              }}
                className="px-5 py-2.5 bg-red-900/60 hover:bg-red-700 border border-red-700/50 text-red-300 text-sm font-bold rounded-xl transition flex items-center gap-2">
                Stop Queue ({queueStatus.pending})
              </button>
            )}
          </>
        )}
      </div>

      {/* Tabs */}
      {selectedFolder && (
        <div className="flex gap-1 border-b border-slate-800">
          <button onClick={() => setTab("messages")}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition ${tab === "messages" ? "border-indigo-500 text-indigo-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            Messages ({pendingMessages.length})
          </button>
          <button onClick={() => setTab("sent")}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition ${tab === "sent" ? "border-indigo-500 text-indigo-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            Sent ({sentMessages.length})
          </button>
          <button onClick={() => setTab("leads")}
            className={`px-4 py-2 text-xs font-bold border-b-2 transition ${tab === "leads" ? "border-indigo-500 text-indigo-300" : "border-transparent text-slate-500 hover:text-slate-300"}`}>
            Leads in Folder ({folderStats ? folderStats.total_leads : "..."})
          </button>
        </div>
      )}

      {/* Tab: Leads list */}
      {selectedFolder && tab === "leads" && folderStats && (
        <div className="space-y-1">
          {folderStats.leads.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No leads in this folder.</p>
          ) : (
            <div className="bg-slate-900/40 border border-slate-800 rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
              <table className="w-full text-xs min-w-[500px]">
                <thead>
                  <tr className="border-b border-slate-800 text-slate-500">
                    <th className="text-left px-4 py-2.5 font-semibold">Name</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Email</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Phone</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Website</th>
                    <th className="text-left px-4 py-2.5 font-semibold">Message</th>
                  </tr>
                </thead>
                <tbody>
                  {folderStats.leads.map(l => {
                    const msgStatus = l.message_status;
                    const msgBadge = msgStatus === "sent"
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-900/60 text-indigo-300 border border-indigo-700/40"><span className="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block" />Sent</span>
                      : msgStatus === "queued"
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-900/60 text-amber-300 border border-amber-700/40"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />Queued</span>
                      : msgStatus === "approved"
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-900/60 text-emerald-300 border border-emerald-700/40"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />Approved</span>
                      : msgStatus === "draft"
                      ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-700 text-slate-300 border border-slate-600"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 inline-block" />Draft</span>
                      : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-900/30 text-red-400 border border-red-800/40"><span className="w-1.5 h-1.5 rounded-full bg-red-500 inline-block" />None</span>;
                    return (
                    <tr key={l.id} className="border-b border-slate-800/50 hover:bg-slate-800/30">
                      <td className="px-4 py-2 text-white font-semibold">{l.name}</td>
                      <td className="px-4 py-2">
                        {l.email ? <span className="text-emerald-400">{l.email}</span> : <span className="text-red-400/60">No email</span>}
                      </td>
                      <td className="px-4 py-2 text-slate-400">{l.phone || "–"}</td>
                      <td className="px-4 py-2 text-slate-400 truncate max-w-[200px]">{l.website || "–"}</td>
                      <td className="px-4 py-2">{msgBadge}</td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Messages list (or "All Messages" mode) */}
      {(tab === "messages" || !selectedFolder) && (
        <>
          {/* Bulk Action Bar */}
          {pendingMessages.length > 0 && (
            <div className="flex items-center gap-3 flex-wrap bg-slate-900/40 border border-slate-800 rounded-xl px-4 py-2.5">
              <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400">
                <input type="checkbox" checked={selectedMsgs.size === pendingMessages.length && pendingMessages.length > 0} onChange={() => { if (selectedMsgs.size === pendingMessages.length) setSelectedMsgs(new Set()); else setSelectedMsgs(new Set(pendingMessages.map(m => m.id))); }}
                  className="accent-indigo-500 w-3.5 h-3.5" />
                {selectedMsgs.size === pendingMessages.length ? "Deselect All" : "Select All"}
              </label>
              {selectedMsgs.size > 0 && (
                <span className="text-xs text-indigo-300 font-semibold">{selectedMsgs.size} selected</span>
              )}
              <div className="flex-1" />
              <button onClick={() => handleBulkApprove([...selectedMsgs])} disabled={selectedMsgs.size === 0}
                className="px-3 py-1.5 bg-emerald-900/40 hover:bg-emerald-800/60 disabled:opacity-30 text-emerald-400 text-[11px] font-bold rounded-lg transition">
                Approve Selected
              </button>
              <button onClick={() => handleBulkApprove(messages.filter(m => m.status === "draft").map(m => m.id))}
                disabled={messages.filter(m => m.status === "draft").length === 0}
                className="px-3 py-1.5 bg-emerald-900/40 hover:bg-emerald-800/60 disabled:opacity-30 text-emerald-400 text-[11px] font-bold rounded-lg transition">
                Approve All Drafts ({messages.filter(m => m.status === "draft").length})
              </button>
              <button onClick={() => handleBulkDelete([...selectedMsgs])} disabled={selectedMsgs.size === 0}
                className="px-3 py-1.5 bg-red-900/30 hover:bg-red-800/40 disabled:opacity-30 text-red-400 text-[11px] font-bold rounded-lg transition">
                Delete Selected
              </button>
              {selectedMsgs.size > 0 && (
                <>
                  <div className="w-px h-5 bg-slate-700" />
                  <button onClick={() => { setBulkFieldsModal("cc"); setBulkFieldValue(""); }}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold rounded-lg transition">
                    Set CC
                  </button>
                  <button onClick={() => { setBulkFieldsModal("bcc"); setBulkFieldValue(""); }}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold rounded-lg transition">
                    Set BCC
                  </button>
                  <button onClick={() => { setBulkFieldsModal("reply_to"); setBulkFieldValue(""); }}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] font-bold rounded-lg transition">
                    Set Reply-To
                  </button>
                </>
              )}
            </div>
          )}

          {/* Bulk CC/BCC/Reply-To Modal */}
          {bulkFieldsModal && (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setBulkFieldsModal(null)}>
              <div className="bg-slate-900 border border-slate-700 rounded-2xl p-5 w-full max-w-sm flex flex-col gap-3" onClick={e => e.stopPropagation()}>
                <h3 className="text-sm font-bold text-white">Set {bulkFieldsModal === "reply_to" ? "Reply-To" : bulkFieldsModal.toUpperCase()} for {selectedMsgs.size} messages</h3>
                <input value={bulkFieldValue} onChange={e => setBulkFieldValue(e.target.value)}
                  placeholder={`Enter ${bulkFieldsModal === "reply_to" ? "reply-to" : bulkFieldsModal} email address...`}
                  className="bg-slate-800 border border-slate-700 focus:border-indigo-500 text-white rounded-lg px-3 py-2.5 text-sm outline-none transition"
                  onKeyDown={e => e.key === "Enter" && handleBulkUpdateFields(bulkFieldsModal)} />
                <p className="text-[10px] text-slate-500">Leave empty to clear the field from selected messages.</p>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setBulkFieldsModal(null)} className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-slate-400 rounded-lg text-xs transition">Cancel</button>
                  <button onClick={() => handleBulkUpdateFields(bulkFieldsModal)} className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg text-xs font-bold transition">Apply</button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : pendingMessages.length === 0 ? (
            <div className="text-center py-16">
              {selectedFolders.size > 0 ? (
                <div>
                  <p className="text-slate-400 text-sm font-semibold mb-1">No pending messages for the selected folder{selectedFolders.size > 1 ? "s" : ""}.</p>
                  <p className="text-slate-500 text-xs">
                    {sentMessages.length > 0
                      ? `All messages have been sent! Check the Sent tab.`
                      : folderStats && folderStats.with_email > 0
                        ? `${folderStats.with_email} lead${folderStats.with_email !== 1 ? "s" : ""} with email ready — click "Generate Messages" above.`
                        : "No leads with email addresses in the selected folder(s). Add leads with emails first."}
                  </p>
                </div>
              ) : (
                <div>
                  <p className="text-slate-400 text-sm font-semibold mb-1">No pending outreach messages.</p>
                  <p className="text-slate-500 text-xs">Select one or more folders above, then click "Generate Messages" to create AI-powered emails for your leads.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {pendingMessages.map(m => (
                <div key={m.id} className={`bg-slate-900/60 border rounded-2xl p-4 space-y-2 ${selectedMsgs.has(m.id) ? "border-indigo-600" : "border-slate-800"}`}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-2.5 flex-1">
                      <input type="checkbox" checked={selectedMsgs.has(m.id)} onChange={() => toggleSelectMsg(m.id)}
                        className="mt-1 accent-indigo-500 w-3.5 h-3.5 cursor-pointer" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-sm font-bold text-white">{m.lead_name}</span>
                          {statusBadge(m.status)}
                          {m.approved && <span className="text-[10px] text-emerald-400 font-bold"> Approved</span>}
                        </div>
                        <span className="text-xs text-slate-500">{m.lead_email}</span>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {m.status === "draft" && (
                        <>
                          <button onClick={() => handleApprove(m.id)} className="px-2 py-1 bg-emerald-900/40 hover:bg-emerald-800/60 text-emerald-400 text-[10px] font-bold rounded-lg transition">Approve</button>
                          <button onClick={() => { setEditingId(m.id); setEditForm({ subject: m.subject, body: m.body, cc: m.cc || "", bcc: m.bcc || "", reply_to: m.reply_to || "" }); }} className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 text-[10px] font-bold rounded-lg transition">Edit</button>
                        </>
                      )}
                      {m.status !== "sent" && (
                        <button onClick={() => handleDelete(m.id)} className="px-2 py-1 bg-red-900/30 hover:bg-red-800/40 text-red-400 text-[10px] font-bold rounded-lg transition">Delete</button>
                      )}
                    </div>
                  </div>

                  {editingId === m.id ? (
                    <div className="space-y-2 pt-2 border-t border-slate-800">
                      <input value={editForm.subject} onChange={e => setEditForm(f => ({ ...f, subject: e.target.value }))}
                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-sm outline-none focus:border-indigo-500"
                        placeholder="Subject line" />
                      <textarea value={editForm.body} onChange={e => setEditForm(f => ({ ...f, body: e.target.value }))}
                        rows={5}
                        className="w-full bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-sm outline-none focus:border-indigo-500 resize-none" />
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        <input value={editForm.cc} onChange={e => setEditForm(f => ({ ...f, cc: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500"
                          placeholder="CC (optional)" />
                        <input value={editForm.bcc} onChange={e => setEditForm(f => ({ ...f, bcc: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500"
                          placeholder="BCC (optional)" />
                        <input value={editForm.reply_to} onChange={e => setEditForm(f => ({ ...f, reply_to: e.target.value }))}
                          className="bg-slate-900 border border-slate-700 text-white rounded-xl px-3 py-2 text-xs outline-none focus:border-indigo-500"
                          placeholder="Reply-To (optional)" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => handleEditSave(m.id)} className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-lg">Save</button>
                        <button onClick={() => handlePreviewTrackedHtml(m.id)} className="px-3 py-1.5 bg-cyan-700 hover:bg-cyan-600 text-white text-xs font-bold rounded-lg">Preview Tracked HTML</button>
                        <button onClick={() => setEditingId(null)} className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="pt-2 border-t border-slate-800/60">
                      <p className="text-xs font-semibold text-indigo-400 mb-0.5">Subject A: {m.subject}</p>
                      {m.subject_b && <p className="text-xs font-semibold text-purple-400 mb-1">Subject B: {m.subject_b}</p>}
                      <p className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{m.body}</p>
                      {(m.cc || m.bcc || m.reply_to) && (
                        <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500">
                          {m.cc && <span>CC: <span className="text-slate-400">{m.cc}</span></span>}
                          {m.bcc && <span>BCC: <span className="text-slate-400">{m.bcc}</span></span>}
                          {m.reply_to && <span>Reply-To: <span className="text-slate-400">{m.reply_to}</span></span>}
                        </div>
                      )}
                    </div>
                  )}

                  {m.sent_at && <p className="text-[10px] text-slate-600">Sent: {new Date(m.sent_at).toLocaleString()}</p>}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Tab: Sent messages */}
      {tab === "sent" && selectedFolder && (
        <>
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              <input
                value={sentSearch}
                onChange={(e) => setSentSearch(e.target.value)}
                placeholder="Filter by lead name/email"
                className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
              />
              <input
                value={sentCountry}
                onChange={(e) => setSentCountry(e.target.value)}
                placeholder="Filter by country"
                className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
              />
              <select
                value={sentEngagement}
                onChange={(e) => setSentEngagement(e.target.value)}
                className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
              >
                <option value="all">All engagement</option>
                <option value="opened">Opened</option>
                <option value="clicked">Clicked</option>
                <option value="not_opened">Not opened</option>
                <option value="not_clicked">Not clicked</option>
              </select>
              <button
                onClick={handleGenerateFollowups}
                disabled={generatingFollowups || filteredSentMessages.length === 0}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-xs font-bold rounded-lg transition"
              >
                {generatingFollowups ? "Generating follow-ups..." : `Generate Follow-ups (${filteredSentMessages.length})`}
              </button>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">This creates AI follow-up drafts from the filtered sent list, then you can approve, queue and send in the Messages tab.</p>
            
            <div className="mt-4 border-t border-slate-700 pt-4">
              <label className="text-xs font-semibold text-slate-300 mb-2 block">Message Style & Instructions (Optional)</label>
              <textarea
                value={followupInstructions}
                onChange={(e) => setFollowupInstructions(e.target.value)}
                placeholder="E.g., Keep it short (2-3 sentences), professional tone, include a CTA, focus on value proposition..."
                className="w-full bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500 resize-none"
                rows={3}
              />
              <p className="text-[11px] text-slate-500 mt-1">Specify how the AI should generate follow-ups: tone, length, keywords, structure, etc. Leave empty for default style.</p>
            </div>
          </div>
        </>
      )}

      <>
        {sentMessages.length > 0 && (
          <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4">
            <div className="flex flex-wrap gap-3 items-center">
              <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-[11px] font-bold">Matched {filteredSentMessages.length} / Sent {sentMessages.length}</span>
              <span className="px-3 py-1 rounded-full bg-emerald-900/40 text-emerald-300 text-[11px] font-bold">Opened {openedSentCount}</span>
              <span className="px-3 py-1 rounded-full bg-indigo-900/40 text-indigo-300 text-[11px] font-bold">Clicked {clickedSentCount}</span>
            </div>
            <p className="text-[11px] text-slate-500 mt-3">Clicked messages are shown first, then opened messages, so callback leads rise to the top.</p>
          </div>
        )}

        {filteredSentMessages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-slate-400 text-sm font-semibold mb-1">No sent emails match your filters.</p>
            <p className="text-slate-500 text-xs">Try clearing country/name/engagement filters.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredSentMessages.map(m => (
              <div key={m.id} className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 space-y-2 opacity-80">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-bold text-white">{m.lead_name}</span>
                      {statusBadge(m.status)}
                    </div>
                    <span className="text-xs text-slate-500">{m.lead_email}</span>
                    {(m.lead_city || m.lead_country) && (
                      <div className="text-[10px] text-slate-500 mt-1">{[m.lead_city, m.lead_country].filter(Boolean).join(", ")}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    {m.sent_at && <span className="text-[10px] text-slate-500">Sent {new Date(m.sent_at).toLocaleString()}</span>}
                    <button
                      onClick={() => setViewSentEmail(m)}
                      className="px-3 py-1.5 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white text-[10px] font-bold transition"
                    >
                      View Email
                    </button>
                  </div>
                </div>
                <div className="pt-2 border-t border-slate-800/60">
                  {(() => { const usedB = m.subject_variant_used === "b"; return (
                  <p className="text-xs font-semibold text-indigo-400 mb-0.5">
                    Subject {usedB ? "B" : "A"}: {usedB ? m.subject_b : m.subject}
                    {m.subject_b && <span className="ml-2 text-[10px] text-slate-600">(other: {usedB ? m.subject : m.subject_b})</span>}
                  </p>
                  ); })()}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(m.open_count || 0) > 0 ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>
                      {(m.open_count || 0) > 0 ? `Opened ${m.open_count}x` : "Not opened yet"}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(m.click_count || 0) > 0 ? "bg-indigo-900/40 text-indigo-300" : "bg-slate-800 text-slate-500"}`}>
                      {(m.click_count || 0) > 0 ? `Clicked ${m.click_count}x` : "No link click yet"}
                    </span>
                  </div>
                  {(m.first_opened_at || m.last_opened_at || m.first_clicked_at || m.last_clicked_at) && (
                    <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500">
                      {m.first_opened_at && <span>First open: <span className="text-slate-400">{formatTrackingTime(m.first_opened_at)}</span></span>}
                      {m.last_opened_at && m.last_opened_at !== m.first_opened_at && <span>Last open: <span className="text-slate-400">{formatTrackingTime(m.last_opened_at)}</span></span>}
                      {m.first_clicked_at && <span>First click: <span className="text-slate-400">{formatTrackingTime(m.first_clicked_at)}</span></span>}
                      {m.last_clicked_at && m.last_clicked_at !== m.first_clicked_at && <span>Last click: <span className="text-slate-400">{formatTrackingTime(m.last_clicked_at)}</span></span>}
                    </div>
                  )}
                  {m.last_clicked_url && (
                    <div className="mt-2 text-[10px] text-slate-500">
                      Last clicked link: <a href={m.last_clicked_url} target="_blank" rel="noreferrer" className="text-indigo-300 hover:text-indigo-200 underline break-all">{compactTrackingUrl(m.last_clicked_url)}</a>
                    </div>
                  )}
                  {(m.cc || m.bcc || m.reply_to) && (
                    <div className="flex flex-wrap gap-3 mt-2 text-[10px] text-slate-500">
                      {m.cc && <span>CC: {m.cc}</span>}
                      {m.bcc && <span>BCC: {m.bcc}</span>}
                      {m.reply_to && <span>Reply-To: {m.reply_to}</span>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </>

      {viewSentEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setViewSentEmail(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <h3 className="text-sm font-bold text-white">Email Sent to {viewSentEmail.lead_name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{viewSentEmail.lead_email}{viewSentEmail.sent_at ? ` · ${new Date(viewSentEmail.sent_at).toLocaleString()}` : ""}</p>
              </div>
              <button onClick={() => setViewSentEmail(null)} className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700">Close</button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Subject</p>
                <p className="text-sm font-semibold text-indigo-300">{viewSentEmail.subject_variant_used === "b" ? viewSentEmail.subject_b : viewSentEmail.subject}</p>
                {viewSentEmail.subject_b && (
                  <p className="text-[10px] text-slate-600 mt-0.5">Other variant: {viewSentEmail.subject_variant_used === "b" ? viewSentEmail.subject : viewSentEmail.subject_b}</p>
                )}
              </div>
              <div className="flex gap-4">
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(viewSentEmail.open_count || 0) > 0 ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>
                  {(viewSentEmail.open_count || 0) > 0 ? `Opened ${viewSentEmail.open_count}x` : "Not opened"}
                </span>
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(viewSentEmail.click_count || 0) > 0 ? "bg-indigo-900/40 text-indigo-300" : "bg-slate-800 text-slate-500"}`}>
                  {(viewSentEmail.click_count || 0) > 0 ? `Clicked ${viewSentEmail.click_count}x` : "No click"}
                </span>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Message Body</p>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{viewSentEmail.body}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPreviewModal && previewTrackedHtml && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="w-full max-w-5xl max-h-[90vh] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
              <div>
                <h3 className="text-sm font-bold text-white">Tracked HTML Preview</h3>
                <p className="text-xs text-slate-400">Message #{previewTrackedHtml.message_id} – Tracking links: {previewTrackedHtml.tracking_links_count}</p>
              </div>
              <button
                onClick={() => setShowPreviewModal(false)}
                className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700"
              >
                Close
              </button>
            </div>

            <div className="grid max-h-[calc(90vh-60px)] grid-cols-1 gap-4 overflow-y-auto p-4 lg:grid-cols-2">
              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400">Tracking Links</h4>
                {previewTrackedHtml.tracking_links?.length ? (
                  <div className="space-y-2">
                    {previewTrackedHtml.tracking_links.map((link) => (
                      <div key={link.id} className="rounded-xl border border-slate-800 bg-slate-900/70 p-3">
                        <div className="text-[11px] text-slate-300 break-all"><span className="text-slate-500">Original:</span> {link.original_url}</div>
                        <div className="mt-1 text-[11px] text-cyan-300 break-all"><span className="text-slate-500">Tracking:</span> {link.tracking_url}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-amber-400">No tracking links were created for this message preview.</p>
                )}
              </div>

              <div className="space-y-2">
                <h4 className="text-xs font-bold uppercase tracking-wide text-slate-400">Tracked HTML (Actual Send Payload)</h4>
                <textarea
                  readOnly
                  value={previewTrackedHtml.tracked_html || ""}
                  className="h-[420px] w-full resize-none rounded-xl border border-slate-700 bg-slate-900 p-3 font-mono text-[11px] text-slate-200 outline-none"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Excel Import Modal ── */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto shadow-2xl">
            <div className="p-6 space-y-5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-base font-bold text-white">Import contacts from Excel / CSV</h2>
                <button onClick={() => { setShowImportModal(false); setImportContacts([]); setImportResult(null); setImportError(""); }}
                  className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white text-sm transition">✕</button>
              </div>

              {/* Step 1 — file upload */}
              {importContacts.length === 0 && !importParsing && !importResult && (
                <div>
                  <p className="text-xs text-slate-400 mb-3">Required column: <span className="text-white font-semibold">email</span>. Optional: name, website, bio / description, contact name, title.</p>
                  <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-700 hover:border-indigo-500 rounded-xl p-10 cursor-pointer transition bg-slate-950/40">
                    <svg className="w-10 h-10 text-slate-600 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                    <span className="text-sm text-slate-400">Drop file here or <span className="text-indigo-400 font-semibold">browse</span></span>
                    <span className="text-xs text-slate-600 mt-1">Supports .xlsx, .xls, .csv — up to 500 contacts</span>
                    <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setImportParsing(true); setImportError("");
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        const resp = await authFetch("/outreach/parse-excel", { method: "POST", body: fd });
                        const data = await resp.json();
                        if (!resp.ok) throw new Error(data.detail || "Parse failed");
                        if (!data.contacts?.length) throw new Error("No contacts found. Check your file has an 'email' column.");
                        setImportContacts(data.contacts);
                      } catch (ex) { setImportError(ex.message); }
                      finally { setImportParsing(false); e.target.value = ""; }
                    }} />
                  </label>
                </div>
              )}

              {/* Parsing spinner */}
              {importParsing && (
                <div className="flex items-center justify-center gap-2 text-slate-400 py-8">
                  <div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  Parsing file…
                </div>
              )}

              {/* Step 2 — preview + template settings */}
              {importContacts.length > 0 && !importResult && (
                <>
                  {/* Contact preview */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-semibold text-white">{importContacts.length} contact{importContacts.length !== 1 ? "s" : ""} found</p>
                      <button onClick={() => setImportContacts([])} className="text-xs text-slate-500 hover:text-slate-300 transition">← Upload different file</button>
                    </div>
                    <div className="overflow-x-auto max-h-36 border border-slate-800 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-slate-800/70 sticky top-0">
                          <tr>
                            {Object.keys(importContacts[0] || {}).filter(k => k !== "extra").map(k => (
                              <th key={k} className="px-3 py-1.5 text-left text-slate-400 font-semibold capitalize whitespace-nowrap">{k.replace(/_/g, " ")}</th>
                            ))}
                            {importContacts[0]?.extra && Object.keys(importContacts[0].extra).length > 0 && (
                              <th className="px-3 py-1.5 text-left text-slate-400 font-semibold whitespace-nowrap">Extra fields</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {importContacts.slice(0, 6).map((c, i) => {
                            const { extra, ...rest } = c;
                            const extraStr = extra && Object.keys(extra).length > 0
                              ? Object.entries(extra).map(([k, v]) => `${k}: ${v}`).join(" · ")
                              : null;
                            return (
                              <tr key={i} className="border-t border-slate-800/50">
                                {Object.values(rest).map((v, j) => (
                                  <td key={j} className="px-3 py-1.5 text-slate-300 truncate max-w-[160px]">{String(v || "")}</td>
                                ))}
                                {extraStr !== null && (
                                  <td className="px-3 py-1.5 text-slate-500 truncate max-w-[200px] text-[10px]">{extraStr}</td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    {importContacts.length > 6 && <p className="text-[11px] text-slate-600 mt-1">Showing 6 of {importContacts.length}</p>}
                  </div>

                  {/* Template settings */}
                  <div className="space-y-4 bg-slate-950/50 border border-slate-800 rounded-xl p-4">
                    <p className="text-xs font-bold text-slate-300 uppercase tracking-wide">Message Template</p>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      {/* Tone */}
                      <div>
                        <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Tone</label>
                        <div className="flex flex-wrap gap-1.5">
                          {["friendly", "professional", "direct", "casual"].map(t => (
                            <button key={t} onClick={() => setImportTone(t)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition capitalize ${importTone === t ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                              {t}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* Length */}
                      <div>
                        <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Length</label>
                        <div className="flex flex-wrap gap-1.5">
                          {[["Short", "50-75"], ["Medium", "100-150"], ["Long", "150-200"]].map(([lbl, val]) => (
                            <button key={val} onClick={() => setImportLength(val)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition ${importLength === val ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      {/* CTA */}
                      <div>
                        <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">CTA</label>
                        <div className="flex flex-wrap gap-1.5">
                          {[["Book meeting", "book_meeting"], ["Ask question", "ask_question"], ["See demo", "see_demo"]].map(([lbl, val]) => (
                            <button key={val} onClick={() => setImportCta(val)}
                              className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition ${importCta === val ? "bg-indigo-900/60 border-indigo-600 text-indigo-300" : "bg-slate-900 border-slate-700 text-slate-400 hover:text-white"}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Booking link */}
                    <div>
                      <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Booking / Calendar link</label>
                      <div className="flex gap-2 items-center">
                        <input value={importBookingLink} onChange={e => setImportBookingLink(e.target.value)}
                          placeholder="https://calendly.com/your-link"
                          className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500" />
                        {importBookingLink && (
                          <a href={importBookingLink} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-emerald-300 rounded-lg text-xs font-semibold transition whitespace-nowrap">
                            Test ↗
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Extra link */}
                    <div>
                      <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Extra link (optional)</label>
                      <div className="flex gap-2 items-center">
                        <input value={importExtraLink} onChange={e => setImportExtraLink(e.target.value)}
                          placeholder="https://yourdemo.com"
                          className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500" />
                        {importExtraLink && (
                          <a href={importExtraLink} target="_blank" rel="noopener noreferrer"
                            className="px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-emerald-300 rounded-lg text-xs font-semibold transition whitespace-nowrap">
                            Test ↗
                          </a>
                        )}
                      </div>
                    </div>

                    {/* Custom instructions */}
                    <div>
                      <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Additional instructions (optional)</label>
                      <textarea value={importInstructions} onChange={e => setImportInstructions(e.target.value)}
                        rows={2} placeholder="e.g. Mention our free trial, keep it under 80 words, always reference their industry…"
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 resize-none" />
                    </div>

                    {/* Folder name */}
                    <div>
                      <label className="text-[11px] text-slate-500 font-semibold block mb-1.5 uppercase">Folder name</label>
                      <input value={importFolderName} onChange={e => setImportFolderName(e.target.value)}
                        placeholder={`Import – ${new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
                        className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-sm text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500" />
                    </div>
                  </div>

                  {importError && <p className="text-sm text-red-400">{importError}</p>}

                  <button onClick={handleGenerateFromImport} disabled={importGenerating}
                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 disabled:text-slate-500 text-white text-sm font-bold rounded-xl transition flex items-center justify-center gap-2">
                    {importGenerating
                      ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Generating {importContacts.length} messages…</>
                      : <>Generate {importContacts.length} message{importContacts.length !== 1 ? "s" : ""}</>}
                  </button>
                </>
              )}

              {/* Step 3 — done */}
              {importResult && (
                <div className="text-center py-8 space-y-3">
                  <div className="w-14 h-14 bg-emerald-900/40 border border-emerald-700/30 rounded-full flex items-center justify-center mx-auto text-2xl text-emerald-400">✓</div>
                  <p className="text-white font-bold text-lg">{importResult.generated} message{importResult.generated !== 1 ? "s" : ""} generated</p>
                  <div className="flex gap-3 justify-center text-xs">
                    {importResult.skipped_no_email > 0 && <span className="text-slate-500">{importResult.skipped_no_email} skipped (no email)</span>}
                    {importResult.skipped_error > 0 && <span className="text-amber-400">{importResult.skipped_error} failed</span>}
                  </div>
                  <p className="text-sm text-slate-400">Saved to: <span className="text-indigo-300 font-semibold">{importResult.folder_name}</span></p>
                  <button onClick={() => {
                    setShowImportModal(false);
                    setImportContacts([]); setImportResult(null); setImportFolderName("");
                    if (refreshFolders) refreshFolders();
                    setTimeout(() => {
                      setSelectedFolder(importResult.folder_id);
                      setSelectedFolders(new Set([importResult.folder_id]));
                      setTab("messages");
                    }, 400);
                  }}
                    className="px-8 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-bold rounded-xl transition">
                    View Messages →
                  </button>
                </div>
              )}

              {importError && importContacts.length === 0 && !importParsing && (
                <p className="text-sm text-red-400 text-center">{importError}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

//  Dedicated Follow-ups Page
function FollowupsPage({ folders, onOpenOutreach }) {
  const { authFetch, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [country, setCountry] = useState("");
  const [engagement, setEngagement] = useState("all");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [genProgress, setGenProgress] = useState(null);
  const [dateFilter, setDateFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [sendingId, setSendingId] = useState(null);
  const [expandedDraft, setExpandedDraft] = useState(null);

  // Follow-up templates per segment
  const [templates, setTemplates] = useState(() => 
    loadLS(`followup_templates_${user?.email || "anon"}`, [
      { id: "clicked", name: "Clicked", icon: "", message: "Thanks for checking that out. Here's a quick follow-up on what we discussed.", instructions: "Keep it brief. Suggest next step." },
      { id: "opened_not_clicked", name: "Opened but didn't click", icon: "", message: "Following up on the email I sent. Happy to clarify anything or jump on a call.", instructions: "Friendly and offer to help. Include a call-to-action." },
      { id: "not_opened", name: "Didn't open", icon: "", message: "Quick follow-up in case this got buried. Worth a look if you have 30 seconds.", instructions: "Humble and casual tone. Make it sound valuable but quick to read." },
      { id: "no_engagement", name: "No engagement", icon: "", message: "One last attempt to connect. Let me know if this isn't a fit and I'll stop reaching out.", instructions: "Respectful and give them an out. Direct call-to-action." },
      { id: "multiple_clicks", name: "Multiple clicks", icon: "", message: "Clearly interested! Let's chat about next steps.", instructions: "Direct and confident. Suggest a meeting or call." }
    ])
  );
  const [expandedTemplate, setExpandedTemplate] = useState(null);

  const templatesKey = useMemo(() => `followup_templates_${user?.email || "anon"}`, [user?.email]);

  useEffect(() => {
    saveLS(templatesKey, templates);
  }, [templates, templatesKey]);

  const fetchDrafts = useCallback(async () => {
    try {
      const resp = await authFetch("/outreach/followup-drafts");
      if (resp.ok) setDrafts(await resp.json());
    } catch (e) { console.error(e); }
  }, [authFetch]);

  const fetchMessages = useCallback(async () => {
    setLoading(true);
    try {
      const [msgResp, draftResp] = await Promise.all([
        authFetch("/outreach/messages"),
        authFetch("/outreach/followup-drafts"),
      ]);
      if (msgResp.ok) {
        const data = await msgResp.json();
        setMessages(data.filter(m => m.status === "sent"));
      }
      if (draftResp.ok) setDrafts(await draftResp.json());
    } catch (e) {
      setError(e.message || "Failed to load messages");
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  const sentMessages = useMemo(
    () => [...messages].sort((a, b) => new Date(b.sent_at || b.created_at || 0) - new Date(a.sent_at || a.created_at || 0)),
    [messages]
  );

  const [followupFilter, setFollowupFilter] = useState("all");

  // Date boundaries for filter
  const dateBounds = useMemo(() => {
    const now = new Date();
    if (dateFilter === "today") {
      // Use last 24h from now — timezone-safe, no UTC midnight mismatch
      return { from: new Date(now - 24*60*60*1000), to: null };
    }
    if (dateFilter === "7d") return { from: new Date(now - 7*86400000), to: null };
    if (dateFilter === "30d") return { from: new Date(now - 30*86400000), to: null };
    if (dateFilter === "custom" && (dateFrom || dateTo)) {
      return {
        from: dateFrom ? new Date(dateFrom + "T00:00:00") : null,
        to: dateTo ? new Date(dateTo + "T23:59:59") : null,
      };
    }
    return { from: null, to: null };
  }, [dateFilter, dateFrom, dateTo]);

  const countryOptions = useMemo(() => {
    const counts = {};
    sentMessages.forEach(m => {
      const c = (m.lead_country || "").trim();
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([c]) => c);
  }, [sentMessages]);

  const filteredMessages = useMemo(() => {
    let result = sentMessages;
    // Date filter
    if (dateBounds.from) result = result.filter(m => {
      const d = new Date(m.sent_at || m.created_at || 0);
      return d >= dateBounds.from && (!dateBounds.to || d <= dateBounds.to);
    });
    if (country) result = result.filter(m => (m.lead_country || "").toLowerCase() === country.toLowerCase());
    if (engagement !== "all") {
      if (engagement === "clicked") result = result.filter(m => (m.click_count || 0) > 0);
      else if (engagement === "opened_not_clicked") result = result.filter(m => (m.open_count || 0) > 0 && (m.click_count || 0) === 0);
      else if (engagement === "not_opened") result = result.filter(m => (m.open_count || 0) === 0);
      else if (engagement === "multiple_clicks") result = result.filter(m => (m.click_count || 0) > 1);
      else if (engagement === "no_engagement") result = result.filter(m => (m.open_count || 0) === 0 && (m.click_count || 0) === 0);
    }
    if (followupFilter === "never") result = result.filter(m => (m.followup_count || 0) === 0);
    else if (followupFilter === "done_today") result = result.filter(m => m.followed_up_today);
    else if (followupFilter === "has_followups") result = result.filter(m => (m.followup_count || 0) > 0);
    else if (followupFilter === "not_today") result = result.filter(m => !m.followed_up_today);
    return result;
  }, [sentMessages, country, engagement, followupFilter, dateBounds]);

  useEffect(() => {
    setSelectedIds(prev => {
      const available = new Set(filteredMessages.map(m => m.id));
      const next = new Set();
      prev.forEach(id => { if (available.has(id)) next.add(id); });
      return next;
    });
  }, [filteredMessages]);

  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    if (selectedIds.size === filteredMessages.length && filteredMessages.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredMessages.map(m => m.id)));
    }
  };

  const updateTemplate = (id, updates) => {
    setTemplates(prev => prev.map(t => t.id === id ? { ...t, ...updates } : t));
  };

  const getSegmentForMessage = (m) => {
    const opens = m.open_count || 0;
    const clicks = m.click_count || 0;
    if (clicks > 1) return "multiple_clicks";
    if (clicks > 0) return "clicked";
    if (opens > 0) return "opened_not_clicked";
    return "not_opened";
  };

  const matchedTemplates = useMemo(() => {
    const matches = {};
    filteredMessages.forEach(m => {
      const segmentId = getSegmentForMessage(m);
      if (!matches[segmentId]) matches[segmentId] = 0;
      matches[segmentId]++;
    });
    return matches;
  }, [filteredMessages]);

  const handleGenerateFollowups = async () => {
    const targetMessages = selectedIds.size > 0
      ? filteredMessages.filter(m => selectedIds.has(m.id))
      : filteredMessages;
    if (!targetMessages.length) {
      setError("No matching sent messages for follow-up generation.");
      return;
    }
    setGenerating(true);
    setError("");
    setInfo("");
    const templateMap = {};
    templates.forEach(t => { templateMap[t.id] = t; });

    let done = 0, failed = 0;
    setGenProgress({ current: 0, total: targetMessages.length, currentName: "", done: 0, failed: 0 });

    for (let i = 0; i < targetMessages.length; i++) {
      const m = targetMessages[i];
      setGenProgress({ current: i + 1, total: targetMessages.length, currentName: m.lead_name || m.lead_email || "...", done, failed });
      try {
        const segment = getSegmentForMessage(m);
        const template = templateMap[segment];
        const instructions = template ? template.instructions : "";
        const resp = await authFetch("/outreach/followups/generate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message_ids: [m.id], custom_instructions: instructions }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || (data.generated || 0) === 0) { failed++; }
        else { done++; }
      } catch (e) {
        failed++;
      }
      // Small pause between AI calls to avoid rate limits
      if (i < targetMessages.length - 1) await new Promise(r => setTimeout(r, 400));
    }

    setGenProgress({ current: targetMessages.length, total: targetMessages.length, currentName: "Done", done, failed, finished: true });
    setInfo(`Generated ${done} follow-up draft${done !== 1 ? "s" : ""}${failed > 0 ? ` – ${failed} failed` : ""}. Review and send below.`);
    setSelectedIds(new Set());
    await fetchMessages();
    setGenerating(false);
    setTimeout(() => setGenProgress(null), 4000);
  };

  const handleApproveDraft = async (draft) => {
    await authFetch(`/outreach/messages/${draft.id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ approved: true }),
    });
    await fetchDrafts();
  };

  const handleSendDraft = async (draft) => {
    setSendingId(draft.id);
    try {
      // Step 1: approve if not already
      if (!draft.approved) {
        await authFetch(`/outreach/messages/${draft.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ approved: true }),
        });
      }
      // Step 2: add to send queue
      const qResp = await authFetch("/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_ids: [draft.id] }),
      });
      const qData = await qResp.json().catch(() => ({}));
      if (!qResp.ok) {
        setError(qData.detail || "Failed to queue email.");
        return;
      }
      // Step 3: trigger send
      await authFetch("/outreach/send-queued", { method: "POST" }).catch(() => {});
      setInfo(`Follow-up to ${draft.lead_name} sent.`);
      await fetchDrafts();
    } catch (e) {
      setError(`Failed to send: ${e.message}`);
    } finally {
      setSendingId(null);
    }
  };

  const handleSendAllDrafts = async () => {
    const toSend = drafts.filter(d => d.status !== "queued" && d.status !== "sent");
    if (!toSend.length) return;
    setSendingId("all");
    try {
      const ids = toSend.map(d => d.id);
      // Approve all that need it
      const needApproval = toSend.filter(d => !d.approved).map(d => d.id);
      if (needApproval.length) {
        await authFetch("/outreach/messages/bulk-approve", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message_ids: needApproval }),
        });
      }
      // Queue all
      const qResp = await authFetch("/outreach/queue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message_ids: ids }),
      });
      const qData = await qResp.json().catch(() => ({}));
      // Trigger send
      await authFetch("/outreach/send-queued", { method: "POST" }).catch(() => {});
      setInfo(`${qData.queued || ids.length} follow-up${ids.length !== 1 ? "s" : ""} queued and sending now.`);
      await fetchDrafts();
    } catch (e) {
      setError(`Failed to send all: ${e.message}`);
    } finally {
      setSendingId(null);
    }
  };

  const handleDeleteDraft = async (draftId) => {
    await authFetch(`/outreach/messages/${draftId}`, { method: "DELETE" });
    setDrafts(prev => prev.filter(d => d.id !== draftId));
  };

  return (
    <div className="p-3 sm:p-6 space-y-5">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white">Follow-up Management</h1>
          <p className="text-xs text-slate-500 mt-1">Customize follow-up templates per engagement level, then generate follow-ups for your sent messages.</p>
        </div>
      </div>

      {(error || info) && (
        <div className={`rounded-xl border px-4 py-2 text-xs ${error ? "bg-red-950/30 border-red-900/50 text-red-300" : "bg-emerald-950/30 border-emerald-900/50 text-emerald-300"}`}>
          {error || info}
        </div>
      )}

      {/* Follow-up Templates Section */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-white"> Follow-up Templates</h2>
          <span className="text-xs text-slate-400">Configure messages for each engagement type</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {templates.map(template => (
            <div key={template.id} className="bg-slate-800/50 border border-slate-700 rounded-xl p-3">
              <div className="flex items-start gap-2 cursor-pointer" onClick={() => setExpandedTemplate(expandedTemplate === template.id ? null : template.id)}>
                <span className="text-xl">{template.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-white text-sm">{template.name}</p>
                  <p className="text-xs text-slate-400 truncate">{template.message}</p>
                </div>
                {matchedTemplates[template.id] && (
                  <span className="px-2 py-0.5 bg-indigo-700/30 text-indigo-300 rounded text-[10px] font-bold shrink-0">{matchedTemplates[template.id]}</span>
                )}
                <svg className={`w-4 h-4 text-slate-400 shrink-0 transition ${expandedTemplate === template.id ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              {expandedTemplate === template.id && (
                <div className="mt-3 pt-3 border-t border-slate-700 space-y-2">
                  <div>
                    <label className="text-xs font-semibold text-slate-300 mb-1 block">Message</label>
                    <textarea
                      value={template.message}
                      onChange={(e) => updateTemplate(template.id, { message: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-2 py-1.5 text-xs outline-none focus:border-indigo-500 resize-none"
                      rows={2}
                      placeholder="Follow-up message"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-slate-300 mb-1 block">Custom Instructions for AI</label>
                    <textarea
                      value={template.instructions}
                      onChange={(e) => updateTemplate(template.id, { instructions: e.target.value })}
                      className="w-full bg-slate-900 border border-slate-600 text-white rounded-lg px-2 py-1.5 text-xs outline-none focus:border-indigo-500 resize-none"
                      rows={2}
                      placeholder="E.g., Keep it brief (1-2 sentences), professional but friendly..."
                    />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Date Filter */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 space-y-3">
        <h2 className="font-bold text-white text-sm"> Filter by Date Sent</h2>
        <div className="flex flex-wrap gap-2">
          {[["all","All time"],["today","Today"],["7d","Last 7 days"],["30d","Last 30 days"],["custom","Custom range"]].map(([val, label]) => (
            <button key={val} onClick={() => setDateFilter(val)}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition ${dateFilter === val ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
              {label} {val !== "custom" && val !== "all" ? `(${sentMessages.filter(m => {
                const d = new Date(m.sent_at || m.created_at || 0);
                if (val === "today") { const s = new Date(); s.setHours(0,0,0,0); return d >= s; }
                if (val === "7d") return d >= new Date(Date.now() - 7*86400000);
                if (val === "30d") return d >= new Date(Date.now() - 30*86400000);
                return true;
              }).length})` : val === "all" ? `(${sentMessages.length})` : ""}
            </button>
          ))}
        </div>
        {dateFilter === "custom" && (
          <div className="flex gap-3 flex-wrap items-center">
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wider text-slate-500">From</label>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-200 outline-none focus:border-indigo-500" />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-[10px] uppercase tracking-wider text-slate-500">To</label>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-200 outline-none focus:border-indigo-500" />
            </div>
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="mt-5 px-3 py-2 text-xs bg-slate-800 hover:bg-slate-700 text-slate-400 rounded-lg">Clear</button>
          </div>
        )}
      </div>

      {/* Message Filtering Section */}
      <div className="bg-slate-900/40 border border-slate-800 rounded-2xl p-4 space-y-3">
        <h2 className="font-bold text-white text-sm"> Filter & Select</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
          >
            <option value="">All countries ({sentMessages.length})</option>
            {countryOptions.map(c => {
              const count = sentMessages.filter(m => (m.lead_country || "").toLowerCase() === c.toLowerCase()).length;
              return <option key={c} value={c}>{c} ({count})</option>;
            })}
          </select>
          <select
            value={engagement}
            onChange={(e) => setEngagement(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
          >
            <option value="all">All engagement</option>
            <option value="multiple_clicks"> Multiple clicks ({sentMessages.filter(m => (m.click_count || 0) > 1).length})</option>
            <option value="clicked"> Clicked ({sentMessages.filter(m => (m.click_count || 0) > 0).length})</option>
            <option value="opened_not_clicked"> Opened, not clicked ({sentMessages.filter(m => (m.open_count || 0) > 0 && (m.click_count || 0) === 0).length})</option>
            <option value="not_opened"> Not opened ({sentMessages.filter(m => (m.open_count || 0) === 0).length})</option>
            <option value="no_engagement"> No engagement ({sentMessages.filter(m => (m.open_count || 0) === 0 && (m.click_count || 0) === 0).length})</option>
          </select>
          <select
            value={followupFilter}
            onChange={(e) => setFollowupFilter(e.target.value)}
            className="bg-slate-900 border border-slate-700 text-white rounded-lg px-3 py-2 text-xs outline-none focus:border-indigo-500"
          >
            <option value="all">All follow-up status</option>
            <option value="never"> Never followed up ({sentMessages.filter(m => (m.followup_count || 0) === 0).length})</option>
            <option value="not_today"> Not followed up today ({sentMessages.filter(m => !m.followed_up_today).length})</option>
            <option value="done_today"> Already done today ({sentMessages.filter(m => m.followed_up_today).length})</option>
            <option value="has_followups"> Has prior follow-ups ({sentMessages.filter(m => (m.followup_count || 0) > 0).length})</option>
          </select>
          <button onClick={() => { setCountry(""); setEngagement("all"); setFollowupFilter("all"); setSelectedIds(new Set()); }} className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg">Clear All</button>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-slate-700">
          <span className="text-xs font-semibold text-slate-400">Showing:</span>
          <span className="px-3 py-1 rounded-full bg-slate-800 text-slate-300 text-[11px] font-bold">{filteredMessages.length} messages</span>
          <span className="px-3 py-1 rounded-full bg-indigo-900/40 text-indigo-300 text-[11px] font-bold">{selectedIds.size} selected</span>
          {sentMessages.filter(m => m.followed_up_today).length > 0 && (
            <span className="px-3 py-1 rounded-full bg-amber-900/40 text-amber-300 text-[11px] font-bold">
               {sentMessages.filter(m => m.followed_up_today).length} already followed up today
            </span>
          )}
          <button onClick={toggleAllFiltered} disabled={filteredMessages.length === 0} className="ml-auto px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 text-xs font-bold rounded-lg">
            {selectedIds.size === filteredMessages.length && filteredMessages.length > 0 ? "Deselect All" : "Select All"}
          </button>
        </div>
      </div>

      {/* Generate Follow-ups Button + Live Progress */}
      <div className="space-y-3">
        <div className="flex gap-2">
          <button
            onClick={handleGenerateFollowups}
            disabled={generating || filteredMessages.length === 0}
            className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-800 disabled:text-slate-500 text-white font-bold rounded-lg transition shadow-lg shadow-emerald-600/20"
          >
            {generating
              ? `Generating... (${genProgress?.current || 0}/${genProgress?.total || 0})`
              : `Generate Follow-ups (${selectedIds.size > 0 ? selectedIds.size : filteredMessages.length} selected)`}
          </button>
          <button
            onClick={onOpenOutreach}
            className="px-4 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold rounded-lg transition"
          >
            View Drafts
          </button>
        </div>

        {/* Live progress bar */}
        {genProgress && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-300 font-semibold">
                {genProgress.finished ? "Done!" : `Generating follow-up for: `}
                {!genProgress.finished && <span className="text-indigo-300 font-bold">{genProgress.currentName}</span>}
              </span>
              <span className="text-slate-500">{genProgress.current}/{genProgress.total}</span>
            </div>
            <div className="w-full h-2 bg-slate-800 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-300 ${genProgress.finished ? "bg-emerald-500" : "bg-indigo-500"}`}
                style={{ width: `${Math.round((genProgress.current / genProgress.total) * 100)}%` }}
              />
            </div>
            <div className="flex gap-4 text-[11px]">
              <span className="text-emerald-400"> {genProgress.done} generated</span>
              {genProgress.failed > 0 && <span className="text-red-400"> {genProgress.failed} failed</span>}
              <span className="text-slate-600 ml-auto">{Math.round((genProgress.current / genProgress.total) * 100)}%</span>
            </div>
          </div>
        )}
      </div>

      {/* Generated Follow-up Drafts — Ready to Send */}
      {drafts.length > 0 && (
        <div className="bg-slate-900 border border-emerald-800/50 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-emerald-950/30">
            <div>
              <span className="font-bold text-white text-sm"> Follow-up Drafts Ready to Send</span>
              <span className="ml-2 px-2 py-0.5 bg-emerald-700/40 text-emerald-300 text-[11px] font-bold rounded-full">{drafts.length}</span>
            </div>
            <button
              onClick={handleSendAllDrafts}
              disabled={sendingId === "all"}
              className="text-xs px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white font-bold rounded-lg transition"
            >
              {sendingId === "all" ? "Sending..." : `Send All (${drafts.filter(d => d.status !== "queued").length})`}
            </button>
          </div>
          <div className="divide-y divide-slate-800">
            {drafts.map(draft => {
              const ordinal = draft.followup_number === 2 ? "1st" : draft.followup_number === 3 ? "2nd" : draft.followup_number === 4 ? "3rd" : `${draft.followup_number - 1}th`;
              const isExpanded = expandedDraft === draft.id;
              const isSending = sendingId === draft.id;
              return (
                <div key={draft.id} className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-white text-sm">{draft.lead_name}</span>
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-indigo-900/50 text-indigo-300">{ordinal} follow-up</span>
                        {draft.parent_opens > 0 && <span className="text-[10px] text-emerald-400"> Opened original</span>}
                        {draft.parent_clicks > 0 && <span className="text-[10px] text-pink-400"> Clicked</span>}
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${draft.status === "queued" ? "bg-blue-900/50 text-blue-300" : draft.approved ? "bg-amber-900/50 text-amber-300" : "bg-slate-700 text-slate-400"}`}>
                          {draft.status === "queued" ? "Queued" : draft.approved ? "Approved" : "Draft"}
                        </span>
                      </div>
                      <p className="text-xs text-slate-400 mb-1"><span className="text-slate-600">To:</span> {draft.lead_email}</p>
                      <p className="text-xs font-semibold text-slate-300 mb-1">{draft.subject}</p>
                      {isExpanded ? (
                        <div className="mt-2 p-3 bg-white rounded-lg text-xs text-gray-800 leading-relaxed max-h-64 overflow-y-auto"
                          dangerouslySetInnerHTML={{ __html: draft.body }} />
                      ) : (
                        <p className="text-xs text-slate-500 truncate">
                          {draft.body.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim().slice(0, 120)}...
                        </p>
                      )}
                      <button onClick={() => setExpandedDraft(isExpanded ? null : draft.id)} className="text-[11px] text-indigo-400 hover:text-indigo-300 mt-1">
                        {isExpanded ? " Collapse" : " Read full message"}
                      </button>
                    </div>
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => handleSendDraft(draft)}
                        disabled={isSending || draft.status === "queued"}
                        className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-700 disabled:text-slate-500 text-white text-xs font-bold rounded-lg transition whitespace-nowrap"
                      >
                        {isSending ? "Sending..." : draft.status === "queued" ? " Queued" : "Send Now "}
                      </button>
                      {!draft.approved && draft.status !== "queued" && (
                        <button onClick={() => handleApproveDraft(draft)} className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-lg transition">
                          Approve
                        </button>
                      )}
                      <button onClick={() => handleDeleteDraft(draft.id)} className="px-4 py-2 bg-slate-900 hover:bg-red-950/40 text-slate-500 hover:text-red-400 text-xs rounded-lg transition">
                        Discard
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Messages List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-slate-400 text-sm font-semibold mb-1">No sent messages match your filters.</p>
          <p className="text-slate-500 text-xs">Adjust country or engagement filters to see messages.</p>
        </div>
      ) : (
        <div>
          <h3 className="font-bold text-white text-sm mb-3">Matching Messages ({filteredMessages.length})</h3>
          <div className="space-y-2">
            {filteredMessages.map(m => {
              const fupCount = m.followup_count || 0;
              const doneToday = m.followed_up_today;
              const segment = getSegmentForMessage(m);
              const segLabel = { multiple_clicks: " Multiple clicks", clicked: " Clicked", opened_not_clicked: " Opened", not_opened: " Not opened", no_engagement: " No activity" }[segment] || "";
              const lastFupDate = m.last_followup_sent_at ? new Date(m.last_followup_sent_at) : null;
              const daysSinceFup = lastFupDate ? Math.floor((Date.now() - lastFupDate.getTime()) / 86400000) : null;
              return (
                <div key={m.id} className={`bg-slate-900/50 border rounded-xl p-3 flex items-start gap-3 transition ${selectedIds.has(m.id) ? "border-indigo-600 bg-indigo-950/20" : doneToday ? "border-amber-800/60 bg-amber-950/10" : "border-slate-800"}`}>
                  <input type="checkbox" checked={selectedIds.has(m.id)} onChange={() => toggleSelected(m.id)} className="mt-1 accent-indigo-500 w-3.5 h-3.5 cursor-pointer shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="text-sm font-bold text-white">{m.lead_name}</p>
                      {/* Engagement badge */}
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${m.click_count > 1 ? "bg-orange-900/40 text-orange-300" : m.click_count > 0 ? "bg-pink-900/40 text-pink-300" : m.open_count > 0 ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-700 text-slate-400"}`}>
                        {segLabel}
                      </span>
                      {/* Follow-up status badge */}
                      {doneToday ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-900/50 text-amber-300"> Done today</span>
                      ) : fupCount > 0 ? (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-blue-900/40 text-blue-300">
                           {fupCount}x {daysSinceFup === 0 ? "today" : daysSinceFup === 1 ? "yesterday" : daysSinceFup != null ? `${daysSinceFup}d ago` : ""}
                        </span>
                      ) : (
                        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-slate-700/60 text-slate-400"> Not followed up</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">{m.lead_email}{m.lead_country ? ` – ${m.lead_country}` : ""}</p>
                    {fupCount > 0 && (
                      <p className="text-[10px] text-slate-600 mt-0.5 italic">
                        {fupCount === 1 ? "1 follow-up sent" : `${fupCount} follow-ups sent`} – use a different angle next time
                      </p>
                    )}
                  </div>
                  <div className="text-right shrink-0 text-[10px] text-slate-600">
                    {m.sent_at ? new Date(m.sent_at).toLocaleDateString() : ""}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

//  A/B Test Panel (lazy-loaded only when tab is active) 
function AbTestPanel({ authFetch }) {
  const [abStats, setAbStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    authFetch("/outreach/ab-stats").then(r => r.ok ? r.json() : null).then(d => {
      if (mounted && d) setAbStats(d);
      if (mounted) setLoading(false);
    }).catch(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [authFetch]);

  const variantStyles = {
    a: { label: "Subject A — Curiosity", border: "border-indigo-600", winBadge: "bg-indigo-900/60 text-indigo-300", heading: "text-indigo-400", stat: "text-indigo-400" },
    b: { label: "Subject B — Benefit",   border: "border-emerald-600", winBadge: "bg-emerald-900/60 text-emerald-300", heading: "text-emerald-400", stat: "text-emerald-400" },
  };

  const hasData = abStats && (abStats.a.sent > 0 || abStats.b.sent > 0);
  const winner = abStats && abStats.a.sent > 0 && abStats.b.sent > 0
    ? (abStats.a.open_rate > abStats.b.open_rate ? "a" : abStats.b.open_rate > abStats.a.open_rate ? "b" : null)
    : null;

  return (
    <>
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
        <h3 className="text-sm font-bold text-white mb-1"> Subject Line A/B Test Results</h3>
        <p className="text-xs text-slate-500 mb-5">Each email is randomly sent with Subject A (curiosity-based) or Subject B (benefit-based). Compare which drives more opens and clicks.</p>
        {loading ? (
          <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
        ) : !hasData ? (
          <div className="text-center py-10">
            <div className="text-4xl mb-3"></div>
            <div className="text-slate-400 text-sm">No A/B data yet</div>
            <div className="text-xs text-slate-600 mt-2">Starts automatically when you generate and send new messages.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {["a", "b"].map(v => {
              const d = abStats[v];
              const s = variantStyles[v];
              const isWinner = winner === v;
              return (
                <div key={v} className={`bg-slate-800/60 border ${isWinner ? s.border : "border-slate-700"} rounded-2xl p-5 relative`}>
                  {isWinner && <span className={`absolute top-3 right-3 text-[10px] font-bold ${s.winBadge} px-2 py-0.5 rounded-full`}>WINNING</span>}
                  <div className={`text-xs font-bold ${s.heading} mb-3`}>{s.label}</div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div><div className="text-2xl font-black text-white">{d.sent}</div><div className="text-[10px] text-slate-500 font-semibold mt-0.5">Sent</div></div>
                    <div><div className={`text-2xl font-black ${s.stat}`}>{d.open_rate}%</div><div className="text-[10px] text-slate-500 font-semibold mt-0.5">Open Rate</div><div className="text-[10px] text-slate-600">{d.opened} opened</div></div>
                    <div><div className="text-2xl font-black text-blue-400">{d.click_rate}%</div><div className="text-[10px] text-slate-500 font-semibold mt-0.5">Click Rate</div><div className="text-[10px] text-slate-600">{d.clicked} clicked</div></div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3 text-xs text-slate-500">
        <strong className="text-slate-300">How it works:</strong> Each new email gets two subject lines from the AI. When sent, one is randomly picked. After enough sends you'll see which style resonates more with your leads.
      </div>
    </>
  );
}

//  Email Tracking Page 
function EmailTrackingPage() {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState(null);
  const [replySummary, setReplySummary] = useState(null);
  const [activeTab, setActiveTab] = useState("tracking"); // tracking | replies | ab
  const [filter, setFilter] = useState("all"); // all, opens, clicks
  const [searchTerm, setSearchTerm] = useState("");
  const [trackPage, setTrackPage] = useState(1);
  const [trackTotal, setTrackTotal] = useState(0);
  const [trackPages, setTrackPages] = useState(1);
  const TRACK_LIMIT = 100;
  const [timeRange, setTimeRange] = useState("all"); // all, today, week, month, year, custom
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [viewTrackEmail, setViewTrackEmail] = useState(null);
  const [viewTrackLoading, setViewTrackLoading] = useState(false);

  // Helper to calculate date range
  const getDateRange = () => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    // "all" means no date filter - show everything
    if (timeRange === "all") {
      return { start: null, end: null };
    }
    
    if (timeRange === "custom") {
      return {
        start: customStartDate || null,
        end: customEndDate || null
      };
    }
    
    if (timeRange === "today") {
      return {
        start: today.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      };
    }
    
    if (timeRange === "week") {
      const weekAgo = new Date(today);
      weekAgo.setDate(weekAgo.getDate() - 7);
      return {
        start: weekAgo.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      };
    }
    
    if (timeRange === "month") {
      const monthAgo = new Date(today);
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      return {
        start: monthAgo.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      };
    }
    
    if (timeRange === "year") {
      const yearAgo = new Date(today);
      yearAgo.setFullYear(yearAgo.getFullYear() - 1);
      return {
        start: yearAgo.toISOString().split('T')[0],
        end: now.toISOString().split('T')[0]
      };
    }
    
    return { start: null, end: null };
  };

  const fetchData = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const dateRange = getDateRange();
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      if (filter === "opens") params.append('event_type', 'open');
      if (filter === "clicks") params.append('event_type', 'click');
      params.append('page', String(trackPage));
      params.append('limit', String(TRACK_LIMIT));

      const queryString = params.toString();
      const eventsUrl = `/outreach/tracking-events?${queryString}`;
      const summaryUrl = `/outreach/tracking-summary${dateRange.start || dateRange.end ? '?' + new URLSearchParams({...(dateRange.start && {start_date: dateRange.start}), ...(dateRange.end && {end_date: dateRange.end})}) : ''}`;
      const replySummaryUrl = `/outreach/replies-summary`;

      const [eventsResp, summaryResp, replySummaryResp] = await Promise.all([
        authFetch(eventsUrl),
        authFetch(summaryUrl),
        authFetch(replySummaryUrl)
      ]);
      if (eventsResp.ok) { const ed = await eventsResp.json(); setEvents(ed.events || ed); setTrackTotal(ed.total || 0); setTrackPages(ed.pages || 1); }
      if (summaryResp.ok) setSummary(await summaryResp.json());
      if (replySummaryResp.ok) setReplySummary(await replySummaryResp.json());
    } catch (e) {
      console.error(e);
    } finally {
      if (showLoader) setLoading(false); else setRefreshing(false);
    }
  }, [authFetch, timeRange, customStartDate, customEndDate, filter, trackPage]);

  // Initial load and on filter/range changes
  useEffect(() => {
    fetchData({ showLoader: true });
  }, [fetchData]);

  // Background auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData({ showLoader: false });
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Filter/search handled server-side (event_type) + client-side (search term only)
  const filteredEvents = searchTerm.trim()
    ? events.filter(e => {
        const term = searchTerm.toLowerCase();
        return (
          (e.lead_name && e.lead_name.toLowerCase().includes(term)) ||
          (e.recipient_email && e.recipient_email.toLowerCase().includes(term)) ||
          (e.subject && e.subject.toLowerCase().includes(term)) ||
          (e.target_url && e.target_url.toLowerCase().includes(term))
        );
      })
    : events;

  const formatDate = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center"><div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Email Tracking</h1>
            <p className="text-sm text-slate-500 mt-1">See who opened and clicked your outreach emails</p>
          </div>
          {refreshing && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
              <span>Auto-updating–</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-slate-800">
          <button onClick={() => setActiveTab("tracking")}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "tracking"
                ? "border-indigo-500 text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-400"
            }`}>
             Email Tracking
          </button>
          <button onClick={() => setActiveTab("replies")}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "replies"
                ? "border-indigo-500 text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-400"
            }`}>
             Reply Analytics
          </button>
          <button onClick={() => setActiveTab("ab")}
            className={`px-4 py-3 text-sm font-semibold border-b-2 transition ${
              activeTab === "ab"
                ? "border-indigo-500 text-indigo-400"
                : "border-transparent text-slate-500 hover:text-slate-400"
            }`}>
             A/B Subject Test
          </button>
        </div>

        {activeTab === "tracking" && (
          <>
        {/* Summary Stats */}
        {!summary && !loading && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-10 text-center text-slate-500 text-sm">
            No email tracking data yet. Send outreach emails to start tracking opens and clicks.
          </div>
        )}
        {summary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Total Sent</div>
              <div className="text-3xl font-bold text-white mt-2">{summary.total_sent}</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Opened</div>
              <div className="text-3xl font-bold text-emerald-400 mt-2">{summary.total_opened}</div>
              <div className="text-xs text-slate-600 mt-1">{summary.open_rate}% open rate – {summary.total_opens} total opens</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Clicked</div>
              <div className="text-3xl font-bold text-blue-400 mt-2">{summary.total_clicked}</div>
              <div className="text-xs text-slate-600 mt-1">{summary.click_rate}% click rate – {summary.total_clicks} total clicks</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Engagement</div>
              <div className="text-xl font-bold text-white mt-2">{summary.total_opens || 0} total opens</div>
              <div className="text-xl font-bold text-white">{summary.total_clicks || 0} total clicks</div>
            </div>
          </div>
        )}

        {/* Top Engaged Leads */}
        {summary && summary.top_engaged_leads && summary.top_engaged_leads.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-white mb-4"> Most Engaged Leads</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {summary.top_engaged_leads.slice(0, 6).map(lead => (
                <div key={lead.id} className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3">
                  <div className="font-semibold text-white text-sm truncate">{lead.name}</div>
                  {lead.phone && (
                    <div className="text-xs text-slate-400 font-mono">{lead.phone}</div>
                  )}
                  <div className="text-xs text-slate-500 truncate">{lead.category}</div>
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs text-emerald-400">{lead.opens} opens</span>
                    <span className="text-xs text-blue-400">{lead.clicks} clicks</span>
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">Last: {formatDate(lead.last_interaction)}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Time Range Filters */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4">
          <div className="flex flex-col gap-3">
            <div className="flex flex-wrap gap-2">
              {[
                { id: "all", label: " All Time" },
                { id: "today", label: " Today" },
                { id: "week", label: " Last 7 Days" },
                { id: "month", label: " Last 30 Days" },
                { id: "year", label: " Last Year" },
                { id: "custom", label: " Custom Range" }
              ].map(t => (
                <button key={t.id} onClick={() => setTimeRange(t.id)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                    timeRange === t.id
                      ? "bg-indigo-700 border-indigo-500 text-white"
                      : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
            
            {/* Custom Date Range Inputs */}
            {timeRange === "custom" && (
              <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-slate-800">
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 mb-1">Start Date</label>
                  <input
                    type="date"
                    value={customStartDate}
                    onChange={e => setCustomStartDate(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-slate-500 mb-1">End Date</label>
                  <input
                    type="date"
                    value={customEndDate}
                    onChange={e => setCustomEndDate(e.target.value)}
                    className="w-full bg-slate-800 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-lg px-3 py-2 text-sm outline-none"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Event Type Filters & Search */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2">
            {[
              { id: "all", label: "All Events" },
              { id: "opens", label: "Opens" },
              { id: "clicks", label: "Clicks" }
            ].map(f => (
              <button key={f.id} onClick={() => { setFilter(f.id); setTrackPage(1); }}
                className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                  filter === f.id
                    ? "bg-indigo-700 border-indigo-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                }`}>
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            placeholder="Search lead name, email, subject, URL..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="flex-1 bg-slate-900 border border-slate-700 focus:border-indigo-500 text-slate-200 rounded-xl px-4 py-2 text-sm outline-none"
          />
        </div>

        {/* Events List */}
        <div className="bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            {filteredEvents.length === 0 ? (
              <div className="text-center py-12 text-slate-600">
                {searchTerm || filter !== "all" ? "No events match your filters" : "No tracking events yet"}
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-slate-800/60 border-b border-slate-700/60">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Event</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Email Subject</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Details</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Time</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {filteredEvents.map(e => (
                    <tr key={e.id} className="hover:bg-slate-800/40 transition">
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold border ${
                          e.event_type === "open" 
                            ? "bg-emerald-900/40 border-emerald-700/40 text-emerald-300"
                            : "bg-blue-900/40 border-blue-700/40 text-blue-300"
                        }`}>
                          {e.event_type === "open" ? " Opened" : " Clicked"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white text-sm truncate max-w-[200px]">
                          {e.lead_name || e.recipient_email}
                        </div>
                        {e.lead_phone && (
                          <div className="text-xs text-slate-400 font-mono">{e.lead_phone}</div>
                        )}
                        <div className="text-xs text-slate-500 truncate">{e.lead_category || "–"}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm text-slate-300 truncate max-w-[250px]">{e.subject || "–"}</div>
                        <div className="text-xs text-slate-600">{e.recipient_email}</div>
                      </td>
                      <td className="px-4 py-3">
                        {e.event_type === "click" && e.target_url ? (
                          <a href={e.target_url} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 underline truncate block max-w-[300px]">
                            {e.target_url}
                          </a>
                        ) : (
                          <div className="text-xs text-slate-500">
                            Opens: {e.message_open_count || 0} – Clicks: {e.message_click_count || 0}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-400">{formatDate(e.created_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {e.message_id && (
                          <button
                            onClick={async () => {
                              if (viewTrackLoading) return;
                              setViewTrackLoading(true);
                              try {
                                const resp = await authFetch(`/outreach/messages/${e.message_id}`);
                                if (resp.ok) setViewTrackEmail(await resp.json());
                              } finally {
                                setViewTrackLoading(false);
                              }
                            }}
                            className="px-2.5 py-1 rounded-lg bg-indigo-700 hover:bg-indigo-600 text-white text-[10px] font-bold transition whitespace-nowrap"
                          >
                            View Email
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {/* Pagination */}
        {trackPages > 1 && (
          <div className="flex items-center justify-between py-3 px-1">
            <span className="text-xs text-slate-500">{trackTotal} total events</span>
            <div className="flex gap-1.5">
              <button onClick={() => setTrackPage(p => Math.max(1, p-1))} disabled={trackPage === 1}
                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 rounded-lg"> Prev</button>
              {Array.from({length: Math.min(trackPages, 7)}, (_, i) => {
                const p = trackPages <= 7 ? i+1 : trackPage <= 4 ? i+1 : trackPage >= trackPages-3 ? trackPages-6+i : trackPage-3+i;
                return (
                  <button key={p} onClick={() => setTrackPage(p)}
                    className={`px-3 py-1.5 text-xs rounded-lg font-medium ${trackPage===p ? "bg-indigo-600 text-white" : "bg-slate-800 hover:bg-slate-700 text-slate-400"}`}>
                    {p}
                  </button>
                );
              })}
              <button onClick={() => setTrackPage(p => Math.min(trackPages, p+1))} disabled={trackPage === trackPages}
                className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 disabled:opacity-40 text-slate-300 rounded-lg">Next </button>
            </div>
          </div>
        )}
          </>
        )}

        {activeTab === "replies" && (
          <>
        {/* Reply Summary Stats */}
        {replySummary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Total Sent</div>
              <div className="text-3xl font-bold text-white mt-2">{replySummary.total_sent}</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Replied</div>
              <div className="text-3xl font-bold text-amber-400 mt-2">{replySummary.total_replied}</div>
              <div className="text-xs text-slate-600 mt-1">{replySummary.reply_rate}% reply rate</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Replied Today</div>
              <div className="text-3xl font-bold text-orange-400 mt-2">{replySummary.replied_today}</div>
              <div className="text-xs text-slate-600 mt-1">{replySummary.total_reply_count} total replies</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Avg Replies</div>
              <div className="text-3xl font-bold text-purple-400 mt-2">
                {(replySummary.total_sent > 0 ? (replySummary.total_reply_count / replySummary.total_sent).toFixed(2) : 0)}
              </div>
              <div className="text-xs text-slate-600 mt-1">per message</div>
            </div>
          </div>
        )}

        {/* Top Responders */}
        {replySummary && replySummary.top_responders && replySummary.top_responders.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-white mb-4"> Top Respondents</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {replySummary.top_responders.map(responder => (
                <div key={responder.id} className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3">
                  <div className="font-semibold text-white text-sm truncate">{responder.name}</div>
                  <div className="text-xs text-slate-400 font-mono truncate">{responder.email}</div>
                  {responder.phone && (
                    <div className="text-xs text-slate-500 font-mono">{responder.phone}</div>
                  )}
                  <div className="flex items-center gap-3 mt-2">
                    <span className="text-xs bg-amber-900/40 border border-amber-700/40 text-amber-300 px-2 py-1 rounded">
                      {responder.reply_count} {responder.reply_count === 1 ? "reply" : "replies"}
                    </span>
                  </div>
                  <div className="text-[10px] text-slate-600 mt-1">
                    Last: {responder.last_reply ? new Date(responder.last_reply).toLocaleDateString() : "–"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!replySummary || (replySummary.total_replied === 0 && (!replySummary.top_responders || replySummary.top_responders.length === 0))) && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center">
            <div className="text-5xl mb-3"></div>
            <div className="text-slate-400">No replies received yet</div>
            <div className="text-xs text-slate-600 mt-2">When you receive replies to your outreach emails, they'll appear here</div>
          </div>
        )}
          </>
        )}

        {activeTab === "ab" && <AbTestPanel authFetch={authFetch} />}
      </div>

      {viewTrackEmail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setViewTrackEmail(null)}>
          <div className="w-full max-w-2xl max-h-[85vh] flex flex-col rounded-2xl border border-slate-700 bg-slate-950 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-800 px-5 py-4">
              <div>
                <h3 className="text-sm font-bold text-white">Email Sent to {viewTrackEmail.lead_name}</h3>
                <p className="text-xs text-slate-400 mt-0.5">{viewTrackEmail.lead_email}{viewTrackEmail.sent_at ? ` · ${new Date(viewTrackEmail.sent_at).toLocaleString()}` : ""}</p>
              </div>
              <button onClick={() => setViewTrackEmail(null)} className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-bold text-slate-300 hover:bg-slate-700">Close</button>
            </div>
            <div className="overflow-y-auto p-5 space-y-4">
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Subject</p>
                <p className="text-sm font-semibold text-indigo-300">{viewTrackEmail.subject_variant_used === "b" ? viewTrackEmail.subject_b : viewTrackEmail.subject}</p>
                {viewTrackEmail.subject_b && (
                  <p className="text-[10px] text-slate-600 mt-0.5">Other variant: {viewTrackEmail.subject_variant_used === "b" ? viewTrackEmail.subject : viewTrackEmail.subject_b}</p>
                )}
              </div>
              <div className="flex gap-4">
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(viewTrackEmail.open_count || 0) > 0 ? "bg-emerald-900/40 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>
                  {(viewTrackEmail.open_count || 0) > 0 ? `Opened ${viewTrackEmail.open_count}x` : "Not opened"}
                </span>
                <span className={`px-2 py-1 rounded-full text-[10px] font-bold ${(viewTrackEmail.click_count || 0) > 0 ? "bg-indigo-900/40 text-indigo-300" : "bg-slate-800 text-slate-500"}`}>
                  {(viewTrackEmail.click_count || 0) > 0 ? `Clicked ${viewTrackEmail.click_count}x` : "No click"}
                </span>
              </div>
              <div>
                <p className="text-[10px] text-slate-500 uppercase font-bold mb-1">Message Body</p>
                <div className="bg-slate-900 border border-slate-800 rounded-xl p-4">
                  <p className="text-sm text-slate-300 whitespace-pre-wrap leading-relaxed">{viewTrackEmail.body}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {viewTrackLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
}

//  Link Analytics Page
function LinkAnalyticsPage() {
  const { authFetch } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [statsData, setStatsData] = useState(null);
  const [linkPerf, setLinkPerf] = useState([]);
  const [leadEng, setLeadEng] = useState([]);
  const [campaignPerf, setCampaignPerf] = useState([]);
  const [days, setDays] = useState(null); // null = all time, or number of days
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadHistory, setLeadHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [sortField, setSortField] = useState('total_clicks');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = useCallback(async ({ showLoader = true } = {}) => {
    if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const daysParam = days !== null ? `?days=${days}` : '';
      const daysParamAmp = days !== null ? `&days=${days}` : '';
      const [statsResp, linksResp, leadsResp, campaignsResp] = await Promise.all([
        authFetch(`/analytics/click-stats${daysParam}`),
        authFetch(`/analytics/link-performance${daysParam}${daysParam ? '&' : '?'}limit=50`),
        authFetch(`/analytics/lead-engagement${daysParam}${daysParam ? '&' : '?'}limit=50`),
        authFetch(`/analytics/campaign-performance${daysParam}`)
      ]);
      if (statsResp.ok) setStatsData(await statsResp.json());
      if (linksResp.ok) setLinkPerf(await linksResp.json());
      if (leadsResp.ok) setLeadEng(await leadsResp.json());
      if (campaignsResp.ok) setCampaignPerf(await campaignsResp.json());
    } catch (e) {
      console.error("Failed to load analytics:", e);
    } finally {
      if (showLoader) setLoading(false); else setRefreshing(false);
    }
  }, [authFetch, days]);

  // Initial load and when day-range changes
  useEffect(() => {
    fetchData({ showLoader: true });
  }, [fetchData]);

  // Background auto-refresh every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData({ showLoader: false });
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchLeadHistory = async (leadId) => {
    setHistoryLoading(true);
    try {
      const resp = await authFetch(`/analytics/lead/${leadId}/clicks?limit=100`);
      if (resp.ok) {
        setLeadHistory(await resp.json());
      }
    } catch (e) {
      console.error("Failed to load lead history:", e);
    }
    setHistoryLoading(false);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedLinkPerf = [...linkPerf].sort((a, b) => {
    let valA = a[sortField];
    let valB = b[sortField];
    if (sortField === 'original_url' || sortField === 'lead_name' || sortField === 'lead_email') {
      valA = (valA || '').toLowerCase();
      valB = (valB || '').toLowerCase();
      return sortDirection === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    }
    if (sortField === 'message_sent_at' || sortField === 'last_clicked_at') {
      valA = valA ? new Date(valA).getTime() : 0;
      valB = valB ? new Date(valB).getTime() : 0;
    }
    return sortDirection === 'asc' ? valA - valB : valB - valA;
  });

  const formatDate = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white"> Link Analytics</h1>
            <p className="text-sm text-slate-500 mt-1">Track all link clicks across emails, website, and campaigns</p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            {refreshing && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                <span>Auto-updating–</span>
              </div>
            )}
            <button onClick={() => setDays(null)}
              className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                days === null
                  ? "bg-indigo-700 border-indigo-500 text-white"
                  : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}>
              All Time
            </button>
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setDays(d)}
                className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                  days === d
                    ? "bg-indigo-700 border-indigo-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                }`}>
                {d} Days
              </button>
            ))}
          </div>
        </div>

        {/* Overall Stats */}
        {statsData && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Total Links</div>
              <div className="text-3xl font-bold text-white mt-2">{statsData.total_links || 0}</div>
              <div className="text-xs text-slate-600 mt-1">Tracked links created</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Total Clicks</div>
              <div className="text-3xl font-bold text-blue-400 mt-2">{statsData.total_clicks || 0}</div>
              <div className="text-xs text-slate-600 mt-1">{statsData.unique_clicks || 0} unique clicks</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Engaged Leads</div>
              <div className="text-3xl font-bold text-emerald-400 mt-2">{statsData.engaged_leads || 0}</div>
              <div className="text-xs text-slate-600 mt-1">Clicked at least once</div>
            </div>
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
              <div className="text-sm text-slate-500 font-semibold">Click Rate</div>
              <div className="text-3xl font-bold text-amber-400 mt-2">
                {(statsData.total_links || 0) > 0 ? (((statsData.total_clicks || 0) / statsData.total_links) * 100).toFixed(1) : 0}%
              </div>
              <div className="text-xs text-slate-600 mt-1">Avg clicks per link</div>
            </div>
          </div>
        )}

        {/* Device & Browser Breakdown */}
        {statsData && (statsData.devices?.length > 0 || statsData.browsers?.length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Devices */}
            {statsData.devices?.length > 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-sm font-bold text-white mb-4"> Device Breakdown</h3>
                <div className="space-y-3">
                  {statsData.devices.map(d => {
                    const total = statsData.devices.reduce((a, b) => a + b.count, 0);
                    const percent = ((d.count / total) * 100).toFixed(1);
                    return (
                      <div key={d.type} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white capitalize">{d.type}</span>
                            <span className="text-xs text-slate-400">{d.count} ({percent}%)</span>
                          </div>
                          <div className="w-full bg-slate-800 rounded-full h-2">
                            <div className="bg-indigo-500 h-2 rounded-full" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            {/* Browsers */}
            {statsData.browsers?.length > 0 && (
              <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
                <h3 className="text-sm font-bold text-white mb-4"> Browser Breakdown</h3>
                <div className="space-y-3">
                  {statsData.browsers.map(b => {
                    const total = statsData.browsers.reduce((a, c) => a + c.count, 0);
                    const percent = ((b.count / total) * 100).toFixed(1);
                    return (
                      <div key={b.name} className="flex items-center gap-3">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm text-white capitalize">{b.name}</span>
                            <span className="text-xs text-slate-400">{b.count} ({percent}%)</span>
                          </div>
                          <div className="w-full bg-slate-800 rounded-full h-2">
                            <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${percent}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Top Engaged Leads */}
        {leadEng.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-white mb-4"> Most Engaged Leads</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/60 border-b border-slate-700/60">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Lead</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Contact</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Engagement</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Last Click</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {leadEng.slice(0, 20).map(lead => (
                    <tr key={lead.lead_id} className="hover:bg-slate-800/40 transition">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-white text-sm">{lead.name}</div>
                        {lead.website && (
                          <a href={lead.website} target="_blank" rel="noopener noreferrer"
                            className="text-xs text-blue-400 hover:text-blue-300 truncate block max-w-[200px]">
                            {lead.website}
                          </a>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {lead.email && <div className="text-xs text-slate-400">{lead.email}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-blue-400">{lead.total_clicks} clicks</span>
                          <span className="text-xs text-slate-500">{lead.unique_links_clicked} unique links</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-400">{formatDate(lead.last_click_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <button onClick={() => { setSelectedLead(lead); fetchLeadHistory(lead.lead_id); }}
                          className="px-3 py-1 bg-indigo-700 hover:bg-indigo-600 border border-indigo-500 rounded-lg text-xs font-bold text-white transition">
                          View History
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Top Performing Links */}
        {linkPerf.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-white mb-4"> Top Performing Links</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/60 border-b border-slate-700/60">
                  <tr>
                    <th onClick={() => handleSort('original_url')} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase cursor-pointer hover:text-slate-200">
                      URL {sortField === 'original_url' && (sortDirection === 'asc' ? '' : '')}
                    </th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Lead Info</th>
                    <th onClick={() => handleSort('total_clicks')} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase cursor-pointer hover:text-slate-200">
                      Clicks {sortField === 'total_clicks' && (sortDirection === 'asc' ? '' : '')}
                    </th>
                    <th onClick={() => handleSort('message_sent_at')} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase cursor-pointer hover:text-slate-200">
                      Sent {sortField === 'message_sent_at' && (sortDirection === 'asc' ? '' : '')}
                    </th>
                    <th onClick={() => handleSort('last_clicked_at')} className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase cursor-pointer hover:text-slate-200">
                      Last Click {sortField === 'last_clicked_at' && (sortDirection === 'asc' ? '' : '')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {sortedLinkPerf.slice(0, 20).map(link => (
                    <tr key={link.id} className="hover:bg-slate-800/40 transition">
                      <td className="px-4 py-3">
                        <a href={link.original_url} target="_blank" rel="noopener noreferrer"
                          className="text-sm text-blue-400 hover:text-blue-300 underline truncate block max-w-[300px]">
                          {link.original_url}
                        </a>
                        <div className="text-xs text-slate-600 font-mono mt-1">{link.tracking_url}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-300">
                          {link.lead_name && <div className="font-semibold"> {link.lead_name}</div>}
                          {link.lead_email && <div className="text-slate-400"> {link.lead_email}</div>}
                          {link.lead_phone && <div className="text-slate-400"> {link.lead_phone}</div>}
                          {!link.lead_name && !link.lead_email && <div className="text-slate-600">No lead info</div>}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-blue-400">{link.total_clicks} total</span>
                          <span className="text-xs text-slate-500">{link.unique_clicks} unique</span>
                          <span className="text-xs text-slate-500">{link.unique_ips} IPs</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-400">{formatDate(link.message_sent_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-xs text-slate-400">{formatDate(link.last_clicked_at)}</div>
                        <div className="text-[10px] text-slate-600">First: {formatDate(link.first_clicked_at)}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Campaign Performance */}
        {campaignPerf.length > 0 && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-5">
            <h3 className="text-sm font-bold text-white mb-4"> Campaign Performance</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-800/60 border-b border-slate-700/60">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Campaign</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Links</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Clicks</th>
                    <th className="text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase">Engaged Leads</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {campaignPerf.map((camp, idx) => (
                    <tr key={idx} className="hover:bg-slate-800/40 transition">
                      <td className="px-4 py-3">
                        <div className="text-sm font-semibold text-white">{camp.campaign_name || "Unnamed"}</div>
                        <div className="text-xs text-slate-500">
                          {camp.campaign_source}/{camp.campaign_medium}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm text-slate-300">{camp.total_links}</span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          <span className="text-sm font-bold text-blue-400">{camp.total_clicks} total</span>
                          <span className="text-xs text-slate-500">{camp.unique_clicks} unique</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-sm font-bold text-emerald-400">{camp.engaged_leads}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Lead Click History Modal */}
        {selectedLead && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedLead(null)}>
            <div className="bg-slate-900 border border-slate-700 rounded-2xl max-w-4xl w-full max-h-[80vh] overflow-hidden flex flex-col"
              onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between p-5 border-b border-slate-800">
                <div>
                  <h3 className="text-lg font-bold text-white">{selectedLead.name}</h3>
                  <p className="text-sm text-slate-500">Click History</p>
                </div>
                <button onClick={() => setSelectedLead(null)}
                  className="text-slate-400 hover:text-white p-2">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto p-5">
                {historyLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : leadHistory.length === 0 ? (
                  <div className="text-center py-12 text-slate-600">No click history</div>
                ) : (
                  <div className="space-y-3">
                    {leadHistory.map((h, idx) => (
                      <div key={idx} className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <a href={h.url} target="_blank" rel="noopener noreferrer"
                              className="text-sm text-blue-400 hover:text-blue-300 underline block mb-2">
                              {h.url}
                            </a>
                            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
                              <span> {h.device}</span>
                              <span> {h.browser}</span>
                              <span> {h.os}</span>
                              {h.campaign_source && <span> {h.campaign_source}/{h.campaign_medium}</span>}
                            </div>
                          </div>
                          <div className="text-xs text-slate-400 whitespace-nowrap">
                            {formatDate(h.clicked_at)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && (!statsData || statsData.total_links === 0) && (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-12 text-center">
            <div className="text-6xl mb-4"></div>
            <h3 className="text-xl font-bold text-white mb-2">No Link Tracking Data Yet</h3>
            <p className="text-slate-500 text-sm max-w-md mx-auto mb-6">
              Start sending outreach emails with links to see tracking data here. All email links are automatically tracked!
            </p>
            <button onClick={() => window.location.href = '/outreach'}
              className="px-6 py-3 bg-indigo-700 hover:bg-indigo-600 border border-indigo-500 rounded-xl text-sm font-bold text-white transition">
              Go to Outreach
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

//  Auto Follow-ups Page 
function DripSequencesPage({ userLimits }) {
  const { authFetch } = useAuth();
  const { canUse } = usePlan();
  const [sequences, setSequences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState("");
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // null | {id?, name, steps, is_active, folder_id}
  const enabled = userLimits?.limits?.drip_sequences_enabled !== false;
  const [activity, setActivity] = useState({}); // {seqId: {loading, data}}

  const loadActivity = async (seqId) => {
    if (activity[seqId]?.data) return; // already loaded
    setActivity(prev => ({ ...prev, [seqId]: { loading: true } }));
    try {
      const r = await authFetch(`/drip-sequences/${seqId}/activity`);
      if (r.ok) { const d = await r.json(); setActivity(prev => ({ ...prev, [seqId]: { loading: false, data: d } })); }
    } catch(e) { setActivity(prev => ({ ...prev, [seqId]: { loading: false } })); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authFetch("/drip-sequences");
      if (r.ok) setSequences(await r.json());
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const SEGMENTS = [
    { value: "all",             label: "All sent leads",           icon: "" },
    { value: "not_opened",      label: "Not opened at all",        icon: "" },
    { value: "opened_no_click", label: "Opened but didn't click",  icon: "" },
    { value: "clicked",         label: "Clicked a link",           icon: "" },
    { value: "multiple_clicks", label: "Clicked multiple times",   icon: "" },
    { value: "replied",         label: "Already replied",          icon: "" },
  ];

  const save = async () => {
    if (!editing) return;
    setSaving(true); setError(""); setInfo("");
    try {
      const isNew = !editing.id;
      const payload = { ...editing, match_send_time: editing.match_send_time !== false };
      const resp = await authFetch(isNew ? "/drip-sequences" : `/drip-sequences/${editing.id}`, {
        method: isNew ? "POST" : "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok) { setError(d.detail || "Failed to save"); return; }
      setInfo(isNew ? "Sequence created!" : "Saved.");
      setEditing(null);
      await load();
    } catch(e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const toggle = async (seq) => {
    await authFetch(`/drip-sequences/${seq.id}`, { method: "PATCH", headers: {"Content-Type":"application/json"}, body: JSON.stringify({is_active: !seq.is_active}) });
    await load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete this auto follow-up?")) return;
    await authFetch(`/drip-sequences/${id}`, { method: "DELETE" });
    await load();
  };

  const addStep = () => setEditing(e => ({ ...e, steps: [...(e.steps||[]), { delay_days: 3, instructions: "" }] }));
  const updateStep = (idx, field, val) => setEditing(e => ({ ...e, steps: e.steps.map((s,i) => i===idx ? {...s,[field]:val} : s) }));
  const removeStep = (idx) => setEditing(e => ({ ...e, steps: e.steps.filter((_,i) => i!==idx) }));

  return (
    <div className="p-3 sm:p-6 space-y-6 max-w-4xl">
      {!canUse('growth') && <UpgradeBanner requiredPlan="growth" featureName="Auto Follow-ups" />}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white"> Auto Follow-ups</h1>
          <p className="text-sm text-slate-500 mt-1">Auto-send follow-ups on a schedule — if no reply in X days, send the next message automatically.</p>
        </div>
        {enabled && canUse('growth') ? (
          <button onClick={() => setEditing({ name:"New Sequence", steps:[{delay_days:3,instructions:""},{delay_days:5,instructions:""}], is_active:true, target_segment:"all", match_send_time:true })}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 text-white font-bold rounded-lg text-sm transition">
            + New Sequence
          </button>
        ) : (
          <div className="px-4 py-2 bg-slate-800 border border-slate-700 text-slate-500 rounded-lg text-sm">Upgrade your plan to enable Auto Follow-ups</div>
        )}
      </div>

      {info && <div className="px-4 py-2 bg-emerald-900/30 border border-emerald-700 text-emerald-300 text-sm rounded-lg">{info}</div>}
      {error && <div className="px-4 py-2 bg-red-900/30 border border-red-700 text-red-300 text-sm rounded-lg">{error}</div>}

      {!enabled && (
        <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 text-center">
          <div className="text-3xl mb-2"></div>
          <h3 className="font-bold text-white mb-1">Auto Follow-ups — Pro Feature</h3>
          <p className="text-sm text-slate-400">Your admin needs to enable this for your account. It automatically sends follow-up 1 after X days, follow-up 2 after Y more days — while you sleep.</p>
        </div>
      )}

      {/* Sequence editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={e => { if(e.target===e.currentTarget) setEditing(null); }}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-bold text-white text-lg">{editing.id ? "Edit Sequence" : "New auto follow-up"}</h2>
              <button onClick={() => setEditing(null)} className="text-slate-500 hover:text-white text-xl">&times;</button>
            </div>
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Sequence Name</label>
              <input value={editing.name} onChange={e => setEditing(s=>({...s,name:e.target.value}))}
                className="w-full mt-1 px-3 py-2 bg-slate-800 border border-slate-700 text-white rounded-lg text-sm outline-none focus:border-indigo-500" />
            </div>

            {/* Target Segment */}
            <div>
              <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Target — Who does this run for?</label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {SEGMENTS.map(seg => (
                  <button key={seg.value} onClick={() => setEditing(s=>({...s,target_segment:seg.value}))}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm text-left transition ${(editing.target_segment||"all")===seg.value ? "bg-indigo-900/50 border-indigo-600 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                    <span>{seg.icon}</span>
                    <span className="text-xs">{seg.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Time matching */}
            <div className="flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded-xl px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-white">Match original send time</p>
                <p className="text-xs text-slate-500 mt-0.5">Send follow-up at the same hour the original email went out (e.g. 2am  next 2am)</p>
              </div>
              <button onClick={() => setEditing(s=>({...s,match_send_time:!(s.match_send_time!==false)}))}
                className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ml-4 ${editing.match_send_time!==false ? "bg-emerald-600" : "bg-slate-700"}`}>
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${editing.match_send_time!==false ? "translate-x-5" : ""}`} />
              </button>
            </div>

            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Steps</label>
                <button onClick={addStep} className="text-xs px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg">+ Add Step</button>
              </div>
              <div className="space-y-3">
                {(editing.steps||[]).map((step, idx) => (
                  <div key={idx} className="bg-slate-800/60 border border-slate-700 rounded-xl p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full bg-indigo-700 flex items-center justify-center text-xs font-bold text-white shrink-0">{idx+1}</div>
                      <div className="flex items-center gap-2 flex-1">
                        <span className="text-sm text-slate-400">Send after</span>
                        <input type="number" min="1" max="60" value={step.delay_days}
                          onChange={e => updateStep(idx,"delay_days",parseInt(e.target.value)||1)}
                          className="w-16 px-2 py-1 bg-slate-900 border border-slate-600 text-white rounded text-sm text-center outline-none" />
                        <span className="text-sm text-slate-400">days with no reply</span>
                      </div>
                      <button onClick={() => removeStep(idx)} className="text-slate-600 hover:text-red-400 text-xs px-2"></button>
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 uppercase tracking-wider">AI Instructions for this step</label>
                      <textarea value={step.instructions} onChange={e => updateStep(idx,"instructions",e.target.value)} rows={2}
                        placeholder="e.g. Friendly nudge, reference the original email, keep it under 3 sentences"
                        className="w-full mt-1 px-3 py-2 bg-slate-900 border border-slate-600 text-white rounded-lg text-xs outline-none focus:border-indigo-500 resize-none" />
                    </div>
                  </div>
                ))}
                {(editing.steps||[]).length === 0 && (
                  <div className="text-center text-slate-600 text-sm py-4">No steps yet. Add a step above.</div>
                )}
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={save} disabled={saving} className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-700 text-white font-bold rounded-lg text-sm transition">
                {saving ? "Saving..." : "Save Sequence"}
              </button>
              <button onClick={() => setEditing(null)} className="px-4 py-2.5 bg-slate-800 text-slate-300 rounded-lg text-sm">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : sequences.length === 0 ? (
        <div className="py-16 text-center text-slate-500">
          <div className="text-4xl mb-3"></div>
          <p className="font-semibold text-slate-400">No Auto Follow-ups yet</p>
          <p className="text-sm mt-1">{enabled ? "Create one to auto-follow-up with leads on a schedule." : "Upgrade to access this feature."}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {sequences.map(seq => (
            <div key={seq.id} className={`bg-slate-900 border rounded-xl p-5 ${seq.is_active ? "border-slate-700" : "border-slate-800 opacity-60"}`}>
              <div className="flex items-start gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-bold text-white">{seq.name}</span>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${seq.is_active ? "bg-emerald-900/50 text-emerald-300" : "bg-slate-800 text-slate-500"}`}>
                      {seq.is_active ? "Active" : "Paused"}
                    </span>
                    {(() => { const seg = SEGMENTS.find(s=>s.value===(seq.target_segment||"all")); return seg ? <span className="text-[10px] px-2 py-0.5 bg-slate-800 text-slate-400 rounded-full">{seg.icon} {seg.label}</span> : null; })()}
                    {seq.match_send_time !== false && <span className="text-[10px] px-2 py-0.5 bg-indigo-900/40 text-indigo-400 rounded-full"> Time-matched</span>}
                  </div>
                  <div className="flex gap-4 text-xs text-slate-500 flex-wrap">
                    {(seq.steps||[]).map((s,i) => (
                      <span key={i} className="flex items-center gap-1">
                        <span className="text-indigo-400 font-bold">Step {i+1}:</span> after {s.delay_days}d
                      </span>
                    ))}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => { loadActivity(seq.id); setActivity(prev => ({ ...prev, [seq.id]: { ...prev[seq.id], open: !prev[seq.id]?.open } })); }}
                    className="px-3 py-1.5 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg">
                    {activity[seq.id]?.open ? "Hide" : "Activity"}
                  </button>
                  <button onClick={() => toggle(seq)} className={`px-3 py-1.5 text-xs font-bold rounded-lg transition ${seq.is_active ? "bg-amber-900/40 text-amber-300 hover:bg-amber-900/60" : "bg-emerald-900/40 text-emerald-300 hover:bg-emerald-900/60"}`}>
                    {seq.is_active ? "Pause" : "Resume"}
                  </button>
                  <button onClick={() => setEditing({...seq, steps: seq.steps||[]})} className="px-3 py-1.5 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg">Edit</button>
                  <button onClick={() => del(seq.id)} className="px-3 py-1.5 text-xs text-red-400 hover:bg-red-900/20 rounded-lg">Delete</button>
                </div>
              </div>

              {/* Activity panel */}
              {activity[seq.id]?.open && (
                <div className="mt-4 border-t border-slate-800 pt-4">
                  {activity[seq.id]?.loading ? (
                    <div className="flex justify-center py-4"><div className="w-5 h-5 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
                  ) : activity[seq.id]?.data ? (() => {
                    const a = activity[seq.id].data;
                    return (
                      <div className="space-y-3">
                        <div className="flex gap-4 text-xs">
                          <span className="text-slate-400"> <span className="text-white font-semibold">{a.total_sent}</span> sent</span>
                          <span className="text-slate-400"> <span className="text-white font-semibold">{a.total_opened}</span> opened</span>
                          <span className="text-slate-400"> <span className="text-white font-semibold">{a.total_clicked}</span> clicked</span>
                          <span className="text-emerald-400"> <span className="text-white font-semibold">{a.total_replied}</span> replied</span>
                          {a.last_sent_at && <span className="text-slate-500 ml-auto">Last sent {new Date(a.last_sent_at).toLocaleDateString()}</span>}
                        </div>
                        {a.recent.length === 0 ? (
                          <p className="text-xs text-slate-600">No emails sent yet by this sequence.</p>
                        ) : (
                          <div className="space-y-1">
                            {a.recent.map(r => (
                              <div key={r.id} className="flex items-center gap-3 text-xs py-1.5 border-b border-slate-800/60">
                                <span className="text-slate-300 font-medium w-36 truncate shrink-0">{r.lead_name}</span>
                                <span className="text-slate-500 flex-1 truncate">{r.subject}</span>
                                <span className="text-slate-600 shrink-0">{r.sent_at ? new Date(r.sent_at).toLocaleDateString() : ""}</span>
                                <span className="shrink-0 flex gap-1.5">
                                  {r.open_count > 0 && <span className="text-indigo-400"> {r.open_count}</span>}
                                  {r.click_count > 0 && <span className="text-amber-400"> {r.click_count}</span>}
                                  {r.reply_received && <span className="text-emerald-400"></span>}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })() : <p className="text-xs text-slate-600">Could not load activity.</p>}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4 text-xs text-slate-500 space-y-1">
        <p className="font-semibold text-slate-400">How Auto Follow-ups work</p>
        <p>– The system checks every 6 hours for leads who haven't replied</p>
        <p>– When a step's delay passes, a follow-up is auto-generated and sent</p>
        <p>– If a lead replies at any point, no more steps run for them</p>
        <p>– You can pause a sequence at any time without losing progress</p>
      </div>
    </div>
  );
}

//  Lead Leaderboard Page 
function LeadLeaderboardPage({ folders }) {
  const { authFetch } = useAuth();
  const { canUse } = usePlan();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderId, setFolderId] = useState("");
  const [folderSearch, setFolderSearch] = useState("");
  const [folderSearchOpen, setFolderSearchOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("all"); // "all" | "today" | "30" | "custom"
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [timeline, setTimeline] = useState(null); // {lead, events} | null
  const [tlLoading, setTlLoading] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [findingEmail, setFindingEmail] = useState(null);
  const [intelResult, setIntelResult] = useState({});
  const [emailResult, setEmailResult] = useState({});
  const [findingDM, setFindingDM] = useState(null);
  const [dmResult, setDmResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ limit: 100 });
      if (folderId) p.set("folder_id", folderId);
      if (dateFilter === "today") {
        const today = new Date().toISOString().slice(0, 10);
        p.set("start_date", today); p.set("end_date", today);
      } else if (dateFilter === "30") {
        const d = new Date(); d.setDate(d.getDate() - 30);
        p.set("start_date", d.toISOString().slice(0, 10));
      } else if (dateFilter === "custom" && customStart) {
        p.set("start_date", customStart);
        if (customEnd) p.set("end_date", customEnd);
      }
      const r = await authFetch(`/leads/leaderboard?${p}`);
      if (r.ok) setLeads(await r.json());
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  }, [authFetch, folderId, dateFilter, customStart, customEnd]);

  useEffect(() => { load(); }, [load]);

  const openTimeline = async (lead) => {
    setTlLoading(true); setTimeline({lead, events:[]});
    try {
      const r = await authFetch(`/leads/${lead.id}/timeline`);
      if (r.ok) { const d = await r.json(); setTimeline(d); }
    } catch(e) { console.error(e); }
    finally { setTlLoading(false); }
  };

  const addNote = async (leadId) => {
    if (!newNote.trim()) return;
    setAddingNote(true);
    await authFetch(`/leads/${leadId}/timeline/note`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify({note:newNote,note_type:"note"}) });
    setNewNote("");
    setAddingNote(false);
    openTimeline(timeline.lead);
  };

  const findEmail = async (lead) => {
    setFindingEmail(lead.id);
    try {
      const r = await authFetch(`/leads/${lead.id}/find-email`, { method:"POST" });
      if (r.ok) { const d = await r.json(); setEmailResult(prev => ({...prev, [lead.id]: d})); }
    } catch(e) { console.error(e); }
    finally { setFindingEmail(null); }
  };

  const findDMs = async (lead) => {
    setFindingDM(lead.id);
    try {
      const r = await authFetch(`/leads/${lead.id}/find-decision-makers`, { method:"POST" });
      if (r.ok) { const d = await r.json(); setDmResult(prev => ({...prev, [lead.id]: d})); }
    } catch(e) { console.error(e); }
    finally { setFindingDM(null); }
  };

  const runIntel = async (lead) => {
    setFindingEmail(lead.id);
    setFindingDM(lead.id);
    try {
      const [emailResp, dmResp] = await Promise.all([
        authFetch(`/leads/${lead.id}/find-email`, { method: "POST" }),
        authFetch(`/leads/${lead.id}/find-decision-makers`, { method: "POST" }),
      ]);
      const emailData = emailResp.ok ? await emailResp.json() : null;
      const dmData = dmResp.ok ? await dmResp.json() : null;
      setIntelResult(prev => ({ ...prev, [lead.id]: { email: emailData, dm: dmData } }));
    } catch(e) { console.error(e); }
    finally { setFindingEmail(null); setFindingDM(null); }
  };

  const [generatingEmail, setGeneratingEmail] = useState(null);
  const [emailDraft, setEmailDraft] = useState({});

  const generateEmail = async (lead, andSend = false) => {
    setGeneratingEmail(lead.id);
    try {
      const r = await authFetch(`/leads/${lead.id}/quick-email?send=${andSend}`, { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        setEmailDraft(prev => ({ ...prev, [lead.id]: d }));
      }
    } catch(e) { console.error(e); }
    finally { setGeneratingEmail(null); }
  };

  const sendDraft = async (lead) => {
    const draft = emailDraft[lead.id];
    if (!draft) return;
    setGeneratingEmail(lead.id);
    try {
      const r = await authFetch(`/leads/${lead.id}/quick-email?send=true`, { method: "POST" });
      if (r.ok) {
        const d = await r.json();
        setEmailDraft(prev => ({ ...prev, [lead.id]: { ...d, queued: true } }));
      }
    } catch(e) { console.error(e); }
    finally { setGeneratingEmail(null); }
  };

  const selectedFolder = (folders||[]).find(f => String(f.id) === String(folderId));
  const filteredFolders = folderSearch.trim()
    ? (folders||[]).filter(f => f.name.toLowerCase().includes(folderSearch.trim().toLowerCase()))
    : (folders||[]);

  const scoreColor = (s) => s >= 30 ? "text-red-400" : s >= 15 ? "text-amber-400" : s >= 5 ? "text-indigo-400" : "text-slate-500";
  const scoreBg = (s) => s >= 30 ? "bg-red-900/30 border-red-700/50" : s >= 15 ? "bg-amber-900/30 border-amber-700/50" : s >= 5 ? "bg-indigo-900/30 border-indigo-700/50" : "bg-slate-800 border-slate-700";
  const scoreLabel = (s) => s >= 30 ? "🔥 Hot" : s >= 15 ? "🌡 Warm" : s >= 5 ? "📬 Active" : "❄ Cold";

  const tlEventIcon = {email_sent:"",email_opened:"",reply:"",site_visit:"",link_click:"",note:"",email_draft:""};

  return (
    <div className="p-3 sm:p-6 space-y-5 max-w-5xl">
      {!canUse('growth') && <UpgradeBanner requiredPlan="growth" featureName="Lead Leaderboard" />}
      <div>
        <h1 className="text-2xl font-bold text-white"> Lead Leaderboard</h1>
        <p className="text-sm text-slate-500 mt-1">Ranked by engagement score — opens, clicks, site visits, replies. Know who's hottest right now.</p>
      </div>

      <div className="space-y-3">
        <div className="flex gap-3 flex-wrap items-center">
          {/* Folder search */}
          <div className="relative">
            <div
              className="flex items-center gap-2 px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg cursor-pointer min-w-48"
              onClick={() => setFolderSearchOpen(o => !o)}
            >
              <svg className="w-3.5 h-3.5 text-slate-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"/></svg>
              <span className="text-sm text-slate-200 flex-1 truncate">{selectedFolder ? selectedFolder.name : "All Folders"}</span>
              {folderId && <button onClick={e => { e.stopPropagation(); setFolderId(""); setFolderSearch(""); }} className="text-slate-500 hover:text-white text-xs"></button>}
            </div>
            {folderSearchOpen && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-slate-900 border border-slate-700 rounded-lg shadow-xl z-20">
                <div className="p-2 border-b border-slate-800">
                  <input
                    autoFocus
                    type="text"
                    value={folderSearch}
                    onChange={e => setFolderSearch(e.target.value)}
                    placeholder="Search folders..."
                    className="w-full px-2 py-1.5 bg-slate-800 border border-slate-700 text-slate-200 rounded text-sm outline-none placeholder-slate-600"
                  />
                </div>
                <div className="max-h-52 overflow-y-auto">
                  <button
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${!folderId ? "text-indigo-400" : "text-slate-300"}`}
                    onClick={() => { setFolderId(""); setFolderSearch(""); setFolderSearchOpen(false); }}
                  >All Folders</button>
                  {filteredFolders.map(f => (
                    <button key={f.id}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-800 ${String(folderId)===String(f.id) ? "text-indigo-400" : "text-slate-300"}`}
                      onClick={() => { setFolderId(String(f.id)); setFolderSearch(""); setFolderSearchOpen(false); }}
                    >{f.name}</button>
                  ))}
                  {filteredFolders.length === 0 && <p className="px-3 py-2 text-xs text-slate-600">No folders found</p>}
                </div>
              </div>
            )}
          </div>

          {/* Date filter pills */}
          <div className="flex gap-1.5">
            {[["all","All time"],["today","Today"],["30","Last 30 days"],["custom","Custom"]].map(([v,l]) => (
              <button key={v} onClick={() => setDateFilter(v)}
                className={`px-3 py-1.5 text-xs rounded-lg font-medium transition ${dateFilter===v ? "bg-indigo-600 text-white" : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
                {l}
              </button>
            ))}
          </div>

          <div className="ml-auto text-xs text-slate-600">Score = opens • 2 + clicks • 5 + replies • 20 + visits • 3</div>
        </div>

        {/* Custom date range row */}
        {dateFilter === "custom" && (
          <div className="flex gap-2 items-center">
            <span className="text-xs text-slate-500">From</span>
            <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)}
              className="px-2 py-1.5 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg text-xs outline-none focus:border-indigo-500" />
            <span className="text-xs text-slate-500">to</span>
            <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              className="px-2 py-1.5 bg-slate-900 border border-slate-700 text-slate-200 rounded-lg text-xs outline-none focus:border-indigo-500" />
            <button onClick={load} className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 text-white rounded-lg font-medium">Apply</button>
          </div>
        )}

        <div className="flex gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1"><span className="text-red-400">🔥</span> Hot = 30+</span>
          <span className="flex items-center gap-1"><span className="text-amber-400">🌡</span> Warm = 15+</span>
          <span className="flex items-center gap-1"><span className="text-indigo-400">📬</span> Active = 5+</span>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : leads.length === 0 ? (
        <div className="py-16 text-center text-slate-500">
          <div className="text-4xl mb-3"></div>
          <p>{dateFilter !== "all" ? "No engaged leads in this time period." : "No engaged leads yet. Send some outreach first."}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {leads.map((lead, idx) => (
            <div key={lead.id} className={`border rounded-xl p-4 ${scoreBg(lead.engagement_score)}`}>
              <div className="flex items-center gap-4">
                <div className="text-2xl font-black text-slate-600 w-8 text-center shrink-0">#{idx+1}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-bold text-white">{lead.name}</span>
                    <span className={`text-xs font-bold ${scoreColor(lead.engagement_score)}`}>{scoreLabel(lead.engagement_score)}</span>
                  </div>
                  <div className="flex gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                    {lead.total_opens > 0 && <span> {lead.total_opens} opens</span>}
                    {lead.total_clicks > 0 && <span> {lead.total_clicks} clicks</span>}
                    {lead.total_replies > 0 && <span className="text-emerald-400"> {lead.total_replies} replies</span>}
                    {lead.site_visits > 0 && <span> {lead.site_visits} visits</span>}
                    {lead.emails_sent > 0 && <span>{lead.emails_sent} emails sent</span>}
                  </div>
                  <div className="flex gap-3 text-xs mt-1.5 flex-wrap">
                    {lead.phone && <span className="text-slate-400"> {lead.phone}</span>}
                    {lead.email && <span className="text-slate-500"> {lead.email}</span>}
                  </div>
                </div>
                <div className={`text-2xl font-black ${scoreColor(lead.engagement_score)} shrink-0`}>{lead.engagement_score}</div>
                <div className="flex gap-1.5 shrink-0 flex-wrap justify-end">
                  <button onClick={() => openTimeline(lead)} className="px-3 py-1.5 text-xs bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-medium">Timeline</button>
                  <button onClick={() => generateEmail(lead)} disabled={generatingEmail===lead.id}
                    className="px-3 py-1.5 text-xs bg-emerald-700 hover:bg-emerald-600 disabled:bg-emerald-900 text-white rounded-lg font-medium flex items-center gap-1.5">
                    {generatingEmail===lead.id ? <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />Writing–</> : "Generate"}
                  </button>
                  <button onClick={() => runIntel(lead)} disabled={findingDM===lead.id || findingEmail===lead.id}
                    className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-500 disabled:bg-indigo-800 text-white rounded-lg font-medium flex items-center gap-1.5">
                    {(findingDM===lead.id || findingEmail===lead.id) ? (
                      <><div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />Searching</>
                    ) : "Intel"}
                  </button>
                </div>
              </div>
              {(findingDM===lead.id || findingEmail===lead.id) && !intelResult[lead.id] && (
                <div className="mt-3 bg-slate-900/70 border border-indigo-800/40 rounded-xl p-4 text-xs">
                  <div className="flex items-center gap-2 text-slate-300">
                    <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin shrink-0" />
                    <div>
                      <p className="font-semibold text-white mb-0.5">Running company intelligence on <span className="text-indigo-300">{lead.name}</span></p>
                      <p className="text-slate-500">Pulling contact data, scanning company profile, finding key people...</p>
                    </div>
                  </div>
                </div>
              )}
              {intelResult[lead.id] && (() => {
                const intel = intelResult[lead.id];
                const em = intel.email || {};
                const dm = intel.dm || {};
                const people = (dm.contacts || []).filter(p => p.name && p.name.split(' ').length >= 2);
                const companyEmail = em.recommended || '';
                const companyUrl = dm.company_url || '';
                if (!companyEmail && people.length === 0) return null;
                return (
                  <div className="mt-3 bg-slate-900/70 border border-slate-700/50 rounded-xl text-xs overflow-hidden">
                    {/* Header — only show if company LinkedIn found */}
                    {companyUrl && (
                      <div className="px-3 py-2 bg-slate-800/60 border-b border-slate-700/50 flex items-center gap-2">
                        <a href={companyUrl} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] px-2 py-0.5 bg-blue-900/40 text-blue-300 hover:text-blue-200 rounded font-medium">
                          View Company on LinkedIn 
                        </a>
                      </div>
                    )}
                    {/* Company email */}
                    {companyEmail && (
                      <div className="px-3 py-2.5 flex items-center gap-2 border-b border-slate-800">
                        <span className="text-slate-500 shrink-0">Company email</span>
                        <span className="text-emerald-300 font-mono font-semibold">{companyEmail}</span>
                      </div>
                    )}
                    {/* People — only if found, no "not found" message */}
                    {people.length > 0 && (
                      <div className="divide-y divide-slate-800">
                        {people.map((p, i) => (
                          <div key={i} className="px-3 py-2.5 flex items-center gap-3 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-white">{p.name}</span>
                                {p.title && <span className="text-indigo-400 text-[11px]">{p.title}</span>}
                              </div>
                              {p.email_guesses?.[0] && (
                                <div className="mt-0.5 font-mono text-slate-400 text-[11px]">{p.email_guesses[0]}</div>
                              )}
                            </div>
                            {p.linkedin_url && (
                              <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer"
                                className="shrink-0 text-[10px] px-2 py-1 bg-blue-900/40 text-blue-300 hover:text-blue-200 rounded font-medium whitespace-nowrap">
                                LinkedIn 
                              </a>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Email draft panel */}
              {emailDraft[lead.id] && (() => {
                const d = emailDraft[lead.id];
                return (
                  <div className="mt-3 bg-slate-900/80 border border-emerald-800/40 rounded-xl text-xs overflow-hidden">
                    <div className="px-3 py-2 bg-slate-800/60 border-b border-slate-700/50 flex items-center justify-between">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-emerald-400 font-semibold"> {d.is_followup ? "Follow-up draft" : "New draft"}</span>
                        {d.contact_name && <span className="text-indigo-300 text-[11px] font-medium"> {d.contact_name}</span>}
                        {d.to_email && <span className="text-slate-400 text-[11px]">{d.to_email}</span>}
                      </div>
                      <div className="flex gap-2">
                        {!d.queued && d.to_email && (
                          <button onClick={() => sendDraft(lead)} disabled={generatingEmail===lead.id}
                            className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-500 text-white rounded font-medium text-[11px]">
                            {generatingEmail===lead.id ? "Queuing–" : "Send"}
                          </button>
                        )}
                        {d.queued && <span className="text-emerald-400 font-semibold"> Queued</span>}
                        <button onClick={() => setEmailDraft(p => { const n={...p}; delete n[lead.id]; return n; })}
                          className="text-slate-500 hover:text-white px-1"></button>
                      </div>
                    </div>
                    <div className="px-3 py-2 border-b border-slate-800">
                      <span className="text-slate-500">Subject: </span>
                      <span className="text-white font-medium">{d.subject}</span>
                    </div>
                    <div className="px-3 py-3 text-slate-300 leading-relaxed whitespace-pre-wrap max-h-48 overflow-y-auto">{d.body}</div>
                  </div>
                );
              })()}

            </div>
          ))}
        </div>
      )}

      {/* Timeline side panel */}
      {timeline && (
        <div className="fixed inset-0 z-50 flex" onClick={e => { if(e.target===e.currentTarget) setTimeline(null); }}>
          <div className="ml-auto w-full max-w-lg bg-slate-950 border-l border-slate-800 flex flex-col h-full shadow-2xl">
            <div className="px-5 py-4 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h2 className="font-bold text-white">{timeline.lead?.name}</h2>
                <p className="text-xs text-slate-500 mt-0.5">Full activity timeline</p>
              </div>
              <button onClick={() => setTimeline(null)} className="text-slate-500 hover:text-white text-xl">&times;</button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {tlLoading ? (
                <div className="flex justify-center py-8"><div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : (timeline.events||[]).length === 0 ? (
                <p className="text-slate-500 text-sm text-center py-8">No activity yet for this lead.</p>
              ) : (
                [...(timeline.events||[])].reverse().map((ev, i) => (
                  <div key={i} className="flex gap-3 text-sm">
                    <div className="text-lg shrink-0 mt-0.5">{tlEventIcon[ev.type] || "–"}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-slate-200 font-medium">{ev.title}</p>
                      {ev.detail && <p className="text-xs text-slate-500 mt-0.5 break-words">{ev.detail}</p>}
                      {ev.ts && <p className="text-[11px] text-slate-600 mt-0.5">{new Date(ev.ts).toLocaleString()}</p>}
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="px-5 py-4 border-t border-slate-800">
              <div className="flex gap-2">
                <input value={newNote} onChange={e => setNewNote(e.target.value)} onKeyDown={e => e.key==="Enter" && addNote(timeline.lead?.id)}
                  placeholder="Add a note (e.g. Called, spoke to manager)..."
                  className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 text-white text-sm rounded-lg outline-none focus:border-indigo-500" />
                <button onClick={() => addNote(timeline.lead?.id)} disabled={addingNote || !newNote.trim()}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-800 text-white text-sm font-bold rounded-lg">
                  {addingNote ? "..." : "Add"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

//  Website Activity Page 
function WebsiteActivityPage() {
  const { authFetch, user } = useAuth();
  const { canUse } = usePlan();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState({});
  const [total, setTotal] = useState(0);
  const [days, setDays] = useState(30);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [eventType, setEventType] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [viewMode, setViewMode] = useState("sessions"); // "sessions" | "events" | "journeys"
  const [siteInsights, setSiteInsights] = useState(null);
  const [journeyVisitors, setJourneyVisitors] = useState([]);
  const [journeyLoading, setJourneyLoading] = useState(false);
  const [eventsOffset, setEventsOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [allEventsLoaded, setAllEventsLoaded] = useState(false);
  const [sortCol, setSortCol] = useState("first_event_at");
  const [sortDir, setSortDir] = useState("desc");
  const PAGE_SIZE = 100;

  const toInputDate = (date) => {
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };

  const applyPresetRange = (nextDays, nextStart = "", nextEnd = "") => {
    setDays(nextDays);
    setStartDate(nextStart);
    setEndDate(nextEnd);
  };

  const load = useCallback(async ({ showLoader = true, append = false, offset = 0 } = {}) => {
    if (append) setLoadingMore(true);
    else if (showLoader) setLoading(true); else setRefreshing(true);
    try {
      const params = new URLSearchParams();
      if (days !== null) params.set("days", String(days));
      if (eventType !== "all") params.set("event_type", eventType);
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      params.set("limit", String(PAGE_SIZE));
      params.set("offset", String(offset));
      const insightsParams = new URLSearchParams();
      if (days !== null) insightsParams.set("days", String(days));
      if (startDate) insightsParams.set("start_date", startDate);
      if (endDate) insightsParams.set("end_date", endDate);

      const [resp, insightsResp] = await Promise.all([
        authFetch(`/track/website-events?${params.toString()}`),
        authFetch(`/analytics/site-insights?${insightsParams.toString()}`),
      ]);
      // Only load journeys on explicit load (not auto-refresh)
      if (showLoader && !append) {
        setJourneyLoading(true);
        authFetch(`/analytics/journey-visitors?days=${days || 30}`)
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setJourneyVisitors(d.visitors || []); })
          .finally(() => setJourneyLoading(false));
      }
      if (resp.ok) {
        const data = await resp.json();
        const newEvents = data.events || [];
        if (append) {
          setEvents(prev => [...prev, ...newEvents]);
        } else {
          setEvents(newEvents);
        }
        setSummary(data.summary || {});
        setTotal(data.total || 0);
        setAllEventsLoaded(newEvents.length < PAGE_SIZE);
      }
      if (insightsResp.ok) {
        setSiteInsights(await insightsResp.json());
      }
    } catch (e) { console.error(e); }
    finally {
      if (append) setLoadingMore(false);
      else if (showLoader) setLoading(false); else setRefreshing(false);
    }
  }, [authFetch, days, eventType, startDate, endDate, PAGE_SIZE]);

  const loadMore = () => {
    const nextOffset = eventsOffset + PAGE_SIZE;
    setEventsOffset(nextOffset);
    load({ showLoader: false, append: true, offset: nextOffset });
  };

  // Reload whenever any filter changes — reset offset
  useEffect(() => {
    if ((startDate && startDate.length < 10) || (endDate && endDate.length < 10)) return;
    setEventsOffset(0);
    setAllEventsLoaded(false);
    load({ showLoader: true, offset: 0 });
  }, [load, days, eventType, startDate, endDate]);

  // Background auto-refresh — only when user is on page 1 (hasn't loaded more)
  useEffect(() => {
    const interval = setInterval(() => {
      if (eventsOffset === 0) load({ showLoader: false });
    }, 30000);
    return () => clearInterval(interval);
  }, [load, eventsOffset]);

  const fmt = (iso) => {
    if (!iso) return "–";
    const d = new Date(iso);
    const diff = Date.now() - d.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "Just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return d.toLocaleString();
  };

  const fmtFull = (iso) => (iso ? new Date(iso).toLocaleString() : "–");

  const shortUrl = (url) => {
    if (!url) return "–";
    try { return new URL(url).pathname || url; } catch { return url; }
  };

  const fmtDuration = (secs) => {
    if (!secs || secs <= 0) return "< 1s";
    if (secs < 60) return `${secs}s`;
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return s > 0 ? `${m}m ${s}s` : `${m}m`;
  };

  const describePerson = (ev) => {
    if (ev.person_name && ev.person_email) return `${ev.person_name}`;
    if (ev.person_name) return ev.person_name;
    if (ev.person_email) return ev.person_email;
    return "Anonymous visitor";
  };

  const formatDetailValue = (value) => {
    if (value === null || value === undefined || value === "") return "–";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    return String(value);
  };

  const formatLocation = (ev) => {
    const city = ev.metadata?.city;
    const region = ev.region || ev.metadata?.region;
    const country = ev.country || ev.metadata?.country;

    if (!city && !country) {
      return "–";
    }

    const parts = [];
    if (city) parts.push(city);
    if (country) parts.push(country);

    return parts.join(", ") || "–";
  };

  const extraMetadataEntries = (metadata) => Object.entries(metadata || {}).filter(([key]) => !["title", "path", "city", "region", "country", "country_code", "source"].includes(key));

  const typeColor = (t) => ({
    page_view: "bg-blue-900/50 text-blue-300",
    scroll_depth: "bg-purple-900/50 text-purple-300",
    form_submit: "bg-green-900/50 text-green-300",
    session_end: "bg-orange-900/50 text-orange-300",
    link_click: "bg-pink-900/50 text-pink-300",
  }[t] || "bg-slate-700 text-slate-300");

  const formatEventName = (eventType, eventName) => {
    const key = eventName || eventType || "";
    const labels = {
      page_view: "Page view",
      scroll_depth: "Scroll depth",
      form_submit: "Form submitted",
      session_end: "Session ended",
      link_click: "Link clicked",
      before_unload: "Page exit",
      visibility_hidden: "Tab hidden / backgrounded",
    };
    return labels[key] || key.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
  };

  const visibleEvents = user?.role === "admin" ? events : events.filter((ev) => !(user?.email && ev.person_email === user.email && !ev.lead_id));

  // Group events by session to show user journeys
  const sessionMap = {};
  visibleEvents.forEach((ev) => {
    const sid = ev.session_id || "unknown";
    if (!sessionMap[sid]) {
      sessionMap[sid] = {
        session_id: sid,
        person_name: ev.person_name || "Anonymous",
        person_email: ev.person_email || "–",
        person_phone: ev.person_phone || "–",
        device_type: ev.device_type || "–",
        location: [ev.city || ev.metadata?.city, ev.country || ev.metadata?.country].filter(Boolean).join(", ") || "–",
        first_event_at: ev.created_at,
        last_event_at: ev.created_at,
        page_sequence: [],
        events: [],
      };
    }
    const sess = sessionMap[sid];
    sess.events.push(ev);
    if (!sess.last_event_at || new Date(ev.created_at) > new Date(sess.last_event_at)) sess.last_event_at = ev.created_at;
    if (!sess.first_event_at || new Date(ev.created_at) < new Date(sess.first_event_at)) sess.first_event_at = ev.created_at;
  });

  // Build page sequences (deduplicated consecutive same pages)
  Object.values(sessionMap).forEach((sess) => {
    sess.events.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    let lastPage = null;
    let totalDwell = 0;
    sess.events.forEach((ev, idx) => {
      const page = ev.metadata?.path || ev.url || "(unknown)";
      if (ev.event_type === "page_view" || ev.dwell_seconds) {
        if (lastPage && page !== lastPage) {
          sess.page_sequence.push(lastPage);
        }
        lastPage = page;
      }
      if (ev.dwell_seconds) totalDwell += ev.dwell_seconds;
    });
    if (lastPage) sess.page_sequence.push(lastPage);
    sess.total_dwell_seconds = totalDwell;
    sess.session_duration_seconds = Math.round((new Date(sess.last_event_at) - new Date(sess.first_event_at)) / 1000);
  });

  const sessions = Object.values(sessionMap).sort((a, b) => new Date(b.last_event_at) - new Date(a.last_event_at));

  const EVENT_TYPES = ["all", "page_view", "scroll_depth", "form_submit", "session_end", "link_click"];

  if (loading) return (
    <div className="flex-1 flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-7xl mx-auto flex flex-col gap-6">
        {!canUse('growth') && <UpgradeBanner requiredPlan="growth" featureName="Website Activity & Visitor Identification" />}

        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-white"> Website Activity</h1>
            <p className="text-sm text-slate-500 mt-1">Events captured by your tracking script — page views, scrolls, forms, sessions</p>
          </div>
          <div className="flex gap-3 flex-wrap items-center">
            {refreshing && (
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <div className="w-3 h-3 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin" />
                <span>Auto-updating–</span>
              </div>
            )}
            <div className="flex gap-2 flex-wrap">
            {[{ label: "Today", value: "today" }, { label: "Last 7d", value: 7 }, { label: "Last 30d", value: 30 }, { label: "All Time", value: null }].map((preset) => (
              <button key={String(preset.value)} onClick={() => {
                // "Today" uses days=1 (last 24h) – timezone-safe, no UTC date mismatch
                if (preset.value === "today") {
                  applyPresetRange(1, "", "");
                } else {
                  applyPresetRange(preset.value, "", "");
                }
              }}
                className={`px-4 py-2 rounded-lg text-xs font-bold border transition ${
                  (preset.value === "today" ? days === 1 && !startDate && !endDate : days === preset.value && !startDate && !endDate)
                    ? "bg-indigo-700 border-indigo-500 text-white"
                    : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                }`}>
                {preset.label}
              </button>
            ))}
            </div>
          </div>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500">From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setDays(null); setStartDate(e.target.value); }}
              className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-200"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-[11px] uppercase tracking-[0.14em] text-slate-500">To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setDays(null); setEndDate(e.target.value); }}
              className="px-3 py-2 rounded-lg bg-slate-950 border border-slate-700 text-sm text-slate-200"
            />
          </div>
          <button
            onClick={() => { applyPresetRange(30, "", ""); setEventType("all"); }}
            className="px-4 py-2 rounded-lg text-xs font-bold border bg-slate-950 border-slate-700 text-slate-300 hover:border-slate-500"
          >
            Reset All
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: "Total Events", value: summary.total_events ?? 0, icon: "" },
            { label: "Page Views", value: summary.page_views ?? 0, icon: "" },
            { label: "Identified People", value: summary.identified_people ?? 0, icon: "" },
            { label: "Unique Visitors", value: summary.unique_visitors ?? 0, icon: "" },
            { label: "Avg Dwell (s)", value: summary.avg_dwell_seconds != null ? `${summary.avg_dwell_seconds}s` : "–", icon: "" },
            { label: "Avg Scroll %", value: summary.avg_scroll_depth != null ? `${summary.avg_scroll_depth}%` : "–", icon: "" },
          ].map(({ label, value, icon }) => (
            <div key={label} className="bg-slate-900 border border-slate-800 rounded-xl p-4 flex flex-col gap-1">
              <span className="text-lg">{icon}</span>
              <span className="text-2xl font-bold text-white">{value}</span>
              <span className="text-xs text-slate-500">{label}</span>
            </div>
          ))}
        </div>

        {/* Smart Insights Panel */}
        {siteInsights && (siteInsights.totals?.total_sessions > 0) && (() => {
          const si = siteInsights;
          const fmtD = (s) => {
            if (!s || s <= 0) return "< 1s";
            if (s < 60) return `${s}s`;
            const m = Math.floor(s / 60); const sec = s % 60;
            return sec > 0 ? `${m}m ${sec}s` : `${m}m`;
          };
          const peakHour = si.by_hour?.[0];
          const peakHourLabel = peakHour ? (() => {
            const h = peakHour.hour_utc;
            const ampm = h >= 12 ? "PM" : "AM";
            const h12 = h % 12 || 12;
            return `${h12}:00 ${ampm} UTC`;
          })() : null;
          const topDevice = si.by_device?.[0];
          return (
            <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-800 flex items-center gap-2">
                <span className="text-lg"></span>
                <div>
                  <div className="font-bold text-white text-sm">Site Activity Insights</div>
                  <div className="text-xs text-slate-500 mt-0.5">Who's engaging, which pages pull them in, and when they show up</div>
                </div>
              </div>
              <div className="p-5 grid grid-cols-1 lg:grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">

                {/* Top Engaged Companies */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Most Engaged Companies</div>
                  <div className="space-y-2">
                    {(si.top_companies || []).slice(0, 6).map((co, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-full bg-indigo-900/60 border border-indigo-700/50 flex items-center justify-center text-[10px] font-bold text-indigo-400 shrink-0">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-200 font-medium truncate">{co.company}</div>
                          <div className="text-[11px] text-slate-500 flex gap-2 mt-0.5">
                            <span>{co.visits} {co.visits === 1 ? "visit" : "visits"}</span>
                            {co.total_dwell > 0 && <span>– {fmtD(co.total_dwell)} total</span>}
                            {co.max_scroll > 0 && <span>– {co.max_scroll}% scroll</span>}
                          </div>
                        </div>
                        <div className="text-xs font-semibold text-indigo-400 shrink-0">{fmtD(Math.round(co.total_dwell / Math.max(co.visits, 1)))}<span className="text-slate-600 font-normal">/visit</span></div>
                      </div>
                    ))}
                    {(si.top_companies || []).length === 0 && <div className="text-sm text-slate-600 italic">No identified companies yet</div>}
                  </div>
                </div>

                {/* Top Pages */}
                <div>
                  <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-3">Pages That Hold Attention</div>
                  <div className="space-y-2">
                    {(si.top_pages || []).slice(0, 6).map((pg, i) => (
                      <div key={i} className="flex items-start gap-3">
                        <div className="w-5 h-5 rounded-full bg-emerald-900/60 border border-emerald-700/50 flex items-center justify-center text-[10px] font-bold text-emerald-400 shrink-0 mt-0.5">{i + 1}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm text-slate-200 font-medium truncate" title={pg.page}>{pg.page}</div>
                          <div className="text-[11px] text-slate-500 flex gap-2 mt-0.5">
                            <span>{pg.unique_sessions} {pg.unique_sessions === 1 ? "visitor" : "visitors"}</span>
                            {pg.avg_dwell > 0 && <span>– {fmtD(pg.avg_dwell)} avg</span>}
                            {pg.avg_scroll > 0 && <span>– {pg.avg_scroll}% scroll</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {(si.top_pages || []).length === 0 && <div className="text-sm text-slate-600 italic">No page data yet</div>}
                  </div>
                </div>

                {/* Right column: peak times + device + repeat visitors */}
                <div className="space-y-5">
                  {/* Peak visit hour */}
                  {peakHour && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Peak Visit Time</div>
                      <div className="bg-slate-800/50 rounded-lg px-4 py-3 flex items-center gap-3">
                        <span className="text-2xl"></span>
                        <div>
                          <div className="text-base font-bold text-white">{peakHourLabel}</div>
                          <div className="text-xs text-slate-500">{peakHour.sessions} sessions – {fmtD(peakHour.avg_dwell)} avg dwell</div>
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {(si.by_hour || []).sort((a,b)=>a.hour_utc-b.hour_utc).map(h => {
                          const maxS = Math.max(...(si.by_hour||[]).map(x=>x.sessions),1);
                          const pct = Math.round((h.sessions/maxS)*100);
                          return (
                            <div key={h.hour_utc} className="flex-1 flex flex-col items-center gap-0.5" title={`${h.hour_utc}:00 UTC – ${h.sessions} sessions`}>
                              <div className="w-full bg-slate-800 rounded-sm overflow-hidden" style={{height:"24px"}}>
                                <div className="w-full bg-indigo-600/70 rounded-sm transition-all" style={{height:`${pct}%`, marginTop:`${100-pct}%`}} />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between text-[10px] text-slate-600 mt-1 px-0.5">
                        <span>12am</span><span>6am</span><span>12pm</span><span>6pm</span><span>11pm</span>
                      </div>
                    </div>
                  )}

                  {/* Device split */}
                  {topDevice && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Device Split</div>
                      <div className="space-y-1.5">
                        {(si.by_device || []).map((d, i) => {
                          const total = (si.by_device||[]).reduce((s,x)=>s+x.sessions,0);
                          const pct = total > 0 ? Math.round(d.sessions/total*100) : 0;
                          const colors = ["bg-indigo-500","bg-emerald-500","bg-amber-500","bg-pink-500"];
                          return (
                            <div key={i} className="flex items-center gap-2">
                              <div className="text-xs text-slate-400 w-16 capitalize shrink-0">{d.device}</div>
                              <div className="flex-1 h-2 bg-slate-800 rounded-full overflow-hidden">
                                <div className={`h-full rounded-full ${colors[i % colors.length]}`} style={{width:`${pct}%`}} />
                              </div>
                              <div className="text-xs text-slate-400 w-8 text-right">{pct}%</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Repeat visitors */}
                  {(si.repeat_visitors || []).length > 0 && (
                    <div>
                      <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Repeat Visitors </div>
                      <div className="space-y-1.5">
                        {(si.repeat_visitors||[]).map((rv, i) => (
                          <div key={i} className="flex items-center justify-between text-xs">
                            <span className="text-slate-300 truncate max-w-[140px]">{rv.company}</span>
                            <span className="text-amber-400 font-semibold shrink-0">{rv.visits}x</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })()}

        {/* View Mode Toggle & Event Type Filter */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
          <div className="flex gap-2">
            <button onClick={() => setViewMode("sessions")}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${
                viewMode === "sessions" ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}>
              📋 Sessions
            </button>
            <button onClick={() => setViewMode("journeys")}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${
                viewMode === "journeys" ? "bg-amber-700 border-amber-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}>
              🔥 High-Intent Visitors {journeyVisitors.length > 0 && <span className="ml-1 px-1.5 py-0.5 bg-amber-500 text-white text-[10px] rounded-full">{journeyVisitors.length}</span>}
            </button>
            <button onClick={() => setViewMode("events")}
              className={`px-4 py-2 rounded-lg text-sm font-bold border transition ${
                viewMode === "events" ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
              }`}>
              📊 Event Details
            </button>
          </div>
          {viewMode === "events" && (
            <div className="flex gap-2 flex-wrap">
              {EVENT_TYPES.map((t) => (
                <button key={t} onClick={() => setEventType(t)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition capitalize ${
                    eventType === t ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-900 border-slate-700 text-slate-400 hover:border-slate-500"
                  }`}>
                  {t === "all" ? "All Types" : t.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Sessions View (User Journeys) */}
        {viewMode === "sessions" && (
          <div className="space-y-3">
            {sessions.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <div className="text-3xl mb-3"></div>
                <p>No sessions yet. Embed <code className="text-indigo-400">tracking.js</code> on your website to start collecting data.</p>
              </div>
            ) : (
              sessions.map((sess) => (
                <div key={sess.session_id} className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden hover:border-slate-700 transition">
                  {/* Session Header */}
                  <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-slate-800/40 cursor-pointer"
                    onClick={() => setExpanded(expanded === sess.session_id ? null : sess.session_id)}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-4 flex-wrap">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Company</div>
                          <div className="font-semibold text-white">{sess.person_name !== "Anonymous" ? sess.person_name : "Anonymous Visitor"}</div>
                        </div>
                        <div className="flex gap-3 flex-wrap text-xs text-slate-400 mt-1 sm:mt-0">
                          {sess.device_type && sess.device_type !== "–" && <span className="capitalize">{sess.device_type}</span>}
                          {sess.location && sess.location !== "–" && <span> {sess.location}</span>}
                          <span className="text-slate-500">{fmt(sess.first_event_at)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4 shrink-0">
                      <div className="text-sm font-semibold text-indigo-400">{sess.page_sequence.length} {sess.page_sequence.length === 1 ? "page" : "pages"}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{fmtDuration(Math.max(0, sess.session_duration_seconds))}</div>
                    </div>
                  </div>

                  {/* Session Content */}
                  {expanded === sess.session_id && (
                    <div className="px-5 py-4 space-y-4">
                      {/* Page Flow Timeline */}
                      <div>
                        <div className="text-xs font-bold uppercase text-slate-500 mb-3">User Navigation Path</div>
                        <div className="space-y-2">
                          {sess.page_sequence.map((page, idx) => {
                            const pageEvents = sess.events.filter(e => (e.metadata?.path || e.url || "(unknown)") === page || idx === 0);
                            const pageDwell = pageEvents.reduce((sum, e) => sum + (e.dwell_seconds || 0), 0);
                            const firstEvent = pageEvents[0];
                            return (
                              <div key={`${page}-${idx}`} className="flex items-start gap-3">
                                <div className="text-lg mt-0.5"></div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-slate-200 break-all">{page}</div>
                                  <div className="flex gap-4 mt-1 text-xs text-slate-500">
                                    {pageDwell > 0 && (
                                      <span className="flex items-center gap-1">
                                        <span></span>
                                        <span>{pageDwell}s on page</span>
                                      </span>
                                    )}
                                    {firstEvent?.scroll_depth != null && (
                                      <span className="flex items-center gap-1">
                                        <span></span>
                                        <span>Scrolled {firstEvent.scroll_depth}%</span>
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {idx < sess.page_sequence.length - 1 && (
                                  <div className="text-slate-600 text-xs font-bold"></div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Session Stats */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t border-slate-800">
                        <div className="bg-slate-800/40 rounded-lg p-3">
                          <div className="text-xs text-slate-500">Time on Site</div>
                          <div className="text-sm font-bold text-slate-200">{fmtDuration(sess.total_dwell_seconds)}</div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-3">
                          <div className="text-xs text-slate-500">Arrived</div>
                          <div className="text-xs text-slate-300 mt-1">{new Date(sess.first_event_at).toLocaleString()}</div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-3">
                          <div className="text-xs text-slate-500">Device</div>
                          <div className="text-sm text-slate-200 capitalize">{sess.device_type !== "–" ? sess.device_type : "Unknown"}</div>
                        </div>
                        <div className="bg-slate-800/40 rounded-lg p-3">
                          <div className="text-xs text-slate-500">Location</div>
                          <div className="text-sm text-slate-200">{sess.location !== "–" ? sess.location : "Unknown"}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}

            {/* Load More / pagination */}
            {!allEventsLoaded && (
              <div className="flex items-center justify-between pt-2">
                <span className="text-xs text-slate-500">
                  Showing {sessions.length} sessions from {events.length} events · {total} total events
                </span>
                <button onClick={loadMore} disabled={loadingMore}
                  className="px-4 py-2 text-xs font-bold bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg border border-slate-700 flex items-center gap-2 transition">
                  {loadingMore ? <><div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"/>Loading...</> : `Load more sessions (${total - events.length} remaining)`}
                </button>
              </div>
            )}
            {allEventsLoaded && events.length > 0 && (
              <p className="text-xs text-slate-600 text-center pt-2">All {sessions.length} sessions loaded</p>
            )}
          </div>
        )}

        {/* 🔥 High-Intent Journey Visitors */}
        {viewMode === "journeys" && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-white">🔥 High-Intent Visitors</h3>
                <p className="text-xs text-slate-500 mt-0.5">People who visited 2+ pages — bots never do this. These are real humans exploring your site.</p>
              </div>
              <span className="text-xs text-slate-500">{journeyVisitors.length} visitors · last {days} days</span>
            </div>

            {journeyLoading ? (
              <div className="flex justify-center py-10"><div className="w-6 h-6 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"/></div>
            ) : journeyVisitors.length === 0 ? (
              <div className="bg-slate-900 border border-slate-800 rounded-xl p-10 text-center text-slate-500 text-sm">
                No multi-page visitors yet in the last {days} days.
              </div>
            ) : (
              <div className="space-y-2">
                {journeyVisitors.map((v, i) => {
                  const hasHighIntent = v.intent_hits && v.intent_hits.length > 0;
                  const mins = Math.round((v.duration_seconds || 0) / 60);
                  const hour = v.hour_of_day;
                  const timeStr = hour != null ? `${hour === 0 ? 12 : hour > 12 ? hour - 12 : hour}:00 ${hour < 12 ? 'AM' : 'PM'}` : '';
                  const firstSeen = v.first_seen ? new Date(v.first_seen) : null;
                  return (
                    <div key={i} className={`border rounded-xl p-4 ${hasHighIntent ? 'bg-amber-950/20 border-amber-700/40' : 'bg-slate-900 border-slate-800'}`}>
                      <div className="flex items-start gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className={`font-bold text-sm ${v.lead ? 'text-white' : 'text-slate-500'}`}>
                              {v.lead ? (v.lead.company || v.lead.name || 'Unknown') : 'Anonymous Visitor'}
                            </span>
                            {hasHighIntent && <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-600/40 rounded-full font-bold">HIGH INTENT</span>}
                            {v.lead && <span className="text-[10px] px-2 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-600/40 rounded-full font-bold">IDENTIFIED</span>}
                            {(v.lead?.country || v.country) && <span className="text-xs text-slate-500">🌍 {v.lead?.country || v.country}</span>}
                          </div>
                          {v.lead && (
                            <div className="flex gap-3 text-xs mb-1.5 flex-wrap">
                              {v.lead.contact_name && <span className="text-slate-300 font-medium">👤 {v.lead.contact_name}</span>}
                              {v.lead.email && <span className="text-indigo-400 font-mono">✉ {v.lead.email}</span>}
                              {v.lead.phone && <span className="text-slate-400 font-mono">📞 {v.lead.phone}</span>}
                              {v.lead.city && <span className="text-slate-500">📍 {v.lead.city}{v.lead.country ? `, ${v.lead.country}` : ''}</span>}
                              {v.lead.industry && <span className="text-slate-500 italic">{v.lead.industry}</span>}
                            </div>
                          )}
                          {/* Page journey */}
                          <div className="flex items-center gap-1 flex-wrap text-xs">
                            {(v.pages || []).map((page, pi) => (
                              <React.Fragment key={pi}>
                                <span className={`px-2 py-0.5 rounded font-mono ${
                                  v.intent_hits?.includes(page)
                                    ? 'bg-amber-900/40 text-amber-300 border border-amber-700/40'
                                    : 'bg-slate-800 text-slate-400'
                                }`}>{page || '/'}</span>
                                {pi < (v.pages||[]).length-1 && <span className="text-slate-600">→</span>}
                              </React.Fragment>
                            ))}
                          </div>
                        </div>
                        <div className="text-right text-xs text-slate-500 shrink-0 space-y-1">
                          {firstSeen && <div>{firstSeen.toLocaleDateString()} {timeStr}</div>}
                          <div>{(v.pages||[]).length} pages · {mins > 0 ? `${mins}m` : `${v.duration_seconds || 0}s`}</div>
                          {v.browser && <div className="text-slate-600">{v.browser} / {v.os}</div>}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Session Summary Table (Event Details view) */}
        {viewMode === "events" && (
          <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-slate-800">
              <span className="text-sm font-semibold text-slate-300">
                Visit Summary <span className="text-slate-500 font-normal">({sessions.length} {sessions.length === 1 ? "visit" : "visits"})</span>
              </span>
            </div>
            {sessions.length === 0 ? (
              <div className="py-16 text-center text-slate-500">
                <div className="text-3xl mb-3"></div>
                <p>No visits yet. Embed <code className="text-indigo-400">tracking.js</code> on your website to start collecting data.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-800 text-slate-500 text-xs uppercase tracking-wider">
                      {[
                        ["person_name","Company"],
                        ["first_event_at","Visit Time"],
                        ["page_count","Pages Visited"],
                        ["total_dwell_seconds","Time on Site"],
                        ["max_scroll","Max Scroll"],
                        ["device_type","Device"],
                        ["location","Location"],
                      ].map(([col, label]) => (
                        <th key={col} className="px-4 py-3 text-left font-medium cursor-pointer select-none hover:text-white transition group"
                          onClick={() => { if (sortCol === col) setSortDir(d => d === "desc" ? "asc" : "desc"); else { setSortCol(col); setSortDir("desc"); } }}>
                          <span className="flex items-center gap-1">
                            {label}
                            <span className="text-[10px] opacity-50 group-hover:opacity-100">
                              {sortCol === col ? (sortDir === "desc" ? "↓" : "↑") : "↕"}
                            </span>
                          </span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...sessions].sort((a, b) => {
                      let av, bv;
                      if (sortCol === "person_name") { av = a.person_name || ""; bv = b.person_name || ""; }
                      else if (sortCol === "first_event_at") { av = a.first_event_at || ""; bv = b.first_event_at || ""; }
                      else if (sortCol === "page_count") { av = new Set(a.page_sequence).size; bv = new Set(b.page_sequence).size; }
                      else if (sortCol === "total_dwell_seconds") { av = a.total_dwell_seconds || 0; bv = b.total_dwell_seconds || 0; }
                      else if (sortCol === "max_scroll") {
                        const ms = s => Math.max(...(s.events.filter(e=>e.event_type==="scroll_depth"&&(e.scroll_depth??0)>0).map(e=>e.scroll_depth??0)), s.events.find(e=>e.event_type==="session_end")?.scroll_depth??0, 0);
                        av = ms(a); bv = ms(b);
                      }
                      else if (sortCol === "device_type") { av = a.device_type || ""; bv = b.device_type || ""; }
                      else if (sortCol === "location") { av = a.location || ""; bv = b.location || ""; }
                      else { av = a[sortCol] ?? ""; bv = b[sortCol] ?? ""; }
                      if (av < bv) return sortDir === "desc" ? 1 : -1;
                      if (av > bv) return sortDir === "desc" ? -1 : 1;
                      return 0;
                    }).map((sess) => {
                      // Pull scroll from milestone events first, fall back to session_end scroll_depth
                      const scrollEvents = sess.events.filter(e => e.event_type === "scroll_depth" && (e.scroll_depth ?? 0) > 0);
                      const sessionEndScroll = sess.events.find(e => e.event_type === "session_end");
                      const maxScroll = scrollEvents.length > 0
                        ? Math.max(...scrollEvents.map(e => e.scroll_depth ?? 0))
                        : (sessionEndScroll?.scroll_depth ?? 0);
                      const uniquePages = [...new Set(sess.page_sequence)];
                      const isExp = expanded === sess.session_id;
                      return (
                        <React.Fragment key={sess.session_id}>
                          <tr className="border-b border-slate-800/50 hover:bg-slate-800/30 transition align-middle">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-white">
                                {sess.person_name !== "Anonymous" ? sess.person_name : "Anonymous Visitor"}
                              </div>
                              {sess.person_phone && sess.person_phone !== "–" && (
                                <div className="text-xs text-slate-500 mt-0.5">{sess.person_phone}</div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-400 whitespace-nowrap">
                              <div>{fmt(sess.first_event_at)}</div>
                              <div className="text-[11px] text-slate-600 mt-0.5">{new Date(sess.first_event_at).toLocaleTimeString()}</div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <span className="text-indigo-400 font-semibold">{uniquePages.length}</span>
                                <span className="text-slate-500 text-xs">{uniquePages.length === 1 ? "page" : "pages"}</span>
                              </div>
                              {uniquePages.length > 0 && (
                                <div className="text-[11px] text-slate-500 mt-0.5 max-w-[180px] truncate" title={uniquePages.join("  ")}>
                                  {uniquePages[0]}{uniquePages.length > 1 ? ` +${uniquePages.length - 1} more` : ""}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 text-slate-300 font-medium whitespace-nowrap">
                              {fmtDuration(sess.total_dwell_seconds)}
                            </td>
                            <td className="px-4 py-3 text-slate-400">
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                                  <div className="h-full bg-indigo-500 rounded-full" style={{ width: `${maxScroll}%` }} />
                                </div>
                                <span className="text-xs">{maxScroll > 0 ? `${maxScroll}%` : "< 1%"}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-slate-400 text-xs capitalize">{sess.device_type !== " • " ? sess.device_type : " • "}</td>
                            <td className="px-4 py-3 text-slate-400 text-xs">{sess.location !== " • " ? sess.location : " • "}</td>
                            <td className="px-4 py-3">
                              <button onClick={() => setExpanded(isExp ? null : sess.session_id)}
                                className="text-indigo-400 hover:text-indigo-300 text-xs font-medium whitespace-nowrap">
                                {isExp ? " Hide" : " Details"}
                              </button>
                            </td>
                          </tr>
                          {isExp && (
                            <tr className="bg-slate-800/30">
                              <td colSpan={8} className="px-6 py-4">
                                <div className="text-xs font-bold uppercase text-slate-500 mb-3">Page-by-Page Breakdown</div>
                                <div className="space-y-2">
                                  {uniquePages.map((page, idx) => {
                                    const pageEvts = sess.events.filter(e => (e.metadata?.path || e.url || "") === page);
                                    const pageDwell = pageEvts.reduce((s, e) => s + (e.dwell_seconds || 0), 0);
                                    const pageScroll = Math.max(0, ...pageEvts.map(e => e.scroll_depth ?? 0));
                                    const clicks = pageEvts.filter(e => e.event_type === "link_click");
                                    return (
                                      <div key={idx} className="flex items-start gap-3 bg-slate-900/60 rounded-lg px-4 py-2">
                                        <span className="text-slate-600 text-xs font-mono mt-0.5 w-4 shrink-0">{idx + 1}</span>
                                        <div className="flex-1 min-w-0">
                                          <div className="text-slate-200 text-sm font-medium break-all">{page}</div>
                                          <div className="flex gap-4 mt-1 text-xs text-slate-500 flex-wrap">
                                            {pageDwell > 0 && <span> {fmtDuration(pageDwell)}</span>}
                                            {pageScroll > 0 && <span> Scrolled {pageScroll}%</span>}
                                            {clicks.length > 0 && <span className="text-pink-400"> {clicks.length} click{clicks.length > 1 ? "s" : ""}</span>}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {sess.person_email && sess.person_email !== "–" && (
                                  <div className="mt-3 pt-3 border-t border-slate-700 text-xs text-slate-500">
                                    Contact: <span className="text-slate-300">{sess.person_email}</span>
                                    {sess.person_phone && sess.person_phone !== " • " && <span>  •  {sess.person_phone}</span>}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {/* Load More for Event Details */}
            {sessions.length > 0 && !allEventsLoaded && (
              <div className="px-5 py-4 border-t border-slate-800 text-center">
                <button onClick={loadMore} disabled={loadingMore}
                  className="px-5 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-sm rounded-xl transition flex items-center gap-2 mx-auto disabled:opacity-50">
                  {loadingMore
                    ? <><div className="w-3 h-3 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"/>Loading...</>
                    : `Load more (${total - events.length} remaining)`}
                </button>
              </div>
            )}
            {allEventsLoaded && sessions.length > 0 && (
              <div className="px-5 py-3 border-t border-slate-800 text-center text-xs text-slate-600">All {events.length} events loaded</div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

//  AI Assistant Page
function AIChatPage() {
  const { authFetch, user } = useAuth();

  //  Sessions 
  const [sessions, setSessions]             = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [sessionsLoaded, setSessionsLoaded] = useState(false);
  const [sessionLimitError, setSessionLimitError] = useState("");
  const [deletingSession, setDeletingSession] = useState(null);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");

  //  Chat 
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [chatSenders, setChatSenders] = useState([]);
  const [selectedSenderId, setSelectedSenderId] = useState(null);
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [executingSteps, setExecutingSteps] = useState([]);
  const [showSteps, setShowSteps] = useState(false);
  const [executingDone, setExecutingDone] = useState(false);
  const [showSidebar, setShowSidebar] = useState(window.innerWidth >= 640);

  //  Call mode 
  const [callMode, setCallMode] = useState(false);
  const [callMuted, setCallMuted] = useState(false);
  const [showChatPanel, setShowChatPanel] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const stepsTimeoutRef = useRef([]);
  const currentSessionRef = useRef(null);
  currentSessionRef.current = currentSessionId;
  const callModeRef = useRef(false);
  callModeRef.current = callMode;
  const callListeningRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const callRecRef = useRef(null);
  const startCallListeningRef = useRef(null);

  //  Load sessions + first session messages on mount 
  useEffect(() => {
    let mounted = true;
    const loadHistory = async (sid) => {
      setMessagesLoading(true);
      try {
        const r = await authFetch(`/ai/chat/history?session_id=${sid}`);
        if (r.ok && mounted) {
          const hist = await r.json();
          setMessages(hist.map(h => ({
            role: h.role, content: h.content,
            actionType: h.action_type,
            actionData: h.action_data ? (typeof h.action_data === "string" ? JSON.parse(h.action_data) : h.action_data) : null
          })));
        }
      } catch {}
      if (mounted) setMessagesLoading(false);
    };
    (async () => {
      try {
        const resp = await authFetch("/ai/chat/sessions");
        if (resp.ok && mounted) {
          const data = await resp.json();
          setSessions(data);
          if (data.length > 0) {
            setCurrentSessionId(data[0].id);
            await loadHistory(data[0].id);
          } else {
            // Auto-create first session
            const cr = await authFetch("/ai/chat/sessions", { method: "POST" });
            if (cr.ok && mounted) {
              const sess = await cr.json();
              setSessions([sess]);
              setCurrentSessionId(sess.id);
            }
          }
        }
      } catch {}
      if (mounted) setSessionsLoaded(true);
    })();
    return () => { mounted = false; };
  }, [authFetch]);

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, loading, executingSteps]);

  // Cleanup speech on unmount
  useEffect(() => {
    return () => {
      window.speechSynthesis?.cancel();
      stepsTimeoutRef.current.forEach(t => clearTimeout(t));
      callRecRef.current?.stop();
      clearTimeout(silenceTimerRef.current);
    };
  }, []);

  // Fetch user's email senders when a send_outreach action is pending
  useEffect(() => {
    if (pendingAction?.action === "send_outreach") {
      authFetch("/settings/email-senders")
        .then(r => r.ok ? r.json() : [])
        .then(data => setChatSenders(Array.isArray(data) ? data.filter(s => s.is_active) : []))
        .catch(() => setChatSenders([]));
      setSelectedSenderId(null);
    } else {
      setChatSenders([]);
      setSelectedSenderId(null);
    }
  }, [pendingAction, authFetch]);

  //  Session management 
  const switchSession = useCallback(async (sessionId) => {
    if (sessionId === currentSessionRef.current) return;
    setCurrentSessionId(sessionId);
    setMessages([]); setPendingAction(null);
    // Restore persisted steps for this session
    try {
      const saved = sessionStorage.getItem(`steps_${sessionId}`);
      if (saved) {
        const { steps, done } = JSON.parse(saved);
        setExecutingSteps(steps || []);
        setShowSteps(true);
        setExecutingDone(done !== false);
      } else {
        setExecutingSteps([]); setShowSteps(false); setExecutingDone(false);
      }
    } catch { setExecutingSteps([]); setShowSteps(false); setExecutingDone(false); }
    setMessagesLoading(true);
    try {
      const r = await authFetch(`/ai/chat/history?session_id=${sessionId}`);
      if (r.ok) {
        const hist = await r.json();
        setMessages(hist.map(h => ({
          role: h.role, content: h.content,
          actionType: h.action_type,
          actionData: h.action_data ? (typeof h.action_data === "string" ? JSON.parse(h.action_data) : h.action_data) : null
        })));
      }
    } catch {}
    setMessagesLoading(false);
  }, [authFetch]);

  const createNewChat = useCallback(async () => {
    setSessionLimitError("");
    try {
      const resp = await authFetch("/ai/chat/sessions", { method: "POST" });
      if (resp.ok) {
        const sess = await resp.json();
        setSessions(p => [sess, ...p]);
        setCurrentSessionId(sess.id);
        setMessages([]); setPendingAction(null); setExecutingSteps([]); setShowSteps(false); setExecutingDone(false);
      } else if (resp.status === 429) {
        const err = await resp.json().catch(() => ({}));
        setSessionLimitError(err.detail || "Chat limit reached. Delete an old chat first.");
      }
    } catch {}
  }, [authFetch]);

  const deleteSession = useCallback(async (sessionId, e) => {
    e.stopPropagation();
    setDeletingSession(sessionId);
    try {
      await authFetch(`/ai/chat/sessions/${sessionId}`, { method: "DELETE" });
      setSessions(prev => {
        const remaining = prev.filter(s => s.id !== sessionId);
        if (currentSessionRef.current === sessionId) {
          if (remaining.length > 0) {
            // switch to first remaining (async, but state will update)
            setTimeout(() => switchSession(remaining[0].id), 0);
          } else {
            // auto-create a new session
            authFetch("/ai/chat/sessions", { method: "POST" }).then(r => {
              if (r.ok) r.json().then(sess => {
                setSessions([sess]);
                setCurrentSessionId(sess.id);
                setMessages([]);
              });
            });
          }
        }
        return remaining;
      });
    } catch {}
    setDeletingSession(null);
  }, [authFetch, switchSession]);

  const speakText = useCallback((text) => {
    if (!voiceEnabled || !window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const clean = text.replace(/[–]/g, "").replace(/\n/g, ". ").replace(/\s+/g, " ").trim();
    if (!clean) return;
    const utt = new SpeechSynthesisUtterance(clean);
    utt.rate = 1.05; utt.pitch = 1.0;
    // Try to find a good English voice
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v => v.name.includes("Google") && v.lang.startsWith("en")) ||
                      voices.find(v => v.lang.startsWith("en-US")) ||
                      voices.find(v => v.lang.startsWith("en"));
    if (preferred) utt.voice = preferred;
    utt.onstart = () => setSpeaking(true);
    utt.onend = () => setSpeaking(false);
    utt.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utt);
  }, [voiceEnabled]);

  // Animate steps appearing one by one
  const animateSteps = useCallback((steps) => {
    stepsTimeoutRef.current.forEach(t => clearTimeout(t));
    stepsTimeoutRef.current = [];
    setExecutingSteps([]);
    setShowSteps(true);
    steps.forEach((step, i) => {
      const tid = setTimeout(() => {
        setExecutingSteps(prev => [...prev, step]);
      }, i * 400);
      stepsTimeoutRef.current.push(tid);
    });
  }, []);

  const saveSessionTitle = useCallback(async (sessionId) => {
    const title = editingTitle.trim() || "New Chat";
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title } : s));
    setEditingSessionId(null);
    try {
      await authFetch(`/ai/chat/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title }),
      });
    } catch {}
  }, [authFetch, editingTitle]);

  const sendMessage = async (text, confirmAction = null) => {
    if (!text && !confirmAction) return;
    if (!currentSessionRef.current) return;
    const sessionId = currentSessionRef.current;
    if (text) {
      setMessages(p => [...p, { role: "user", content: text }]);
    }
    setInput(""); setLoading(true); setPendingAction(null);
    setExecutingSteps([]); setShowSteps(false); setExecutingDone(false);

    //  Action execution: use streaming endpoint so steps appear in real-time 
    if (confirmAction) {
      try {
        setShowSteps(true);
        const sessionId2 = currentSessionRef.current;
        const resp = await authFetch("/ai/chat/execute/stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ confirm_action: confirmAction, session_id: sessionId }),
        });
        if (resp.ok && resp.body) {
          const reader = resp.body.getReader();
          const decoder = new TextDecoder();
          let buf = "";
          let liveSteps = [];
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const parts = buf.split("\n\n");
            buf = parts.pop();
            for (const part of parts) {
              if (!part.startsWith("data: ")) continue;
              try {
                const d = JSON.parse(part.slice(6));
                if (d.type === "step") {
                  liveSteps = [...liveSteps, d.step];
                  setExecutingSteps(liveSteps);
                  // Persist to sessionStorage
                  try { sessionStorage.setItem(`steps_${sessionId2}`, JSON.stringify({ steps: liveSteps, done: false })); } catch {}
                } else if (d.type === "done") {
                  setExecutingDone(true);
                  try { sessionStorage.setItem(`steps_${sessionId2}`, JSON.stringify({ steps: liveSteps, done: true })); } catch {}
                  setMessages(p => [...p, { role: "assistant", content: d.message || "Done!", success: d.success }]);
                  speakText(d.message || "Done!");
                  if (d.pending_next_action) {
                    const next = d.pending_next_action;
                    const confirmMsg = d.pending_next_message || "Ready to proceed. Tap Confirm to continue.";
                    setMessages(p => [...p, { role: "assistant", content: confirmMsg }]);
                    setPendingAction({ action: next.action, params: next.params || {}, next_actions: [] });
                    speakText(confirmMsg);
                  }
                  setLoading(false);
                } else if (d.type === "error") {
                  setExecutingDone(true);
                  setMessages(p => [...p, { role: "assistant", content: d.message || "Something went wrong." }]);
                  setLoading(false);
                }
              } catch {}
            }
          }
        } else {
          const err = await resp.json().catch(() => ({}));
          setMessages(p => [...p, { role: "assistant", content: err.detail || "Sorry, something went wrong." }]);
        }
      } catch {
        setMessages(p => [...p, { role: "assistant", content: "Connection error. Please try again." }]);
      }
      setLoading(false);
      return;
    }

    //  Regular chat message (streaming) 
    try {
      const resp = await authFetch("/ai/chat/stream", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ message: text, session_id: sessionId }) });
      if (resp.ok) {
        // Add a placeholder streaming message
        const streamId = Date.now();
        setMessages(p => [...p, { role: "assistant", content: "", _streamId: streamId, _streaming: true }]);
        const reader = resp.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let finalData = null;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop(); // keep incomplete line
          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;
            try {
              const evt = JSON.parse(line.slice(6));
              if (evt.type === "token") {
                setMessages(p => p.map(m => m._streamId === streamId ? { ...m, content: m.content + evt.text } : m));
              } else if (evt.type === "done") {
                finalData = evt.data;
              }
            } catch {}
          }
        }
        // Finalize the message based on parsed data
        if (finalData) {
          if (finalData.session_title) {
            setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, title: finalData.session_title } : s));
          }
          if (finalData.type === "action") {
            setMessages(p => p.map(m => m._streamId === streamId
              ? { role: "assistant", content: finalData.summary || "I'd like to perform an action for you.", actionType: finalData.action, actionData: finalData.params }
              : m));
            setPendingAction({ action: finalData.action, params: finalData.params, next_actions: finalData.next_actions || [] });
            speakText(finalData.summary || "Shall I proceed with this action?");
          } else {
            const finalMsg = finalData.message || "Done!";
            setMessages(p => p.map(m => m._streamId === streamId
              ? { role: "assistant", content: finalMsg, success: finalData.success, _streaming: false }
              : m));
            speakText(finalMsg);
          }
        } else {
          // Stream ended without done event — keep whatever was accumulated
          setMessages(p => p.map(m => m._streamId === streamId ? { ...m, _streaming: false } : m));
        }
      } else {
        const err = await resp.json().catch(() => ({}));
        const errMsg = err.detail || "Sorry, something went wrong.";
        setMessages(p => [...p, { role: "assistant", content: errMsg }]);
        speakText(errMsg);
      }
    } catch (e) {
      setMessages(p => [...p, { role: "assistant", content: "Connection error. Please try again." }]);
    }
    setLoading(false);
  };

  const handleConfirm = () => {
    if (!pendingAction) return;
    const label = (pendingAction.action || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
    setMessages(p => [...p, { role: "user", content: ` Confirmed – ${label}` }]);
    const actionToSend = (pendingAction.action === "send_outreach" && selectedSenderId)
      ? { ...pendingAction, params: { ...(pendingAction.params || {}), sender_id: selectedSenderId } }
      : pendingAction;
    sendMessage("", actionToSend);
  };

  const handleCancel = () => {
    setPendingAction(null);
    setMessages(p => [...p, { role: "user", content: " Cancelled" }, { role: "assistant", content: "No problem! Action cancelled. What else can I help with?" }]);
  };

  const endCall = () => {
    setCallMode(false);
    setShowChatPanel(false);
    setCallMuted(false);
    callRecRef.current?.stop();
    callListeningRef.current = false;
    clearTimeout(silenceTimerRef.current);
    setListening(false);
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      clearTimeout(silenceTimerRef.current);
      setListening(false);
      return;
    }
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { alert("Speech recognition is not supported in this browser. Try Chrome."); return; }
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    let finalTranscript = "";
    rec.onresult = (e) => {
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript + " ";
        else interim += e.results[i][0].transcript;
      }
      setInput((finalTranscript + interim).trim());
      // Reset silence timer — send after 2.5 s of quiet
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => rec.stop(), 2500);
    };
    rec.onend = () => {
      setListening(false);
      clearTimeout(silenceTimerRef.current);
      const text = finalTranscript.trim();
      if (text) { setInput(text); setTimeout(() => sendMessage(text), 100); }
    };
    rec.onerror = () => { setListening(false); clearTimeout(silenceTimerRef.current); };
    recognitionRef.current = rec;
    rec.start(); setListening(true);
  };

  //  Call-mode continuous listen loop 
  const startCallListening = () => {
    if (!callModeRef.current || callListeningRef.current) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const rec = new SR();
    rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
    let finalTranscript = "";
    callListeningRef.current = true;
    rec.onresult = (e) => {
      // User is speaking — interrupt AI if it's talking
      if (window.speechSynthesis?.speaking) {
        window.speechSynthesis.cancel();
        setSpeaking(false);
      }
      let interim = "";
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) finalTranscript += e.results[i][0].transcript + " ";
        else interim += e.results[i][0].transcript;
      }
      setListening(true);
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = setTimeout(() => rec.stop(), 2500);
    };
    rec.onend = () => {
      callListeningRef.current = false;
      setListening(false);
      clearTimeout(silenceTimerRef.current);
      const text = finalTranscript.trim();
      if (text && callModeRef.current) {
        setInput("");
        sendMessage(text);
      }
    };
    rec.onerror = (err) => {
      callListeningRef.current = false;
      setListening(false);
      clearTimeout(silenceTimerRef.current);
      // Auto-retry on transient errors (not permission denied)
      if (callModeRef.current && err.error !== "not-allowed") {
        setTimeout(() => startCallListeningRef.current?.(), 1500);
      }
    };
    callRecRef.current = rec;
    try { rec.start(); } catch { callListeningRef.current = false; }
  };
  startCallListeningRef.current = startCallListening;

  // Auto-restart mic in call mode after AI finishes speaking / thinking
  useEffect(() => {
    if (!callMode || callMuted) return;
    if (!loading && !speaking) {
      const t = setTimeout(() => startCallListeningRef.current?.(), 600);
      return () => clearTimeout(t);
    }
  }, [callMode, callMuted, loading, speaking]);

  const clearHistory = async () => {
    try { await authFetch("/ai/chat/history", { method: "DELETE" }); } catch {}
    setMessages([]); setPendingAction(null); setExecutingSteps([]); setShowSteps(false); setExecutingDone(false);
  };

  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey && input.trim()) { e.preventDefault(); sendMessage(input.trim()); } };

  const lastAiMessage = messages.filter(m => m.role === "assistant").slice(-1)[0]?.content || "";

  const suggestions = [
    { text: "Generate 10 plumber leads in London", icon: "" },
    { text: "How many leads have I saved?", icon: "" },
    { text: "Create a folder called Hot Leads", icon: "" },
    { text: "Show me my stats", icon: "" },
    { text: "Send outreach emails to my leads", icon: "" },
    { text: "What can you do?", icon: "" },
  ];

  // CSS animations injected once
  const cssAnimations = `
    @keyframes aiBlinkEye { 0%,88%,100%{transform:scaleY(1)} 92%{transform:scaleY(0.08)} }
    @keyframes aiIdleBob  { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-5px)} }
    @keyframes aiTalkBob  { 0%{transform:translateY(0px) scale(1)} 100%{transform:translateY(-7px) scale(1.025)} }
    @keyframes aiMouthOpen{ from{height:5px;border-radius:4px} to{height:20px;border-radius:50%} }
  `;

  //  Markdown renderer (bold, italic, bullets, headers) 
  const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = text.split("\n");
    const elements = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      // Blank line
      if (!line.trim()) { elements.push(<div key={i} className="h-2" />); i++; continue; }
      // H3 ###
      if (line.startsWith("### ")) {
        elements.push(<p key={i} className="font-bold text-white text-sm mt-2 mb-0.5">{renderInline(line.slice(4))}</p>); i++; continue;
      }
      // H2 ##
      if (line.startsWith("## ")) {
        elements.push(<p key={i} className="font-bold text-white text-base mt-2 mb-1">{renderInline(line.slice(3))}</p>); i++; continue;
      }
      // H1 #
      if (line.startsWith("# ")) {
        elements.push(<p key={i} className="font-bold text-white text-base mt-2 mb-1">{renderInline(line.slice(2))}</p>); i++; continue;
      }
      // Bullet * or -
      if (/^[\*\-] /.test(line)) {
        elements.push(
          <div key={i} className="flex gap-2 items-start">
            <span className="text-indigo-400 mt-0.5 shrink-0">–</span>
            <span>{renderInline(line.replace(/^[\*\-] /, ""))}</span>
          </div>
        ); i++; continue;
      }
      // Plain line
      elements.push(<p key={i} className="leading-relaxed">{renderInline(line)}</p>); i++;
    }
    return <div className="flex flex-col gap-0.5 text-sm">{elements}</div>;
  };

  const renderInline = (text) => {
    // Split on **bold** and *italic*
    const parts = text.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**"))
        return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      if (part.startsWith("*") && part.endsWith("*"))
        return <em key={i} className="italic text-slate-300">{part.slice(1, -1)}</em>;
      return part;
    });
  };

  //  Animated Avatar 
  const AiAvatar = ({ size = "md" }) => {
    const dim = size === "xl" ? "w-52 h-52" : size === "lg" ? "w-20 h-20" : "w-9 h-9";
    const eyeDim = size === "xl" ? "w-6 h-6" : size === "lg" ? "w-3 h-3" : "w-2 h-2";
    const pupilDim = size === "xl" ? "w-3.5 h-3.5" : size === "lg" ? "w-1.5 h-1.5" : "w-1 h-1";
    const eyeGap = size === "xl" ? "gap-8" : size === "lg" ? "gap-3.5" : "gap-1.5";
    const eyeMb = size === "xl" ? "mb-5" : size === "lg" ? "mb-2.5" : "mb-1.5";
    const mouthW = size === "xl" ? 40 : size === "lg" ? 16 : 8;
    return (
      <div className={`relative ${dim} shrink-0`}>
        {/* Glow rings when speaking (xl only) */}
        {speaking && size === "xl" && [0,1,2].map(i => (
          <div key={i} className="absolute inset-0 rounded-full border-2 border-indigo-400/15 animate-ping"
            style={{ margin: `-${(i+1)*14}px`, animationDelay: `${i*0.35}s`, animationDuration: "2s" }} />
        ))}
        {listening && size === "xl" && [0,1].map(i => (
          <div key={i} className="absolute inset-0 rounded-full border-2 border-red-400/20 animate-ping"
            style={{ margin: `-${(i+1)*14}px`, animationDelay: `${i*0.4}s`, animationDuration: "1.6s" }} />
        ))}
        {/* Face sphere */}
        <div className={`${dim} rounded-full bg-gradient-to-br from-indigo-400 via-purple-600 to-indigo-800 flex items-center justify-center shadow-xl relative overflow-hidden`}
          style={{
            boxShadow: speaking ? "0 0 40px 8px rgba(99,102,241,0.35)" : "0 8px 32px rgba(99,102,241,0.18)",
            animation: speaking
              ? "aiTalkBob 0.45s ease-in-out infinite alternate"
              : "aiIdleBob 4s ease-in-out infinite"
          }}>
          {/* Glare */}
          <div className="absolute rounded-full bg-white/20 blur-sm"
            style={{ width: "38%", height: "28%", top: "12%", left: "18%", transform: "rotate(-30deg)" }} />
          {/* Face */}
          <div className="relative z-10" style={{ marginTop: size === "xl" ? "-8px" : "-3px" }}>
            {/* Eyes */}
            <div className={`flex ${eyeGap} ${eyeMb} justify-center`}>
              {[0, 1].map(e => (
                <div key={e} className={`${eyeDim} bg-white rounded-full flex items-center justify-center overflow-hidden`}
                  style={{ animation: `aiBlinkEye ${3.5 + e * 0.4}s ease-in-out infinite`, animationDelay: `${e * 0.12}s` }}>
                  <div className={`${pupilDim} bg-slate-900 rounded-full`} />
                  {size === "xl" && <div className="absolute top-1 right-1 w-1.5 h-1.5 bg-white/80 rounded-full" />}
                </div>
              ))}
            </div>
            {/* Mouth */}
            <div className="flex justify-center">
              {speaking
                ? <div className="bg-white rounded-full" style={{ width: `${mouthW}px`, animation: "aiMouthOpen 0.18s ease-in-out infinite alternate" }} />
                : <div className="bg-white rounded-full" style={{ width: `${mouthW}px`, height: size === "xl" ? "5px" : "3px" }} />
              }
            </div>
          </div>
        </div>
        {/* Sound bars (xl speaking) */}
        {speaking && size === "xl" && (
          <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 flex gap-1 items-end">
            {[6,10,15,9,13,11,7,14,8,12].map((h, i) => (
              <div key={i} className="w-1.5 bg-indigo-400/80 rounded-full animate-bounce"
                style={{ height: `${h}px`, animationDelay: `${i * 0.07}s`, animationDuration: "0.45s" }} />
            ))}
          </div>
        )}
      </div>
    );
  };

  //  CALL MODE UI 
  if (callMode) {
    return (
      <div className="fixed inset-0 z-50 flex flex-col"
        style={{ background: "radial-gradient(ellipse at 35% 25%, #1e1b4b 0%, #0f172a 45%, #020617 100%)" }}>
        <style>{cssAnimations}</style>

        {/* Top bar */}
        <div className="shrink-0 flex items-center justify-between px-5 py-3 bg-black/20 backdrop-blur-sm border-b border-white/5">
          <div className="flex items-center gap-2.5">
            <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="text-white text-sm font-semibold">AI Call</span>
          </div>
          <div className="flex items-center gap-3 text-xs">
            {speaking  && <span className="text-indigo-300 animate-pulse flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-indigo-400 inline-block" />Speaking</span>}
            {listening && <span className="text-red-300 animate-pulse flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400 inline-block" />Listening</span>}
            {callMuted && !listening && !speaking && !loading && <span className="text-slate-400 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-500 inline-block" />Muted</span>}
            {loading   && <span className="text-amber-300 flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-amber-400 inline-block" />Thinking–</span>}
          </div>
          <button onClick={endCall}
            className="text-slate-400 hover:text-white text-xs flex items-center gap-1.5 transition px-2 py-1 rounded-lg hover:bg-white/5">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            Exit
          </button>
        </div>

        {/* Main area */}
        <div className="flex-1 flex overflow-hidden">

          {/* Centre — avatar */}
          <div className="flex-1 flex flex-col items-center justify-center relative">
            {/* Bokeh ambient blobs */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              {[...Array(10)].map((_, i) => (
                <div key={i} className="absolute rounded-full blur-2xl animate-pulse"
                  style={{
                    width:  `${70 + (i * 43) % 130}px`,
                    height: `${70 + (i * 43) % 130}px`,
                    left:   `${(i * 19 + 7) % 88}%`,
                    top:    `${(i * 27 + 5) % 78}%`,
                    background: i % 3 === 0 ? "rgba(99,102,241,0.08)" : i % 3 === 1 ? "rgba(168,85,247,0.07)" : "rgba(59,130,246,0.06)",
                    animationDelay: `${i * 0.5}s`,
                    animationDuration: `${3 + i * 0.4}s`,
                  }} />
              ))}
            </div>

            <AiAvatar size="xl" />

            <div className="mt-10 text-center relative z-10">
              <p className="text-white text-lg font-semibold">AI Assistant</p>
              <p className="text-slate-400 text-sm mt-1">
                {speaking ? "Speaking • " : listening ? "Listening • " : loading ? "Thinking • " : callMuted ? "Muted  •  tap mic to unmute" : "Ready"}
              </p>
            </div>

            {/* User self-view bottom-right */}
            <div className="absolute bottom-6 right-6 w-20 h-20 rounded-2xl bg-slate-800 border border-slate-700/70 shadow-xl flex flex-col items-center justify-center gap-1"
              title="You — camera off">
              <span className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-sm font-bold text-white">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </span>
              <span className="text-slate-500 text-[9px]">You</span>
            </div>
          </div>

          {/* Chat side panel */}
          {showChatPanel && (
            <div className="w-72 bg-black/30 backdrop-blur-md border-l border-white/8 flex flex-col">
              <div className="shrink-0 px-4 py-3 border-b border-white/8 flex items-center justify-between">
                <p className="text-white text-sm font-semibold">Messages</p>
                <button onClick={() => setShowChatPanel(false)} className="text-slate-500 hover:text-white transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto px-3 py-3 space-y-2.5">
                {messages.filter(m => !m.content?.startsWith("[Confirmed action:")).map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[88%] px-3 py-2 rounded-xl text-xs leading-relaxed whitespace-pre-wrap ${
                      m.role === "user" ? "bg-indigo-600/70 text-white" : "bg-white/10 text-slate-200"
                    }`}>{m.content}</div>
                  </div>
                ))}
                {loading && (
                  <div className="flex gap-1 px-3 py-2 items-center">
                    {[0, 0.15, 0.3].map((d, i) => <div key={i} className="w-2 h-2 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />)}
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
              {pendingAction && !loading && (
                <div className="shrink-0 px-3 py-2.5 border-t border-white/8 flex flex-col gap-2">
                  {pendingAction.action === "send_outreach" && chatSenders.length === 0 && (
                    <div className="bg-amber-900/30 border border-amber-700/40 rounded-xl px-3 py-2.5 flex flex-col gap-1.5">
                      <p className="text-[10px] font-bold text-amber-300"> No sender configured — using platform default</p>
                      <p className="text-[10px] text-amber-200/70 leading-relaxed">
                        Emails will be sent from <span className="font-semibold">TimingoFlow</span>. You'll be <span className="font-semibold">CC'd and set as reply-to</span> at <span className="font-semibold">{user?.email}</span> so all replies come to you.
                      </p>
                      <p className="text-[10px] text-amber-200/50">
                        Want your own address? <span className="underline cursor-pointer text-indigo-400" onClick={handleCancel}>Configure a sender in Settings</span> – Need help? Contact <span className="text-indigo-400">team@timingoflow.com</span>
                      </p>
                    </div>
                  )}
                  {pendingAction.action === "send_outreach" && chatSenders.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 items-center">
                      <span className="text-[10px] text-slate-400 shrink-0">From:</span>
                      <button onClick={() => setSelectedSenderId(null)}
                        className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition ${!selectedSenderId ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
                         Auto
                      </button>
                      {chatSenders.map(s => (
                        <button key={s.id} onClick={() => setSelectedSenderId(s.id)}
                          className={`px-2 py-0.5 rounded-lg text-[10px] font-bold border transition ${selectedSenderId === s.id ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400"}`}>
                          {s.from_email}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button onClick={handleConfirm} className="flex-1 py-2 bg-emerald-600 text-white text-xs font-bold rounded-lg hover:bg-emerald-500 transition">Confirm</button>
                    <button onClick={handleCancel}  className="flex-1 py-2 bg-slate-700 text-slate-300 text-xs font-bold rounded-lg hover:bg-slate-600 transition">Cancel</button>
                  </div>
                </div>
              )}
              <div className="shrink-0 px-3 py-2.5 border-t border-white/8 flex gap-2">
                <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                  placeholder="Type here–" disabled={loading}
                  className="flex-1 bg-white/8 text-white text-xs placeholder-slate-500 rounded-lg px-3 py-2 outline-none border border-white/10 focus:border-indigo-500/40" />
                <button onClick={() => input.trim() && sendMessage(input.trim())} disabled={loading || !input.trim()}
                  className="px-3 py-2 bg-indigo-600 rounded-lg text-white disabled:opacity-40 transition">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Last AI message caption */}
        {lastAiMessage && !showChatPanel && (
          <div className="shrink-0 px-6 py-3 flex justify-center">
            <div className="bg-black/55 backdrop-blur-sm text-white/85 text-sm text-center px-6 py-2.5 rounded-2xl max-w-xl">
              {(lastAiMessage.replace(/\*+/g, "").trim().length > 180 ? lastAiMessage.replace(/\*+/g, "").trim().slice(0, 180) + "–" : lastAiMessage.replace(/\*+/g, "").trim())}
            </div>
          </div>
        )}

        {/* Call controls */}
        <div className="shrink-0 py-5 bg-black/25 backdrop-blur-sm border-t border-white/5 flex items-center justify-center gap-4">
          {/* AI voice toggle */}
          <button onClick={() => { setVoiceEnabled(v => !v); window.speechSynthesis?.cancel(); }}
            title={voiceEnabled ? "Mute AI voice" : "Unmute AI voice"}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition ${voiceEnabled ? "bg-slate-700/80 text-white hover:bg-slate-600" : "bg-slate-800/80 text-slate-500 hover:bg-slate-700"}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
          </button>
          {/* Mic — toggles mute in call mode */}
          <button onClick={() => {
            if (callMuted) {
              setCallMuted(false);
            } else {
              setCallMuted(true);
              callRecRef.current?.stop();
              callListeningRef.current = false;
              clearTimeout(silenceTimerRef.current);
              setListening(false);
            }
          }} title={callMuted ? "Unmute mic" : listening ? "Listening–" : "Mute mic"}
            className={`w-12 h-12 rounded-full flex items-center justify-center transition ${callMuted ? "bg-slate-800/80 text-slate-500 hover:bg-slate-700" : listening ? "bg-red-600/80 text-white animate-pulse shadow-lg shadow-red-600/30" : "bg-slate-700/80 text-white hover:bg-slate-600"}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
          </button>
          {/* End call */}
          <button onClick={endCall}
            title="End call"
            className="w-16 h-16 rounded-full bg-red-600 text-white flex items-center justify-center hover:bg-red-500 transition shadow-xl shadow-red-600/25 active:scale-95">
            <svg className="w-6 h-6 rotate-[135deg]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24c1.12.37 2.33.57 3.58.57a1 1 0 011 1V20a1 1 0 01-1 1C9.61 21 3 14.39 3 6a1 1 0 011-1h3.5a1 1 0 011 1c0 1.25.2 2.46.57 3.58a1 1 0 01-.25 1.01l-2.2 2.2z"/>
            </svg>
          </button>
          {/* Chat panel toggle */}
          <button onClick={() => setShowChatPanel(v => !v)} title="Toggle chat"
            className={`w-12 h-12 rounded-full flex items-center justify-center transition ${showChatPanel ? "bg-indigo-600 text-white" : "bg-slate-700/80 text-white hover:bg-slate-600"}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
          </button>
          {/* Type shortcut */}
          <button onClick={endCall} title="Back to chat"
            className="w-12 h-12 rounded-full bg-slate-700/80 text-slate-300 flex items-center justify-center hover:bg-slate-600 transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
          </button>
        </div>
      </div>
    );
  }

  //  REGULAR CHAT MODE 
  return (
    <div className="flex h-full overflow-hidden">
      <style>{cssAnimations}</style>

      {/* Sessions sidebar */}
      <div className={`${showSidebar ? 'flex' : 'hidden'} sm:flex w-36 sm:w-52 shrink-0 border-r border-slate-800/60 bg-slate-900/40 flex-col`}>
        {/* New Chat */}
        <div className="shrink-0 p-3 border-b border-slate-800/50">
          <button onClick={createNewChat}
            className="w-full flex items-center gap-2 px-3 py-2 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl transition shadow-lg shadow-indigo-600/20 active:scale-95">
            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
            New Chat
          </button>
          {sessionLimitError && (
            <p className="text-red-400 text-[10px] mt-1.5 px-1 leading-snug">{sessionLimitError}</p>
          )}
        </div>

        {/* Session list */}
        <div className="flex-1 overflow-auto py-1.5 space-y-0.5 px-2">
          {!sessionsLoaded ? (
            <div className="py-6 text-center text-slate-600 text-xs">Loading–</div>
          ) : sessions.length === 0 ? (
            <div className="py-6 text-center text-slate-600 text-xs">No chats yet</div>
          ) : sessions.map(s => (
            <div key={s.id} onClick={() => switchSession(s.id)}
              className={`group flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition ${
                s.id === currentSessionId
                  ? "bg-indigo-600/20 border border-indigo-500/30 text-white"
                  : "text-slate-400 hover:bg-slate-800/50 hover:text-slate-200 border border-transparent"
              }`}>
              <svg className="w-3 h-3 shrink-0 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
              {editingSessionId === s.id ? (
                <input
                  autoFocus
                  value={editingTitle}
                  onChange={e => setEditingTitle(e.target.value)}
                  onBlur={() => saveSessionTitle(s.id)}
                  onKeyDown={e => { e.stopPropagation(); if (e.key === "Enter") saveSessionTitle(s.id); if (e.key === "Escape") setEditingSessionId(null); }}
                  onClick={e => e.stopPropagation()}
                  className="flex-1 bg-slate-700 text-white text-[11px] px-1.5 py-0.5 rounded outline-none border border-indigo-500/60 min-w-0"
                />
              ) : (
                <span className="flex-1 text-[11px] truncate">{s.title}</span>
              )}
              {editingSessionId !== s.id && (
                <>
                  <button onClick={(e) => { e.stopPropagation(); setEditingSessionId(s.id); setEditingTitle(s.title); }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-indigo-400 shrink-0 transition"
                    title="Rename chat">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                  </button>
                  <button onClick={(e) => deleteSession(s.id, e)}
                    className="opacity-0 group-hover:opacity-100 p-0.5 rounded hover:text-red-400 shrink-0 transition"
                    title="Delete chat">
                    {deletingSession === s.id
                      ? <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75"/></svg>
                      : <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    }
                  </button>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Chat count */}
        <div className="shrink-0 px-3 py-2 border-t border-slate-800/50 text-center">
          <span className="text-[9px] text-slate-600">{sessions.length} chat{sessions.length !== 1 ? "s" : ""}</span>
        </div>
      </div>

      {/* Main chat panel */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* Header */}
        <div className="shrink-0 px-5 py-3.5 border-b border-slate-800/50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => setShowSidebar(v => !v)} className="sm:hidden p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition" title="Toggle chats">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" /></svg>
            </button>
            <AiAvatar size="sm" />
            <div className="min-w-0">
              <h1 className="text-sm font-bold text-white flex items-center gap-2">
                AI Assistant
                {speaking  && <span className="text-[10px] font-normal text-indigo-400 animate-pulse">Speaking–</span>}
                {listening && <span className="text-[10px] font-normal text-red-400 animate-pulse">Listening–</span>}
              </h1>
              <p className="hidden sm:block text-[10px] text-slate-500">Ask anything or command actions on your behalf</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Start Call */}
            <button onClick={() => { recognitionRef.current?.stop(); clearTimeout(silenceTimerRef.current); setListening(false); setCallMuted(false); setCallMode(true); }}
              className="px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white transition flex items-center gap-1 sm:gap-1.5 shadow-lg shadow-indigo-600/20 active:scale-95">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              <span className="hidden sm:inline">Start Call</span>
            </button>
            {/* Voice toggle */}
            <button onClick={() => { setVoiceEnabled(v => !v); if (speaking) window.speechSynthesis?.cancel(); }}
              className={`px-2 sm:px-3 py-1.5 rounded-lg text-xs font-semibold transition flex items-center gap-1 sm:gap-1.5 ${voiceEnabled ? "bg-indigo-600/20 text-indigo-400 border border-indigo-500/30" : "bg-slate-800 text-slate-500 border border-slate-700"}`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" /></svg>
              <span className="hidden sm:inline">{voiceEnabled ? "Voice On" : "Voice Off"}</span>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div ref={messagesContainerRef} className="flex-1 overflow-auto px-5 py-4">
          {messagesLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex gap-2 items-center text-slate-500 text-sm">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75"/></svg>
                Loading messages–
              </div>
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full max-w-xl mx-auto text-center">
              <AiAvatar size="lg" />
              <h2 className="text-xl font-bold text-white mt-5 mb-1">
                Hi{user?.name ? `, ${user.name.split(" ")[0]}` : ""}!
              </h2>
              <p className="text-slate-400 text-sm mb-6 max-w-xs">Generate leads, manage folders, send outreach, check stats — just ask!</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 w-full">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s.text)}
                    className="flex items-center gap-2.5 px-3.5 py-2.5 bg-slate-800/60 border border-slate-700/50 rounded-xl text-left hover:bg-slate-800 hover:border-slate-600 hover:text-white transition group">
                    <span className="text-base shrink-0">{s.icon}</span>
                    <span className="text-xs text-slate-300 group-hover:text-white">{s.text}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto space-y-4">
              {messages.filter(m => !m.content?.startsWith("[Confirmed action:")).map((m, i) => (
                <div key={i} className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && <AiAvatar size="sm" />}
                  <div className={`max-w-[76%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-indigo-600 text-white rounded-br-sm whitespace-pre-wrap"
                      : "bg-slate-800/80 text-slate-200 rounded-bl-sm border border-slate-700/50"
                  }`}>
                    {m.role === "assistant" ? renderMarkdown(m.content) : m.content}
                    {m._streaming && <span className="inline-block w-0.5 h-4 bg-indigo-400 animate-pulse ml-0.5 align-middle" />}
                    {m.role === "assistant" && m.actionType && (
                      <div className="mt-2 pt-2 border-t border-slate-700/50 flex items-center gap-2">
                        <div className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shrink-0" />
                        <p className="text-xs text-amber-400 font-bold uppercase tracking-wide">Action: {m.actionType.replace(/_/g, " ")}</p>
                      </div>
                    )}
                  </div>
                  {m.role === "user" && (
                    <div className="w-8 h-8 rounded-full bg-slate-700 shrink-0 flex items-center justify-center text-xs font-bold text-white">
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                </div>
              ))}

              {/* Step execution */}
              {showSteps && executingSteps.length > 0 && (
                <div className="flex items-end gap-2 justify-start">
                  <AiAvatar size="sm" />
                  <div className="bg-slate-800/80 border border-slate-700/50 rounded-2xl rounded-bl-sm px-4 py-3 max-w-[76%]">
                    <p className="text-xs font-bold text-indigo-400 uppercase tracking-wide mb-2 flex items-center gap-2">
                      {!executingDone
                        ? <svg className="w-3.5 h-3.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
                        : <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      }
                      {executingDone ? "Completed" : "Executing"}
                    </p>
                    <div className="space-y-1.5">
                      {executingSteps.map((step, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          {step.status === "done"
                            ? <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            : <svg className="w-4 h-4 text-red-400 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                          }
                          <span className={step.status === "error" ? "text-red-400" : "text-slate-300"}>{step.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Confirm / Cancel */}
              {pendingAction && !loading && (
                <div className="flex flex-col gap-2 pl-10">
                  {pendingAction.action === "send_outreach" && chatSenders.length === 0 && (
                    <div className="bg-amber-900/25 border border-amber-700/35 rounded-xl px-4 py-3 flex flex-col gap-1.5 max-w-sm">
                      <p className="text-xs font-bold text-amber-300"> Default sender active</p>
                      <p className="text-xs text-amber-200/70 leading-relaxed">
                        Emails will be sent from <span className="font-semibold">TimingoFlow</span>. Any replies will go directly to <span className="font-semibold">{user?.email}</span> so you won't miss them.
                      </p>
                      <p className="text-[11px] text-amber-200/50">Want to send from your own email instead? Go to  Settings to configure a sender, then come back.<br />Or contact <span className="font-semibold">team@timingoflow.com</span> for help.</p>
                    </div>
                  )}
                  {pendingAction.action === "send_outreach" && chatSenders.length > 0 && (
                    <div className="flex flex-col gap-1.5">
                      <p className="text-[11px] text-slate-400 font-semibold">Send from:</p>
                      <div className="flex flex-wrap gap-1.5">
                        <button onClick={() => setSelectedSenderId(null)}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${!selectedSenderId ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                           Auto (rotate)
                        </button>
                        {chatSenders.map(s => (
                          <button key={s.id} onClick={() => setSelectedSenderId(s.id)}
                            className={`px-2.5 py-1 rounded-lg text-[10px] font-bold border transition ${selectedSenderId === s.id ? "bg-indigo-700 border-indigo-500 text-white" : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500"}`}>
                            {s.from_email}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3">
                    <button onClick={handleConfirm} className="px-5 py-2.5 bg-emerald-600 text-white text-sm font-bold rounded-xl hover:bg-emerald-500 transition flex items-center gap-2 shadow-lg shadow-emerald-600/20">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                      Confirm
                    </button>
                    <button onClick={handleCancel} className="px-5 py-2.5 bg-slate-700 text-slate-300 text-sm font-bold rounded-xl hover:bg-slate-600 transition">Cancel</button>
                  </div>
                  {(pendingAction.action === "send_outreach" || (pendingAction.next_actions && pendingAction.next_actions.some(a => a.action === "send_outreach"))) && (
                    <p className="text-[11px] text-slate-500 flex items-center gap-1.5 max-w-sm">
                      <span></span>
                      Note: emails are sent with 43–100s intervals between each to protect your domain from spam filters.
                    </p>
                  )}
                </div>
              )}

              {/* Loading */}
              {loading && (
                <div className="flex items-end gap-2 justify-start">
                  <AiAvatar size="sm" />
                  <div className="bg-slate-800/80 border border-slate-700/50 px-5 py-3.5 rounded-2xl rounded-bl-sm flex gap-2 items-center">
                    {[0, 0.15, 0.3].map((d, i) => <div key={i} className="w-2.5 h-2.5 bg-indigo-400 rounded-full animate-bounce" style={{ animationDelay: `${d}s` }} />)}
                    <span className="text-xs text-slate-500 ml-1">Thinking–</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input bar */}
        <div className="shrink-0 px-5 py-4 border-t border-slate-800/50">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center gap-2.5 bg-slate-800/80 border border-slate-700/50 rounded-2xl px-4 py-2 focus-within:border-indigo-500/50 transition">
              <button onClick={toggleVoice}
                className={`p-2 rounded-xl transition shrink-0 ${listening ? "bg-red-600 text-white animate-pulse shadow-lg shadow-red-600/30" : "text-slate-400 hover:text-white hover:bg-slate-700"}`}
                title={listening ? "Stop" : "Speak"}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>
              </button>
              <input ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown}
                placeholder={listening ? " Listening • " : "Type a message or click mic to speak • "}
                className="flex-1 bg-transparent text-sm text-white placeholder-slate-500 outline-none py-2" disabled={loading} />
              <button onClick={() => input.trim() && sendMessage(input.trim())} disabled={loading || !input.trim()}
                className="p-2.5 rounded-xl bg-indigo-600 text-white hover:bg-indigo-500 disabled:bg-slate-700 disabled:text-slate-500 transition shrink-0 shadow-lg shadow-indigo-600/20 disabled:shadow-none">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
              </button>
            </div>
            <p className="text-[10px] text-slate-600 text-center mt-2">AI may make mistakes. Always verify important actions.</p>
          </div>
        </div>

      </div>
    </div>
  );
}

function AppShell() {

  const { user, authFetch } = useAuth();
  const { theme } = useTheme();

  const location = useLocation();
  const navigate = useNavigate();
  const validPages = ["dashboard", "search", "saved", "pipeline", "history", "analytics", "reminders", "outreach", "followups", "drip", "leaderboard", "tracking", "linkanalytics", "siteactivity", "organization", "campaign", "assistant", "settings"];
  const pathPage = location.pathname.replace("/app/", "").replace("/app", "") || "dashboard";
  const page = validPages.includes(pathPage) ? pathPage : "dashboard";
  const setPage = useCallback((p) => navigate("/app/" + p), [navigate]);

  const [rerunQuery, setRerunQuery] = useState(null);

  const [userLimits, setUserLimits] = useState(null);

  const [saved, setSaved] = useState([]);
  const [leadsLoaded, setLeadsLoaded] = useState(false);
  const [folders, setFolders] = useState([]);

  // Load user limits
  const fetchLimits = useCallback(async () => {
    try {
      const resp = await authFetch("/user/limits");
      if (resp.ok) setUserLimits(await resp.json());
    } catch (e) { console.error("Failed to load user limits:", e); }
  }, [authFetch]);

  // Load folders from DB
  const fetchFolders = useCallback(async () => {
    try {
      const resp = await authFetch("/folders");
      if (resp.ok) setFolders(await resp.json());
    } catch (e) { console.error("Failed to load folders:", e); }
  }, [authFetch]);

  // Load saved leads from DB on mount
  const fetchLeads = useCallback(async () => {
    try {
      const resp = await authFetch("/leads");
      if (resp.ok) {
        const data = await resp.json();
        setSaved(data);
      }
    } catch (e) { console.error("Failed to load leads:", e); }
    finally { setLeadsLoaded(true); }
  }, [authFetch]);

  useEffect(() => { fetchLeads(); fetchFolders(); fetchLimits(); }, [fetchLeads, fetchFolders, fetchLimits]);

  // Refresh leads when compatibility scores are updated
  useEffect(() => {
    const handler = () => fetchLeads();
    window.addEventListener("refreshLeads", handler);
    return () => window.removeEventListener("refreshLeads", handler);
  }, [fetchLeads]);

  // Folder CRUD
  const createFolder = useCallback(async (name, color = "#6366f1") => {
    try {
      const resp = await authFetch("/folders", { method: "POST", body: JSON.stringify({ name, color }) });
      if (resp.ok) { const data = await resp.json(); const f = { id: data.id, name, color, created_at: data.created_at, user_email: "" }; setFolders(p => [...p, f]); return f; }
    } catch (e) { console.error("Create folder error:", e); }
    return null;
  }, [authFetch]);

  const updateFolder = useCallback(async (id, updates) => {
    setFolders(p => p.map(f => f.id === id ? { ...f, ...updates } : f));
    try { await authFetch(`/folders/${id}`, { method: "PATCH", body: JSON.stringify(updates) }); }
    catch (e) { console.error("Update folder error:", e); fetchFolders(); }
  }, [authFetch, fetchFolders]);

  const deleteFolder = useCallback(async (id, action = "unfiled", targetFolderId = null) => {
    setFolders(p => p.filter(f => f.id !== id));
    if (action === "delete_leads") {
      setSaved(p => p.filter(l => l.folder_id !== id));
    } else if (action === "move" && targetFolderId) {
      setSaved(p => p.map(l => l.folder_id === id ? { ...l, folder_id: targetFolderId } : l));
    } else {
      setSaved(p => p.map(l => l.folder_id === id ? { ...l, folder_id: null } : l));
    }
    const params = new URLSearchParams({ action });
    if (targetFolderId) params.append("target_folder_id", targetFolderId);
    try { await authFetch(`/folders/${id}?${params}`, { method: "DELETE" }); }
    catch (e) { console.error("Delete folder error:", e); fetchFolders(); fetchLeads(); }
  }, [authFetch, fetchFolders, fetchLeads]);

  // Save a single lead to DB (with optional folder)
  const saveLead = useCallback(async (lead, folderId = null) => {
    try {
      const resp = await authFetch("/leads", {
        method: "POST",
        body: JSON.stringify({
          name: lead.name, phone: lead.phone || null, email: lead.email || null,
          address: lead.address || null, website: lead.website || null,
          rating: lead.rating || null, reviews: lead.reviews || null,
          category: lead.category || null, status: lead.status || "new",
          notes: lead.notes || "", detected_issues: lead.detected_issues || [],
          folder_id: folderId,
        }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setSaved((p) => [{ ...lead, id: data.id, status: lead.status || "new", saved_at: data.saved_at, notes: lead.notes || "", folder_id: folderId }, ...p]);
        return true;
      }
    } catch (e) { console.error("Save lead error:", e); }
    return false;
  }, [authFetch]);

  // Bulk save leads to DB
  const bulkSaveLeads = useCallback(async (leads, folderId = null) => {
    try {
      const resp = await authFetch("/leads/bulk", {
        method: "POST",
        body: JSON.stringify({
          leads: leads.map((l) => ({
            name: l.name, phone: l.phone || null, email: l.email || null,
            address: l.address || null, website: l.website || null,
            rating: l.rating || null, reviews: l.reviews || null,
            category: l.category || null, status: "new", notes: "",
            detected_issues: l.detected_issues || [],
            folder_id: folderId,
          })),
        }),
      });
      if (resp.ok) {
        await fetchLeads(); // Refresh from DB
        return (await resp.json()).saved;
      }
    } catch (e) { console.error("Bulk save error:", e); }
    return 0;
  }, [authFetch, fetchLeads]);

  // Update a single lead (status or notes)
  const updateLead = useCallback(async (id, updates) => {
    // Optimistic update
    setSaved((p) => p.map((l) => l.id === id ? { ...l, ...updates } : l));
    try {
      await authFetch(`/leads/${id}`, { method: "PATCH", body: JSON.stringify(updates) });
    } catch (e) { console.error("Update lead error:", e); fetchLeads(); }
  }, [authFetch, fetchLeads]);

  // Bulk update status
  const bulkUpdateLeads = useCallback(async (ids, status) => {
    setSaved((p) => p.map((l) => ids.includes(l.id) ? { ...l, status } : l));
    try {
      await authFetch("/leads/bulk-update", { method: "POST", body: JSON.stringify({ ids, status }) });
    } catch (e) { console.error("Bulk update error:", e); fetchLeads(); }
  }, [authFetch, fetchLeads]);

  // Delete a single lead
  const deleteLead = useCallback(async (id) => {
    setSaved((p) => p.filter((l) => l.id !== id));
    try {
      await authFetch(`/leads/${id}`, { method: "DELETE" });
    } catch (e) { console.error("Delete lead error:", e); fetchLeads(); }
  }, [authFetch, fetchLeads]);

  // Bulk delete
  const bulkDeleteLeads = useCallback(async (ids) => {
    const idSet = new Set(ids);
    setSaved((p) => p.filter((l) => !idSet.has(l.id)));
    try {
      await authFetch("/leads/bulk-delete", { method: "POST", body: JSON.stringify({ ids }) });
    } catch (e) { console.error("Bulk delete error:", e); fetchLeads(); }
  }, [authFetch, fetchLeads]);

  // Bulk move leads to folder
  const bulkMoveLeads = useCallback(async (ids, folderId) => {
    setSaved(p => p.map(l => ids.includes(l.id) ? { ...l, folder_id: folderId } : l));
    try {
      await authFetch("/leads/bulk-move", { method: "POST", body: JSON.stringify({ ids, folder_id: folderId }) });
    } catch (e) { console.error("Bulk move error:", e); fetchLeads(); }
  }, [authFetch, fetchLeads]);


  const handleRerun = useCallback((cat, loc, max) => {
    setRerunQuery({ category: cat, location: loc, max: max || 20 });
    setPage("search");
  }, []);

  const pageMap = {
    dashboard: <DashboardPage />,
    search:    <SearchPage saved={saved} saveLead={saveLead} bulkSaveLeads={bulkSaveLeads} folders={folders} createFolder={createFolder} rerunQuery={rerunQuery} clearRerun={() => setRerunQuery(null)} userLimits={userLimits} refreshLimits={fetchLimits} />,
    saved:     <SavedPage  saved={saved} updateLead={updateLead} deleteLead={deleteLead} bulkUpdateLeads={bulkUpdateLeads} bulkDeleteLeads={bulkDeleteLeads} folders={folders} createFolder={createFolder} updateFolder={updateFolder} deleteFolder={deleteFolder} bulkMoveLeads={bulkMoveLeads} userLimits={userLimits} leadsLoaded={leadsLoaded} />,
    pipeline:  <PipelinePage saved={saved} updateLead={updateLead} leadsLoaded={leadsLoaded} />,
    outreach:  <OutreachPage folders={folders} refreshFolders={fetchFolders} />,
    followups: <FollowupsPage folders={folders} onOpenOutreach={() => setPage("outreach")} />,
    drip:      <DripSequencesPage userLimits={userLimits} />,
    leaderboard: <LeadLeaderboardPage folders={folders} />,
    tracking:  <EmailTrackingPage />,
    linkanalytics: <LinkAnalyticsPage />,
    siteactivity: <WebsiteActivityPage />,
    organization: <OrganizationPage />,
    campaign:  <CampaignSettingsPage />,
    linkedin:  <LinkedInOutreachPage />,
    history:   <HistoryPage onRerun={handleRerun} />,
    analytics: <AnalyticsPage saved={saved} leadsLoaded={leadsLoaded} />,
    reminders: <RemindersPage userLimits={userLimits} />,
    settings:  <SettingsPage />,
    assistant: userLimits?.limits?.ai_chatbot_enabled !== false ? <AIChatPage /> : <div className="flex items-center justify-center h-full text-slate-500">AI Assistant is not enabled for your account. Contact your admin.</div>,
  };





  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (


    <div className={`flex h-screen ${theme === 'dark' ? 'bg-slate-950 text-white' : 'bg-gray-100 text-gray-900'} overflow-hidden`}>


      <Sidebar page={page} setPage={setPage} open={sidebarOpen} onClose={() => setSidebarOpen(false)} />


      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile top bar */}
        {/* Mobile top bar */}
        <header className={`md:hidden flex items-center gap-3 px-4 py-2.5 border-b shrink-0 ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          <button onClick={() => setSidebarOpen(true)} className={`p-2 rounded-lg ${theme === 'dark' ? 'text-slate-400 hover:text-white hover:bg-slate-800' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <div className="w-7 h-7 rounded-lg bg-indigo-600 flex items-center justify-center font-bold text-xs shadow-lg shadow-indigo-900/50 shrink-0">T</div>
          <span className={`font-bold text-sm ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>TimingoFlow</span>
          <div className="ml-auto flex items-center gap-1">
            {/* Quick page label */}
            <span className={`text-xs px-2 py-1 rounded-md ${theme === 'dark' ? 'bg-slate-800 text-slate-400' : 'bg-gray-100 text-gray-500'}`}>
              {[...Object.values({dashboard:"Dashboard",search:"Search",saved:"Leads",pipeline:"Pipeline",outreach:"Outreach",followups:"Follow-ups",drip:"Drip",leaderboard:"Leaderboard",tracking:"Tracking",linkanalytics:"Links",siteactivity:"Site",organization:"Orgs",campaign:"Campaign",history:"History",analytics:"Analytics",reminders:"Reminders",settings:"Settings",assistant:"AI"})].find((_, i) => Object.keys({dashboard:0,search:1,saved:2,pipeline:3,outreach:4,followups:5,drip:6,leaderboard:7,tracking:8,linkanalytics:9,siteactivity:10,organization:11,campaign:12,history:13,analytics:14,reminders:15,settings:16,assistant:17})[i] === page) || page}
            </span>
          </div>
        </header>

        <div className="flex-1 overflow-auto pb-16 md:pb-0">
          {pageMap[page] || pageMap.search}
        </div>

        {/* Mobile bottom navigation — quick access to most used pages */}
        <nav className={`md:hidden fixed bottom-0 left-0 right-0 z-30 border-t flex items-center justify-around px-2 py-1.5 ${theme === 'dark' ? 'bg-slate-900 border-slate-800' : 'bg-white border-gray-200'}`}>
          {[
            { id: "dashboard", icon: "D", label: "Home" },
            { id: "search", icon: "S", label: "Search" },
            { id: "saved", icon: "L", label: "Leads" },
            { id: "outreach", icon: "O", label: "Outreach" },
            { id: "followups", icon: "F", label: "Follow-ups" },
          ].map(item => (
            <button key={item.id} onClick={() => setPage(item.id)}
              className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-xl min-w-[56px] transition ${page === item.id ? "text-indigo-400" : theme === 'dark' ? "text-slate-500" : "text-gray-400"}`}>
              <span className="text-xl leading-none">{item.icon}</span>
              <span className="text-[10px] font-medium">{item.label}</span>
            </button>
          ))}
          <button onClick={() => setSidebarOpen(true)}
            className={`flex flex-col items-center gap-0.5 py-1 px-2 rounded-xl min-w-[56px] transition ${theme === 'dark' ? "text-slate-500" : "text-gray-400"}`}>
            <span className="text-xl leading-none">+</span>
            <span className="text-[10px] font-medium">More</span>
          </button>
        </nav>
      </div>

    </div>


  );


}





// --- Root ---------------------------------------------------------------------


export default function App() {


  return (


    <ThemeProvider>
      <AuthProvider>


        <BrowserRouter>


          <Routes>


            {/* Public marketing pages */}


            <Route path="/" element={<HomePage />} />


            <Route path="/pricing" element={<PricingPage />} />


            <Route path="/demo" element={<DemoBookingPage />} />


            <Route path="/about" element={<AboutPage />} />


            <Route path="/contact" element={<ContactPage />} />

            <Route path="/features" element={<FeaturesPage />} />

            <Route path="/privacy" element={<PrivacyPolicyPage />} />

            <Route path="/terms" element={<TermsPage />} />

            <Route path="/refund-policy" element={<RefundPolicyPage />} />

            <Route path="/security" element={<SecurityPage />} />


            


            {/* Auth pages */}


            <Route path="/landing"  element={<LandingPage />} />


            <Route path="/login"    element={<LoginPage />} />


            <Route path="/signup"   element={<SignupPage />} />


            <Route path="/pending"  element={<PendingPage />} />


          <Route path="/rejected" element={<RejectedPage />} />





          {/* Admin */}


          <Route path="/admin" element={
            <RequireAuth><RequireAdmin><AdminPage /></RequireAdmin></RequireAuth>
          } />
          <Route path="/intelligence" element={
            <RequireAuth><RequireAdmin><EnterpriseAdminPage /></RequireAdmin></RequireAuth>
          } />
          <Route path="/billing" element={
            <RequireAuth><RequireAdmin><BillingPage /></RequireAdmin></RequireAuth>


          } />





          {/* Authenticated app ... default landing for logged-in users */}


          <Route path="/app/*" element={


            <RequireAuth>


              <AppShell />


            </RequireAuth>


          } />


        </Routes>


      </BrowserRouter>


    </AuthProvider>
    </ThemeProvider>


  );


}
